import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { initializeApp } from 'firebase/app';
import { getAuth, onAuthStateChanged, createUserWithEmailAndPassword, signInWithEmailAndPassword, signOut, sendEmailVerification, updatePassword, EmailAuthProvider, reauthenticateWithCredential, deleteUser, sendPasswordResetEmail } from 'firebase/auth';
import { getFirestore, collection, addDoc, onSnapshot, doc, updateDoc, query, where, serverTimestamp, setDoc, writeBatch, orderBy, runTransaction, getDoc, getDocs, deleteDoc } from 'firebase/firestore';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { 
    Briefcase, CircleUserRound, PlusCircle, X, CheckCircle, Loader2, ShoppingCart, Activity, Store,
    Search, Edit, Trash2, ImagePlus, ArrowLeft, MapPin, Compass, Globe, Clock, Phone, Mail, Package, 
    Wrench, ChevronRight, Award, ShieldCheck, Diamond, UploadCloud, Sparkles, Send, Languages, Home, Ticket, Calendar,
    LogOut, User, Eye, EyeOff, MessageSquare, Star, Coins, CreditCard, DollarSign, Shield, Users, 
    AlertCircle, UserCheck, UserX, Bell, Moon, Sun, Camera, RefreshCw // Added RefreshCw (for switch camera)
} from 'lucide-react';
import { Bar, Pie, Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';

// --- Firebase Configuration ---
// IMPORTANT: To make this app fully functional, you MUST replace this with your own Firebase project configuration.
const firebaseConfig = {
  apiKey: "AIzaSyBXS_1LPuZzNxDe4Y4xETc9zqxfy076sHs",
  authDomain: "gighub-236f6.firebaseapp.com",
  projectId: "gighub-236f6",
  storageBucket: "gighub-236f6.firebasestorage.app",
  messagingSenderId: "129752654224",
  appId: "1:129752654224:web:1d24c0ea0905b78ec69315",
  measurementId: "G-60LC839C10"
};
const appId = firebaseConfig.projectId; 
const ADMIN_EMAIL = 'eazynoble3@gmail.com';

// --- Internationalization (i18n) & Data ---
const translations = {
    en: { welcomeMessage: "Welcome to the Marketplace", tagline: "Find local goods, services, and gigs", marketplace: "Marketplace", myStore: "My Store", profile: "Profile", postGig: "Post a Gig", goods: "Shop for Goods", services: "Book a Service", gigs: "Find a Gig", explore: "Explore", searchFor: "Search for", language: "Language", myGigs: "My Gigs", chats: "Chats", goodsDesc: "Browse and buy products from various stores.", servicesDesc: "Find and book services offered by local professionals.", gigsDesc: "Take on one-time tasks and get paid.", welcomeBack: "Welcome Back!", createAccount: "Create Account", "auth.signInContinue": "Sign in to continue", "auth.signUpContinue": "Get started with a new account", "auth.firstName": "First Name", "auth.lastName": "Last Name", "auth.profileName": "Shop / Profile Name", "auth.profileNamePlaceholder": "Your public name on GigNile", "auth.country": "Country", "auth.city": "City", "auth.selectCity": "Select a city", "auth.otherCity": "Other (Please specify)", "auth.newCity": "New City", "auth.newCityPlaceholder": "Enter your city name", "auth.location": "Your Location (Mandatory)", "auth.locationFetching": "Fetching your location... (Requires permission)", "auth.locationNote": "Note: Location is auto-detected. You can edit this precise pin placement after registration.", "auth.phone": "Phone Number", "auth.whatsapp": "This is my WhatsApp number", "auth.gender": "Gender", "auth.dob": "Date of Birth", "auth.signUpDelivery": "Sign up for delivery & gig notifications", "auth.email": "Email Address", "auth.password": "Password", "auth.forgot": "Forgot?", "auth.confirmPassword": "Confirm Password", "auth.login": "Login", "auth.register": "Register", "auth.noAccount": "Don't have an account?", "auth.haveAccount": "Already have an account?", "auth.gender.male": "Male", "auth.gender.female": "Female", "auth.gender.other": "Other", "auth.error.invalidCredential": "Invalid email or password. Please check your credentials.", "auth.error.emailInUse": "This email is already registered. Try logging in.", "auth.error.weakPassword": "Password is too weak. It must be at least 6 characters long.", "auth.error.default": "Authentication failed. Please check your network or try again.", "auth.error.passwordsNoMatch": "Passwords do not match.", "auth.error.marketRequired": "Market name is required.", "auth.error.regionRequired": "Please enter the name of your new region.", "auth.error.idRequired": "Please upload your ID document (Residence Permit or Passport).", "auth.error.licenseRequired": "Please upload your driver's license to sign up for deliveries.", "auth.error.locationRequired": "Store Location is mandatory. Please wait for it to fetch or click to set it on the map.", "auth.success.registration": "Registration successful! Please check your email to verify your account. Your account is now pending admin approval.", "auth.error.operationNotAllowed": "Email/Password sign-in is not enabled. Please enable it in your Firebase project's Authentication -> Sign-in method tab.", "auth.doc.id": "Residence Permit or Passport", "auth.doc.license": "Driver's License", "auth.profilePicture": "Profile Picture (Optional)", "city.casablanca": "Casablanca", "city.rabat": "Rabat", "city.tangier": "Tangier", "city.lagos": "Lagos", "city.accra": "Accra", "uploader.uploading": "Uploading...", "uploader.acceptedTypes": "Image or PDF accepted", "uploader.error": "Upload failed. Please try again.", "forgot.title": "Reset Password", "forgot.desc": "Enter your email address and we'll send you a link to reset your password.", "forgot.error.emailRequired": "Please enter your email address.", "forgot.success": "Password reset link sent! Please check your email inbox (and spam folder).", "forgot.sendLink": "Send Reset Link", "profile.dashboardTitle": "My Activity Dashboard", "profile.gigsPosted": "Total Gigs Posted", "profile.gigsCompleted": "Gigs Completed (as Worker)", "profile.productsPosted": "Products/Services Posted", "profile.ordersPlaced": "Orders Placed", "profile.totalSpent": "Total Spent", "profile.totalGigEarnings": "Total Gig Earnings", "profile.tier.novice": "Novice", "profile.tier.silver": "Silver", "profile.tier.gold": "Gold", "profile.tier.platinum": "Platinum", "profile.seller": "Seller", "profile.myOrders": "My Orders & Bookings", "profile.editProfile": "Edit Profile", "profile.changePassword": "Change Password", "profile.adminPanel": "Admin Panel", "profile.logout": "Logout", "profile.deleteAccount": "Delete Account", "profile.backToProfile": "Back to Profile", "profile.noOrders": "You have no orders or bookings yet.", "profile.deleteRecord": "Delete this record", "fab.postGig": "Post a Gig", "fab.addService": "Add Service", "fab.addGoods": "Add Goods", "fab.createStoreNote": "Create a store in your profile to post.", "gig.createTitle": "Post a One-Time Gig", "gig.title": "Gig Title", "gig.titlePlaceholder": "e.g., Assemble IKEA shelf", "gig.description": "Description", "gig.descriptionPlaceholder": "Describe the task...", "gig.aiSuggest": "Suggest Description", "gig.aiSuggesting": "Generating...", "gig.region": "Region/Area", "gig.regionSelect": "Select a Region", "gig.regionNew": "Other / New Region", "gig.regionNewPlaceholder": "Enter new region name", "gig.when": "When should this gig be done?", "gig.immediately": "Immediately", "gig.schedule": "Schedule for later", "gig.image": "Image (Max 1)", "gig.phone": "Contact Phone (Optional)", "gig.phonePlaceholder": "Overrides store phone number", "gig.paymentType": "Payment Type", "gig.paymentFixed": "Fixed Price", "gig.paymentHourly": "Per Hour", "gig.paymentAmount": "Payment Amount", "gig.location": "Location", "gig.locationFetching": "Fetching your location...", "gig.postButton": "Post Gig", "product.editTitle": "Edit Item", "product.addTitle": "Add New Item", "product.type": "Type", "product.goods": "Goods", "product.service": "Service", "product.name": "Name", "product.description": "Description", "product.aiGenerate": "Generate Description ✨", "product.aiRefine": "Tune/Refine Description ✨", "product.tags": "Categories / Tags", "product.tagsPlaceholder": "Add a tag...", "product.tagsHelp": "At least one category is required. Press Enter or comma to add.", "product.price": "Price", "product.unit": "Unit (Optional)", "product.unitPlaceholder": "e.g. session", "product.stock": "Stock Qty", "product.published": "Published", "product.unpublished": "Unpublished", "product.inStock": "In Stock", "product.outStock": "Out of Stock", "product.available": "Available", "product.unavailable": "Unavailable", "product.image": "Product Image (Max 1)", "product.save": "Save Item", "store.manageTitle": "Manage Your Store", "store.createTitle": "Create Your Store", "store.name": "Store Name", "store.description": "Description", "store.city": "City", "store.selectCity": "Select a city", "store.otherCity": "Other (Please specify)", "store.newCity": "New City Name", "store.enterCity": "Enter your city name", "store.contactPhone": "Contact Phone", "store.whatsapp": "This is my WhatsApp number", "store.contactEmail": "Contact Email", "store.location": "Location", "store.fetchingLocation": "Fetching your location...", "store.setLocation": "Set Store Location", "store.profilePicture": "Profile Picture (Used on Gigs/Chats)", "store.removePicture": "Remove Picture", "store.storeImages": "Store Images (Max 3)", "store.deliveryNotifs": "Receive notifications for new gigs & deliveries", "store.save": "Save Store", "store.myItems": "My Items", "store.noItems": "Your store has no items.", "store.noGoods": "Your store has no goods.", "store.noServices": "Your store has no services.", "store.setupDesc": "Set up your store to sell items and post gigs.", "store.getStarted": "Get Started", "store.manage": "Manage", "store.addItem": "Add Item", "store.filterCategory": "Filter by Category:", "common.back": "Back",      "common.directions": "Get Directions",
      "common.viewMap": "View Map",
      "common.close": "Close",
      "common.cancel": "Cancel",
      "common.delete": "Delete",
      "common.edit": "Edit",
      "common.save": "Save",
      "common.share": "Share",
      "common.total": "Total",
      "common.free": "FREE",
      "common.soldOut": "Sold Out",
      "event.title": "Events",
      "event.desc": "Find events, parties, and local entertainment.",
      "event.organizerTag": "You are the Organizer",
      "event.manageHint": "Manage this event from your Profile > My Events menu.",
      "event.downloadAgenda": "Download Agenda (PDF)",
      "event.joinLink": "Join Link",
      "event.chatHost": "Chat Host",
      "event.callHost": "Call Host",
      "event.youAreGoing": "You are going!",
      "event.ticketCount": "You have {{count}} ticket(s).",
      "event.printTicket": "Print Ticket",
      "event.attendeeNames": "Attendee Names (Required)",
      "event.guestNames": "Guest Names (Required)",
      "event.bookMore": "Book More Tickets",
      "event.confirmAttendance": "Confirm Attendance",
      "event.bookTicket": "Book Ticket",
      "event.attendees": "Attendees",
      "checkout.title": "Checkout",
      "checkout.ownerError": "You own this item.",
      "checkout.ownerErrorDesc": "Tap to manage your store inventory",
      "checkout.paymentMethod": "Payment Method",
      "checkout.cash": "Cash Payment",
      "checkout.cashDesc": "Pay in-person upon delivery or pickup",
     "checkout.placeOrder": "Place Order",
      "checkout.confirming": "Confirming Payment...",
      "checkout.orderConfirmed": "Order Confirmed!",
      "admin.dashboard": "Admin Dashboard",
      "admin.totalUsers": "Total Users",
      "admin.userGrowth": "User Growth",
      "admin.activity": "Activity This Year",
      "admin.contentBreakdown": "Content Breakdown",
      "admin.usersByCountry": "Users by Country",
      "admin.topCities": "Top Performing Cities",
      "admin.topPosters": "Top Gig Posters",
      "admin.topSellers": "Top Sellers",
      "admin.topDeliverers": "Top Deliverers",
      "gig.scheduled": "Scheduled:",
      "gig.posted": "Posted:",
      "gig.required": "Required:",
      "checkout.serviceType": "Service:",
      "checkout.productType": "Product:",
      "checkout.from": "From:",
      "checkout.quantity": "Quantity:",
      "checkout.inStockCount": "in stock",
      "checkout.outOfStock": "Out of stock",
      "checkout.categories": "Categories:",
      "store.goodsTab": "Goods",
      "store.servicesTab": "Services",
      "event.createTitle": "Create Event 🎟️",
      "event.editTitle": "Edit Event ✏️",
      "event.nameLabel": "Event Name",
      "event.typeLabel": "Event Type",
      "event.inPerson": "In-Person",
      "event.online": "Online",
      "event.startDate": "Start Date",
      "event.startTime": "Start Time",
      "event.endDate": "End Date",
      "event.endTime": "End Time",
      "event.priceLabel": "Price",
      "event.ticketsLabel": "Available Tickets",
      "event.banner": "Event Banner",
      "event.linkLabel": "Event Link",
      "event.publish": "Publish Event",
      "auth.changePasswordTitle": "Change Password",
      "auth.currentPassword": "Current Password",
      "auth.newPassword": "New Password",
      "auth.confirmNewPassword": "Confirm New Password",
      "auth.updatePassword": "Update Password",
      "profile.myEventsTickets": "My Events & Tickets 🎟️",
      "event.tickets": "Tickets",
      "event.attendeeNamePlaceholder": "Attendee {{index}} Name",
      "event.guestNamePlaceholder": "Guest {{index}} Name",
      "event.saveChanges": "Save Changes",
      "multi.title": "Multiple Stores Detected",
      "multi.desc": "You have items from multiple stores. How would you like them delivered?",
      "multi.fast": "Faster Delivery",
      "multi.fastDesc": "Create separate gigs. Drivers pick up simultaneously.",
      "multi.consolidated": "Consolidated Delivery",
      "multi.consolidatedDesc": "Create 1 gig. One driver visits all stores (Cheaper, but slower).",
      "camera.choose": "Choose Image Source",
      "camera.library": "Upload from Library",
      "camera.photo": "Take Photo Now",
      "camera.capture": "Capture",
      "nav.calculating": "Calculating route...",
      "nav.destination": "Destination",
      "fab.addEvent": "Add Event",
      "event.join": "Join Event",
      "event.book": "Book Event",
      "event.daysRemaining": "in {{count}} day(s)",
      "event.today": "TODAY!",
      "event.ended": "Event Ended",
      "event.agendaLabel": "Event Agenda (PDF - Optional)",
      "event.uploadAgenda": "Upload Agenda",
      "event.descriptionLabel": "Description",
      "event.about": "About this Event",
      "event.ticketsLeft": "{{count}} left",
      "profile.eventsHosted": "Events Hosted",
      "event.upcomingTickets": "Upcoming Tickets",
      "event.pastCompleted": "Past / Completed",
      "event.manage": "Manage Event",
      "event.markComplete": "Mark Complete",
      "event.rateHost": "Rate Host:",
      "common.location": "Location",
      "gig.myGigsTitle": "My Gigs",
      "marketplace.searchPlaceholder": "Search for anything...",
      "event.categoriesLabel": "Categories",
      "event.addCategoryPlaceholder": "Add category...",
      "uploader.addImage": "Add Image",
      "event.pricePlaceholder": "0.00 (Empty = Free)",
      "event.ticketsPlaceholder": "Limit (Empty = ∞)",
      "status.in-progress": "In Progress",
      "status.cancelled": "Cancelled",
      "status.completed": "Completed",
      "status.open": "Open",
      "status.closed": "Closed",
      "status.processed": "Processed",
      "common.translate": "Translate",
      "common.translating": "Translating...",
      "common.showOriginal": "Show Original",
      "common.translationFailed": "Translation failed.",
      "store.countryWarning": "Warning: Changing country will update your marketplace view.",
      "common.youMightLike": "You might also like",
      "event.aiGenerate": "Generate with AI",
      "event.aiRefine": "Refine with AI",
      "event.aiGenerating": "Generating...",
      "notif.appSubmitted": "Application submitted successfully!",
      "notif.bookingConfirmed": "Booking Confirmed! Ticket added to 'My Events'. 🎟️",
      "notif.bookingFailed": "Booking failed. Please try again.",
      "notif.eventPublished": "Event Published Successfully! 🎉",
      "notif.eventClosed": "Event closed successfully.",
      "notif.eventCloseFailed": "Failed to close event.",
      "notif.attendeeRemoved": "Attendee removed.",
      "notif.orderPlaced": "Order placed! Check the 'My Orders' button to arrange delivery.",
      "notif.orderDeleted": "Order record deleted.",
      "notif.orderDeleteFailed": "Failed to delete order.",
      "notif.itemRemoved": "Item removed from list.",
      "notif.productDeleteFailed": "Failed to delete product.",
      "notif.reportSubmitted": "Your report has been submitted. We will review it shortly.",
      "notif.eventUpdated": "Event updated successfully!",
      "notif.eventUpdateFailed": "Failed to update event.",
      "notif.productSubmitted": "Product submitted for review.",
      "notif.coinsPurchased": "{{amount}} coins purchased successfully!",
      "notif.ratingThanks": "Thanks for your rating!",
      "notif.gigToggleOn": "Gig & Delivery notifications ENABLED.",
      "notif.gigToggleOff": "Gig & Delivery notifications DISABLED.",
      "msg.newMessage": "New message from {{name}}",
      "msg.newAttendee": "New Attendee: {{name}} booked {{qty}} ticket(s) for \"{{event}}\"",
      "msg.gigApplied": "{{name}} applied for your gig: \"{{gig}}\" with an offer of {{price}}",
      "msg.gigChosen": "You have been chosen for the gig: \"{{gig}}\" for {{price}} coins.",
      "msg.newService": "New Service Booking: {{title}}",
      "msg.productApproved": "Your product has been approved and is now live!",
      "msg.productRejected": "Your product submission was rejected.",
      "msg.accountApproved": "Congratulations! Your account has been approved.",
      "msg.bookingCancelled": "Your booking for the event was cancelled by the host.",
      "msg.newGig": "New gig in {{market}}: \"{{title}}\""
    },

    fr: { welcomeMessage: "Bienvenue sur le Marché", tagline: "Trouvez des biens, services et gigs locaux", marketplace: "Marché", myStore: "Ma Boutique", profile: "Profil", postGig: "Publier un Gig", goods: "Acheter des Biens", services: "Réserver un Service", gigs: "Trouver un Gig", explore: "Explorer", searchFor: "Rechercher des", language: "Langue", myGigs: "Mes Gigs", chats: "Chats", goodsDesc: "Parcourez et achetez des produits de diverses boutiques.", servicesDesc: "Trouvez et réservez des services offerts par des professionnels locaux.", gigsDesc: "Acceptez des tâches ponctuelles et soyez payé.", welcomeBack: "Content de vous revoir!", createAccount: "Créer un compte", "auth.signInContinue": "Connectez-vous pour continuer", "auth.signUpContinue": "Commencez avec un nouveau compte", "auth.firstName": "Prénom", "auth.lastName": "Nom de famille", "auth.profileName": "Nom du Profil / Boutique", "auth.profileNamePlaceholder": "Votre nom public sur GigNile", "auth.country": "Pays", "auth.city": "Ville", "auth.selectCity": "Sélectionnez une ville", "auth.otherCity": "Autre (Veuillez préciser)", "auth.newCity": "Nouvelle Ville", "auth.newCityPlaceholder": "Entrez le nom de votre ville", "auth.location": "Votre Emplacement (Obligatoire)", "auth.locationFetching": "Récupération de votre position... (Autorisation requise)", "auth.locationNote": "Note: L'emplacement est auto-détecté. Vous pouvez le modifier après l'inscription.", "auth.phone": "Numéro de téléphone", "auth.whatsapp": "C'est mon numéro WhatsApp", "auth.gender": "Genre", "auth.dob": "Date de naissance", "auth.signUpDelivery": "S'inscrire pour les livraisons et notifications de gigs", "auth.email": "Adresse e-mail", "auth.password": "Mot de passe", "auth.forgot": "Oublié?", "auth.confirmPassword": "Confirmer le mot de passe", "auth.login": "Connexion", "auth.register": "S'inscrire", "auth.noAccount": "Vous n'avez pas de compte?", "auth.haveAccount": "Vous avez déjà un compte?", "auth.gender.male": "Homme", "auth.gender.female": "Femme", "auth.gender.other": "Autre", "auth.error.invalidCredential": "Email ou mot de passe incorrect. Veuillez vérifier vos identifiants.", "auth.error.emailInUse": "Cet email est déjà enregistré. Essayez de vous connecter.", "auth.error.weakPassword": "Le mot de passe est trop faible. Il doit contenir au moins 6 caractères.", "auth.error.default": "Échec de l'authentification. Veuillez vérifier votre connexion ou réessayer.", "auth.error.passwordsNoMatch": "Les mots de passe ne correspondent pas.", "auth.error.marketRequired": "Le nom du marché est requis.", "auth.error.regionRequired": "Veuillez saisir le nom de votre nouvelle région.", "auth.error.idRequired": "Veuillez télécharger votre document d'identité (permis de séjour ou passeport).", "auth.error.licenseRequired": "Veuillez télécharger votre permis de conduire pour vous inscrire aux livraisons.", "auth.error.locationRequired": "L'emplacement du magasin est obligatoire. Veuillez patienter...", "auth.success.registration": "Inscription réussie! Veuillez vérifier votre e-mail pour valider votre compte. Votre compte est en attente d'approbation.", "auth.error.operationNotAllowed": "La connexion par e-mail/mot de passe n'est pas activée. Veuillez l'activer dans votre projet Firebase.", "auth.doc.id": "Permis de séjour ou Passeport", "auth.doc.license": "Permis de conduire", "auth.profilePicture": "Photo de profil (Facultatif)", "city.casablanca": "Casablanca", "city.rabat": "Rabat", "city.tangier": "Tanger", "city.lagos": "Lagos", "city.accra": "Accra", "uploader.uploading": "Téléchargement...", "uploader.acceptedTypes": "Image ou PDF accepté", "uploader.error": "Échec du téléchargement. Veuillez réessayer.", "forgot.title": "Réinitialiser le mot de passe", "forgot.desc": "Entrez votre adresse e-mail et nous vous enverrons un lien.", "forgot.error.emailRequired": "Veuillez entrer votre adresse e-mail.", "forgot.success": "Lien de réinitialisation envoyé! Veuillez vérifier votre e-mail.", "forgot.sendLink": "Envoyer le lien de réinitialisation", "profile.dashboardTitle": "Mon tableau de bord", "profile.gigsPosted": "Total des Gigs postés", "profile.gigsCompleted": "Gigs terminés (en tant que travailleur)", "profile.productsPosted": "Produits/Services postés", "profile.ordersPlaced": "Commandes passées", "profile.totalSpent": "Total dépensé", "profile.totalGigEarnings": "Gains totaux des Gigs", "profile.tier.novice": "Novice", "profile.tier.silver": "Argent", "profile.tier.gold": "Or", "profile.tier.platinum": "Platine", "profile.seller": "Vendeur", "profile.myOrders": "Mes Commandes", "profile.editProfile": "Modifier le profil", "profile.changePassword": "Changer le mot de passe", "profile.adminPanel": "Panel Admin", "profile.logout": "Déconnexion", "profile.deleteAccount": "Supprimer le compte", "profile.backToProfile": "Retour au Profil", "profile.noOrders": "Vous n'avez aucune commande ou réservation pour le moment.", "profile.deleteRecord": "Supprimer cet enregistrement", "fab.postGig": "Publier un Gig", "fab.addService": "Ajouter un Service", "fab.addGoods": "Ajouter un Article", "fab.createStoreNote": "Créez une boutique dans votre profil pour poster.", "gig.createTitle": "Publier un Gig ponctuel", "gig.title": "Titre du Gig", "gig.titlePlaceholder": "ex: Assembler une étagère IKEA", "gig.description": "Description", "gig.descriptionPlaceholder": "Décrivez la tâche...", "gig.aiSuggest": "Suggérer une description", "gig.aiSuggesting": "Génération...", "gig.region": "Région/Zone", "gig.regionSelect": "Sélectionnez une région", "gig.regionNew": "Autre / Nouvelle région", "gig.regionNewPlaceholder": "Entrez le nom de la nouvelle région", "gig.when": "Quand ce gig doit-il être fait?", "gig.immediately": "Immédiatement", "gig.schedule": "Planifier pour plus tard", "gig.image": "Image (Max 1)", "gig.phone": "Téléphone (Optionnel)", "gig.phonePlaceholder": "Remplace le téléphone du magasin", "gig.paymentType": "Type de paiement", "gig.paymentFixed": "Prix fixe", "gig.paymentHourly": "Par heure", "gig.paymentAmount": "Montant du paiement", "gig.location": "Emplacement", "gig.locationFetching": "Récupération de votre position...", "gig.postButton": "Publier le Gig", "product.editTitle": "Modifier l'article", "product.addTitle": "Ajouter un nouvel article", "product.type": "Type", "product.goods": "Biens", "product.service": "Service", "product.name": "Nom", "product.description": "Description", "product.aiGenerate": "Générer la description ✨", "product.aiRefine": "Affiner la description ✨", "product.tags": "Catégories / Tags", "product.tagsPlaceholder": "Ajouter un tag...", "product.tagsHelp": "Au moins une catégorie est requise. Appuyez sur Entrée ou virgule pour ajouter.", "product.price": "Prix", "product.unit": "Unité (Optionnel)", "product.unitPlaceholder": "ex. session", "product.stock": "Qté en stock", "product.published": "Publié", "product.unpublished": "Non publié", "product.inStock": "En stock", "product.outStock": "Rupture de stock", "product.available": "Disponible", "product.unavailable": "Indisponible", "product.image": "Image du produit (Max 1)", "product.save": "Enregistrer", "store.manageTitle": "Gérer votre boutique", "store.createTitle": "Créer votre boutique", "store.name": "Nom de la boutique", "store.description": "Description", "store.city": "Ville", "store.selectCity": "Sélectionnez une ville", "store.otherCity": "Autre (Veuillez préciser)", "store.newCity": "Nom de la nouvelle ville", "store.enterCity": "Entrez le nom de votre ville", "store.contactPhone": "Téléphone de contact", "store.whatsapp": "C'est mon numéro WhatsApp", "store.contactEmail": "Email de contact", "store.location": "Emplacement", "store.fetchingLocation": "Récupération de votre position...", "store.setLocation": "Définir l'emplacement", "store.profilePicture": "Photo de profil (Utilisée sur Gigs/Chats)", "store.removePicture": "Supprimer la photo", "store.storeImages": "Images de la boutique (Max 3)", "store.deliveryNotifs": "Recevoir des notifications pour les nouveaux gigs et livraisons", "store.save": "Enregistrer la boutique", "store.myItems": "Mes articles", "store.noItems": "Votre boutique n'a aucun article.", "store.noGoods": "Votre boutique n'a pas de biens.", "store.noServices": "Votre boutique n'a pas de services.", "store.setupDesc": "Configurez votre boutique pour vendre des articles et publier des gigs.", "store.getStarted": "Commencer", "store.manage": "Gérer", "store.addItem": "Ajouter un article", "store.filterCategory": "Filtrer par catégorie :", "common.back": "Retour",
      "common.directions": "Itinéraire",
      "common.viewMap": "Voir la carte",
      "common.close": "Fermer",
      "common.cancel": "Annuler",
      "common.delete": "Supprimer",
      "common.edit": "Modifier",
      "common.save": "Enregistrer",
      "common.share": "Partager",
      "common.total": "Total",
      "common.free": "GRATUIT",
      "common.soldOut": "Épuisé",
      "event.title": "Événements",
      "event.desc": "Trouvez des événements et divertissements locaux.",
      "event.organizerTag": "Vous êtes l'organisateur",
      "event.manageHint": "Gérez cet événement depuis Profil > Mes Événements.",
      "event.about": "À propos",
      "event.downloadAgenda": "Télécharger l'agenda (PDF)",
      "event.joinLink": "Lien de connexion",
      "event.chatHost": "Chatter avec l'hôte",
      "event.callHost": "Appeler l'hôte",
      "event.youAreGoing": "Vous y allez !",
      "event.ticketCount": "Vous avez {{count}} billet(s).",
      "event.printTicket": "Imprimer le billet",
      "event.attendeeNames": "Noms des participants",
      "event.guestNames": "Noms des invités",
      "event.bookMore": "Réserver plus",
      "event.confirmAttendance": "Confirmer la présence",
      "event.bookTicket": "Réserver un billet",
      "event.attendees": "Participants",
      "checkout.title": "Caisse",
      "checkout.ownerError": "Vous possédez cet article.",
      "checkout.ownerErrorDesc": "Touchez pour gérer votre stock",
      "checkout.paymentMethod": "Moyen de paiement",
      "checkout.cash": "Paiement en espèces",
      "checkout.cashDesc": "Payer en personne à la livraison",
      "checkout.placeOrder": "Commander",
      "checkout.confirming": "Confirmation...",
      "checkout.orderConfirmed": "Commande confirmée !",
      "admin.dashboard": "Tableau de bord Admin",
      "admin.totalUsers": "Total Utilisateurs",
      "admin.userGrowth": "Croissance",
      "admin.activity": "Activité annuelle",
      "admin.contentBreakdown": "Contenu",
      "admin.usersByCountry": "Utilisateurs par pays",
      "admin.topCities": "Meilleures villes",
      "admin.topPosters": "Top Posteurs",
      "admin.topSellers": "Top Vendeurs",
      "admin.topDeliverers": "Top Livreurs",
      "gig.scheduled": "Prévu :",
      "gig.posted": "Posté :",
      "gig.required": "Requis :",
      "checkout.serviceType": "Service :",
      "checkout.productType": "Produit :",
      "checkout.from": "De :",
      "checkout.quantity": "Quantité :",
      "checkout.inStockCount": "en stock",
      "checkout.outOfStock": "Rupture de stock",
      "checkout.categories": "Catégories :",
      "store.goodsTab": "Biens",
      "store.servicesTab": "Services",
      "event.createTitle": "Créer un événement 🎟️",
      "event.editTitle": "Modifier l'événement ✏️",
      "event.nameLabel": "Nom de l'événement",
      "event.typeLabel": "Type d'événement",
      "event.inPerson": "En personne",
      "event.online": "En ligne",
      "event.startDate": "Date de début",
      "event.startTime": "Heure de début",
      "event.endDate": "Date de fin",
      "event.endTime": "Heure de fin",
      "event.priceLabel": "Prix",
      "event.ticketsLabel": "Billets disponibles",
      "event.banner": "Bannière de l'événement",
      "event.linkLabel": "Lien de l'événement",
      "event.publish": "Publier l'événement",
      "event.saveChanges": "Enregistrer les modifications",
      "auth.changePasswordTitle": "Changer le mot de passe",
      "auth.currentPassword": "Mot de passe actuel",
      "auth.newPassword": "Nouveau mot de passe",
      "auth.confirmNewPassword": "Confirmer le nouveau mot de passe",
      "auth.updatePassword": "Mettre à jour le mot de passe",
      "profile.myEventsTickets": "Mes Événements & Billets 🎟️",
      "event.tickets": "Billets",
      "event.attendeeNamePlaceholder": "Nom du participant {{index}}",
      "event.guestNamePlaceholder": "Nom de l'invité {{index}}",
      "multi.title": "Plusieurs boutiques détectées",
      "multi.desc": "Vous avez des articles de plusieurs boutiques. Comment souhaitez-vous être livré ?",
      "multi.fast": "Livraison plus rapide",
      "multi.fastDesc": "Créez des gigs séparés. Les chauffeurs récupèrent simultanément.",
      "multi.consolidated": "Livraison groupée",
      "multi.consolidatedDesc": "Créez 1 gig. Un chauffeur visite toutes les boutiques (Moins cher, mais plus lent).",
      "camera.choose": "Choisir la source de l'image",
      "camera.library": "Télécharger depuis la bibliothèque",
      "camera.photo": "Prendre une photo",
      "camera.capture": "Capturer",
      "nav.calculating": "Calcul de l'itinéraire...",
      "nav.destination": "Destination",
      "event.book": "Réserver",
      "event.upcomingTickets": "Billets à venir",
      "event.pastCompleted": "Passés / Terminés",
      "event.manage": "Gérer l'événement",
      "event.markComplete": "Marquer comme terminé",
      "event.rateHost": "Evaluer l'hôte :",
      "common.location": "Localisation",
      "gig.myGigsTitle": "Mes Gigs",
      "marketplace.searchPlaceholder": "Rechercher n'importe quoi...",
      "event.categoriesLabel": "Catégories",
      "event.addCategoryPlaceholder": "Ajouter une catégorie...",
      "uploader.addImage": "Ajouter une image",
      "event.pricePlaceholder": "0.00 (Vide = Gratuit)",
      "event.ticketsPlaceholder": "Limite (Vide = ∞)",
      "status.in-progress": "En cours",
      "status.cancelled": "Annulé",
      "status.completed": "Terminé",
      "status.open": "Ouvert",
      "status.closed": "Fermé",
      "status.processed": "Traité",
      "fab.addEvent": "Ajouter un événement",
      "event.join": "Rejoindre",
      "event.daysRemaining": "dans {{count}} jour(s)",
      "event.today": "AUJOURD'HUI !",
      "event.ended": "Événement terminé",
      "event.agendaLabel": "Agenda (PDF - Optionnel)",
      "event.uploadAgenda": "Télécharger l'agenda",
      "event.descriptionLabel": "Description",
      "event.ticketsLeft": "{{count}} restants",
      "profile.eventsHosted": "Événements organisés",
      "common.translate": "Traduire",
      "common.translating": "Traduction...",
      "common.showOriginal": "Voir l'original",
      "common.translationFailed": "Échec de la traduction.",
      "store.countryWarning": "Attention : Changer de pays mettra à jour votre vue du marché.",
      "common.youMightLike": "Vous pourriez aussi aimer",
      "event.aiGenerate": "Générer avec l'IA",
      "event.aiRefine": "Affiner avec l'IA",
      "event.aiGenerating": "Génération...",
      "notif.appSubmitted": "Candidature soumise avec succès !",
      "notif.bookingConfirmed": "Réservation confirmée ! Billet ajouté à 'Mes Événements'. 🎟️",
      "notif.bookingFailed": "Échec de la réservation. Veuillez réessayer.",
      "notif.eventPublished": "Événement publié avec succès ! 🎉",
      "notif.eventClosed": "Événement fermé avec succès.",
      "notif.eventCloseFailed": "Échec de la fermeture de l'événement.",
      "notif.attendeeRemoved": "Participant retiré.",
      "notif.orderPlaced": "Commande passée ! Vérifiez 'Mes Commandes' pour la livraison.",
      "notif.orderDeleted": "Enregistrement de commande supprimé.",
      "notif.orderDeleteFailed": "Échec de la suppression de la commande.",
      "notif.itemRemoved": "Article retiré de la liste.",
      "notif.productDeleteFailed": "Échec de la suppression du produit.",
      "notif.reportSubmitted": "Votre signalement a été soumis. Nous l'examinerons bientôt.",
      "notif.eventUpdated": "Événement mis à jour avec succès !",
      "notif.eventUpdateFailed": "Échec de la mise à jour de l'événement.",
      "notif.productSubmitted": "Produit soumis pour examen.",
      "notif.coinsPurchased": "{{amount}} pièces achetées avec succès !",
      "notif.ratingThanks": "Merci pour votre évaluation !",
      "notif.gigToggleOn": "Notifications Gigs & Livraison ACTIVÉES.",
      "notif.gigToggleOff": "Notifications Gigs & Livraison DÉSACTIVÉES.",
      "msg.newMessage": "Nouveau message de {{name}}",
      "msg.newAttendee": "Nouveau participant : {{name}} a réservé {{qty}} billet(s) pour \"{{event}}\"",
      "msg.gigApplied": "{{name}} a postulé pour votre gig : \"{{gig}}\" avec une offre de {{price}}",
      "msg.gigChosen": "Vous avez été choisi pour le gig : \"{{gig}}\" pour {{price}} pièces.",
      "msg.newService": "Nouvelle réservation de service : {{title}}",
      "msg.productApproved": "Votre produit a été approuvé et est maintenant en ligne !",
      "msg.productRejected": "Votre soumission de produit a été rejetée.",
      "msg.accountApproved": "Félicitations ! Votre compte a été approuvé.",
      "msg.bookingCancelled": "Votre réservation pour l'événement a été annulée par l'hôte.",
      "msg.newGig": "Nouveau gig à {{market}} : \"{{title}}\""
    },

    es: { welcomeMessage: "Bienvenido al Mercado", tagline: "Encuentra bienes, servicios y gigs locales", marketplace: "Mercado", myStore: "Mi Tienda", profile: "Perfil", postGig: "Publicar un Gig", goods: "Comprar Productos", services: "Reservar un Service", gigs: "Encontrar un Gig", explore: "Explorar", searchFor: "Buscar", language: "Idioma", myGigs: "Mis Gigs", chats: "Chats", goodsDesc: "Explora y compra productos de varias tiendas.", servicesDesc: "Encuentra y reserva servicios ofrecidos por profesionales locales.", gigsDesc: "Asume tareas únicas y recibe un pago.", welcomeBack: "¡Bienvenido de nuevo!", createAccount: "Crear cuenta", "auth.signInContinue": "Inicia sesión para continuar", "auth.signUpContinue": "Comienza con una nueva cuenta", "auth.firstName": "Nombre", "auth.lastName": "Apellido", "auth.profileName": "Nombre de Perfil / Tienda", "auth.profileNamePlaceholder": "Tu nombre público en GigNile", "auth.country": "País", "auth.city": "Ciudad", "auth.selectCity": "Selecciona una ciudad", "auth.otherCity": "Otra (Por favor especifica)", "auth.newCity": "Nueva Ciudad", "auth.newCityPlaceholder": "Ingresa el nombre de tu ciudad", "auth.location": "Tu Ubicación (Obligatorio)", "auth.locationFetching": "Obteniendo tu ubicación... (Se requiere permiso)", "auth.locationNote": "Nota: La ubicación se detecta automáticamente. Puedes editarla después de registrarte.", "auth.phone": "Número de teléfono", "auth.whatsapp": "Este es mi número de WhatsApp", "auth.gender": "Género", "auth.dob": "Fecha de nacimiento", "auth.signUpDelivery": "Registrarse para notificaciones de entregas y gigs", "auth.email": "Dirección de correo electrónico", "auth.password": "Contraseña", "auth.forgot": "¿Olvidaste?", "auth.confirmPassword": "Confirmar contraseña", "auth.login": "Iniciar sesión", "auth.register": "Registrarse", "auth.noAccount": "¿No tienes una cuenta?", "auth.haveAccount": "¿Ya tienes una cuenta?", "auth.gender.male": "Hombre", "auth.gender.female": "Mujer", "auth.gender.other": "Otro", "auth.error.invalidCredential": "Correo electrónico o contraseña incorrectos. Por favor, comprueba tus credenciales.","auth.error.emailInUse": "Este correo electrónico ya está registrado. Intenta iniciar sesión.", "auth.error.weakPassword": "La contraseña es demasiado débil. Debe tener al menos 6 caracteres.", "auth.error.default": "Fallo de autenticación. Por favor, comprueba tu conexión o inténtalo de nuevo.", "auth.error.passwordsNoMatch": "Las contraseñas no coinciden.", "auth.error.marketRequired": "Se requiere el nombre del mercado.", "auth.error.regionRequired": "Por favor, introduce el nombre de tu nueva región.", "auth.error.idRequired": "Por favor, sube tu documento de identidad (permiso de residencia o pasaporte).", "auth.error.licenseRequired": "Por favor, sube tu permiso de conducir para registrarte para entregas.", "auth.error.locationRequired": "La ubicación de la tienda es obligatoria. Por favor, espere...", "auth.success.registration": "¡Registro exitoso! Por favor, revisa tu correo electrónico para verificar tu cuenta. Tu cuenta está pendiente de aprobación.", "auth.error.operationNotAllowed": "El inicio de sesión con correo electrónico/contraseña no está habilitado. Habilítalo en tu proyecto de Firebase.", "auth.doc.id": "Permiso de residencia o Pasaporte", "auth.doc.license": "Permiso de conducir", "auth.profilePicture": "Foto de perfil (Opcional)", "city.casablanca": "Casablanca", "city.rabat": "Rabat", "city.tangier": "Tánger", "city.lagos": "Lagos", "city.accra": "Accra", "uploader.uploading": "Subiendo...", "uploader.acceptedTypes": "Imagen o PDF aceptado", "uploader.error": "Error al subir. Por favor, inténtalo de nuevo.", "forgot.title": "Restablecer contraseña", "forgot.desc": "Introduce tu dirección de correo electrónico y te enviaremos un enlace.", "forgot.error.emailRequired": "Por favor, introduce tu dirección de correo electrónico.", "forgot.success": "¡Enlace de restablecimiento enviado! Revisa tu correo.", "forgot.sendLink": "Enviar enlace de restablecimiento", "profile.dashboardTitle": "Mi Panel de Actividad", "profile.gigsPosted": "Gigs Publicados", "profile.gigsCompleted": "Gigs Completados (como trabajador)", "profile.productsPosted": "Productos/Servicios Publicados", "profile.ordersPlaced": "Pedidos Realizados", "profile.totalSpent": "Total Gastado", "profile.totalGigEarnings": "Ingresos Totales de Gigs", "profile.tier.novice": "Novato", "profile.tier.silver": "Plata", "profile.tier.gold": "Oro", "profile.tier.platinum": "Platino", "profile.seller": "Vendedor", "profile.myOrders": "Mis Pedidos y Reservas", "profile.editProfile": "Editar Perfil", "profile.changePassword": "Cambiar Contraseña", "profile.adminPanel": "Panel de Administración", "profile.logout": "Cerrar Sesión", "profile.deleteAccount": "Eliminar Cuenta", "profile.backToProfile": "Volver al Perfil", "profile.noOrders": "Todavía no tienes pedidos ni reservas.", "profile.deleteRecord": "Eliminar este registro", "fab.postGig": "Publicar un Gig", "fab.addService": "Añadir Servicio", "fab.addGoods": "Añadir Producto", "fab.createStoreNote": "Crea una tienda en tu perfil para publicar.", "gig.createTitle": "Publicar un Gig único", "gig.title": "Título del Gig", "gig.titlePlaceholder": "ej: Montar estantería IKEA", "gig.description": "Descripción", "gig.descriptionPlaceholder": "Describe la tarea...", "gig.aiSuggest": "Sugerir Descripción", "gig.aiSuggesting": "Generando...", "gig.region": "Región/Área", "gig.regionSelect": "Selecciona una Región", "gig.regionNew": "Otra / Nueva Región", "gig.regionNewPlaceholder": "Introduce un nuevo nombre de región", "gig.when": "¿Cuándo debe hacerse este gig?", "gig.immediately": "Inmediatamente", "gig.schedule": "Programar para más tarde", "gig.image": "Imagen (Max 1)", "gig.phone": "Teléfono (Opcional)", "gig.phonePlaceholder": "Sustituye al teléfono de la tienda", "gig.paymentType": "Tipo de Pago", "gig.paymentFixed": "Precio Fijo", "gig.paymentHourly": "Por Hora", "gig.paymentAmount": "Importe del Pago", "gig.location": "Ubicación", "gig.locationFetching": "Obteniendo tu ubicación...", "gig.postButton": "Publicar Gig", "product.editTitle": "Editar artículo", "product.addTitle": "Añadir nuevo artículo", "product.type": "Tipo", "product.goods": "Productos", "product.service": "Servicio", "product.name": "Nombre", "product.description": "Descripción", "product.aiGenerate": "Generar descripción ✨", "product.aiRefine": "Afinar descripción ✨", "product.tags": "Categorías / Etiquetas", "product.tagsPlaceholder": "Añadir etiqueta...", "product.tagsHelp": "Se requiere al menos una categoría. Pulsa Intro o coma para añadir.", "product.price": "Precio", "product.unit": "Unidad (Opcional)", "product.unitPlaceholder": "ej. sesión", "product.stock": "Cant. en stock", "product.published": "Publicado", "product.unpublished": "No publicado", "product.inStock": "En stock", "product.outStock": "Agotado", "product.available": "Disponible", "product.unavailable": "No disponible", "product.image": "Imagen del producto (Max 1)", "product.save": "Guardar artículo", "store.manageTitle": "Gestionar tu tienda", "store.createTitle": "Crear tu tienda", "store.name": "Nombre de la tienda", "store.description": "Descripción", "store.city": "Ciudad", "store.selectCity": "Selecciona una ciudad", "store.otherCity": "Otra (Por favor especifica)", "store.newCity": "Nombre de la nueva ciudad", "store.enterCity": "Ingresa el nombre de tu ciudad", "store.contactPhone": "Teléfono de contacto", "store.whatsapp": "Este es mi número de WhatsApp", "store.contactEmail": "Email de contacto", "store.location": "Ubicación", "store.fetchingLocation": "Obteniendo tu ubicación...", "store.setLocation": "Establecer ubicación de la tienda", "store.profilePicture": "Foto de perfil (Usada en Gigs/Chats)", "store.removePicture": "Eliminar foto", "store.storeImages": "Imágenes de la tienda (Máx 3)", "store.deliveryNotifs": "Recibir notificaciones de nuevos gigs y entregas", "store.save": "Guardar tienda", "store.myItems": "Mis artículos", "store.noItems": "Tu tienda no tiene artículos.", "store.noGoods": "Tu tienda no tiene productos.", "store.noServices": "Tu tienda no tiene servicios.", "store.setupDesc": "Configura tu tienda para vender artículos y publicar gigs.", "store.getStarted": "Empezar", "store.manage": "Gestionar", "store.addItem": "Añadir artículo", "store.filterCategory": "Filtrar por categoría:", "common.back": "Atrás",
      "common.directions": "Direcciones",
      "common.viewMap": "Ver mapa",
      "common.close": "Cerrar",
      "common.cancel": "Cancelar",
      "common.delete": "Eliminar",
      "common.edit": "Editar",
      "common.save": "Guardar",
      "common.share": "Compartir",
      "common.total": "Total",
      "common.free": "GRATIS",
      "common.soldOut": "Agotado",
      "event.title": "Eventos",
      "event.desc": "Encuentra eventos y entretenimiento local.",
      "event.organizerTag": "Eres el organizador",
      "event.manageHint": "Gestiona este evento desde Perfil > Mis Eventos.",
      "event.about": "Acerca de",
      "event.downloadAgenda": "Descargar Agenda (PDF)",
      "event.joinLink": "Enlace de unión",
      "event.chatHost": "Chat anfitrión",
      "event.callHost": "Llamar anfitrión",
      "event.youAreGoing": "¡Vas a ir!",
      "event.ticketCount": "Tienes {{count}} entrada(s).",
      "event.printTicket": "Imprimir entrada",
      "event.attendeeNames": "Nombres de asistentes",
      "event.guestNames": "Nombres de invitados",
      "event.bookMore": "Reservar más",
      "event.confirmAttendance": "Confirmar asistencia",
      "event.bookTicket": "Reservar entrada",
      "event.attendees": "Asistentes",
      "checkout.title": "Pagar",
      "checkout.ownerError": "Eres dueño de esto.",
      "checkout.ownerErrorDesc": "Toca para gestionar tu inventario",
      "checkout.paymentMethod": "Método de pago",
      "checkout.cash": "Pago en efectivo",
      "checkout.cashDesc": "Pagar en persona al entregar",
      "checkout.placeOrder": "Realizar pedido",
      "checkout.confirming": "Confirmando...",
      "checkout.orderConfirmed": "¡Pedido confirmado!",
      "admin.dashboard": "Panel de Admin",
      "admin.totalUsers": "Total Usuarios",
      "admin.userGrowth": "Crecimiento",
      "admin.activity": "Actividad anual",
      "admin.contentBreakdown": "Contenido",
      "admin.usersByCountry": "Usuarios por país",
      "admin.topCities": "Mejores ciudades",
      "admin.topPosters": "Top Publicadores",
      "admin.topSellers": "Top Vendedores",
      "admin.topDeliverers": "Top Repartidores",
      "gig.scheduled": "Programado:",
      "gig.posted": "Publicado:",
      "gig.required": "Requerido:",
      "checkout.serviceType": "Servicio:",
      "checkout.productType": "Producto:",
      "checkout.from": "De:",
      "checkout.quantity": "Cantidad:",
      "checkout.inStockCount": "en stock",
      "checkout.outOfStock": "Agotado",
      "checkout.categories": "Categorías:",
      "store.goodsTab": "Productos",
      "store.servicesTab": "Servicios",
      "event.createTitle": "Crear Evento 🎟️",
      "event.editTitle": "Editar Evento ✏️",
      "event.nameLabel": "Nombre del Evento",
      "event.typeLabel": "Tipo de Evento",
      "event.inPerson": "En persona",
      "event.online": "En línea",
      "event.startDate": "Fecha de inicio",
      "event.startTime": "Hora de inicio",
      "event.endDate": "Fecha de fin",
      "event.endTime": "Hora de fin",
      "event.priceLabel": "Precio",
      "event.ticketsLabel": "Entradas disponibles",
      "event.banner": "Banner del evento",
      "event.linkLabel": "Enlace del evento",
      "event.publish": "Publicar Evento",
      "event.saveChanges": "Guardar cambios",
      "auth.changePasswordTitle": "Cambiar Contraseña",
      "auth.currentPassword": "Contraseña Actual",
      "auth.newPassword": "Nueva Contraseña",
      "auth.confirmNewPassword": "Confirmar Nueva Contraseña",
      "auth.updatePassword": "Actualizar Contraseña",
      "profile.myEventsTickets": "Mis Eventos y Entradas 🎟️",
      "event.tickets": "Entradas",
      "event.attendeeNamePlaceholder": "Nombre del Asistente {{index}}",
      "event.guestNamePlaceholder": "Nombre del Invitado {{index}}",
      "multi.title": "Múltiples tiendas detectadas",
      "multi.desc": "Tienes artículos de varias tiendas. ¿Cómo te gustaría que te los entreguen?",
      "multi.fast": "Entrega más rápida",
      "multi.fastDesc": "Crea gigs separados. Los conductores recogen simultáneamente.",
      "multi.consolidated": "Entrega consolidada",
      "multi.consolidatedDesc": "Crea 1 gig. Un conductor visita todas las tiendas (Más barato, pero más lento).",
      "camera.choose": "Elegir fuente de imagen",
      "camera.library": "Subir desde la biblioteca",
      "camera.photo": "Tomar foto ahora",
      "camera.capture": "Capturar",
      "nav.calculating": "Calculando ruta...",
      "nav.destination": "Destino",
      "fab.addEvent": "Añadir evento",
      "event.join": "Unirse",
      "event.book": "Reservar",
      "event.upcomingTickets": "Próximos Tickets",
      "event.pastCompleted": "Pasados / Completados",
      "event.manage": "Gestionar Evento",
      "event.markComplete": "Marcar Completado",
      "event.rateHost": "Calificar Anfitrión:",
      "common.location": "Ubicación",
      "gig.myGigsTitle": "Mis Gigs",
      "marketplace.searchPlaceholder": "Buscar cualquier cosa...",
      "event.categoriesLabel": "Categorías",
      "event.addCategoryPlaceholder": "Añadir categoría...",
      "uploader.addImage": "Añadir imagen",
      "event.pricePlaceholder": "0.00 (Vacío = Gratis)",
      "event.ticketsPlaceholder": "Límite (Vacío = ∞)",
      "status.in-progress": "En Progreso",
      "status.cancelled": "Cancelado",
      "status.completed": "Completado",
      "status.open": "Abierto",
      "status.closed": "Cerrado",
      "status.processed": "Procesado",
      "event.daysRemaining": "en {{count}} día(s)",
      "event.today": "¡HOY!",
      "event.ended": "Evento finalizado",
      "event.agendaLabel": "Agenda (PDF - Opcional)",
      "event.uploadAgenda": "Subir agenda",
      "event.descriptionLabel": "Descripción",
      "event.ticketsLeft": "{{count}} restantes",
      "profile.eventsHosted": "Eventos organizados",
      "common.translate": "Traducir",
      "common.translating": "Traduciendo...",
      "common.showOriginal": "Mostrar original",
      "common.translationFailed": "Error de traducción.",
      "common.youMightLike": "También te podría gustar",
      "store.countryWarning": "Advertencia: Cambiar de país actualizará tu vista del mercado.",
      "event.aiGenerate": "Generar con IA",
      "event.aiRefine": "Refinar con IA",
      "event.aiGenerating": "Generando...",
      "notif.appSubmitted": "¡Solicitud enviada con éxito!",
      "notif.bookingConfirmed": "¡Reserva confirmada! Entrada añadida a 'Mis Eventos'. 🎟️",
      "notif.bookingFailed": "La reserva falló. Inténtalo de nuevo.",
      "notif.eventPublished": "¡Evento publicado con éxito! 🎉",
      "notif.eventClosed": "Evento cerrado con éxito.",
      "notif.eventCloseFailed": "Error al cerrar el evento.",
      "notif.attendeeRemoved": "Asistente eliminado.",
      "notif.orderPlaced": "¡Pedido realizado! Revisa 'Mis Pedidos' para organizar la entrega.",
      "notif.orderDeleted": "Registro de pedido eliminado.",
      "notif.orderDeleteFailed": "Error al eliminar el pedido.",
      "notif.itemRemoved": "Artículo eliminado de la lista.",
      "notif.productDeleteFailed": "Error al eliminar el producto.",
      "notif.reportSubmitted": "Tu reporte ha sido enviado. Lo revisaremos pronto.",
      "notif.eventUpdated": "¡Evento actualizado con éxito!",
      "notif.eventUpdateFailed": "Error al actualizar el evento.",
      "notif.productSubmitted": "Producto enviado para revisión.",
     "notif.coinsPurchased": "¡{{amount}} monedas compradas con éxito!",
      "notif.ratingThanks": "¡Gracias por tu calificación!",
      "notif.gigToggleOn": "Notificaciones de Gigs y Entregas ACTIVADAS.",
      "notif.gigToggleOff": "Notificaciones de Gigs y Entregas DESACTIVADAS.",
      "msg.newMessage": "Nuevo mensaje de {{name}}",
      "msg.newAttendee": "Nuevo asistente: {{name}} reservó {{qty}} entrada(s) para \"{{event}}\"",
      "msg.gigApplied": "{{name}} solicitó tu gig: \"{{gig}}\" con una oferta de {{price}}",
      "msg.gigChosen": "Has sido elegido para el gig: \"{{gig}}\" por {{price}} monedas.",
      "msg.newService": "Nueva reserva de servicio: {{title}}",
      "msg.productApproved": "¡Tu producto ha sido aprobado y ya está disponible!",
      "msg.productRejected": "Tu envío de producto fue rechazado.",
      "msg.accountApproved": "¡Felicidades! Tu cuenta ha sido aprobada.",
      "msg.bookingCancelled": "Tu reserva para el evento fue cancelada por el anfitrión.",
      "msg.newGig": "Nuevo gig en {{market}}: \"{{title}}\""
    },

    ar: { welcomeMessage: "مرحباً بكم في سوق جيج نايل", tagline: "ابحث عن السلع والخدمات والمهام المحلية", marketplace: "السوق", myStore: "متجري", profile: "الملف الشخصي", postGig: "انشر مهمة", goods: "تسوق السلع", services: "احجز خدمة", gigs: "البحث عن المهام", explore: "استكشف", searchFor: "ابحث عن", language: "اللغة", myGigs: "مهامي", chats: "المحادثات", goodsDesc: "تصفح واشترِ المنتجات من متاجر مختلفة.", servicesDesc: "ابحث عن الخدمات التي يقدمها المحترفون المحليون واحجزها.", gigsDesc: "تولى المهام لمرة واحدة واحصل على أجر.", welcomeBack: "مرحبا بعودتك!", createAccount: "إنشاء حساب", "auth.signInContinue": "سجل الدخول للمتابعة", "auth.signUpContinue": "ابدأ بحساب جديد", "auth.firstName": "الاسم الأول", "auth.lastName": "اسم العائلة", "auth.profileName": "اسم الملف الشخصي / المتجر", "auth.profileNamePlaceholder": "اسمك العام على GigNile", "auth.country": "الدولة", "auth.city": "المدينة", "auth.selectCity": "اختر مدينة", "auth.otherCity": "أخرى (يرجى التحديد)", "auth.newCity": "مدينة جديدة", "auth.newCityPlaceholder": "أدخل اسم مدينتك", "auth.location": "موقعك (إلزامي)", "auth.locationFetching": "جاري جلب موقعك... (الإذن مطلوب)", "auth.locationNote": "ملاحظة: يتم اكتشاف الموقع تلقائيًا. يمكنك تعديله بعد التسجيل.", "auth.phone": "رقم الهاتف", "auth.whatsapp": "هذا هو رقم الواتساب الخاص بي", "auth.gender": "الجنس", "auth.dob": "تاريخ الميلاد", "auth.signUpDelivery": "الاشتراك في إشعارات التوصيل والمهام", "auth.email": "عنوان البريد الإلكتروني", "auth.password": "كلمة المرور", "auth.forgot": "نسيت؟", "auth.confirmPassword": "تأكيد كلمة المرور", "auth.login": "تسجيل الدخول", "auth.register": "تسجيل", "auth.noAccount": "ليس لديك حساب؟", "auth.haveAccount": "هل لديك حساب بالفعل?", "auth.gender.male": "ذكر", "auth.gender.female": "أنثى", "auth.gender.other": "آخر", "auth.error.invalidCredential": "البريد الإلكتروني أو كلمة المرور غير صالحة. يرجى التحقق من بياناتك.", "auth.error.emailInUse": "هذا البريد الإلكتروني مسجل بالفعل. حاول تسجيل الدخول.", "auth.error.weakPassword": "كلمة المرور ضعيفة جداً. يجب أن تتكون من 6 أحرف على الأقل.", "auth.error.default": "فشل المصادقة. يرجى التحقق من اتصالك بالشبكة أو المحاولة مرة أخرى.", "auth.error.passwordsNoMatch": "كلمات المرور غير متطابقة.", "auth.error.marketRequired": "اسم السوق مطلوب.", "auth.error.regionRequired": "يرجى إدخال اسم منطقتك الجديدة.", "auth.error.idRequired": "يرجى تحميل وثيقة الهوية الخاصة بك (تصريح إقامة أو جواز سفر).", "auth.error.licenseRequired": "يرجى تحميل رخصة القيادة الخاصة بك للتسجيل في خدمة التوصيل.", "auth.error.locationRequired": "موقع المتجر إلزامي. يرجى الانتظار...", "auth.success.registration": "تم التسجيل بنجاح! يرجى مراجعة بريدك الإلكتروني لتفعيل حسابك. حسابك الآن قيد المراجعة.", "auth.error.operationNotAllowed": "تسجيل الدخول بالبريد الإلكتروني/كلمة المرور غير مفعل. يرجى تفعيله في مشروع Firebase الخاص بك.", "auth.doc.id": "تصريح إقامة أو جواز سفر", "auth.doc.license": "رخصة قيادة", "auth.profilePicture": "صورة الملف الشخصي (اختياري)", "city.casablanca": "الدار البيضاء", "city.rabat": "الرباط", "city.tangier": "طنجة", "city.lagos": "لاغوس", "city.accra": "أكرا", "uploader.uploading": "جاري الرفع...", "uploader.acceptedTypes": "يتم قبول الصور أو PDF", "uploader.error": "فشل الرفع. يرجى المحاولة مرة أخرى.", "forgot.title": "إعادة تعيين كلمة المرور", "forgot.desc": "أدخل عنوان بريدك الإلكتروني وسنرسل لك رابطًا.", "forgot.error.emailRequired": "يرجى إدخال عنوان بريدك الإلكتروني.", "forgot.success": "تم إرسال رابط إعادة التعيين! يرجى مراجعة بريدك الإلكتروني.", "forgot.sendLink": "إرسال رابط إعادة التعيين", "profile.dashboardTitle": "لوحة تحكم نشاطي", "profile.gigsPosted": "إجمالي Gigs المنشورة", "profile.gigsCompleted": "Gigs المكتملة (كعامل)", "profile.productsPosted": "المنتجات/الخدمات المنشورة", "profile.ordersPlaced": "الطلبات المقدمة", "profile.totalSpent": "إجمالي الإنفاق", "profile.totalGigEarnings": "إجمالي أرباح Gigs", "profile.tier.novice": "مبتدئ", "profile.tier.silver": "فضي", "profile.tier.gold": "ذهبي", "profile.tier.platinum": "بلاتيني", "profile.seller": "بائع", "profile.myOrders": "طلباتي وحجوزاتي", "profile.editProfile": "تعديل الملف الشخصي", "profile.changePassword": "تغيير كلمة المرور", "profile.adminPanel": "لوحة الإدارة", "profile.logout": "تسجيل الخروج", "profile.deleteAccount": "حذف الحساب", "profile.backToProfile": "الرجوع إلى الملف الشخصي", "profile.noOrders": "ليس لديك أي طلبات أو حجوزات حتى الآن.", "profile.deleteRecord": "حذف هذا السجل", "fab.postGig": "انشر مهمة", "fab.addService": "أضف خدمة", "fab.addGoods": "أضف سلعة", "fab.createStoreNote": "أنشئ متجرًا في ملفك الشخصي للنشر.", "gig.createTitle": "انشر مهمة لمرة واحدة", "gig.title": "عنوان المهمة", "gig.titlePlaceholder": "مثال: تجميع رفوف إيكيا", "gig.description": "الوصف", "gig.descriptionPlaceholder": "صف المهمة...", "gig.aiSuggest": "اقترح وصفًا", "gig.aiSuggesting": "جاري الإنشاء...", "gig.region": "المنطقة/الحي", "gig.regionSelect": "اختر منطقة", "gig.regionNew": "أخرى / منطقة جديدة", "gig.regionNewPlaceholder": "أدخل اسم المنطقة الجديدة", "gig.when": "متى يجب إنجاز هذه المهمة؟", "gig.immediately": "فوراً", "gig.schedule": "جدولة لوقت لاحق", "gig.image": "صورة (1 بحد أقصى)", "gig.phone": "هاتف الاتصال (اختياري)", "gig.phonePlaceholder": "يحل محل هاتف المتجر", "gig.paymentType": "نوع الدفع", "gig.paymentFixed": "سعر ثابت", "gig.paymentHourly": "بالساعة", "gig.paymentAmount": "مبلغ الدفع", "gig.location": "الموقع", "gig.locationFetching": "جاري جلب موقعك...", "gig.postButton": "انشر المهمة", "product.editTitle": "تعديل العنصر", "product.addTitle": "إضافة عنصر جديد", "product.type": "النوع", "product.goods": "سلع", "product.service": "خدمة", "product.name": "الاسم", "product.description": "الوصف", "product.aiGenerate": "توليد الوصف ✨", "product.aiRefine": "تحسين الوصف ✨", "product.tags": "الفئات / العلامات", "product.tagsPlaceholder": "أضف علامة...", "product.tagsHelp": "مطلوب فئة واحدة على الأقل. اضغط Enter أو فاصلة للإضافة.", "product.price": "السعر", "product.unit": "الوحدة (اختياري)", "product.unitPlaceholder": "مثال: جلسة", "product.stock": "كمية المخزون", "product.published": "منشور", "product.unpublished": "غير منشور", "product.inStock": "متوفر", "product.outStock": "نفد المخزون", "product.available": "متاح", "product.unavailable": "غير متاح", "product.image": "صورة المنتج (حد أقصى 1)", "product.save": "حفظ العنصر", "store.manageTitle": "إدارة متجرك", "store.createTitle": "إنشاء متجرك", "store.name": "اسم المتجر", "store.description": "الوصف", "store.city": "المدينة", "store.selectCity": "اختر مدينة", "store.otherCity": "أخرى (يرجى التحديد)", "store.newCity": "اسم المدينة الجديدة", "store.enterCity": "أدخل اسم مدينتك", "store.contactPhone": "هاتف الاتصال", "store.whatsapp": "هذا هو رقم الواتساب الخاص بي", "store.contactEmail": "البريد الإلكتروني للاتصال", "store.location": "الموقع", "store.fetchingLocation": "جاري جلب موقعك...", "store.setLocation": "تحديد موقع المتجر", "store.profilePicture": "صورة الملف الشخصي (مستخدمة في Gigs/Chats)", "store.removePicture": "إزالة الصورة", "store.storeImages": "صور المتجر (3 كحد أقصى)", "store.deliveryNotifs": "تلقي إشعارات للمهام الجديدة وعمليات التوصيل", "store.save": "حفظ المتجر", "store.myItems": "أغراضي", "store.noItems": "متجرك لا يحتوي على أي عناصر.", "store.noGoods": "متجرك لا يحتوي على سلع.", "store.noServices": "متجرك لا يحتوي على خدمات.", "store.setupDesc": "قم بإعداد متجرك لبيع العناصر ونشر المهام.", "store.getStarted": "البدء", "store.manage": "إدارة", "store.addItem": "إضافة عنصر", "store.filterCategory": "تصفية حسب الفئة:", "common.back": "رجوع",
      "common.directions": "الاتجاهات",
      "common.viewMap": "عرض الخريطة",
      "common.close": "إغلاق",
      "common.cancel": "إلغاء",
      "common.delete": "حذف",
      "common.edit": "تعديل",
      "common.save": "حفظ",
      "common.share": "مشاركة",
      "common.total": "المجموع",
      "common.free": "مجاني",
      "common.soldOut": "نفذت الكمية",
      "event.title": "الفعاليات",
      "event.desc": "اعثر على الفعاليات والترفيه المحلي.",
      "event.organizerTag": "أنت المنظم",
      "event.manageHint": "أدر هذا الحدث من الملف الشخصي > فعالياتي.",
      "event.about": "عن هذا الحدث",
      "event.downloadAgenda": "تحميل الأجندة (PDF)",
      "event.joinLink": "رابط الانضمام",
      "event.chatHost": "محادثة المضيف",
      "event.callHost": "اتصال بالمضيف",
      "event.youAreGoing": "أنت ذاهب!",
      "event.ticketCount": "لديك {{count}} تذكرة/تذاكر.",
      "event.printTicket": "طباعة التذكرة",
      "event.attendeeNames": "أسماء الحاضرين",
      "event.guestNames": "أسماء الضيوف",
      "event.bookMore": "حجز المزيد",
      "event.confirmAttendance": "تأكيد الحضور",
      "event.bookTicket": "حجز تذكرة",
      "event.attendees": "الحاضرون",
      "checkout.title": "الدفع",
      "checkout.ownerError": "أنت تملك هذا العنصر.",
      "checkout.ownerErrorDesc": "اضغط لإدارة مخزونك",
      "checkout.paymentMethod": "طريقة الدفع",
      "checkout.cash": "الدفع نقداً",
      "checkout.cashDesc": "ادفع شخصياً عند الاستلام",
      "checkout.placeOrder": "إتمام الطلب",
      "checkout.confirming": "جاري التأكيد...",
      "checkout.orderConfirmed": "تم تأكيد الطلب!",
      "admin.dashboard": "لوحة التحكم",
      "admin.totalUsers": "إجمالي المستخدمين",
      "admin.userGrowth": "نمو المستخدمين",
      "admin.activity": "النشاط هذا العام",
      "admin.contentBreakdown": "توزيع المحتوى",
      "admin.usersByCountry": "المستخدمين حسب الدولة",
      "admin.topCities": "أفضل المدن",
      "admin.topPosters": "أكثر الناشرين",
      "admin.topSellers": "أفضل البائعين",
      "admin.topDeliverers": "أفضل الموصلين",
      "gig.scheduled": "مجدول:",
      "gig.posted": "نشر:",
      "gig.required": "مطلوب:",
      "checkout.serviceType": "خدمة:",
      "checkout.productType": "منتج:",
      "checkout.from": "من:",
      "checkout.quantity": "الكمية:",
      "checkout.inStockCount": "في المخزون",
      "checkout.outOfStock": "نفد المخزون",
      "checkout.categories": "الفئات:",
      "store.goodsTab": "سلع",
      "store.servicesTab": "خدمات",
      "event.createTitle": "إنشاء حدث 🎟️",
      "event.editTitle": "تعديل الحدث ✏️",
      "event.nameLabel": "اسم الحدث",
      "event.typeLabel": "نوع الحدث",
      "event.inPerson": "شخصي",
      "event.online": "عبر الإنترنت",
      "event.startDate": "تاريخ البدء",
      "event.startTime": "وقت البدء",
      "event.endDate": "تاريخ الانتهاء",
      "event.endTime": "وقت الانتهاء",
      "event.priceLabel": "السعر",
      "event.ticketsLabel": "التذاكر المتاحة",
      "event.banner": "لافتة الحدث",
      "event.linkLabel": "رابط الحدث",
      "event.publish": "نشر الحدث",
      "event.saveChanges": "حفظ التغييرات",
      "auth.changePasswordTitle": "تغيير كلمة المرور",
      "auth.currentPassword": "كلمة المرور الحالية",
      "auth.newPassword": "كلمة المرور الجديدة",
      "auth.confirmNewPassword": "تأكيد كلمة المرور الجديدة",
      "auth.updatePassword": "تحديث كلمة المرور",
      "profile.myEventsTickets": "فعالياتي وتذاكري 🎟️",
      "event.tickets": "التذاكر",
      "event.attendeeNamePlaceholder": "اسم الحاضر {{index}}",
      "event.guestNamePlaceholder": "اسم الضيف {{index}}",
      "multi.title": "تم اكتشاف متاجر متعددة",
      "multi.desc": "لديك عناصر من متاجر متعددة. كيف تريد تسليمها؟",
      "multi.fast": "توصيل أسرع",
      "multi.fastDesc": "إنشاء مهام منفصلة. يقوم السائقون بالاستلام في وقت واحد.",
      "multi.consolidated": "توصيل موحد",
      "multi.consolidatedDesc": "إنشاء مهمة واحدة. سائق واحد يزور جميع المتاجر (أرخص، ولكن أبطأ).",
      "camera.choose": "اختر مصدر الصورة",
      "camera.library": "تحميل من المكتبة",
      "camera.photo": "التقط صورة الآن",
      "camera.capture": "التقاط",
      "nav.calculating": "جارٍ حساب المسار...",
      "nav.destination": "الوجهة",
      "fab.addEvent": "إضافة حدث",
      "event.join": "انضم للحدث",
      "event.book": "حجز",
      "event.upcomingTickets": "التذاكر القادمة",
      "event.pastCompleted": "الماضية / المكتملة",
      "event.manage": "إدارة الحدث",
      "event.markComplete": "وضع علامة مكتمل",
      "event.rateHost": "تقييم المضيف:",
      "common.location": "الموقع",
      "gig.myGigsTitle": "مهامي",
      "marketplace.searchPlaceholder": "ابحث عن أي شيء...",
      "event.categoriesLabel": "الفئات",
      "event.addCategoryPlaceholder": "أضف فئة...",
      "event.pricePlaceholder": "0.00 (فارغ = مجاني)",
      "event.ticketsPlaceholder": "حد (فارغ = ∞)",
      "uploader.addImage": "إضافة صورة",
      "status.in-progress": "جاري التنفيذ",
      "status.cancelled": "ملغي",
      "status.completed": "مكتمل",
      "status.open": "مفتوح",
      "status.closed": "مغلق",
      "status.processed": "تمت المعالجة",
      "event.daysRemaining": "in {{count}} day(s)",
      "event.today": "TODAY!",
      "event.ended": "Event Ended",
      "event.agendaLabel": "Event Agenda (PDF - Optional)",
      "event.uploadAgenda": "Upload Agenda",
      "event.descriptionLabel": "الوصف",
      "event.ticketsLeft": "{{count}} متبقية",
      "profile.eventsHosted": "الفعاليات المستضافة",
      "common.translate": "ترجمة",
      "common.translating": "جاري الترجمة...",
      "common.showOriginal": "عرض الأصلي",
      "common.translationFailed": "فشلت الترجمة.",
      "common.youMightLike": "قد يعجبك ايضا",
      "store.countryWarning": "تحذير: تغيير البلد سيؤدي إلى تحديث عرض السوق الخاص بك.",
      "event.aiGenerate": "توليد بالذكاء الاصطناعي",
      "event.aiRefine": "تحسين بالذكاء الاصطناعي",
      "event.aiGenerating": "جاري التوليد...",
      "notif.appSubmitted": "تم تقديم الطلب بنجاح!",
      "notif.bookingConfirmed": "تم تأكيد الحجز! تمت إضافة التذكرة إلى 'فعالياتي'. 🎟️",
      "notif.bookingFailed": "فشل الحجز. حاول مرة اخرى.",
      "notif.eventPublished": "تم نشر الحدث بنجاح! 🎉",
      "notif.eventClosed": "تم إغلاق الحدث بنجاح.",
      "notif.eventCloseFailed": "فشل إغلاق الحدث.",
      "notif.attendeeRemoved": "تمت إزالة الحاضر.",
      "notif.orderPlaced": "تم الطلب! تحقق من زر 'طلباتي' لترتيب التسليم.",
      "notif.orderDeleted": "تم حذف سجل الطلب.",
      "notif.orderDeleteFailed": "فشل حذف الطلب.",
      "notif.itemRemoved": "تمت إزالة العنصر من القائمة.",
      "notif.productDeleteFailed": "فشل حذف المنتج.",
      "notif.reportSubmitted": "تم تقديم تقريرك. سنراجعه قريبا.",
      "notif.eventUpdated": "تم تحديث الحدث بنجاح!",
      "notif.eventUpdateFailed": "فشل تحديث الحدث.",
      "notif.productSubmitted": "تم تقديم المنتج للمراجعة.",
      "notif.coinsPurchased": "تم شراء {{amount}} عملات بنجاح!",
      "notif.ratingThanks": "شكرا لتقييمك!",
      "notif.gigToggleOn": "تم تفعيل إشعارات المهام والتوصيل.",
      "notif.gigToggleOff": "تم تعطيل إشعارات المهام والتوصيل.",
      "msg.newMessage": "رسالة جديدة من {{name}}",
      "msg.newAttendee": "حاضر جديد: {{name}} حجز {{qty}} تذكرة/تذاكر لـ \"{{event}}\"",
      "msg.gigApplied": "تقدم {{name}} لطلبك: \"{{gig}}\" بعرض {{price}}",
      "msg.gigChosen": "لقد تم اختيارك للمهمة: \"{{gig}}\" مقابل {{price}} عملة.",
      "msg.newService": "حجز خدمة جديد: {{title}}",
      "msg.productApproved": "تمت الموافقة على منتجك وهو الآن متاح!",
      "msg.productRejected": "تم رفض تقديم منتجك.",
      "msg.accountApproved": "تهانينا! تمت الموافقة على حسابك.",
      "msg.bookingCancelled": "تم إلغاء حجزك للحدث من قبل المضيف.",
      "msg.newGig": "مهمة جديدة في {{market}}: \"{{title}}\""
     },
};

const countries = [
    // Europe
    { code: 'AL', name: 'Albania', phoneCode: '+355', currency: 'ALL', currencySymbol: 'L' }, 
    { code: 'AD', name: 'Andorra', phoneCode: '+376', currency: 'EUR', currencySymbol: '€' },
    { code: 'AT', name: 'Austria', phoneCode: '+43', currency: 'EUR', currencySymbol: '€' }, 
    { code: 'BY', name: 'Belarus', phoneCode: '+375', currency: 'BYN', currencySymbol: 'Br' },
    { code: 'BE', name: 'Belgium', phoneCode: '+32', currency: 'EUR', currencySymbol: '€' }, 
    { code: 'BA', name: 'Bosnia and Herzegovina', phoneCode: '+387', currency: 'BAM', currencySymbol: 'KM' },
    { code: 'BG', name: 'Bulgaria', phoneCode: '+359', currency: 'BGN', currencySymbol: 'лв' }, 
    { code: 'HR', name: 'Croatia', phoneCode: '+385', currency: 'EUR', currencySymbol: '€' },
    { code: 'CY', name: 'Cyprus', phoneCode: '+357', currency: 'EUR', currencySymbol: '€' }, 
    { code: 'CZ', name: 'Czech Republic', phoneCode: '+420', currency: 'CZK', currencySymbol: 'Kč' },
    { code: 'DK', name: 'Denmark', phoneCode: '+45', currency: 'DKK', currencySymbol: 'kr' }, 
    { code: 'EE', name: 'Estonia', phoneCode: '+372', currency: 'EUR', currencySymbol: '€' },
    { code: 'FO', name: 'Faroe Islands', phoneCode: '+298', currency: 'DKK', currencySymbol: 'kr' }, 
    { code: 'FI', name: 'Finland', phoneCode: '+358', currency: 'EUR', currencySymbol: '€' },
    { code: 'FR', name: 'France', phoneCode: '+33', currency: 'EUR', currencySymbol: '€' }, 
    { code: 'DE', name: 'Germany', phoneCode: '+49', currency: 'EUR', currencySymbol: '€' },
    { code: 'GI', name: 'Gibraltar', phoneCode: '+350', currency: 'GIP', currencySymbol: '£' }, 
    { code: 'GR', name: 'Greece', phoneCode: '+30', currency: 'EUR', currencySymbol: '€' },
    { code: 'HU', name: 'Hungary', phoneCode: '+36', currency: 'HUF', currencySymbol: 'Ft' }, 
    { code: 'IS', name: 'Iceland', phoneCode: '+354', currency: 'ISK', currencySymbol: 'kr' },
    { code: 'IE', name: 'Ireland', phoneCode: '+353', currency: 'EUR', currencySymbol: '€' }, 
    { code: 'IT', name: 'Italy', phoneCode: '+39', currency: 'EUR', currencySymbol: '€' },
    { code: 'LV', name: 'Latvia', phoneCode: '+371', currency: 'EUR', currencySymbol: '€' }, 
    { code: 'LI', name: 'Liechtenstein', phoneCode: '+423', currency: 'CHF', currencySymbol: 'CHF' },
    { code: 'LT', name: 'Lithuania', phoneCode: '+370', currency: 'EUR', currencySymbol: '€' }, 
    { code: 'LU', name: 'Luxembourg', phoneCode: '+352', currency: 'EUR', currencySymbol: '€' },
    { code: 'MK', name: 'Macedonia', phoneCode: '+389', currency: 'MKD', currencySymbol: 'ден' }, 
    { code: 'MT', name: 'Malta', phoneCode: '+356', currency: 'EUR', currencySymbol: '€' },
    { code: 'MD', name: 'Moldova', phoneCode: '+373', currency: 'MDL', currencySymbol: 'L' }, 
    { code: 'MC', name: 'Monaco', phoneCode: '+377', currency: 'EUR', currencySymbol: '€' },
    { code: 'ME', name: 'Montenegro', phoneCode: '+382', currency: 'EUR', currencySymbol: '€' }, 
    { code: 'NL', name: 'Netherlands', phoneCode: '+31', currency: 'EUR', currencySymbol: '€' },
    { code: 'NO', name: 'Norway', phoneCode: '+47', currency: 'NOK', currencySymbol: 'kr' }, 
    { code: 'PL', name: 'Poland', phoneCode: '+48', currency: 'PLN', currencySymbol: 'zł' },
    { code: 'PT', name: 'Portugal', phoneCode: '+351', currency: 'EUR', currencySymbol: '€' }, 
    { code: 'RO', name: 'Romania', phoneCode: '+40', currency: 'RON', currencySymbol: 'lei' },
    { code: 'RU', name: 'Russia', phoneCode: '+7', currency: 'RUB', currencySymbol: '₽' }, 
    { code: 'SM', name: 'San Marino', phoneCode: '+378', currency: 'EUR', currencySymbol: '€' },
    { code: 'RS', name: 'Serbia', phoneCode: '+381', currency: 'RSD', currencySymbol: 'дин.' }, 
    { code: 'SK', name: 'Slovakia', phoneCode: '+421', currency: 'EUR', currencySymbol: '€' },
    { code: 'SI', name: 'Slovenia', phoneCode: '+386', currency: 'EUR', currencySymbol: '€' }, 
    { code: 'ES', name: 'Spain', phoneCode: '+34', currency: 'EUR', currencySymbol: '€' },
    { code: 'SE', name: 'Sweden', phoneCode: '+46', currency: 'SEK', currencySymbol: 'kr' }, 
    { code: 'CH', name: 'Switzerland', phoneCode: '+41', currency: 'CHF', currencySymbol: 'CHF' },
    { code: 'UA', name: 'Ukraine', phoneCode: '+380', currency: 'UAH', currencySymbol: '₴' }, 
    { code: 'GB', name: 'United Kingdom', phoneCode: '+44', currency: 'GBP', currencySymbol: '£' },
    // Africa
    { code: 'DZ', name: 'Algeria', phoneCode: '+213', currency: 'DZD', currencySymbol: 'DA' }, 
    { code: 'AO', name: 'Angola', phoneCode: '+244', currency: 'AOA', currencySymbol: 'Kz' },
    { code: 'BJ', name: 'Benin', phoneCode: '+229', currency: 'XOF', currencySymbol: 'CFA' }, 
    { code: 'BW', name: 'Botswana', phoneCode: '+267', currency: 'BWP', currencySymbol: 'P' },
    { code: 'BF', name: 'Burkina Faso', phoneCode: '+226', currency: 'XOF', currencySymbol: 'CFA' }, 
    { code: 'BI', name: 'Burundi', phoneCode: '+257', currency: 'BIF', currencySymbol: 'FBu' },
    { code: 'CV', name: 'Cabo Verde', phoneCode: '+238', currency: 'CVE', currencySymbol: 'Esc' }, 
    { code: 'CM', name: 'Cameroon', phoneCode: '+237', currency: 'XAF', currencySymbol: 'FCFA' },
    { code: 'CF', name: 'Central African Republic', phoneCode: '+236', currency: 'XAF', currencySymbol: 'FCFA' }, 
    { code: 'TD', name: 'Chad', phoneCode: '+235', currency: 'XAF', currencySymbol: 'FCFA' },
    { code: 'KM', name: 'Comoros', phoneCode: '+269', currency: 'KMF', currencySymbol: 'CF' }, 
    { code: 'CG', name: 'Congo', phoneCode: '+242', currency: 'XAF', currencySymbol: 'FCFA' },
    { code: 'CD', name: 'Congo, DRC', phoneCode: '+243', currency: 'CDF', currencySymbol: 'FC' }, 
    { code: 'CI', name: 'Côte d\'Ivoire', phoneCode: '+225', currency: 'XOF', currencySymbol: 'CFA' },
    { code: 'DJ', name: 'Djibouti', phoneCode: '+253', currency: 'DJF', currencySymbol: 'Fdj' }, 
    { code: 'EG', name: 'Egypt', phoneCode: '+20', currency: 'EGP', currencySymbol: 'E£' },
    { code: 'GQ', name: 'Equatorial Guinea', phoneCode: '+240', currency: 'XAF', currencySymbol: 'FCFA' }, 
    { code: 'ER', name: 'Eritrea', phoneCode: '+291', currency: 'ERN', currencySymbol: 'Nfk' },
    { code: 'SZ', name: 'Eswatini', phoneCode: '+268', currency: 'SZL', currencySymbol: 'L' }, 
    { code: 'ET', name: 'Ethiopia', phoneCode: '+251', currency: 'ETB', currencySymbol: 'Br' },
    { code: 'GA', name: 'Gabon', phoneCode: '+241', currency: 'XAF', currencySymbol: 'FCFA' }, 
    { code: 'GM', name: 'Gambia', phoneCode: '+220', currency: 'GMD', currencySymbol: 'D' },
    { code: 'GH', name: 'Ghana', phoneCode: '+233', currency: 'GHS', currencySymbol: 'GH₵' }, 
    { code: 'GN', name: 'Guinea', phoneCode: '+224', currency: 'GNF', currencySymbol: 'FG' },
    { code: 'GW', name: 'Guinea-Bissau', phoneCode: '+245', currency: 'XOF', currencySymbol: 'CFA' }, 
    { code: 'KE', name: 'Kenya', phoneCode: '+254', currency: 'KES', currencySymbol: 'KSh' },
    { code: 'LS', name: 'Lesotho', phoneCode: '+266', currency: 'LSL', currencySymbol: 'L' }, 
    { code: 'LR', name: 'Liberia', phoneCode: '+231', currency: 'LRD', currencySymbol: 'L$' },
    { code: 'LY', name: 'Libya', phoneCode: '+218', currency: 'LYD', currencySymbol: 'LD' }, 
    { code: 'MG', name: 'Madagascar', phoneCode: '+261', currency: 'MGA', currencySymbol: 'Ar' },
    { code: 'MW', name: 'Malawi', phoneCode: '+265', currency: 'MWK', currencySymbol: 'MK' }, 
    { code: 'ML', name: 'Mali', phoneCode: '+223', currency: 'XOF', currencySymbol: 'CFA' },
    { code: 'MR', name: 'Mauritania', phoneCode: '+222', currency: 'MRU', currencySymbol: 'UM' }, 
    { code: 'MU', name: 'Mauritius', phoneCode: '+230', currency: 'MUR', currencySymbol: '₨' },
    { code: 'MA', name: 'Morocco', phoneCode: '+212', currency: 'MAD', currencySymbol: 'DH' }, 
    { code: 'MZ', name: 'Mozambique', phoneCode: '+258', currency: 'MZN', currencySymbol: 'MT' },
    { code: 'NA', name: 'Namibia', phoneCode: '+264', currency: 'NAD', currencySymbol: 'N$' }, 
    { code: 'NE', name: 'Niger', phoneCode: '+227', currency: 'XOF', currencySymbol: 'CFA' },
    { code: 'NG', name: 'Nigeria', phoneCode: '+234', currency: 'NGN', currencySymbol: '₦' }, 
    { code: 'RW', name: 'Rwanda', phoneCode: '+250', currency: 'RWF', currencySymbol: 'R₣' },
    { code: 'ST', name: 'Sao Tome and Principe', phoneCode: '+239', currency: 'STN', currencySymbol: 'Db' }, 
    { code: 'SN', name: 'Senegal', phoneCode: '+221', currency: 'XOF', currencySymbol: 'CFA' },
    { code: 'SC', name: 'Seychelles', phoneCode: '+248', currency: 'SCR', currencySymbol: '₨' }, 
    { code: 'SL', name: 'Sierra Leone', phoneCode: '+232', currency: 'SLL', currencySymbol: 'Le' },
    { code: 'SO', name: 'Somalia', phoneCode: '+252', currency: 'SOS', currencySymbol: 'S' }, 
    { code: 'ZA', name: 'South Africa', phoneCode: '+27', currency: 'ZAR', currencySymbol: 'R' },
    { code: 'SS', name: 'South Sudan', phoneCode: '+211', currency: 'SSP', currencySymbol: '£' }, 
    { code: 'SD', name: 'Sudan', phoneCode: '+249', currency: 'SDG', currencySymbol: '£' },
    { code: 'TZ', name: 'Tanzania', phoneCode: '+255', currency: 'TZS', currencySymbol: 'TSh' }, 
    { code: 'TG', name: 'Togo', phoneCode: '+228', currency: 'XOF', currencySymbol: 'CFA' },
    { code: 'TN', name: 'Tunisia', phoneCode: '+216', currency: 'TND', currencySymbol: 'DT' }, 
    { code: 'UG', name: 'Uganda', phoneCode: '+256', currency: 'UGX', currencySymbol: 'USh' },
    { code: 'ZM', name: 'Zambia', phoneCode: '+260', currency: 'ZMW', currencySymbol: 'ZK' }, 
    { code: 'ZW', name: 'Zimbabwe', phoneCode: '+263', currency: 'USD', currencySymbol: '$' }
  ];

// Helper function to translate city names (if key exists)
const getTranslatedCity = (cityKey, t) => {
    const key = `city.${cityKey.toLowerCase()}`;
    return t(key) === key ? cityKey : t(key);
};

// Function to get currency information based on country code
const getCurrencyInfo = (countryCode) => {
    return countries.find(c => c.code === countryCode) || { currencySymbol: '$', currency: 'USD' };
};

// --- Chart.js Registration ---
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

// --- Helper Functions ---
const haversineDistance = (coords1, coords2) => { if (!coords1 || !coords2) return null; const toRad = (x) => (x * Math.PI) / 180; const R = 6371; const dLat = toRad(coords2.latitude - coords1.latitude); const dLon = toRad(coords2.longitude - coords1.longitude); const lat1 = toRad(coords1.latitude); const lat2 = toRad(coords2.latitude); const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) + Math.sin(dLon / 2) * Math.sin(dLon / 2) * Math.cos(lat1) * Math.cos(lat2); const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a)); return R * c; };
const formatTimeAgo = (timestamp) => {
    if (!timestamp) return null;
    const now = new Date();
    const seconds = Math.floor((now - timestamp.toDate()) / 1000);
    if(seconds < 60) return "just now";
    let interval = seconds / 31536000;
    if (interval > 1) return Math.floor(interval) + "y ago";
    interval = seconds / 2592000;
    if (interval > 1) return Math.floor(interval) + "mo ago";
    interval = seconds / 86400;
    if (interval > 1) return Math.floor(interval) + "d ago";
    interval = seconds / 3600;
    if (interval > 1) return Math.floor(interval) + "h ago";
    interval = seconds / 60;
    if (interval > 1) return Math.floor(interval) + "m ago";
    return Math.floor(seconds) + "s ago";
};
const getSellerTier = (sales, rating, t) => {
    if (sales >= 10000 && rating >= 4.5) return { name: t('profile.tier.platinum'), icon: <Diamond size={16} className="text-cyan-500" />, color: 'text-cyan-500' };
    if (sales >= 5000 && rating >= 4.0) return { name: t('profile.tier.gold'), icon: <Award size={16} className="text-amber-500" />, color: 'text-amber-500' };
    if (sales >= 1000 && rating >= 3.5) return { name: t('profile.tier.silver'), icon: <ShieldCheck size={16} className="text-slate-500" />, color: 'text-slate-500' };
    return { name: t('profile.tier.novice'), icon: <Sparkles size={16} className="text-indigo-400" />, color: 'text-indigo-400' };
};
const formatPhoneNumberForWhatsApp = (phone) => {
    if (!phone) return '';
    return phone.replace(/[^0-9]/g, '');
};
const getAddressFromCoords = async (lat, lng) => {
    const apiKey = 'e46f090e2b894388a0ed20f3e851b678';
    try {
        const response = await fetch(`https://api.geoapify.com/v1/geocode/reverse?lat=${lat}&lon=${lng}&apiKey=${apiKey}`);
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();
        if (data.features && data.features.length > 0) {
            return data.features[0].properties.formatted;
        }
        return "Address not found";
    } catch (error) {
        console.error("Error fetching address from coords:", error);
        return "Could not fetch address";
    }
};

const convertToWebP = (file) => {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => {
            const canvas = document.createElement('canvas');
            canvas.width = img.width;
            canvas.height = img.height;
            const ctx = canvas.getContext('2d');
            ctx.drawImage(img, 0, 0);
            // Actual re-encoding happens here
            canvas.toBlob((blob) => {
                if (blob) resolve(blob);
                else reject(new Error("WebP conversion failed"));
            }, 'image/webp', 0.8);
        };
        img.onerror = (err) => reject(err);
        img.src = URL.createObjectURL(file);
    });
};

// --- GEMINI API UTILITY ---
const GEMINI_API_URL = "https://generativelanguage.googleapis.com/v1beta/models/gemini-2.5-flash-preview-09-2025:generateContent";
const geminiApiKey = "AIzaSyBTnhLhAYH5ZbFMFJSMtQmEZxxd308nDW0"; // Should be handled securely in your mobile environment

const fetchWithBackoff = async (url, options, retries = 3) => {
    for (let i = 0; i < retries; i++) {
        try {
            const response = await fetch(url, options);
            if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
            return await response.json();
        } catch (error) {
            if (i < retries - 1) {
                const delay = Math.pow(2, i) * 1000;
                await new Promise(resolve => setTimeout(resolve, delay));
            } else {
                console.error("Gemini API failed after multiple retries:", error);
                throw new Error("Could not connect to AI service.");
            }
        }
    }
};

// --- UI Components ---
const ToggleSwitch = ({ checked, onChange }) => (
    <label className="relative inline-flex items-center cursor-pointer">
        <input type="checkbox" checked={checked} onChange={onChange} className="sr-only peer" />
        <div className="w-11 h-6 bg-gray-200 rounded-full peer peer-focus:ring-2 peer-focus:ring-sky-300 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-500"></div>
    </label>
);

const ModalWrapper = ({ children, onClose, isFullScreen = true }) => { 
    return (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex justify-center items-end z-50 animate-fade-in">
            <div 
                className={`bg-slate-50 rounded-t-2xl shadow-2xl w-full ${isFullScreen ? 'h-full' : 'max-h-[90vh]'} p-4 flex flex-col transform transition-all animate-slide-in-up`}
            >
                <div className="flex-shrink-0 flex justify-between items-center p-2 border-b mb-4">
                    <h2 className="text-lg font-bold"> </h2>
                    <button onClick={onClose} className="p-2 rounded-full bg-slate-100 hover:bg-slate-200"><X size={20}/></button>
                </div>
                <div className="flex-grow overflow-y-auto px-2 pb-4">{children}</div>
            </div>
        </div>
    ); 
};
const LoadingSpinner = () => { return <div className="flex justify-center items-center p-10"><Loader2 className="w-12 h-12 text-sky-500 animate-spin" /></div>; };
const GigStatusChip=({status})=>{ const styles = { open: 'bg-green-100 text-green-700', 'in-progress': 'bg-sky-100 text-sky-700', completed: 'bg-amber-100 text-amber-700', closed: 'bg-red-100 text-red-700' }; return <span className={`px-2 py-1 text-xs font-bold rounded-full capitalize ${styles[status] || 'bg-slate-100 text-slate-700'}`}>{status.replace('-', ' ')}</span> };
const StarRating = ({ rating, onRating, readOnly = false }) => (
    <div className="flex gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
            <Star
                key={star}
                className={`cursor-pointer ${rating >= star ? 'text-amber-400' : 'text-slate-300'}`}
                fill={rating >= star ? 'currentColor' : 'none'}
                onClick={() => !readOnly && onRating(star)}
            />
        ))}
    </div>
);
const Header = ({ onLogoClick, language, setLanguage, myStore, onNotificationClick, unreadBellCount, unreadChatCount, onToggleGigNotifications, isDarkMode, onToggleDarkMode, onChatsClick }) => {
    // NEW: Handler for the <select> dropdown
    const onSelectLanguage = (e) => {
        setLanguage(e.target.value); // 'setLanguage' is the prop name for 'setAndSaveLanguage'
    };
    return ( 
    <header className="bg-white/80 backdrop-blur-sm shadow-sm sticky top-0 z-30 dark:bg-slate-900/80 dark:border-b dark:border-slate-700">
        {/* Use px-2 for very small screens, sm:px-4 for larger */}
        <div className="container mx-auto px-2 sm:px-4 py-2 sm:py-3 flex justify-between items-center">
            <div className="flex items-center gap-2 sm:gap-4"> {/* Reduced gap */}
                <div onClick={onLogoClick} className="flex items-center gap-2 cursor-pointer">
                        <img 
                            src="/gignile.png" 
                            alt="GigNile Logo" 
                            className="h-8 w-auto" 
                            onError={(e) => { e.target.onerror = null; e.target.src='/favicon.ico'; }} />
                </div>
                {myStore && <div className="hidden md:flex items-center gap-2 bg-slate-100 dark:bg-slate-800 px-3 py-1 rounded-full"><Globe size={14} className="text-slate-500 dark:text-slate-400" /><span className="text-sm font-semibold dark:text-slate-200">{myStore.country}</span></div>}
            </div>
            <div className="flex items-center gap-1 sm:gap-2"> {/* Reduced gap */}
                {myStore && (
                    <div title={myStore.isDeliveryDriver ? "Gig notifications ON" : "Gig notifications OFF"} className="flex items-center gap-1 sm:gap-2 p-1.5 sm:p-2 bg-slate-100 dark:bg-slate-800 rounded-full"> {/* Reduced padding & gap */}
                        <Briefcase size={20} className={`transition-colors ${myStore.isDeliveryDriver ? 'text-purple-500' : 'text-slate-400'}`}/>
                        <ToggleSwitch 
                            checked={myStore.isDeliveryDriver} 
                            onChange={onToggleGigNotifications}
                        />
                    </div>
                )}
                {/* NEW: Chat Button in Header */}
                <button onClick={onChatsClick} className="relative p-1.5 sm:p-2 rounded-full bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700">
                    <MessageSquare size={20} />
                    {unreadChatCount > 0 && (
                        <span className="absolute top-0 right-0 w-4 h-4 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                            {unreadChatCount}
                        </span>
                    )}
                </button>
                 <button onClick={onNotificationClick} className="relative p-1.5 sm:p-2 rounded-full bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700"> {/* Reduced padding */}
                    <Bell size={20} />
                    {unreadBellCount > 0 && (
                        <span className="absolute top-0 right-0 w-4 h-4 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                            {unreadBellCount}
                        </span>
                    )}
                </button>
                <button onClick={onToggleDarkMode} className="p-1.5 sm:p-2 rounded-full bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700"> {/* Reduced padding */}
                    {isDarkMode ? <Sun size={20} /> : <Moon size={20} />}
                </button>
            </div>
        </div>
    </header>
    );
};
const BottomNavBar = ({ page, setPage, t, isAdmin, onPostClick }) => {
    const NavButton = ({ targetPage, Icon, children }) => (
        <button onClick={() => setPage(targetPage)} className={`flex flex-col items-center justify-center gap-1 w-full h-14 transition-colors ${page.startsWith(targetPage) ? 'text-sky-500 font-bold' : 'text-slate-500 hover:text-sky-500'}`}>
            <Icon className="w-6 h-6" />
            <span className="text-xs font-bold">{children}</span>
        </button>
    );

    return (
        <nav className="fixed bottom-0 left-0 right-0 bg-white/80 backdrop-blur-sm border-t shadow-lg z-40 h-16 dark:bg-indigo-950/90 dark:border-slate-700">
            <div className="container mx-auto h-full flex justify-around items-center">
                <div className="w-1/5"><NavButton targetPage="marketplace" Icon={Home}>{t('explore')}</NavButton></div>
                <div className="w-1/5"><NavButton targetPage="my-gigs" Icon={Briefcase}>{t('myGigs')}</NavButton></div>
                <div className="w-1/5 flex justify-center">
                    <button 
                        onClick={onPostClick}
                        className="-mt-8 bg-purple-500 text-white w-16 h-16 rounded-full flex items-center justify-center shadow-lg hover:bg-purple-600 transition-transform transform hover:scale-110"
                    >
                        <PlusCircle className="w-8 h-8" />
                    </button>
                </div>
                <div className="w-1/5"><NavButton targetPage="my-store" Icon={Store}>{t('myStore')}</NavButton></div>
                <div className="w-1/5"><NavButton targetPage="profile" Icon={User}>{t('profile')}</NavButton></div>
            </div>
        </nav>
    );
};

const EventCard = ({ product, onActionClick, t, currencySymbol, allStores }) => { 
    const event = product; 
    const isFree = event?.price === 0;
    const mainCategory = event?.categories?.[0] || 'Entertainment';
     
     // Find the poster's store profile (Moved hook up to prevent conditional execution error)
    const poster = useMemo(() => allStores?.find(s => s.id === event?.storeId), [allStores, event?.storeId]);
    const posterImage = poster?.profileImageUrl || 'https://placehold.co/40x40/EEE/31343C?text=👤';
    if (!event) return null;
    return (
        <div className="bg-white rounded-2xl shadow-md border overflow-hidden group hover:shadow-xl transition-all duration-300 flex flex-col">
            <div className="relative h-48 overflow-hidden">
                <img 
                    src={event.images?.[0] || 'https://placehold.co/400x300/8b5cf6/ffffff?text=Event'} 
                    alt={event.name} 
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110 group-hover:brightness-105"
                />
                <div className="absolute top-2 right-2 bg-white/90 backdrop-blur-md px-3 py-1 rounded-full text-xs font-bold text-slate-700 shadow-sm">
                    {mainCategory}
                </div>
                {/* Poster Profile Image Overlay */}
                <div className="absolute bottom-2 left-2 flex items-center gap-2 bg-black/50 backdrop-blur-sm px-2 py-1 rounded-full">
                    <img src={posterImage} alt="Poster" className="w-6 h-6 rounded-full object-cover border border-white" />
                    <span className="text-xs text-white font-medium truncate max-w-[100px]">{event.storeName}</span>
                </div>
                <div className="absolute top-2 right-2 bg-white/90 backdrop-blur-md px-3 py-1 rounded-full text-xs font-bold text-slate-700 shadow-sm">
                    {mainCategory}
                </div>
            </div>
            <div className="p-4 flex flex-col flex-grow">
                <div className="flex justify-between items-start mb-2">
                    <h3 className="text-lg font-bold text-slate-800 line-clamp-1">{event.name}</h3>
                    <span className={`px-2 py-1 rounded text-xs font-bold ${isFree ? 'bg-green-100 text-green-700' : 'bg-purple-100 text-purple-700'}`}>
                        {isFree ? t('common.free') : `${currencySymbol || '$'}${event.price}`}
                    </span>
                </div>
                
                <div className="space-y-2 text-sm text-slate-500 mb-4 flex-grow">
                    <div className="flex items-center gap-2">
                        <Calendar size={14} className="text-purple-500"/>
                        <span>{new Date(event.date).toLocaleString()}</span>
                    </div>
                    <div className="flex items-center gap-2">
                        <MapPin size={14} className="text-purple-500"/>
                        <span className="truncate">{event.location?.address || 'Location TBD'}</span>
                    </div>
                    <p className="line-clamp-2 text-xs mt-2">{event.description}</p>
                </div>

                <button 
                    onClick={() => onActionClick(event)}
                    className="w-full bg-purple-600 hover:bg-purple-700 text-white font-bold py-2.5 rounded-xl shadow-md transition-colors flex items-center justify-center gap-2"
                >
                    <Ticket size={18} /> {isFree ? t('event.join') : t('event.book')}
                </button>
            </div>
        </div>
    );
};

const ProductCard = ({ product, onActionClick, allStores, onViewStore, t }) => {
    const seller = useMemo(() => allStores.find(s => s.id === product.storeId), [allStores, product.storeId]);
    const averageRating = seller && seller.ratingCount > 0 ? seller.totalRating / seller.ratingCount : 0;
    const sellerTier = getSellerTier(seller?.sales || 0, averageRating, t);
    const profileImage = seller?.profileImageUrl || 'https://placehold.co/40x40/EEE/31343C?text=👤';
    const currencySymbol = product.currencySymbol || '$'; // Use stamped currency, fallback to $
    const isStockManaged = product.type === 'goods' && product.stock !== undefined;
    const stockCount = product.stock || 0;
    const isOutOfStock = isStockManaged && stockCount <= 0;
    

    return ( <div className="bg-white rounded-xl shadow-md border p-4 flex flex-col group relative"> 
    {(!product.isInStock || isOutOfStock) && (
        <div className="absolute inset-0 bg-white/70 flex items-center justify-center z-10 rounded-xl">
            <span className="bg-red-500 text-white font-bold py-1 px-3 rounded-full">{isOutOfStock ? 'Out of Stock' : 'Not Available'}</span>
        </div>
    )}
    <div className="overflow-hidden rounded-xl mb-4 h-48 relative shadow-sm">
        <img src={product.images?.[0] || 'https://placehold.co/400x300/EEE/31343C?text=No+Image'} alt={product.name} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110 group-hover:brightness-105" />
    </div> 
    <h3 className="text-lg font-bold text-slate-800 flex-grow mt-2">{product.name}</h3> 
    {product.categories && product.categories.length > 0 && (
        <div className="flex flex-wrap gap-1 my-2">
            {product.categories.slice(0, 3).map(cat => (
                <span key={cat} className="text-xs bg-slate-100 text-slate-600 px-2 py-1 rounded-full">{cat}</span>
            ))}
        </div>
    )}
    <div onClick={() => onViewStore && onViewStore(seller.id)} className="flex items-center gap-2 text-sm text-slate-500 cursor-pointer hover:underline mt-auto pt-2">
        <img src={profileImage} alt={seller?.name} className="w-5 h-5 rounded-full object-cover" onError={(e) => { e.target.onerror = null; e.target.src='https://placehold.co/40x40/EEE/31343C?text=👤'; }}/>
        <span>{product.storeName}</span>
        <span className="ml-auto flex items-center gap-1 text-xs">{sellerTier.icon}</span>
    </div>
    {averageRating > 0 && <div className="flex items-center gap-1 mt-1"><Star size={16} className="text-amber-400" fill="currentColor"/><span className="font-bold text-sm">{averageRating.toFixed(1)}</span><span className="text-xs text-slate-400">({seller.ratingCount})</span></div>}
    <div className="border-t pt-4 mt-4 flex justify-between items-end"> 
    <div>
        <p className="text-xl font-extrabold text-teal-600">{currencySymbol}{(product.price || 0).toFixed(2)} {product.unit && <span className="text-sm font-normal text-slate-500">/ {product.unit}</span>}</p> 
        {isStockManaged && !isOutOfStock && (
            <span className="text-xs font-semibold text-green-700 bg-green-100 px-2 py-0.5 rounded-full">{stockCount} in Stock</span>
        )}
    </div>
    <button onClick={onActionClick} disabled={!product.isInStock || isOutOfStock} className={`bg-teal-500 text-white font-bold py-2 px-4 rounded-lg shadow-md ${(!product.isInStock || isOutOfStock) ? 'opacity-50 cursor-not-allowed' : 'hover:bg-teal-600'}`}>{product.type === 'service' ? 'Book' : 'Buy'}</button>
    </div> </div> ); 
};
const GigCard = ({ gig, onClick, userLocation, showStatus, allStores, t })=>{ 
    const creator = useMemo(() => allStores.find(s => s.id === gig.creatorId), [allStores, gig.creatorId]);
    const averageRating = creator && creator.ratingCount > 0 ? creator.totalRating / creator.ratingCount : 0;
    const sellerTier = getSellerTier(creator?.sales || 0, averageRating, t);
    const profileImage = creator?.profileImageUrl || 'https://placehold.co/40x40/EEE/31343C?text=👤';
    const isDelivery = gig.type === 'delivery'; 
    const distance = useMemo(() => haversineDistance(userLocation, gig.location?.coords), [userLocation, gig.location]);
    const currencySymbol = gig.currencySymbol || '$'; // Use stamped currency, fallback to $
    const payment = gig.paymentType === 'hourly' ? `${currencySymbol}${gig.pay}/hr` : `${currencySymbol}${gig.pay}`;
    const timeAgo = formatTimeAgo(gig.createdAt);

    // NEW: Scheduling Display Logic
    const isScheduled = gig.scheduleType === 'specificTime' && gig.scheduledAt;
    // Format the scheduled time if it exists, otherwise use 'timeAgo'
    const displayTime = isScheduled ? formatTimeAgo(gig.scheduledAt) : timeAgo;
    const timeLabel = isScheduled ? t('gig.scheduled') : (timeAgo ? t('gig.posted') : null);

    return ( 
        <div onClick={onClick} className="bg-white rounded-lg shadow border p-4 cursor-pointer hover:border-sky-500 flex flex-col justify-between group">
            <div> 
                {showStatus && <div className="flex justify-end mb-2"><GigStatusChip status={gig.status}/></div>} 
                <div className="h-48 w-full mb-4 overflow-hidden rounded-lg relative shadow-sm">
                    <img src={gig.images?.[0] || 'https://placehold.co/400x300/EEE/31343C?text=Gig'} alt={gig.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110 group-hover:brightness-105" />
                </div>
                <div className="flex justify-between items-center text-xs text-slate-500 mb-2">
                    <span className="flex items-center gap-1"><span className={sellerTier.color}>{sellerTier.icon}</span>{isDelivery ? `From: ${gig.storeName}` : `By: ${gig.creatorName}`}</span>
                    {timeLabel && <span className={`flex items-center gap-1 ${isScheduled ? 'font-bold text-purple-600' : ''}`}><Clock size={12} className="inline-block"/>{displayTime}</span>}
                </div>
                {averageRating > 0 && <div className="flex items-center gap-1 mb-2"><Star size={14} className="text-amber-400" fill="currentColor"/><span className="font-bold text-xs">{averageRating.toFixed(1)}</span><span className="text-xs text-slate-400">({creator.ratingCount} ratings)</span></div>}
                <h3 className="font-bold">{gig.title}</h3> 
            </div> 
            <div className="flex justify-between items-end mt-4"> 
                <div className="flex items-center gap-2">
                   {distance !== null && (<span className="text-sm font-semibold text-slate-600 bg-slate-100 px-2 py-1 rounded-full">{distance.toFixed(1)} km</span>)}
                   
                </div>
                <p className={`font-bold text-lg ${isDelivery ? 'text-purple-600' : 'text-sky-600'}`}>{payment}</p> 
            </div> 
        </div> 
    ); 
};
const GigList = ({gigs,onGigClick,title, userLocation, showStatus, allStores, t})=>{if(gigs.length===0){return(<div>{title && <h2 className="text-2xl md:text-3xl font-bold mb-6">{title}</h2>}<div className="text-center bg-white p-8 rounded-xl shadow-sm border"><p className="text-slate-500">No gigs here right now.</p></div></div>)}return(<div>{title && <h2 className="text-2xl md:text-3xl font-bold mb-6 text-slate-700">{title}</h2>}<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">{gigs.map(gig=><GigCard key={gig.id} gig={gig} onClick={()=>onGigClick(gig)} userLocation={userLocation} showStatus={showStatus} allStores={allStores} t={t} />)}</div></div>)};

const ProductApprovalModal = ({ product, onClose, onApprove, onReject }) => {
    if (!product) return null;

    return (
        <ModalWrapper onClose={onClose}>
            <h2 className="text-2xl font-bold mb-4">Review Product</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                    {product.images && product.images.length > 0 ? (
                        product.images.map((img, i) => (
                            <img key={i} src={img} alt={product.name} className="w-full h-64 object-contain bg-gray-100 rounded-lg mb-2 shadow-md" />
                        ))
                    ) : (
                        <div className="w-full h-48 bg-slate-200 rounded-lg flex items-center justify-center">
                            <span className="text-slate-500">No Image</span>
                        </div>
                    )}
                </div>
                <div className="space-y-3">
                    <div>
                        <label className="font-semibold text-slate-500 text-sm">Product Name</label>
                        <p className="text-lg font-bold">{product.name}</p>
                    </div>
                     <div>
                        <label className="font-semibold text-slate-500 text-sm">Store Name</label>
                        <p>{product.storeName}</p>
                    </div>
                     <div>
                        <label className="font-semibold text-slate-500 text-sm">Type</label>
                        <p className="capitalize">{product.type}</p>
                    </div>
                    <div>
                        <label className="font-semibold text-slate-500 text-sm">Price</label>
                        <p className="text-xl font-bold text-teal-600">${product.price.toFixed(2)} {product.unit && `/ ${product.unit}`}</p>
                    </div>
                    <div>
                        <label className="font-semibold text-slate-500 text-sm">Description</label>
                        <p className="whitespace-pre-wrap bg-slate-100 p-2 rounded-md">{product.description}</p>
                    </div>
                </div>
            </div>

            <div className="flex justify-end gap-4 pt-6 border-t mt-6">
                <button onClick={() => onReject(product.id, product.storeId)} className="bg-red-500 text-white font-bold py-2 px-6 rounded-lg flex items-center gap-2">
                    <X size={18} /> Reject
                </button>
                <button onClick={() => onApprove(product.id, product.storeId)} className="bg-green-500 text-white font-bold py-2 px-6 rounded-lg flex items-center gap-2">
                    <CheckCircle size={18} /> Approve
                </button>
            </div>
        </ModalWrapper>
    );
};

const ApplyForGigModal = ({ gig, onClose, onApplySubmit, myStore }) => {
    const [proposedPrice, setProposedPrice] = useState(gig.pay || '');
    const [isLoading, setIsLoading] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        const price = parseFloat(proposedPrice);
        if (isNaN(price) || price <= 0) {
            alert("Please enter a valid price.");
            return;
        }
        setIsLoading(true);
        try {
            // Pass the proposed price to the submission handler
            await onApplySubmit(gig.id, gig.creatorId, gig.title, price);
            onClose();
        } catch (error) {
            console.error("Failed to apply for gig:", error);
            alert("Failed to submit application. " + error.message);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <ModalWrapper onClose={onClose} isFullScreen={false}>
            <h2 className="text-2xl font-bold mb-4">Apply for "{gig.title}"</h2>
            <p className="text-slate-600 mb-2">The poster's initial price is <span className="font-bold text-lg text-sky-600">{gig.currencySymbol || '$'}{gig.pay.toFixed(2)}</span> ({gig.paymentType}).</p>
            <p className="text-slate-600 mb-6">You can propose your own price for this gig. The poster will see your offer.</p>
            <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                    <label className="font-medium text-slate-600">Your Proposed Price ({gig.currencySymbol || '$'})</label>
                    <input 
                        type="number"
                        step="0.01"
                        value={proposedPrice}
                        onChange={e => setProposedPrice(e.target.value)}
                        className="w-full mt-1 p-3 border-2 border-slate-200 rounded-lg"
                        required
                    />
                </div>
                <button type="submit" disabled={isLoading} className="w-full bg-sky-600 text-white font-bold py-3 rounded-lg disabled:bg-sky-300 flex justify-center items-center">
                    {isLoading ? <Loader2 className="animate-spin" /> : 'Submit Application'}
                </button>
            </form>
        </ModalWrapper>
    );
};

const ReportModal = ({ onClose, onSubmit, reportedUserName }) => {
    const [reason, setReason] = useState('');
    const [isLoading, setIsLoading] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (reason.trim() === '') {
            alert('Please provide a reason for the report.');
            return;
        }
        setIsLoading(true);
        try {
            await onSubmit(reason);
        } catch (error) {
            console.error("Failed to submit report:", error);
            alert("Failed to submit report. Please try again.");
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <ModalWrapper onClose={onClose} isFullScreen={false}>
            <h2 className="text-2xl font-bold mb-4">Report {reportedUserName || 'User'}</h2>
            <p className="text-slate-600 mb-6">Please provide a detailed reason for reporting this user. Your feedback helps us maintain a safe community.</p>
            <form onSubmit={handleSubmit}>
                <textarea
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    rows="5"
                    className="w-full p-2 border rounded-lg"
                    placeholder="Describe the issue..."
                    required
                />
                <div className="flex justify-end mt-4">
                    <button type="submit" disabled={isLoading} className="w-full bg-red-600 text-white font-bold py-3 rounded-lg disabled:bg-red-300 flex justify-center items-center">
                        {isLoading ? <Loader2 className="animate-spin" /> : 'Submit Report'}
                    </button>
                </div>
            </form>
        </ModalWrapper>
    );
};

const ReportDetailModal = ({ report, allStores, gigs, onClose, onChat, onViewProfile, onViewGig, onResolve }) => {
    if (!report) return null;

    const reporter = allStores.find(s => s.id === report.reporterId);
    const reported = allStores.find(s => s.id === report.reportedId);
    const gig = gigs.find(g => g.id === report.gigId);

    return (
        <ModalWrapper onClose={onClose}>
            <h2 className="text-2xl font-bold mb-4">Report Details</h2>
            <div className="space-y-4 text-sm">
                <p><span className="font-semibold">Reported by:</span> <span className="cursor-pointer text-sky-600 hover:underline" onClick={() => onViewProfile(reporter)}>{reporter?.name || 'Unknown User'}</span></p>
                <p><span className="font-semibold">Reported User:</span> <span className="cursor-pointer text-sky-600 hover:underline" onClick={() => onViewProfile(reported)}>{reported?.name || 'Unknown User'}</span></p>
                <p><span className="font-semibold">Date:</span> {report.timestamp ? new Date(report.timestamp.seconds * 1000).toLocaleString() : 'N/A'}</p>
                {gig && <p><span className="font-semibold">Regarding Gig:</span> <span className="cursor-pointer text-sky-600 hover:underline" onClick={() => onViewGig(gig)}>{gig.title}</span></p>}
                <div>
                    <p className="font-semibold">Reason:</p>
                    <p className="bg-slate-100 p-3 rounded-md mt-1 whitespace-pre-wrap">{report.reason}</p>
                </div>
            </div>
             <div className="flex justify-end gap-4 pt-6 border-t mt-6 flex-wrap">
                <button onClick={() => onChat(report.reporterId, true)} className="bg-sky-500 text-white font-bold py-2 px-4 rounded-lg">Chat with Reporter</button>
                <button onClick={() => onChat(report.reportedId, true)} className="bg-purple-500 text-white font-bold py-2 px-4 rounded-lg">Chat with Reported</button>
                {report.status === 'new' && <button onClick={() => onResolve(report.id)} className="bg-green-500 text-white font-bold py-2 px-4 rounded-lg">Mark as Resolved</button>}
            </div>
        </ModalWrapper>
    );
};

const AdminConfirmDeleteModal = ({ onClose, onConfirm, userName }) => (
    <ModalWrapper onClose={onClose} isFullScreen={false}>
        <div className="text-center">
            <AlertCircle size={48} className="mx-auto text-red-500 mb-4" />
            <h2 className="text-2xl font-bold mb-2">Confirm Permanent Deletion</h2>
            <p className="text-slate-600 mb-6">
                Are you sure you want to permanently delete <strong>{userName}</strong> and all their associated data (store, products, gigs)? This action cannot be undone.
            </p>
            <div className="flex justify-center gap-4">
                <button onClick={onClose} className="bg-slate-200 font-bold py-2 px-6 rounded-lg">Cancel</button>
                <button onClick={onConfirm} className="bg-red-600 text-white font-bold py-2 px-6 rounded-lg">Yes, Delete Permanently</button>
            </div>
        </div>
    </ModalWrapper>
);

const ConfirmDeleteModal = ({ onClose, onConfirm, itemName, isLoading }) => (
    <ModalWrapper onClose={onClose} isFullScreen={false}>
        <div className="text-center">
            <AlertCircle size={48} className="mx-auto text-red-500 mb-4" />
            <h2 className="text-2xl font-bold mb-2">Confirm Deletion</h2>
            <p className="text-slate-600 mb-6">
                Are you sure you want to permanently delete this item?
                {itemName && <strong className="block mt-2">"{itemName}"</strong>}
                <br/>This action cannot be undone.
            </p>
            <div className="flex justify-center gap-4">
                <button onClick={onClose} className="bg-slate-200 font-bold py-2 px-6 rounded-lg">Cancel</button>
                <button onClick={onConfirm} disabled={isLoading} className="bg-red-600 text-white font-bold py-2 px-6 rounded-lg disabled:bg-red-400">
                    {isLoading ? <Loader2 className="animate-spin"/> : 'Yes, Delete'}
                </button>
            </div>
        </div>
    </ModalWrapper>
);

const ImageLightboxModal = ({ imageUrl, onClose }) => {
    return (
        <div className="fixed inset-0 bg-black/80 z-[100] flex items-center justify-center p-4 animate-fade-in" onClick={onClose}>
            {/* Close Button */}
            <button onClick={onClose} className="absolute top-4 right-4 text-white p-2 rounded-full bg-black/50 hover:bg-black/80 transition-opacity">
                <X size={30} />
            </button>
            
            {/* Image */}
            <img 
                src={imageUrl} 
                alt="Zoomed view" 
                className="max-w-full max-h-full rounded-lg shadow-2xl object-contain" 
                onClick={(e) => e.stopPropagation()} // Stop click from closing if they click the image
            />
        </div>
    );
};

const RejectionModal = ({ onClose, onConfirm, userName }) => {
    const [reason, setReason] = useState('');
    const [isLoading, setIsLoading] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (reason.trim() === '') {
            alert('A reason for rejection is required.');
            return;
        }
        setIsLoading(true);
        await onConfirm(reason);
    };

    return (
        <ModalWrapper onClose={onClose} isFullScreen={false}>
            <div className="text-center">
                <UserX size={48} className="mx-auto text-red-500 mb-4" />
                <h2 className="text-2xl font-bold mb-2">Reject User: {userName}</h2>
                <p className="text-slate-600 mb-6">
                    Provide a reason for rejection. This will be sent to the user, and their application data will be deleted, allowing them to sign up again.
                </p>
                <form onSubmit={handleSubmit} className="space-y-4">
                    <textarea
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        rows="4"
                        className="w-full p-2 border rounded-lg"
                        placeholder="e.g., ID document was unclear, information provided could not be verified..."
                        required
                    />
                    <button type="submit" disabled={isLoading} className="w-full bg-red-600 text-white font-bold py-3 rounded-lg disabled:bg-red-300 flex justify-center items-center">
                        {isLoading ? <Loader2 className="animate-spin" /> : 'Confirm Rejection & Delete'}
                    </button>
                </form>
            </div>
        </ModalWrapper>
    );
};


const NotificationPanel = ({ notifications, onClose, onNotificationClick }) => {
    return (
        <div className="fixed inset-0 bg-black bg-opacity-20 z-40" onClick={onClose}>
            <div className="absolute top-16 right-4 w-80 max-w-sm bg-white rounded-lg shadow-xl border" onClick={e => e.stopPropagation()}>
                <div className="p-4 border-b flex justify-between items-center">
                    <h3 className="font-bold">Notifications</h3>
                    <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X size={18} /></button>
                </div>
                <div className="max-h-96 overflow-y-auto">
                    {notifications.length === 0 ? (
                        <p className="text-slate-500 text-center p-4">No new notifications.</p>
                    ) : (
                        notifications.map(n => (
                            <div key={n.id} className={`p-4 border-b hover:bg-slate-50 cursor-pointer ${!n.read ? 'bg-sky-50' : ''}`} onClick={() => onNotificationClick(n)}>
                                <p className="text-sm">{n.message}</p>
                                <p className="text-xs text-slate-400 mt-1">{formatTimeAgo(n.timestamp)}</p>
                            </div>
                        ))
                    )}
                </div>
            </div>
        </div>
    );
};

const ImageUploader = ({ onUpload, storage, folder, t }) => {
    const [uploading, setUploading] = useState(false);
    const [preview, setPreview] = useState(null);
    const [isSelectionModalOpen, setIsSelectionModalOpen] = useState(false);
    const [isCameraOpen, setIsCameraOpen] = useState(false); // NEW: State for camera modal

    const handleFileChange = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        if (file.size > 1 * 1024 * 1024) {
            alert("Image is too large. Please choose a file smaller than 2MB.");
            return; // Stop execution immediately
        }

        setUploading(true);
        setPreview(URL.createObjectURL(file)); 

        try {
            // 1. Physically re-encode image to WebP
            const webpBlob = await convertToWebP(file);

            // 2. Rename extension
            const fileNameWithoutExt = file.name.substring(0, file.name.lastIndexOf('.')) || file.name;
            const storageRef = ref(storage, `${folder}/${Date.now()}_${fileNameWithoutExt}.webp`);
            
            // 3. Upload new blob
            const snapshot = await uploadBytes(storageRef, webpBlob);
            const downloadURL = await getDownloadURL(snapshot.ref);
            onUpload(downloadURL);

        } catch (error) {
            console.error("Image upload failed:", error);
            alert("Image upload failed. Please try again.");
        } finally {
            setUploading(false);
        }
    };

    const handleCameraSelect = () => {
        setIsSelectionModalOpen(false);
        setIsCameraOpen(true);
    };

    const handleFileSelect = () => {
        setIsSelectionModalOpen(false);
        // Manually trigger the hidden file input click
        document.getElementById(`file-input-${folder}`).click(); 
    };

    return (
        <>
        <div onClick={() => !uploading && setIsSelectionModalOpen(true)} className={`relative flex flex-col items-center justify-center gap-2 border-2 border-dashed rounded-lg p-4 text-center text-sm ${uploading ? 'text-slate-400' : 'text-slate-500 cursor-pointer hover:bg-sky-50 hover:border-sky-500'} transition-colors ${preview ? 'border-sky-500' : 'border-slate-300'}`}>
            {uploading ? (
                <div className="space-y-2">
                    <Loader2 className="animate-spin w-10 h-10 text-sky-500" />
                    <span>Uploading...</span>
                </div>
            ) : preview ? (
                 <img src={preview} className="w-full h-24 object-cover rounded-lg shadow-md" />
            ) : (
                <>
                    <ImagePlus size={30} className="text-slate-400"/>
                    <span className="font-semibold">{t ? t('uploader.addImage') : 'Add Image'}</span>
                </>
            )}
            {/* Hidden Input for manual file selection */}
            <input id={`file-input-${folder}`} type="file" accept="image/*" className="hidden" onChange={handleFileChange} disabled={uploading}/>
        </div>

        {/* This is the new, more interesting UI/UX modal */}
        {isSelectionModalOpen && (
            <ModalWrapper onClose={() => setIsSelectionModalOpen(false)} isFullScreen={false}>
                <h3 className="text-xl font-bold mb-6 text-center">{t('camera.choose')}</h3>
                <div className="flex flex-col sm:flex-row gap-4">
                    <button 
                        type="button" // FIX: Prevent form submission
                        onClick={handleFileSelect} 
                        className="flex-1 flex flex-col items-center p-8 bg-slate-100 dark:bg-slate-700 rounded-lg hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors"
                    >
                        <UploadCloud size={40} className="text-sky-500 mb-3"/>
                        <span className="font-semibold text-lg dark:text-slate-50">{t('camera.library')}</span>
                    </button>
                    <button 
                        type="button" // FIX: Prevent form submission
                        onClick={handleCameraSelect} 
                        className="flex-1 flex flex-col items-center p-8 bg-slate-100 dark:bg-slate-700 rounded-lg hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors"
                    >
                        <Camera size={40} className="text-purple-500 mb-3"/>
                        <span className="font-semibold text-lg dark:text-slate-50">{t('camera.photo')}</span>
                    </button>
                </div>
            </ModalWrapper>
        )}
        {isCameraOpen && <CameraModal 
            onClose={() => setIsCameraOpen(false)} 
            onUpload={(downloadURL) => {
                onUpload(downloadURL);     // 1. Saves the URL to the parent modal (Gig/Product)
                setPreview(downloadURL);  // 2. FIX: Sets the local preview in this component
            }} 
            storage={storage} 
            folder={folder}
            t={t} />}
        </>
        
    );
};

// NEW: CameraModal Component
const CameraModal = ({ onClose, onUpload, storage, folder, t }) => {
    const videoRef = useRef(null);
    const canvasRef = useRef(null);
    const [isLoading, setIsLoading] = useState(true);
    const [stream, setStream] = useState(null);
    const [facingMode, setFacingMode] = useState('user'); // 'user' (front) or 'environment' (back)
    const [videoDevices, setVideoDevices] = useState([]);
    const [cameraError, setCameraError] = useState(null);

    useEffect(() => {
        let currentStream = null;
        const constraints = { video: { facingMode: facingMode } };

        // Stop previous stream if switching cameras
        if (stream) {
            stream.getTracks().forEach(track => track.stop());
            setStream(null);
        }
        
        setIsLoading(true);
        setCameraError(null);
        navigator.mediaDevices.getUserMedia(constraints) // Use dynamic facingMode
            .then(stream => {
                currentStream = stream;
                if (videoRef.current) {
                    videoRef.current.srcObject = stream;
                    setIsLoading(false);

                    // FIX: Enumerate devices *after* getting permission
                    navigator.mediaDevices.enumerateDevices()
                        .then(devices => {
                            setVideoDevices(devices.filter(device => device.kind === 'videoinput'));
                        })
                        .catch(err => console.error("Could not enumerate devices:", err));
                }
            })
            .catch(err => {
                console.error("Camera access denied:", err);
                if (facingMode === 'environment') {
                    setFacingMode('user'); // Fallback to front camera if back camera fails
                } else {
                    setCameraError("Camera access denied or device not found. Check permissions.");
                    setIsLoading(false);
                }
            });

        return () => {
            // Stop the stream when component unmounts
            if (currentStream) {
                currentStream.getTracks().forEach(track => track.stop());
            }
        };
    }, [facingMode, onClose]); // Re-run when facingMode changes

    const handleSwitchCamera = () => {
        if (videoDevices.length > 1) {
            setFacingMode(prevMode => (prevMode === 'user' ? 'environment' : 'user'));
        }
    };

    // 2. Handle Capture and Upload
    const handleCapture = async () => {
        if (!videoRef.current || !canvasRef.current || !storage) {
            alert("Capture components are not ready.");
            return;
        }

        const video = videoRef.current;
        const canvas = canvasRef.current;
        const context = canvas.getContext('2d');

        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;

        // Draw the current video frame onto the canvas
        context.drawImage(video, 0, 0, video.videoWidth, video.videoHeight);

        // Stop the video stream
        if (stream) {
            stream.getTracks().forEach(track => track.stop());
        }
        
        setIsLoading(true); // Show spinner

        // 3. Convert canvas to Blob (file)
        canvas.toBlob(async (blob) => {
            if (!blob) {
                alert("Failed to create image file.");
                setIsLoading(false);
                onClose();
                return;
            }
            const fileName = `${Date.now()}_snapshot.webp`; // New extension
            const storageRef = ref(storage, `${folder}/${fileName}`);
            try {
                const snapshot = await uploadBytes(storageRef, blob);
                const downloadURL = await getDownloadURL(snapshot.ref);
                onUpload(downloadURL); 
            } catch (error) {
                console.error("Snapshot upload failed:", error);
                alert("Snapshot upload failed. Please try again.");
            } finally {
                setIsLoading(false);
                onClose(); 
            }
        }, 'image/webp', 0.8);
    };

    return (
        <ModalWrapper onClose={onClose} isFullScreen={true}>
            <div className="relative w-full h-full flex flex-col items-center justify-center bg-black">
                
                {/* Error/Loading Display */}
                {cameraError && (
                    <div className="p-4 bg-red-800 text-white rounded-lg absolute top-10 z-10 w-3/4 text-center">
                        {cameraError}
                    </div>
                )}
                {isLoading && !cameraError && <Loader2 className="w-12 h-12 text-sky-500 animate-spin absolute" />}
                
                <video 
                    ref={videoRef} 
                    className={`w-full max-w-lg shadow-lg ${!isLoading && !cameraError ? 'block' : 'hidden'} ${facingMode === 'user' ? 'transform scale-x-1' : ''}`} // Removed mirror for 'user'
                    playsInline // Important for mobile browsers
                    autoPlay // Ensure video plays
                />
                
                {/* Hidden canvas for processing the snapshot */}
                <canvas ref={canvasRef} className="hidden" />

                {/* Controls */}
                <div className="flex items-center gap-6 mt-6 p-4">
                    {/* Switch Button */}
                    {videoDevices.length > 1 && !cameraError && (
                        <button type="button" onClick={handleSwitchCamera} disabled={isLoading} className="bg-slate-200 text-slate-700 font-bold p-3 rounded-full shadow-lg hover:bg-slate-300 transition-colors disabled:opacity-50">
                            <RefreshCw size={24} />
                        </button>
                    )}
                    {/* Capture Button */}
                    <button 
                        type="button" // FIX: Prevent form submission
                        onClick={handleCapture} 
                        disabled={isLoading || !!cameraError}
                        className="bg-sky-600 text-white font-bold py-3 px-6 rounded-lg shadow-lg hover:bg-sky-700 transition-colors disabled:bg-slate-400 flex items-center justify-center gap-2"
                    >
                        <Camera size={20} /> {t ? t('camera.capture') : 'Capture'}
                    </button>
                </div>
            </div>
        </ModalWrapper>
    );
};

const LocationPickerModal = ({ pinLocation, userLocation, onClose, onLocationSelect }) => {
    const mapContainerRef = useRef(null);
    const mapRef = useRef(null);
    const markerRef = useRef(null); // Draggable selection marker
    const userLocationMarkerRef = useRef(null); // Pulsing dot for user's device location
    const accuracyCircleRef = useRef(null); // Accuracy circle for user's device location

    const [selectedLocation, setSelectedLocation] = useState(null);
    const [address, setAddress] = useState('Fetching address...');
    const [isFetchingAddress, setIsFetchingAddress] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [apiError, setApiError] = useState(null);
    const geocodeTimeoutRef = useRef(null);

    const updateAddress = useCallback((lat, lng) => {
        setIsFetchingAddress(true);
        setApiError(null);
        if (geocodeTimeoutRef.current) { clearTimeout(geocodeTimeoutRef.current); }
        
        geocodeTimeoutRef.current = setTimeout(async () => {
            const apiKey = 'e46f090e2b894388a0ed20f3e851b678';
            try {
                const response = await fetch(`https://api.geoapify.com/v1/geocode/reverse?lat=${lat}&lon=${lng}&apiKey=${apiKey}`);
                if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
                const data = await response.json();
                setAddress(data.features?.[0]?.properties.formatted || 'Address not found.');
            } catch (error) {
                setAddress('Could not fetch address.');
                setApiError(error.toString());
            } finally {
                setIsFetchingAddress(false);
            }
        }, 500);
    }, []);

    // Effect for map initialization and dynamic updates
    useEffect(() => {
        const initialMapLocation = pinLocation || userLocation;
        if (!initialMapLocation || !mapContainerRef.current) return;

        // Initialize map ONCE when component mounts and location is available
        if (!mapRef.current) {
            let attempts = 0;
            const tryInitMap = () => {
                if (window.L) {
                    try {
                        const mapInstance = window.L.map(mapContainerRef.current).setView([initialMapLocation.latitude, initialMapLocation.longitude], 16);
                        mapRef.current = mapInstance;

                        window.L.tileLayer(`https://maps.geoapify.com/v1/tile/carto/{z}/{x}/{y}.png?apiKey=e46f090e2b894388a0ed20f3e851b678`, { attribution: '&copy; Geoapify' }).addTo(mapInstance);
                        
                        // Draggable marker for selecting the location
                        markerRef.current = window.L.marker([initialMapLocation.latitude, initialMapLocation.longitude], { draggable: true }).addTo(mapInstance);

                        // Visual indicator for user's actual current location, if available
                        if (userLocation) {
                            const pulseIcon = window.L.divIcon({ className: 'pulse-icon', iconSize: [20, 20], iconAnchor: [10, 10] });
                            userLocationMarkerRef.current = window.L.marker([userLocation.latitude, userLocation.longitude], { icon: pulseIcon, interactive: false }).addTo(mapInstance);
                            
                            // Accuracy circle
                            accuracyCircleRef.current = window.L.circle([userLocation.latitude, userLocation.longitude], {
                                radius: userLocation.accuracy || 20,
                                weight: 1,
                                color: '#3b82f6',
                                fillColor: '#3b82f6',
                                fillOpacity: 0.1,
                                interactive: false
                            }).addTo(mapInstance);
                        }

                        // Set initial state
                        setSelectedLocation({ latitude: initialMapLocation.latitude, longitude: initialMapLocation.longitude });
                        updateAddress(initialMapLocation.latitude, initialMapLocation.longitude);

                        // Event handlers
                        const handleLocationChange = (lat, lng) => {
                            setSelectedLocation({ 
                                latitude: lat, 
                                longitude: lng, 
                                // Preserve accuracy if we started with user location
                                accuracy: (pinLocation === null && userLocation) ? userLocation.accuracy : undefined 
                            });
                            updateAddress(lat, lng);
                        };

                        markerRef.current.on('dragend', (e) => handleLocationChange(e.target.getLatLng().lat, e.target.getLatLng().lng));
                        mapInstance.on('click', (e) => {
                            markerRef.current.setLatLng(e.latlng);
                            handleLocationChange(e.latlng.lat, e.latlng.lng);
                        });

                        setTimeout(() => mapInstance.invalidateSize(), 400);

                    } catch (error) {
                        console.error("Leaflet initialization failed:", error);
                        setApiError("Map failed to load. Please try again.");
                    }
                } else if (attempts < 50) {
                    attempts++;
                    setTimeout(tryInitMap, 100);
                } else {
                    console.error("Leaflet.js failed to load.");
                    setApiError("Map library failed to load. Check your connection.");
                }
            };
            tryInitMap();
        }

        // On subsequent updates of the user's location, just move the markers
        if (userLocation && userLocationMarkerRef.current && accuracyCircleRef.current) {
            const userLatLng = [userLocation.latitude, userLocation.longitude];
            userLocationMarkerRef.current.setLatLng(userLatLng);
            accuracyCircleRef.current.setLatLng(userLatLng).setRadius(userLocation.accuracy || 20);
        }

    }, [pinLocation, userLocation, updateAddress]);

    // Cleanup map instance on unmount
    useEffect(() => {
        return () => {
            if (mapRef.current) {
                mapRef.current.remove();
                mapRef.current = null;
            }
        };
    }, []);

    const handleGoToMyLocation = () => {
        if (userLocation && mapRef.current) {
            const { latitude, longitude } = userLocation;
            mapRef.current.setView([latitude, longitude], 16);
            markerRef.current?.setLatLng([latitude, longitude]);
            setSelectedLocation({ latitude, longitude, accuracy: userLocation.accuracy });
            updateAddress(latitude, longitude);
        }
    };

    const handleSearch = async (e) => {
        e.preventDefault();
        if (!searchQuery || !mapRef.current) return;
        setIsFetchingAddress(true);
        const apiKey = 'e46f090e2b894388a0ed20f3e851b678';
        try {
            const response = await fetch(`https://api.geoapify.com/v1/geocode/search?text=${encodeURIComponent(searchQuery)}&apiKey=${apiKey}`);
            if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
            const data = await response.json();
            if (data.features?.length > 0) {
                const { lat, lon } = data.features[0].properties;
                mapRef.current.setView([lat, lon], 16);
                markerRef.current?.setLatLng([lat, lon]);
                setSelectedLocation({ latitude: lat, longitude: lon, accuracy: undefined });
                updateAddress(lat, lon);
            } else {
                alert('Location not found.');
            }
        } catch (error) {
            alert('Could not search for location.');
        } finally {
            setIsFetchingAddress(false);
        }
    };
    
    const handleConfirm = () => {
        // Ensure only latitude and longitude are passed back
        const sanitizedCoords = selectedLocation ? { 
            latitude: selectedLocation.latitude, 
            longitude: selectedLocation.longitude 
        } : null;
        
        onLocationSelect({ coords: sanitizedCoords, address });
        onClose();
    };

    return (
        <ModalWrapper onClose={onClose}>
            <h2 className="text-xl font-bold mb-4">Select a Location</h2>
            <form onSubmit={handleSearch} className="flex gap-2 mb-4">
                <input type="text" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} placeholder="Search for an address..." className="flex-grow p-2 border rounded-lg"/>
                <button type="submit" className="bg-sky-500 text-white p-2 rounded-lg flex items-center justify-center"><Search size={20}/></button>
                <button type="button" onClick={handleGoToMyLocation} className="bg-slate-200 text-slate-700 p-2 rounded-lg flex items-center justify-center" title="Go to my current location">
                    <Compass size={20}/>
                </button>
            </form>
            <p className="text-xs text-slate-500 mb-2 text-center">Move the main pin to refine the location. The pulsing dot is your device's live position.</p>
            <div ref={mapContainerRef} className="h-[40vh] w-full rounded-lg z-0 bg-slate-200">
                {(!(pinLocation || userLocation) || apiError) && (
                    <div className="flex h-full items-center justify-center text-slate-500 p-4 text-center">
                        {apiError ? apiError : 'Waiting for high-accuracy location...'}
                    </div>
                )}
            </div>
            <div className="mt-4 p-3 bg-slate-100 rounded-lg min-h-[60px]">
                <p className="font-semibold text-sm">Selected Address:</p>
                 {isFetchingAddress ? (
                    <div className="flex items-center gap-2 text-slate-500"><Loader2 size={16} className="animate-spin" /><span>Fetching...</span></div>
                ) : (
                    <p className="text-sm text-slate-600">{address}</p>
                )}
                {apiError && !isFetchingAddress && <div className="mt-2 p-2 bg-red-100 text-red-700 text-xs rounded-lg">Error: {apiError}</div>}
            </div>
            <div className="flex justify-end mt-4">
                <button onClick={handleConfirm} disabled={!selectedLocation} className="bg-sky-600 text-white font-bold py-2 px-6 rounded-lg disabled:bg-slate-400">Confirm Location</button>
            </div>
        </ModalWrapper>
    );
};

const LiveNavigationModal = ({ origin, destination, onClose, isGig = false, gigId = null, db = null, t }) => {
    const mapContainerRef = useRef(null);
    const mapRef = useRef(null);
    const userMarkerRef = useRef(null);
    const trajectoryLineRef = useRef(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [routeInfo, setRouteInfo] = useState(null);
    const userPath = useRef([]);
    const [countdown, setCountdown] = useState(null);
    const lastLocationUpdate = useRef(0);

    useEffect(() => {
        if (!mapContainerRef.current || !origin || !destination) return;

        let mapInstance;
        let watchId;
        const apiKey = 'e46f090e2b894388a0ed20f3e851b678';

        try {
            mapInstance = window.L.map(mapContainerRef.current).setView([origin.latitude, origin.longitude], 13);
            mapRef.current = mapInstance;

            // Invalidate size after modal animation to prevent rendering glitches
            setTimeout(() => {
                if(mapRef.current) {
                    mapRef.current.invalidateSize();
                }
            }, 400);

            window.L.tileLayer(`https://maps.geoapify.com/v1/tile/carto/{z}/{x}/{y}.png?apiKey=${apiKey}`, { attribution: '&copy; Geoapify' }).addTo(mapInstance);

            const endIcon = window.L.divIcon({ className: 'nav-marker end-marker', html: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="w-6 h-6 text-white"><path fill-rule="evenodd" d="M12 2.25c-5.385 0-9.75 4.365-9.75 9.75s4.365 9.75 9.75 9.75 9.75-4.365 9.75-9.75S17.385 2.25 12 2.25ZM12.75 6a.75.75 0 0 0-1.5 0v6c0 .414.336.75.75.75h4.5a.75.75 0 0 0 0-1.5h-3.75V6Z" clip-rule="evenodd" /></svg>`, iconSize: [32, 32], iconAnchor: [16, 32]});
            window.L.marker([destination.latitude, destination.longitude], {icon: endIcon}).addTo(mapInstance).bindPopup("Destination");


            const pulseIcon = window.L.divIcon({ className: 'pulse-icon', iconSize: [20, 20], iconAnchor: [10, 10] });
            userMarkerRef.current = window.L.marker([origin.latitude, origin.longitude], { icon: pulseIcon }).addTo(mapInstance);

            trajectoryLineRef.current = window.L.polyline([], { color: '#8b5cf6', weight: 5, opacity: 0.8 }).addTo(mapInstance);

            const fetchRoute = async (currentOrigin) => {
                setIsLoading(true);
                try {
                    const response = await fetch(`https://api.geoapify.com/v1/routing?waypoints=${currentOrigin.latitude},${currentOrigin.longitude}|${destination.latitude},${destination.longitude}&mode=drive&apiKey=${apiKey}`);
                    if (!response.ok) throw new Error('Failed to fetch route');
                    const result = await response.json();
                    if (result.features && result.features.length > 0) {
                        const routeLayer = window.L.geoJSON(result.features[0], { style: { color: '#0ea5e9', weight: 6, opacity: 0.8 } }).addTo(mapInstance);
                        mapInstance.fitBounds(routeLayer.getBounds(), { padding: [40, 40] });
                        const properties = result.features[0].properties;
                        const timeInMinutes = Math.round(properties.time / 60);
                        setRouteInfo({
                            distance: (properties.distance / 1000).toFixed(1),
                            time: timeInMinutes
                        });
                        if (isGig) {
                            setCountdown(timeInMinutes);
                        }
                    } else {
                        throw new Error('No route found.');
                    }
                } catch (err) {
                    console.error("Routing error:", err);
                    setError("Could not calculate the route.");
                } finally {
                    setIsLoading(false);
                }
            };
            fetchRoute(origin);

            watchId = navigator.geolocation.watchPosition(
                (position) => {
                    const { latitude, longitude } = position.coords;
                    const newLatLng = [latitude, longitude];
                    
                    if (userMarkerRef.current) {
                        userMarkerRef.current.setLatLng(newLatLng);
                    }
                    
                    userPath.current.push(newLatLng);
                    if (trajectoryLineRef.current) {
                        trajectoryLineRef.current.setLatLngs(userPath.current);
                    }
                    
                    if(mapRef.current) {
                        mapRef.current.panTo(newLatLng);
                    }

                    // Update location in Firestore for gigs, throttled to every 10 seconds
                    if (isGig && db && gigId && Date.now() - lastLocationUpdate.current > 10000) {
                        lastLocationUpdate.current = Date.now();
                        const gigRef = doc(db, `artifacts/${appId}/public/data/gigs`, gigId);
                        updateDoc(gigRef, {
                            workerLocation: {
                                latitude,
                                longitude,
                                timestamp: serverTimestamp()
                            }
                        }).catch(err => console.error("Failed to update worker location:", err));
                    }
                },
                (err) => { console.warn(`Geolocation watch failed: ${err.message}`); },
                { enableHighAccuracy: true, timeout: 5000, maximumAge: 0 }
            );

        } catch (err) {
            console.error("Map initialization failed:", err);
            setError("Map failed to load.");
            setIsLoading(false);
        }

        return () => {
            if (watchId) navigator.geolocation.clearWatch(watchId);
            if (mapRef.current) { 
                mapRef.current.remove(); 
                mapRef.current = null; 
            }
        };
    }, []);

    // Countdown timer effect
    useEffect(() => {
        if (!isGig || countdown === null || countdown <= 0) return;

        const timerId = setInterval(() => {
            setCountdown(prev => Math.max(0, prev - 1));
        }, 60000); // decrement every minute

        return () => clearInterval(timerId);
    }, [isGig, countdown]);
    
    const recenterMap = () => {
        if (mapRef.current && userMarkerRef.current) {
            mapRef.current.setView(userMarkerRef.current.getLatLng(), 16);
        }
    };

    return (
        <ModalWrapper onClose={onClose}>
             <div className="relative h-full flex flex-col">
                <div ref={mapContainerRef} className="flex-grow w-full rounded-lg z-0 bg-slate-200">
                    {(isLoading || error) && (
                        <div className="flex h-full items-center justify-center text-slate-500 p-4 text-center">
                            {isLoading ? <><Loader2 className="animate-spin mr-2"/> {t('nav.calculating')}</> : error}
                        </div>
                    )}
                </div>
                {routeInfo && (
                     <div className="absolute top-2 left-1/2 -translate-x-1/2 z-10 bg-white/80 backdrop-blur-sm p-3 rounded-xl shadow-lg flex items-center gap-4">
                        <div><p className="text-2xl font-bold">{routeInfo.distance}</p><p className="text-xs text-slate-500 -mt-1">km</p></div>
                        <div><p className="text-2xl font-bold">{isGig && countdown !== null ? countdown : routeInfo.time}</p><p className="text-xs text-slate-500 -mt-1">min</p></div>
                    </div>
                )}
                <button onClick={recenterMap} className="absolute bottom-4 right-4 z-10 bg-white p-3 rounded-full shadow-lg text-slate-600 hover:bg-slate-100" title="Recenter on me">
                    <Compass size={24} />
                </button>
            </div>
        </ModalWrapper>
    );
};

const GigTrackingModal = ({ gig, db, onClose }) => {
    const mapContainerRef = useRef(null);
    const mapRef = useRef(null);
    const workerMarkerRef = useRef(null);
    const routeLineRef = useRef(null);
    const [eta, setEta] = useState(null);
    const [workerLocation, setWorkerLocation] = useState(null);
    const destination = useMemo(() => {
        if (gig.type === 'delivery') return gig.dropoffLocation?.coords;
        return gig.location?.coords;
    }, [gig]);

    // Listen for worker location updates on the gig document
    useEffect(() => {
        if (!db || !gig?.id) return;
        const gigRef = doc(db, `artifacts/${appId}/public/data/gigs`, gig.id);
        const unsubscribe = onSnapshot(gigRef, (doc) => {
            const data = doc.data();
            if (data && data.workerLocation) {
                setWorkerLocation(data.workerLocation);
            }
        });
        return () => unsubscribe();
    }, [db, gig?.id]);

    // Initialize map
    useEffect(() => {
        if (!mapContainerRef.current || mapRef.current || !destination) return;

        let attempts = 0;
        const tryInitMap = () => {
            if (window.L) {
                const mapInstance = window.L.map(mapContainerRef.current).setView([destination.latitude, destination.longitude], 13);
                mapRef.current = mapInstance;

                window.L.tileLayer(`https://maps.geoapify.com/v1/tile/carto/{z}/{x}/{y}.png?apiKey=e46f090e2b894388a0ed20f3e851b678`, { attribution: '&copy; Geoapify' }).addTo(mapInstance);

                // Destination marker
                window.L.marker([destination.latitude, destination.longitude]).addTo(mapInstance).bindPopup("Destination");
                
                // Worker marker
                const pulseIcon = window.L.divIcon({ className: 'pulse-icon', iconSize: [20, 20], iconAnchor: [10, 10] });
                workerMarkerRef.current = window.L.marker(workerLocation ? [workerLocation.latitude, workerLocation.longitude] : [destination.latitude, destination.longitude], { icon: pulseIcon }).addTo(mapInstance);

                routeLineRef.current = window.L.polyline([], { color: '#0ea5e9', weight: 6, opacity: 0.8 }).addTo(mapInstance);

            } else if (attempts < 50) {
                attempts++;
                setTimeout(tryInitMap, 100);
            }
        };
        tryInitMap();

        return () => {
            if (mapRef.current) {
                mapRef.current.remove();
                mapRef.current = null;
            }
        };
    }, [gig, destination]);

    // Update map when worker location changes
    useEffect(() => {
        if (!workerLocation || !mapRef.current || !destination) return;
        
        const workerLatLng = [workerLocation.latitude, workerLocation.longitude];
        const apiKey = 'e46f090e2b894388a0ed20f3e851b678';
        
        if (workerMarkerRef.current) {
            workerMarkerRef.current.setLatLng(workerLatLng);
        }

        // Fetch new route and ETA
        const fetchRoute = async () => {
             try {
                const response = await fetch(`https://api.geoapify.com/v1/routing?waypoints=${workerLocation.latitude},${workerLocation.longitude}|${destination.latitude},${destination.longitude}&mode=drive&apiKey=${apiKey}`);
                const result = await response.json();
                if (result.features && result.features.length > 0) {
                    if (routeLineRef.current) {
                        const latLngs = result.features[0].geometry.coordinates[0].map(coord => [coord[1], coord[0]]);
                        routeLineRef.current.setLatLngs(latLngs);
                    }
                    const properties = result.features[0].properties;
                    setEta(Math.round(properties.time / 60)); // ETA in minutes
                    
                    const bounds = window.L.latLngBounds(workerLatLng, [destination.latitude, destination.longitude]);
                    mapRef.current.fitBounds(bounds, { padding: [50, 50] });
                }
            } catch (err) {
                console.error("Routing error:", err);
            }
        };

        fetchRoute();

    }, [workerLocation, gig, destination]);


    return (
        <ModalWrapper onClose={onClose}>
            <div className="relative h-full flex flex-col">
                <h2 className="text-xl font-bold mb-4 text-center">Tracking Worker for "{gig.title}"</h2>
                <div ref={mapContainerRef} className="flex-grow w-full rounded-lg z-0 bg-slate-200">
                    {!workerLocation && (
                        <div className="flex h-full items-center justify-center text-slate-500 p-4 text-center">
                            <Loader2 className="animate-spin mr-2"/> Waiting for worker's location...
                        </div>
                    )}
                </div>
                {eta !== null && (
                     <div className="absolute top-12 left-1/2 -translate-x-1/2 z-10 bg-white/80 backdrop-blur-sm p-3 rounded-xl shadow-lg flex items-center gap-4">
                        <p className="text-sm font-semibold">Estimated Arrival:</p>
                        <div><p className="text-2xl font-bold">{eta}</p><p className="text-xs text-slate-500 -mt-1">min</p></div>
                    </div>
                )}
            </div>
        </ModalWrapper>
    );
};

const MultiStoreChoiceModal = ({ onClose, onChoice, storeCount, t }) => (
    <ModalWrapper onClose={onClose} isFullScreen={false}>
        <h2 className="text-xl font-bold mb-4">{t('multi.title')}</h2>
        <p className="text-slate-600 mb-6">
            {t('multi.desc').replace('{{count}}', storeCount)}
        </p>
        <div className="flex flex-col gap-4">
            <button onClick={() => onChoice('fast')} className="flex items-center gap-3 p-4 border-2 border-sky-100 bg-sky-50 rounded-xl hover:border-sky-500 hover:shadow-md transition-all text-left group">
                <div className="bg-sky-500 text-white p-3 rounded-full group-hover:scale-110 transition-transform">
                    <Briefcase size={24} />
                </div>
                <div>
                    <h3 className="font-bold text-sky-700">{t('multi.fast')}</h3>
                    <p className="text-xs text-sky-600">{t('multi.fastDesc').replace('{{count}}', storeCount)}</p>
                </div>
            </button>
            <button onClick={() => onChoice('consolidated')} className="flex items-center gap-3 p-4 border-2 border-purple-100 bg-purple-50 rounded-xl hover:border-purple-500 hover:shadow-md transition-all text-left group">
                <div className="bg-purple-500 text-white p-3 rounded-full group-hover:scale-110 transition-transform">
                    <Package size={24} />
                </div>
                <div>
                    <h3 className="font-bold text-purple-700">{t('multi.consolidated')}</h3>
                    <p className="text-xs text-purple-600">{t('multi.consolidatedDesc')}</p>
                </div>
            </button>
        </div>
    </ModalWrapper>
);

// --- Modals ---
const CreateGigModal = ({onClose, onCreate, userLocation, storage, myStore, allStores, currencyInfo, t})=>{
    const [title, setTitle]=useState(''), [desc, setDesc]=useState(''), [pay, setPay]=useState(''); 
    const [phone, setPhone]=useState(''); 
    const [location, setLocation]=useState(null);
    const [description, setDescription] = useState('');
    const [isAILoading, setIsAILoading] = useState(false);
    const [aiError, setAIError] = useState(null);
    const [scheduleType, setScheduleType] = useState('immediately'); // NEW: 'immediately' or 'specificTime'
    const [scheduledHour, setScheduledHour] = useState('12'); // 24-hour format
    const [scheduledMinute, setScheduledMinute] = useState('00'); // Minute 
    const [paymentType, setPaymentType]=useState('fixed');
    const [scheduledDate, setScheduledDate] = useState(() => new Date().toISOString().split('T')[0]); // FIX: Default to today's date
    const [images, setImages] = useState([]); 
    const [isMapOpen, setIsMapOpen] = useState(false);

    useEffect(() => {
        if (userLocation && !location) {
            const setDefaultLocation = async () => {
                const address = await getAddressFromCoords(userLocation.latitude, userLocation.longitude);
                setLocation({ coords: userLocation, address: address });
            };
            setDefaultLocation();
        }
    }, [userLocation, location]);

    const handleSubmit=(e)=>{
        e.preventDefault();
        const payNum=parseFloat(pay);
        let finalScheduledAt = null;

        // NEW: Validation for Scheduling
        if(scheduleType === 'specificTime' && (!scheduledDate || !scheduledHour || !scheduledMinute)) {
            alert("Please select a specific date and time for your scheduled gig."); return;
        }
        if (scheduleType === 'specificTime') {
            // Combine the date and time strings into a valid Date object
            finalScheduledAt = new Date(`${scheduledDate}T${scheduledHour}:${scheduledMinute}`);
        }
        if(scheduleType === 'specificTime' && finalScheduledAt < new Date()) {
            alert("Scheduled time must be in the future."); return;
        }
        if(!title||!description||isNaN(payNum)||!location){alert("Please fill all required fields (including Location).");return}

        onCreate({
            title,description,pay:payNum,location,paymentType,images, contactPhone: phone, 
            scheduleType: scheduleType,
            scheduledAt: scheduleType === 'specificTime' ? finalScheduledAt : null // Store as JS Date
        }); 
    };
     
    // NEW: AI Description Generator for Gigs
    const generateDescriptionWithGemini = useCallback(async () => {
        if (!title.trim()) { alert("Please enter a Gig Title first."); return; }
        setAIError(null); setIsAILoading(true);
        const userQuery = `Write a compelling and professional job description for a gig marketplace. The gig title is "${title}" and the budget is $${pay || 'N/A'}. Include details a worker would need to know and a strong call-to-action. Limit the response to 4 sentences.`;
        const payload = {
            contents: [{ parts: [{ text: userQuery }] }],
            systemInstruction: { parts: [{ text: "Act as a professional marketing copywriter for a gig economy marketplace. Generate concise, engaging, and structured job descriptions." }] },
        };
        try {
            const url = `${GEMINI_API_URL}?key=${geminiApiKey}`;
            const response = await fetchWithBackoff(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
            const generatedText = response.candidates?.[0]?.content?.parts?.[0]?.text || "AI suggestion failed to generate valid text.";
            setDescription(generatedText);
        } catch (error) { setAIError(error.message || "An unknown AI error occurred."); } finally { setIsAILoading(false); }
    }, [title, pay]);  
    
    return(
    <>
        <ModalWrapper onClose={onClose}>
            <h2 className="text-2xl font-bold mb-6">{t('gig.createTitle')}</h2>
            <form onSubmit={handleSubmit} className="space-y-4">
                <div><label className="font-medium">{t('gig.title')}</label><input value={title} onChange={e=>setTitle(e.target.value)} placeholder={t('gig.titlePlaceholder')} className="w-full mt-1 p-2 border rounded"/></div>
                <div>
                    <label className="font-medium flex justify-between items-center">{t('gig.description')}
                        <button type="button" onClick={generateDescriptionWithGemini} disabled={isAILoading || !title.trim()} className="text-sm text-purple-600 hover:underline flex items-center gap-1">
                            {isAILoading ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14}/>} {isAILoading ? t('gig.aiSuggesting') : t('gig.aiSuggest')}
                        </button>
                    </label>
                    {aiError && <p className="text-red-500 text-xs mt-1">{aiError}</p>}
                    <textarea value={description} onChange={e=>setDescription(e.target.value)} rows="3" placeholder={t('gig.descriptionPlaceholder')} className="w-full mt-1 p-2 border rounded" required></textarea>
                </div>
                {/* NEW: Gig Scheduling UI */}
                <div>
                    <label className="font-medium">{t('gig.when')}</label>
                    <div className="flex gap-4 mt-2">
                        <label className="flex items-center gap-2">
                            <input type="radio" value="immediately" checked={scheduleType === 'immediately'} onChange={e => setScheduleType(e.target.value)} name="scheduleType"/>
                            {t('gig.immediately')}
                        </label>
                        <label className="flex items-center gap-2">
                            <input type="radio" value="specificTime" checked={scheduleType === 'specificTime'} onChange={e => setScheduleType(e.target.value)} name="scheduleType"/>
                            {t('gig.schedule')}
                        </label>
                    </div>
                    {scheduleType === 'specificTime' && (
                        <div className="mt-2 grid grid-cols-3 gap-2">
                            <input type="date" value={scheduledDate} onChange={e => setScheduledDate(e.target.value)} className="col-span-3 sm:col-span-1 mt-1 p-3 border-2 border-slate-200 rounded-lg bg-white" required />
                            <select value={scheduledHour} onChange={e => setScheduledHour(e.target.value)} className="col-span-1 sm:col-span-1 mt-1 p-3 border-2 border-slate-200 rounded-lg bg-white appearance-none" required>
                                {Array.from({ length: 24 }, (_, i) => String(i).padStart(2, '0')).map(hour => <option key={hour} value={hour}>{hour}</option>)}
                            </select>
                            <select value={scheduledMinute} onChange={e => setScheduledMinute(e.target.value)} className="col-span-1 sm:col-span-1 mt-1 p-3 border-2 border-slate-200 rounded-lg bg-white appearance-none" required>
                                {['00', '15', '30', '45'].map(min => <option key={min} value={min}>{min}</option>)}
                            </select>
                        </div>
                    )}
                </div>
                <div><label className="font-medium">{t('gig.image')}</label><ImageUploader storage={storage} folder="gigs" onUpload={(url) => setImages([url])} t={t}/></div>
                <div><label className="font-medium">{t('gig.phone')}</label><input value={phone} onChange={e=>setPhone(e.target.value)} type="tel" placeholder={t('gig.phonePlaceholder')} className="w-full mt-1 p-2 border rounded"/></div>
                <div><label className="font-medium">{t('gig.paymentType')}</label><div className="flex gap-4 mt-1"><label className="flex items-center gap-2"><input type="radio" value="fixed" checked={paymentType==='fixed'} onChange={e=>setPaymentType(e.target.value)} name="paymentType"/>{t('gig.paymentFixed')}</label><label className="flex items-center gap-2"><input type="radio" value="hourly" checked={paymentType==='hourly'} onChange={e=>setPaymentType(e.target.value)} name="paymentType"/>{t('gig.paymentHourly')}</label></div></div>
                <div><label className="font-medium">{t('gig.paymentAmount')} ({currencyInfo.currencySymbol})</label><input value={pay} onChange={e=>setPay(e.target.value)} type="number" placeholder={paymentType==='fixed'?'50':'20'} className="w-full mt-1 p-2 border rounded"/></div>
                <div><label className="font-medium">{t('gig.location')}</label>
                {
                    !location ? (
                        <div className="flex items-center gap-2 mt-1 text-slate-500">
                            <Loader2 size={18} className="animate-spin" />
                            <span>{t('gig.locationFetching')}</span>
                        </div>
                    ) :
                    <div className="text-sm p-2 bg-green-100 text-green-800 rounded-lg mt-1 cursor-pointer" onClick={()=> setIsMapOpen(true)}>
                        <p className="font-bold truncate">{location.address}</p>
                    </div>
                }
                </div>
                <div className="flex justify-end pt-4"><button type="submit" className="bg-purple-500 text-white font-bold py-2 px-6 rounded-lg">{t('gig.postButton')}</button></div>
            </form>
        </ModalWrapper>
        {isMapOpen && <LocationPickerModal pinLocation={location?.coords} userLocation={userLocation} onClose={() => setIsMapOpen(false)} onLocationSelect={setLocation} />}
    </>
    ) 
};
const GigDetailModal = ({gig,onClose,onApply, onChooseApplicant, onComplete,onCloseGig,currentUser,userLocation, onStartChat, allStores, onViewProfile, myStore, db, setLightboxImageUrl, language, t, allGigs, onSwitchItem})=>{
    const isCreator = gig.creatorId === currentUser?.uid;
    const hasApplied = gig.applicants && gig.applicants.some(app => app.uid === currentUser.uid);
    const canApply = useMemo(() => {
        // To apply for ANY gig (task or delivery), user must have a store AND have gig notifications enabled.
        return !!(myStore && myStore.isDeliveryDriver);
    }, [myStore]);
    const [isNavigationVisible, setNavigationVisible] = useState(false);
    const [navDestination, setNavDestination] = useState(null);
    const [isTrackingVisible, setTrackingVisible] = useState(false);
    const [translatedDescription, setTranslatedDescription] = useState(null);
    const [isTranslating, setIsTranslating] = useState(false);
    const workerStore = useMemo(() => allStores.find(s => s.id === gig.workerId), [allStores, gig.workerId]);
    const creatorStore = useMemo(() => allStores.find(s => s.id === gig.creatorId), [allStores, gig.creatorId]);
    let displayPhone = gig.contactPhone || creatorStore?.contactPhone;
    let displayIsWhatsapp = creatorStore?.isWhatsapp;

    if (isCreator && gig.workerId && workerStore) {
        displayPhone = workerStore.contactPhone;
        displayIsWhatsapp = workerStore.isWhatsapp;
    }

    const cleanPhone = formatPhoneNumberForWhatsApp(displayPhone);
    const targetLoc = gig.pickupLocation || gig.location;
    const distance = useMemo(() => targetLoc ? haversineDistance(userLocation, targetLoc.coords) : null, [userLocation, targetLoc]);
    const currencySymbol = gig.currencySymbol || '$';
    const payment=gig.paymentType==='hourly'?`${currencySymbol}${gig.pay}/hr`:`${currencySymbol}${gig.pay}`;

    // NEW: Scheduling Display Logic
    const scheduledTime = (gig.scheduleType === 'specificTime' && gig.scheduledAt) ? new Date(gig.scheduledAt.seconds * 1000).toLocaleString() : 'Immediately';
    // NEW: Translation Handler
    const handleTranslateDescription = async () => {
       if (translatedDescription) { // Toggle off
            setTranslatedDescription(null);
            return;
        }
        setIsTranslating(true);
        const langMap = { en: 'English', fr: 'French', es: 'Spanish', ar: 'Arabic' };
        const targetLang = langMap[language] || 'English';
        const userQuery = `Translate the following text to ${targetLang}: "${gig.description}"`;
        const payload = { contents: [{ parts: [{ text: userQuery }] }], systemInstruction: { parts: [{ text: `You are a universal translator. Only return the translated text.` }] }};
        try {
            const url = `${GEMINI_API_URL}?key=${geminiApiKey}`;
            const response = await fetchWithBackoff(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
            setTranslatedDescription(response.candidates?.[0]?.content?.parts?.[0]?.text || "Translation failed.");
        } catch (error) { console.error("Translation error:", error); setTranslatedDescription("Translation error."); } finally { setIsTranslating(false); }
    };
    const handleShowDirections = (destination) => {
        if (!userLocation) {
            alert("Could not get your current location to start navigation.");
            return;
        }
        setNavDestination(destination);
        setNavigationVisible(true);
    }

    // UPDATED: ActionIcon component with a more "iconic" button style
    const ActionIcon = ({ href, onClick, Icon, label }) => (
        <a href={href} onClick={onClick} target="_blank" rel="noopener noreferrer" 
           className={`flex flex-col items-center gap-2 text-sm font-semibold text-slate-700 dark:text-slate-200 hover:opacity-80 transition-opacity`}>
            <div className={`w-14 h-14 rounded-xl flex items-center justify-center shadow-lg ${
                label === 'Call' ? 'bg-sky-500' :
                label === 'WhatsApp' ? 'bg-green-500' :
                'bg-purple-500' 
            }`}>
                <Icon size={24} className="text-white"/>
            </div>
            <span>{label}</span>
        </a>
    );
    const creatorProfileImage = creatorStore?.profileImageUrl || 'https://placehold.co/40x40/EEE/31343C?text=👤';
    const workerProfileImage = workerStore?.profileImageUrl || 'https://placehold.co/40x40/EEE/31343C?text=👤';
    return(
    <>
        <ModalWrapper onClose={onClose}>
                <img src={gig.images?.[0]||'https://placehold.co/600x400/EEE/31343C?text=Gig'} alt={gig.title} 
                    className="w-full h-64 object-contain bg-slate-100 rounded-lg mb-4 cursor-zoom-in shadow-inner" 
                    onClick={() => setLightboxImageUrl(gig.images?.[0]||'https://placehold.co/600x400/EEE/31343C?text=Gig')} />
            <div className="flex justify-between items-start"><h2 className="text-3xl font-bold text-slate-800">{gig.title}</h2>{distance!==null&&<span className="font-bold text-slate-600 bg-slate-200 px-3 py-1 rounded-full">{distance.toFixed(1)} km</span>}</div>
            <p className="text-sm text-slate-500 mb-4">Posted by {gig.creatorName} {formatTimeAgo(gig.createdAt)}</p>
            <div className="flex items-baseline gap-4 mb-2"><span className="text-3xl font-extrabold text-sky-600">{payment}</span><GigStatusChip status={gig.status}/></div>
            {/* NEW: Show Scheduled Time */}
            <div className="text-sm font-semibold text-purple-600 bg-purple-100 p-2 rounded-lg mb-6 flex items-center gap-2">
                <Clock size={16} />
                {t('gig.required')} <span className="font-bold">{scheduledTime}</span>
            </div>
            {isCreator&&gig.workerId&&(<div className="mb-6 p-4 bg-sky-50 border rounded-lg"><h4 className="font-bold text-sky-800">Accepted By: {gig.workerName}</h4>{gig.acceptedAt&&<p className="text-sm">On: {new Date(gig.acceptedAt.seconds*1000).toLocaleString()}</p>}</div>)}
            {/* Description Section with Translation */}
            <div className="border-t pt-4">
                <div className="flex justify-between items-center mb-2">
                    <h3 className="text-lg font-bold">Task Details</h3>
                    <button onClick={handleTranslateDescription} disabled={isTranslating} className="text-xs text-sky-600 hover:underline flex items-center gap-1">
                        <Languages size={14} /> {isTranslating ? 'Translating...' : (translatedDescription ? 'Show Original' : 'Translate')}
                    </button>
                </div>
                <p className="whitespace-pre-wrap text-slate-600">{translatedDescription || gig.description}</p>
            </div>
     {(() => {
        const isWorkerInProgress = currentUser?.uid === gig.workerId && gig.status === 'in-progress';
        const isCreatorInProgress = isCreator && gig.status === 'in-progress' && gig.workerId;
        
        // WORKER'S VIEW (IN-PROGRESS)
        if (isWorkerInProgress) {
            if (gig.type === 'delivery') {
                return (
                    <div className="my-4 grid grid-cols-2 gap-4">
                        {gig.pickupLocation?.coords && (
                            <button onClick={() => handleShowDirections(gig.pickupLocation.coords)} className="w-full flex items-center justify-center gap-2 bg-sky-500 text-white font-bold py-3 px-4 rounded-lg shadow-md hover:bg-sky-600">
                                <Compass size={20}/> To Pickup
                            </button>
                        )}
                        {gig.dropoffLocation?.coords && (
                            <button onClick={() => handleShowDirections(gig.dropoffLocation.coords)} className="w-full flex items-center justify-center gap-2 bg-purple-500 text-white font-bold py-3 px-4 rounded-lg shadow-md hover:bg-purple-600">
                                <MapPin size={20}/> To Dropoff
                            </button>
                        )}
                    </div>
                );
            } else if (gig.location?.coords) { // Regular gig
                return (
                    <div className="my-4">
                        <button onClick={() => handleShowDirections(gig.location.coords)} className="w-full flex items-center justify-center gap-2 bg-purple-600 text-white font-bold py-3 px-4 rounded-lg shadow-md hover:bg-purple-700">
                            <Compass size={20}/> Start Gig Navigation
                        </button>
                    </div>
                );
            }
            else {
                // Remote Service - No Location Shared
                return (
                    <div className="my-4 p-4 bg-purple-50 text-purple-800 rounded-lg text-center font-bold border border-purple-100">
                        Remote Service. Connect online to proceed.
                    </div>
                );
            }
        }
    
        // CREATOR'S VIEW (IN-PROGRESS)
        if (isCreatorInProgress) {
            if (!gig.location && gig.type === 'service') {
                return (
                    <div className="my-4 p-4 bg-green-50 text-green-800 rounded-lg text-center font-bold border border-green-100">
                        Service in progress (Online).
                    </div>
                );
            }
            return (
                <div className="my-4">
                    <button onClick={() => setTrackingVisible(true)} disabled={!gig.workerLocation} className="w-full flex items-center justify-center gap-2 bg-sky-500 text-white font-bold py-3 px-4 rounded-lg shadow-md hover:bg-sky-600 disabled:bg-slate-400 disabled:cursor-not-allowed">
                        <MapPin size={20}/> {gig.workerLocation ? 'Track Worker Live' : 'Waiting for worker...'}
                    </button>
                </div>
            );
        }
        
        // PUBLIC/OTHER VIEW (OPEN GIGS)
        if (gig.status !== 'closed' && !isWorkerInProgress && !isCreatorInProgress && !isCreator) { // <-- FIX: Hide button if user is the creator
             const location = gig.type === 'delivery' ? gig.pickupLocation : gig.location;
             const buttonText = gig.type === 'delivery' ? 'Directions to Pickup' : 'Get Directions';
             if (location?.coords) {
                 return (
                     <div className="my-4">
                        <button onClick={() => handleShowDirections(location.coords)} className="w-full flex items-center justify-center gap-2 bg-sky-500 text-white font-bold py-3 px-4 rounded-lg shadow-md hover:bg-sky-600">
                            <Compass size={20}/> {buttonText}
                        </button>
                    </div>
                 );
             }
        }
    
        return null; // No button to show
    })()}


    {isCreator && gig.status === 'open' && (
        <div className="pt-6 border-t mt-6 space-y-4">
            <h3 className="text-lg font-bold">Applicants</h3>
            {gig.applicants && gig.applicants.length > 0 ? (
                <ul className="space-y-2">
                    {gig.applicants.map(applicant => {
                         const applicantStore = allStores.find(s => s.id === applicant.uid);
                         const applicantProfileImage = applicantStore?.profileImageUrl || 'https://placehold.co/40x40/EEE/31343C?text=👤';
                         const tier = getSellerTier(applicant.sales, 5, t); // Using 5 as placeholder, as we don't have applicant's rating
                         return (
                            <li key={applicant.uid} className="flex justify-between items-center bg-slate-100 p-2 rounded-lg">
                                <div>
                                    <span className="font-bold cursor-pointer hover:underline" onClick={() => onViewProfile(applicant.uid)}>{applicant.name}</span>
                                    <div className="flex items-center gap-2 text-xs text-slate-500">
                                    <img src={applicantProfileImage} alt={applicant.name} className="w-5 h-5 rounded-full object-cover" onError={(e) => { e.target.onerror = null; e.target.src='https://placehold.co/40x40/EEE/31343C?text=👤'; }}/>
                                        <span className={tier.color}>{tier.icon} {tier.name}</span>
                                        <span>-</span>
                                        <span>{applicant.sales} jobs done</span>
                                    </div>
                                    {(() => {
                                        // Calculate distance & time from applicant's device location to gig location
                                        const gigLocation = gig.type === 'delivery' ? gig.pickupLocation?.coords : gig.location?.coords;
                                        // 'applicant.location' is the device location saved during application
                                        const applicantDeviceLocation = applicant.location; 
                                        
                                        if (applicantDeviceLocation && gigLocation) {
                                            const applicantDistance = haversineDistance(applicantDeviceLocation, gigLocation); // This is already { latitude, longitude }
                                            if (applicantDistance !== null) {
                                                // Estimate travel time: ~2.4 mins per km (avg speed ~25km/h)
                                                const travelTimeMinutes = Math.round(applicantDistance * 2.4); 
                                                return (
                                                    <div className="flex items-center gap-3 text-xs font-semibold text-sky-600 mt-1.5">
                                                        <div className="flex items-center gap-1">
                                                            <MapPin size={12} />
                                                            <span>{applicantDistance.toFixed(1)} km</span>
                                                        </div>
                                                        <div className="flex items-center gap-1">
                                                            <Clock size={12} />
                                                            <span>~{travelTimeMinutes} min away</span>
                                                        </div>
                                                    </div>
                                                );
                                            }
                                        }
                                        return (
                                            <div className="flex items-center gap-1 text-xs font-semibold text-slate-400 mt-1.5">
                                                <MapPin size={12} />
                                                <span>Location N/A</span>
                                            </div>
                                        );
                                    })()}
                                </div>
                                <div className="text-right">
                                    <span className="font-bold text-lg text-sky-600">{gig.currencySymbol || '$'}{(applicant.proposedPrice || gig.pay).toFixed(2)}</span>
                                    <button onClick={() => onChooseApplicant(gig.id, applicant, gig.title, (applicant.proposedPrice || gig.pay))} className="bg-green-500 text-white font-bold py-1 px-3 rounded-lg text-sm mt-1">Choose</button>
                                </div>
                            </li>
                         )
                    })}
                </ul>
            ) : <p className="text-slate-500 text-sm">No applications yet.</p>}
        </div>
    )}

    <div className="pt-6 border-t mt-6 space-y-4">
        {gig.status !== 'open' && <div className="flex justify-around items-center">
            {displayPhone && <ActionIcon href={`tel:${displayPhone}`} Icon={Phone} label="Call"/>}
            {displayIsWhatsapp && <ActionIcon href={`https://wa.me/${cleanPhone}`} Icon={MessageSquare} label="WhatsApp"/>}
            {isCreator && gig.workerId && <ActionIcon onClick={() => onStartChat(gig.workerId, true)} Icon={Mail} label="Chat"/>}
            {!isCreator && <ActionIcon onClick={() => onStartChat(gig.creatorId, true)} Icon={Mail} label="Chat"/>}
        </div>}
        <div className="space-y-2">
            {!isCreator && gig.status === 'open' && !hasApplied && canApply && (
                <button onClick={() => onApply(gig)} className="w-full bg-sky-500 text-white font-bold py-3 rounded-lg hover:bg-sky-600 transition-colors">Apply for Gig</button>
            )}
            {!isCreator && gig.status === 'open' && !hasApplied && !canApply && (<button className="w-full bg-slate-300 text-slate-500 font-bold py-3 rounded-lg cursor-not-allowed" disabled title="You must enable gig notifications in your profile to apply.">{!myStore ? 'Create a store to apply' : 'Enable gig notifications in profile to apply'}</button>
            )}
            {!isCreator && gig.status==='open' && hasApplied && <button className="w-full bg-gray-400 text-white font-bold py-3 rounded-lg" disabled>Applied</button>}
            {currentUser?.uid === gig.workerId && gig.status==='in-progress' &&<button onClick={()=>onComplete(gig.id)} className="w-full bg-sky-500 text-white font-bold py-3 rounded-lg">Mark as Complete</button>}
            {isCreator && gig.status === 'open' &&<button onClick={()=>onCloseGig(gig.id)} className="w-full bg-red-500 text-white font-bold py-2 rounded-lg">Close Gig</button>}
        </div>
        <AlternativeItemsCarousel 
        currentItem={gig} 
        allItems={allGigs} 
        onItemClick={onSwitchItem} 
        t={t} 
    />
    </div>
    </ModalWrapper>
    {isNavigationVisible && <LiveNavigationModal origin={userLocation} destination={navDestination} onClose={() => setNavigationVisible(false)} isGig={currentUser?.uid === gig.workerId} gigId={gig.id} db={db} t={t} />}
    {isTrackingVisible && <GigTrackingModal gig={gig} db={db} onClose={() => setTrackingVisible(false)} />}
    </>
    ) 
};
const ManageStoreModal = ({store,onClose,onSave,userLocation, storage, allStores, myStore, t})=>{
    const[n,sN]=useState(store?.name||'');
    const[d,sD]=useState(store?.description||'');
    const [profileImageUrl, setProfileImageUrl] = useState(store?.profileImageUrl || null);
    const[p,sP]=useState(store?.contactPhone||'');
    const[e,sE]=useState(store?.contactEmail||'');
    
    const[images, setImages]=useState(store?.images||[]);
    const[l,sL]=useState(store?.location||null);
    const [isWhatsapp, setIsWhatsapp] = useState(store?.isWhatsapp || true);
    const [isDeliveryDriver, setIsDeliveryDriver] = useState(store?.isDeliveryDriver || false);
    const [isMapOpen, setIsMapOpen] = useState(false);
    // NEW: Country Switching Logic
    const [selectedCountryCode, setSelectedCountryCode] = useState(store?.countryCode || 'MA');
    const selectedCountryData = countries.find(c => c.code === selectedCountryCode);
    
    // Re-calculate currency based on the *currently selected* country in the modal, not just the old one
    const currencyInfo = getCurrencyInfo(selectedCountryCode);
    const handleProfileImageUpload = (url) => {
        setProfileImageUrl(url);
    };
    const handleRemoveProfileImage = () => {
        setProfileImageUrl(null);
    };

    useEffect(() => {
        // Only set default location automatically when CREATING a new store.
        if (userLocation && !store && !l) {
            const setDefaultLocation = async () => {
                const address = await getAddressFromCoords(userLocation.latitude, userLocation.longitude);
                sL({ coords: userLocation, address: address });
            };
            setDefaultLocation();
        }
    }, [userLocation, l, store]);

    const countryMarkets = useMemo(() => {
        if (!allStores || !selectedCountryCode) return [];
        const marketsInCountry = allStores
            .filter(s => s.countryCode === selectedCountryCode && s.marketName) // Filter by new country selection
            .map(s => s.marketName);
        return [...new Set(marketsInCountry)];
    }, [allStores, selectedCountryCode]);

    const initialMarketIsOther = store?.marketName && !countryMarkets.includes(store.marketName);
    const [selectedMarket, setSelectedMarket] = useState(initialMarketIsOther ? 'Other' : (store?.marketName || ''));
    const [newMarketName, setNewMarketName] = useState(initialMarketIsOther ? store.marketName : '');
    
    const hS=(evt)=>{
        evt.preventDefault();
        const finalMarketName = selectedMarket === 'Other' ? newMarketName : selectedMarket;

        if (!l || !l.coords) {
             alert("Store Location is not set. Please wait for the location to fetch or click the address to set it manually on the map.");
             return;
        }

        if (!finalMarketName.trim()) {
             alert("City name is required.");
             return;
        }
        onSave({
            name:n, description:d, marketName: finalMarketName, images:images, location:l, 
            contactPhone:p, contactEmail:e, isWhatsapp, isDeliveryDriver, profileImageUrl: profileImageUrl || null,
            
            // NEW: Save updated country data
            country: selectedCountryData.name,
            countryCode: selectedCountryData.code,
            currencySymbol: selectedCountryData.currencySymbol,
            currencyCode: selectedCountryData.currency,
            // Note: We keep the phone number as is, but user might need to update it manually
        });
    };
    
    return(
        <>
            <ModalWrapper onClose={onClose}>
                <h2 className="text-2xl font-bold mb-6">{store ? t('store.manageTitle') : t('store.createTitle')}</h2>
                <form onSubmit={hS} className="space-y-4">
                    <div><label className="font-medium">{t('store.name')}</label><input value={n} onChange={e=>sN(e.target.value)} className="w-full mt-1 p-2 border rounded"/></div>
                    <div><label className="font-medium">{t('store.description')}</label><textarea value={d} onChange={e=>sD(e.target.value)} rows="2" className="w-full mt-1 p-2 border rounded"></textarea></div>
                    {/* NEW: Country Selector */}
                    <div>
                        <label className="font-medium">{t('auth.country')}</label>
                        <select value={selectedCountryCode} onChange={e => { setSelectedCountryCode(e.target.value); setSelectedMarket(''); }} className="w-full mt-1 p-3 border-2 border-slate-200 rounded-lg bg-white">
                            {countries.map(c => <option key={c.code} value={c.code}>{c.name}</option>)}
                        </select>
                        <p className="text-xs text-slate-500 mt-1">{t('store.countryWarning')}</p>
                    </div>
                    <div>
                        <label className="font-medium">{t('store.city')}</label>
                        <select value={selectedMarket} onChange={e => setSelectedMarket(e.target.value)} className="w-full mt-1 p-3 border-2 border-slate-200 rounded-lg bg-white appearance-none">
                            <option value="" disabled>{t('store.selectCity')}</option>
                            {countryMarkets.map(m => <option key={m} value={m}>{m}</option>)}
                            <option value="Other">{t('store.otherCity')}</option>
                        </select>
                    </div>

                    {selectedMarket === 'Other' && (
                        <div>
                            <label className="font-medium">{t('store.newCity')}</label>
                            <input type="text" value={newMarketName} onChange={e => setNewMarketName(e.target.value)} placeholder={t('store.enterCity')} className="w-full mt-1 p-3 border-2 border-slate-200 rounded-lg" required />
                        </div>
                    )}
                    
                    <div><label className="font-medium">{t('store.contactPhone')}</label><input value={p} onChange={e=>sP(e.target.value)} type="tel" className="w-full mt-1 p-2 border rounded"/></div>
                    <div className="flex items-center mt-2"><input type="checkbox" checked={isWhatsapp} onChange={e => setIsWhatsapp(e.target.checked)} id="isWhatsappEdit" className="mr-2"/><label htmlFor="isWhatsappEdit" className="text-sm text-slate-600">{t('store.whatsapp')}</label></div>
                    <div><label className="font-medium">{t('store.contactEmail')}</label><input value={e} onChange={e=>sE(e.target.value)} type="email" className="w-full mt-1 p-2 border rounded"/></div>
                    <div><label className="font-medium">{t('store.location')}</label>
                        {
                           !l ?
                           (!store ? 
                                <div className="flex items-center gap-2 mt-1 text-slate-500">
                                    <Loader2 size={18} className="animate-spin" />
                                    <span>{t('store.fetchingLocation')}</span>
                                </div> :
                                <button type="button" onClick={()=> setIsMapOpen(true)} className="flex items-center gap-2 mt-1 bg-slate-200 font-bold py-2 px-4 rounded-lg"><MapPin size={18}/>{t('store.setLocation')}</button>
                           ) :
                           <div className="text-sm p-2 bg-green-100 text-green-800 rounded-lg mt-1 cursor-pointer" onClick={()=> setIsMapOpen(true)}>
                               <p className="font-bold truncate">{l.address}</p>
                           </div>
                        }
                    </div>
                    {/* NEW: Profile Picture Management */}
                    <div className="pt-4 border-t space-y-3">
                        <label className="font-medium text-slate-600 block">{t('store.profilePicture')}</label>
                        {profileImageUrl && (
                            <div className="flex items-center gap-4">
                                <img src={profileImageUrl} alt="Profile" className="w-20 h-20 rounded-full object-cover border-2 border-sky-500" />
                                <button type="button" onClick={handleRemoveProfileImage} className="text-red-500 text-sm hover:underline flex items-center gap-1"><X size={14}/> {t('store.removePicture')}</button>
                            </div>
                        )}
                        <div className="max-w-xs">
                            {!profileImageUrl && <ImageUploader storage={storage} folder="profiles" onUpload={handleProfileImageUpload} t={t} />}
                        </div>
                    </div>
                    {/* Store Images (MAX 3) */}
                    <div>
                        <label className="font-medium">{t('store.storeImages')}</label>
                        <div className="grid grid-cols-3 gap-2 mt-1">
                            {Array(3).fill(0).map((_, i) => images[i] ? (
                                <div key={i} className="relative w-24 h-24">
                                    <img src={images[i]} className="w-full h-full object-cover rounded-lg"/>
                                    <button type="button" onClick={() => setImages(images.filter((_, idx) => idx !== i))} className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-0.5"><X size={14}/></button>
                                </div>
                            ) : (
                                images.length < 3 && <ImageUploader key={i} onUpload={(url) => setImages(prev => [...prev, url])} storage={storage} folder="stores" t={t}/>
                            ))}
                        </div>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-slate-600"><input type="checkbox" checked={isDeliveryDriver} onChange={e => setIsDeliveryDriver(e.target.checked)} id="isDeliveryDriverEdit" className="mr-2 h-4 w-4 rounded border-gray-300 text-sky-600 focus:ring-sky-500"/><label htmlFor="isDeliveryDriverEdit">{t('store.deliveryNotifs')}</label></div>
                    <div className="flex justify-end pt-4"><button type="submit" className="bg-sky-600 text-white font-bold py-2 px-6 rounded-lg">{t('store.save')}</button></div>
                </form>
            </ModalWrapper>
            {isMapOpen && <LocationPickerModal pinLocation={l?.coords} userLocation={userLocation} onClose={() => setIsMapOpen(false)} onLocationSelect={sL} />}
        </>
    )
};

const EventDetailModal = ({ event, onClose, onBook, onCloseEvent, onRemoveAttendee, userLocation, t, currentUser, gigs, onStartChat, allStores, onEdit, myStore, onGoToMyEvents, language, setLightboxImageUrl, allProducts, onSwitchItem }) => {
    const [isMapOpen, setIsMapOpen] = useState(false);
    const [ticketQty, setTicketQty] = useState(1);
    const [guestNames, setGuestNames] = useState([]);
    const isFree = event.price === 0;
    const dateObj = new Date(event.date);
    
    const isCreator = currentUser?.uid === event.storeId;
    const ticketsSold = event.bookingsCount || 0;
    const ticketsLeft = event.ticketLimit ? event.ticketLimit - ticketsSold : Infinity;
    const isSoldOut = ticketsLeft <= 0;
    const canEdit = isCreator && event.viewContext === 'profile';
    const isOnline = event.eventType === 'online';
    const isTerminated = new Date(event.endDate) < new Date() || event.status === 'cancelled' || event.status === 'closed';

    // Get ALL bookings for this user (Moved up to avoid ReferenceError)
    const myBookings = useMemo(() => {
      if (!currentUser || isCreator) return [];
      return gigs.filter(g => g.type === 'event-booking' && g.eventId === event.id && g.creatorId === currentUser.uid);
    }, [gigs, event.id, currentUser, isCreator]);

    const totalTicketsOwned = myBookings.reduce((acc, curr) => acc + (curr.orderQuantity || 1), 0);
    const hasBooked = myBookings.length > 0;

    // NEW: Translation State & Handler
    const [translatedDescription, setTranslatedDescription] = useState(null);
    const [isTranslating, setIsTranslating] = useState(false);

    const handleTranslateDescription = async () => {
        if (translatedDescription) { 
            setTranslatedDescription(null);
            return;
        }
        setIsTranslating(true);
        const langMap = { en: 'English', fr: 'French', es: 'Spanish', ar: 'Arabic' };
        const targetLang = langMap[language] || 'English';
        const userQuery = `Translate the following text to ${targetLang}: "${event.description}"`;
        const payload = { contents: [{ parts: [{ text: userQuery }] }], systemInstruction: { parts: [{ text: `You are a universal translator. Only return the translated text.` }] }};
        try {
            const url = `${GEMINI_API_URL}?key=${geminiApiKey}`;
            const response = await fetchWithBackoff(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
            setTranslatedDescription(response.candidates?.[0]?.content?.parts?.[0]?.text || t('common.translationFailed'));
        } catch (error) { console.error("Translation error:", error); setTranslatedDescription(t('common.translationFailed')); } finally { setIsTranslating(false); }
    };

    // Sync guest name inputs with quantity
   useEffect(() => {
       setGuestNames(prev => {
           // If user has booked before, ALL new tickets need names. 
           // If first time, 1st ticket is for them, rest for guests.
           const needed = hasBooked ? ticketQty : Math.max(0, ticketQty - 1);
           if (prev.length === needed) return prev;
           if (prev.length > needed) return prev.slice(0, needed);
           return [...prev, ...Array(needed - prev.length).fill('')];
       });
   }, [ticketQty, hasBooked]);

   const handleGuestNameChange = (idx, val) => {
       const newNames = [...guestNames];
       newNames[idx] = val;
       setGuestNames(newNames);
    };

    const handleShareEvent = () => {
       // Generate a deep link using the current URL and event ID
       const appUrl = window.location.origin + window.location.pathname;
       // This creates a link like: https://your-domain.com/?action=view_event&id=12345
       const deepLink = `${appUrl}?action=view_event&id=${event.id}`;
       
       const shareText = `Join me at "${event.name}" hosted by ${event.storeName}!\nDate: ${new Date(event.date).toLocaleString()}\n\nRegister or Login to book your tickets here:\n${deepLink}`;
       
       if (navigator.clipboard && navigator.clipboard.writeText) {
           navigator.clipboard.writeText(shareText)
               .then(() => alert("Event details copied to clipboard!"))
               .catch(err => console.error('Failed to copy: ', err));
       } else {
           // Fallback for older browsers/webviews
           const textArea = document.createElement("textarea");
           textArea.value = shareText;
           document.body.appendChild(textArea);
           textArea.focus();
           textArea.select();
           try {
               document.execCommand('copy');
               alert("Event details copied to clipboard!");
           } catch (err) {
               console.error('Fallback: Oops, unable to copy', err);
           }
           document.body.removeChild(textArea);
       }
    };

    const daysUntil = useMemo(() => {
        const now = new Date();
        const evtDate = new Date(event.date);
        const diffTime = evtDate - now;
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)); 
        return diffDays;
    }, [event.date]);

    const handlePrintTicket = () => {
       let allTicketsHtml = '';
       const bookerEmail = currentUser?.email;
       const eventStart = new Date(event.date).toLocaleString();
       const eventEnd = new Date(event.endDate).toLocaleString();
       
       myBookings.forEach(booking => {
           // Main ticket for the booker
           const bookerName = `${myStore?.firstName || ''} ${myStore?.lastName || ''}`.trim() || currentUser?.email;
           const guests = booking.guestNames || [];
           const ticketId = booking.id;
           
           const generateTicketHTML = (attendeeName, isGuest = false) => {
               // QR Data: Simple JSON string of validation info
               const qrData = encodeURIComponent(JSON.stringify({
                   event: event.name,
                   attendee: attendeeName,
                   id: ticketId,
                   bookedBy: bookerEmail
               }));
               const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${qrData}`;

               return `
               <div style="border: 2px solid #000; border-radius: 16px; padding: 0; margin-bottom: 30px; font-family: sans-serif; page-break-inside: avoid; overflow: hidden; box-shadow: 0 4px 6px rgba(0,0,0,0.1);">
                   <div style="background: #7c3aed; color: white; padding: 20px; text-align: center;">
                        <h1 style="margin:0; font-size: 24px;">${event.name}</h1>
                        <p style="margin:5px 0 0; font-size: 14px;">HOSTED BY: ${event.storeName.toUpperCase()}</p>
                   </div>
                   <div style="padding: 20px; display: flex; justify-content: space-between; align-items: center;">
                       <div style="flex: 1; padding-right: 20px;">
                           <div style="margin-bottom: 15px;">
                               <p style="font-size: 12px; color: #666; margin: 0; text-transform: uppercase; letter-spacing: 1px;">Attendee</p>
                               <p style="font-size: 18px; font-weight: bold; margin: 5px 0 0;">${attendeeName}</p>
                               ${isGuest ? `<p style="font-size: 12px; color: #888; margin: 2px 0 0;">(Guest of ${bookerName})</p>` : ''}
                           </div>
                           <div style="margin-bottom: 15px;">
                               <p style="font-size: 12px; color: #666; margin: 0; text-transform: uppercase; letter-spacing: 1px;">Date & Time</p>
                               <p style="font-size: 14px; font-weight: bold; margin: 5px 0 0;">Start: ${eventStart}</p>
                               <p style="font-size: 14px; font-weight: bold; margin: 2px 0 0;">End: ${eventEnd}</p>
                           </div>
                           <div style="margin-bottom: 15px;">
                               <p style="font-size: 12px; color: #666; margin: 0; text-transform: uppercase; letter-spacing: 1px;">Location</p>
                               <p style="font-size: 14px; font-weight: bold; margin: 5px 0 0;">${event.location ? event.location.address : 'Online Event'}</p>
                           </div>
                           <div>
                               <p style="font-size: 12px; color: #666; margin: 0; text-transform: uppercase; letter-spacing: 1px;">Booked By</p>
                               <p style="font-size: 14px; font-weight: bold; margin: 5px 0 0;">${bookerEmail}</p>
                           </div>
                       </div>
                       <div style="text-align: center; border-left: 2px dashed #ccc; padding-left: 20px;">
                           <img src="${qrUrl}" alt="Ticket QR" style="width: 120px; height: 120px; display: block; margin-bottom: 10px;" />
                           <div style="font-size: 10px; color: #999;">SCAN FOR ENTRY</div>
                           <div style="margin-top: 10px; font-weight:bold; font-size: 16px; color: #7c3aed; border: 2px solid #7c3aed; padding: 5px 10px; border-radius: 8px;">ADMIT ONE</div>
                       </div>
                   </div>
                   <div style="background: #f3f4f6; padding: 10px 20px; text-align: center; font-size: 10px; color: #666; border-top: 1px solid #e5e7eb;">
                       Ticket ID: ${ticketId.substring(0, 8).toUpperCase()} • Presented by GigNile
                   </div>
               </div>
               `;
           };

           // Generate ticket for Booker (if they haven't assigned all slots to guests, typically booker takes 1 slot)
           allTicketsHtml += generateTicketHTML(bookerName);

           // Generate tickets for Guests
           guests.forEach((guestName, i) => {
                allTicketsHtml += generateTicketHTML(guestName || `Guest ${i+1}`, true);
           });
        });

       const content = `<html><head><title>Print Tickets</title></head><body style="background-color: #fff; padding: 20px; max-width: 800px; margin: 0 auto;">${allTicketsHtml}</body></html>`;
       const win = window.open('', '', 'width=800,height=800');
       win.document.write(content);
       win.document.close();
       setTimeout(() => {
           win.focus();
           win.print();
       }, 500);
    };

    // Get Host Info
    const host = useMemo(() => allStores?.find(s => s.id === event.storeId), [allStores, event.storeId]);
    const hostPhone = host?.contactPhone;

    // Get Attendee Info for Host View (Enhanced with profile data)
    const attendees = useMemo(() => {
        if (!isCreator || !gigs) return [];
        const rawAttendees = gigs.filter(g => g.type === 'event-booking' && g.eventId === event.id);
        return rawAttendees.map(att => {
            const profile = allStores?.find(s => s.id === att.creatorId);
            return { 
                ...att,
                displayName: profile ? `${profile.firstName || ''} ${profile.lastName || ''}`.trim() : att.creatorName,
                email: profile?.contactEmail || 'N/A',
                phone: profile?.contactPhone || 'N/A',
                profileImage: profile?.profileImageUrl || 'https://placehold.co/40x40/EEE/31343C?text=👤',
            };
        });
    }, [gigs, event.id, isCreator, allStores]);

    return (
        <>
        <ModalWrapper onClose={onClose}>
            <div className="relative w-full h-72 rounded-xl overflow-hidden mb-4 shadow-lg group">
                <img 
                    src={event.images?.[0] || 'https://placehold.co/600x400/f43f5e/ffffff?text=Event'} 
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110 cursor-zoom-in"
                    onClick={() => setLightboxImageUrl && setLightboxImageUrl(event.images?.[0] || 'https://placehold.co/600x400/f43f5e/ffffff?text=Event')}
                />
                {isCreator && <span className="absolute top-2 left-2 bg-purple-600 text-white text-xs font-bold px-3 py-1 rounded-full shadow-lg border border-white/20 z-10">{t('event.organizerTag')}</span>}
            </div>
            
            <div className="flex justify-between items-start">
                <h2 className="text-3xl font-bold text-slate-800">{event.name}</h2>

                {daysUntil > 0 ? (
                        <span className="inline-block mt-1 text-xs font-bold bg-blue-100 text-blue-700 px-2 py-1 rounded-md">
                            {t('event.daysRemaining').replace('{{count}}', daysUntil)}
                        </span>
                    ) : daysUntil === 0 ? (
                         <span className="inline-block mt-1 text-xs font-bold bg-red-100 text-red-700 px-2 py-1 rounded-md animate-pulse">
                            {t('event.today')}
                        </span>
                    ) : (
                         <span className="inline-block mt-1 text-xs font-bold bg-slate-200 text-slate-600 px-2 py-1 rounded-md">
                            {t('event.ended')}
                        </span>
                    )}

                <div className="text-right">
                    <span className={`px-3 py-1 rounded-full text-sm font-bold ${isFree ? 'bg-green-100 text-green-700' : 'bg-purple-100 text-purple-700'}`}>
                        {isFree ? t('common.free') : `${event.currencySymbol || '$'}${event.price}`}
                    </span>
                    {event.ticketLimit && <p className="text-xs text-slate-500 mt-1">{ticketsLeft > 0 ? t('event.ticketsLeft').replace('{{count}}', ticketsLeft) : t('common.soldOut')}</p>}
                </div>
            </div>
            
            <div className="flex flex-wrap gap-4 my-4 text-slate-600 text-sm">
                <div className="flex items-center gap-2 bg-slate-100 px-3 py-2 rounded-lg">
                    <Calendar size={18} className="text-purple-500"/>
                    <span className="font-semibold">{dateObj.toLocaleString()} - {new Date(event.endDate).toLocaleString()}</span>
                </div>
                {isOnline ? (
                   <div className="flex items-center gap-2 bg-green-100 px-3 py-2 rounded-lg text-green-700">
                       <Globe size={18} />
                       <span className="font-bold">Online Event</span>
                   </div>
               ) : (
                   <div className="flex items-center gap-2 bg-slate-100 px-3 py-2 rounded-lg cursor-pointer hover:bg-purple-50 transition-colors" onClick={() => setIsMapOpen(true)}>
                       <MapPin size={18} className="text-purple-500"/>
                       <span className="font-semibold underline decoration-purple-300">{event.location?.address || t('common.viewMap')}</span>
                   </div>
               )}
            </div>

            <div className="bg-slate-50 p-4 rounded-xl border border-slate-100 mb-6">
                <div className="flex justify-between items-center mb-2">
                    <h3 className="font-bold text-slate-700">{t('event.about')}</h3>
                    <button onClick={handleTranslateDescription} disabled={isTranslating} className="text-xs text-sky-600 hover:underline flex items-center gap-1">
                        <Languages size={14} /> {isTranslating ? t('common.translating') : (translatedDescription ? t('common.showOriginal') : t('common.translate'))}
                    </button>
                </div>
                <p className="text-slate-600 whitespace-pre-wrap leading-relaxed">{translatedDescription || event.description}</p>
                {/* Agenda visible if Creator OR (Booked AND in profile view) */}
                {event.agendaUrl && (isCreator || (hasBooked && event.viewContext === 'profile')) && (
                   <a href={event.agendaUrl} target="_blank" rel="noopener noreferrer" className="mt-4 inline-flex items-center gap-2 text-sky-600 hover:underline font-semibold">
                       <Briefcase size={16} /> {t('event.downloadAgenda')}
                   </a>
               )}
               {isOnline && (isCreator || hasBooked) && (
                   <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                       <p className="text-xs font-bold text-green-800 uppercase mb-1">{t('event.joinLink')}</p>
                       <a href={event.meetingLink} target="_blank" rel="noopener noreferrer" className="text-green-700 underline break-all">{event.meetingLink}</a>
                   </div>
               )}
            </div>

            {/* CREATOR VIEW: Manage Attendees */}
            {isCreator ? (
                <div className="mb-6 p-4 border-2 border-purple-100 rounded-xl bg-purple-50/50">
                    <h3 className="font-bold text-purple-800 mb-3 flex items-center gap-2">
                        <Users size={18}/> {t('event.attendees')} ({attendees.length} / {event.ticketLimit || '∞'})
                    </h3>
                    {attendees.length === 0 ? (
                        <p className="text-slate-500 text-sm italic">No bookings yet.</p>
                    ) : (
                        <div className="flex flex-col gap-3 max-h-80 overflow-y-auto">
                            {attendees.map(att => (
                                <div key={att.id} className="bg-white p-4 rounded-lg shadow-sm border border-purple-100">
                                    <div className="flex items-start justify-between mb-2">
                                        <div className="flex items-center gap-3">
                                            <img src={att.profileImage} className="w-12 h-12 rounded-full object-cover border border-purple-200" alt="Guest"/>
                                            <div>
                                                <p className="text-md font-bold text-slate-800">{att.displayName}</p>
                                                <p className="text-xs text-slate-500">{att.email}</p>
                                            </div>
                                        </div>
                                        <span className="bg-purple-100 text-purple-700 text-xs font-bold px-2 py-1 rounded-full">{att.orderQuantity || 1} Tickets</span>
                                    </div>
                                    
                                    <div className="text-sm text-slate-600 mb-3 grid grid-cols-1 sm:grid-cols-2 gap-1">
                                        <div className="flex items-center gap-2"><Phone size={14} className="text-slate-400"/> {att.phone}</div>
                                        <div className="flex items-center gap-2"><Calendar size={14} className="text-slate-400"/> Booked: {formatTimeAgo(att.createdAt)}</div>
                                    </div>
                                    
                                    {att.guestNames && att.guestNames.length > 0 && (
                                        <div className="mb-3 p-2 bg-slate-50 rounded text-xs text-slate-600">
                                            <span className="font-bold">Guests:</span> {att.guestNames.join(', ')}
                                        </div>
                                    )}

                                    <div className="flex gap-2 border-t pt-2">
                                        <button onClick={() => onStartChat(att.creatorId, true)} className="flex-1 py-2 bg-sky-50 text-sky-600 font-bold rounded-lg hover:bg-sky-100 text-xs flex items-center justify-center gap-1"><MessageSquare size={14}/> Chat</button>
                                        <button onClick={() => onRemoveAttendee(att.id, event.id, att.creatorId, att.orderQuantity || 1)} className="flex-1 py-2 bg-red-50 text-red-600 font-bold rounded-lg hover:bg-red-100 text-xs flex items-center justify-center gap-1"><UserX size={14}/> Remove</button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                    {canEdit ? (
                       <div className="mt-4 pt-4 border-t border-purple-200 flex gap-2">
                           <button onClick={onEdit} className="flex-1 bg-sky-600 text-white font-bold py-3 rounded-xl hover:bg-sky-700 flex items-center justify-center gap-2 transition-colors">
                               <Edit size={18} /> {t('common.edit')}
                           </button>
                           <button onClick={handleShareEvent} className="flex-1 bg-indigo-600 text-white font-bold py-3 rounded-xl hover:bg-indigo-700 flex items-center justify-center gap-2 transition-colors">
                             <Send size={18} /> {t('common.share')}
                           </button>
                           <button onClick={() => onCloseEvent(event.id)} className="flex-1 bg-slate-800 text-white font-bold py-3 rounded-xl hover:bg-slate-900 flex items-center justify-center gap-2 transition-colors"><Trash2 size={18} /> Cancel</button>
                       </div>
                   ) : (
                        <div 
                           onClick={onGoToMyEvents}
                           className="mt-4 pt-4 border-t border-purple-200 text-center text-sm text-purple-800 italic cursor-pointer hover:text-purple-900 hover:underline transition-all"
                       >
                           {t('event.manageHint')}
                        </div>
                   )}
                </div>
            ) : (
                /* USER VIEW: Booking & Contact */
                <div className="mb-6">
                    <div className="flex gap-3 mb-4">
                        <button onClick={() => onStartChat(event.storeId, true)} className="flex-1 flex items-center justify-center gap-2 bg-sky-100 text-sky-700 font-bold py-3 rounded-xl hover:bg-sky-200 transition-colors">
                            <MessageSquare size={20}/> {t('event.chatHost')}
                        </button>
                        {hostPhone && (
                            <a href={`tel:${hostPhone}`} className="flex-1 flex items-center justify-center gap-2 bg-green-100 text-green-700 font-bold py-3 rounded-xl hover:bg-green-200 transition-colors">
                                <Phone size={20}/> {t('event.callHost')}
                            </a>
                        )}
                    </div>
                    
                    {/* Show "Booked" state if user already has a ticket */}
                    {hasBooked && (
                        <div className="bg-green-50 p-6 rounded-xl border-2 border-green-200 text-center mb-4">
                            <div className="bg-green-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-3">
                                <CheckCircle size={32} className="text-green-600" />
                            </div>
                            <h3 className="text-xl font-bold text-green-800 mb-1">{t('event.youAreGoing')}</h3>
                            <p className="text-green-700 mb-4">{t('event.ticketCount').replace('{{count}}', totalTicketsOwned)}</p>
                            <div className="flex gap-2 justify-center">
                                <button onClick={handlePrintTicket} className="bg-white text-green-700 px-4 py-2 rounded-lg font-bold shadow-sm text-sm flex items-center gap-2 border border-green-200"><Ticket size={16}/> {t('event.printTicket')}</button>
                                {event.location && <button onClick={() => setIsMapOpen(true)} className="bg-white text-green-700 px-4 py-2 rounded-lg font-bold shadow-sm text-sm">{t('common.directions')}</button>}
                            </div>
                        </div>
                    )}
                    
                    {/* Show Booking Form (Allow booking more even if already booked) */}
                    {!isTerminated && (!isSoldOut || hasBooked) ? (
                        <div className="bg-white p-4 rounded-xl border-2 border-purple-100 shadow-sm">
                            <div className="flex justify-between items-center mb-4">
                                <span className="font-bold text-slate-700">{t('event.tickets')}</span>
                                <div className="flex items-center gap-3 bg-slate-100 rounded-lg p-1">
                                    <button onClick={() => setTicketQty(Math.max(1, ticketQty - 1))} className="w-8 h-8 flex items-center justify-center bg-white rounded shadow-sm font-bold hover:bg-slate-50">-</button>
                                    <span className="w-6 text-center font-bold">{ticketQty}</span>
                                    <button onClick={() => setTicketQty(Math.min(ticketsLeft, ticketQty + 1))} className="w-8 h-8 flex items-center justify-center bg-white rounded shadow-sm font-bold hover:bg-slate-50">+</button>
                                </div>
                            </div>

                             {/* Guest Name Inputs */}
                             {guestNames.length > 0 && (
                                 <div className="mb-4 space-y-2 bg-slate-50 p-3 rounded-lg">
                                     <p className="text-xs font-bold text-slate-500 uppercase">
                                        {hasBooked ? t('event.attendeeNames') : t('event.guestNames')}
                                     </p>
                                     {guestNames.map((name, i) => (
                                         <input 
                                             key={i}
                                             placeholder={hasBooked ? t('event.attendeeNamePlaceholder').replace('{{index}}', i + 1) : t('event.guestNamePlaceholder').replace('{{index}}', i + 1)}
                                             value={name}
                                             onChange={(e) => handleGuestNameChange(i, e.target.value)}
                                             className="w-full p-2 border rounded text-sm"
                                             required
                                         />
                                     ))}
                                 </div>
                              )}

                            <div className="flex justify-between items-center font-bold text-lg mb-4">
                                <span>{t('common.total')}</span>
                                <span className="text-purple-600">{isFree ? t('common.free') : `${event.currencySymbol || '$'}${((event.price || 0) * ticketQty).toFixed(2)}`}</span>
                            </div>
                            <button 
                                onClick={() => {
                                   if (guestNames.length > 0 && guestNames.some(n => !n.trim())) {
                                        alert("Please enter names for all guests/attendees.");
                                       return;
                                   }
                                   onBook(event, ticketQty, guestNames);
                               }}
                                className="w-full bg-purple-600 hover:bg-purple-700 text-white font-bold py-3.5 rounded-xl shadow-lg flex items-center justify-center gap-2 transition-all active:scale-95"
                            >
                                <Ticket size={20} /> {hasBooked ? t('event.bookMore') : (isFree ? t('event.confirmAttendance') : t('event.bookTicket'))}
                            </button>
                        </div>
                    ) : isTerminated || isSoldOut ? (
                        <div className="p-4 bg-red-100 text-red-700 font-bold text-center rounded-xl border border-red-200">
                            {isTerminated ? t('event.ended') || 'Event Ended' : t('common.soldOut')}
                        </div>
                    ) : null}
                </div>
            )}
            <AlternativeItemsCarousel 
                currentItem={event} 
                allItems={allProducts} 
                onItemClick={onSwitchItem} 
                t={t} 
            />
        </ModalWrapper>
        {isMapOpen && <LiveNavigationModal origin={userLocation} destination={event.location?.coords} onClose={() => setIsMapOpen(false)} />}
        </>
    );
};
const MyEventsActivity = ({ bookings, hostedEvents, onRate, userLocation, setPage, t, onMarkComplete, onOpenEventDetail, allProducts }) => {
    const [activeTab, setActiveTab] = useState('upcoming'); 
    const [isNavOpen, setIsNavOpen] = useState(false);
    const [navDest, setNavDest] = useState(null);

    const now = new Date();

    const getFilteredList = () => {
        if (activeTab === 'hosted') {
            // Show all hosted events unless hard deleted
            return hostedEvents || [];
        }
        // Filter bookings (tickets)
        return bookings.filter(b => {
            // Use End Date if available, otherwise fallback to Start Date (legacy support)
            const eventEnd = b.eventEndDate ? new Date(b.eventEndDate) : (b.eventDate ? new Date(b.eventDate) : new Date());
           
            // Event is "past" only if the END date has passed OR status is explicitly closed/cancelled
            const isPast = eventEnd < now || b.status === 'completed' || b.status === 'cancelled' || b.status === 'closed';
            return activeTab === 'upcoming' ? !isPast : isPast;
        });
    };

    const itemsToDisplay = getFilteredList();
    const handleNavigate = (coords) => { setNavDest(coords); setIsNavOpen(true); };

    return (
        <div>
            <button onClick={() => setPage('profile')} className="flex items-center gap-2 mb-6 font-semibold text-slate-600 hover:text-slate-900">
                <ArrowLeft size={20}/> {t('profile.backToProfile')}
            </button>
            <h2 className="text-2xl md:text-3xl font-bold mb-6">{t('profile.myEventsTickets')}</h2>
            
            <div className="flex gap-2 mb-6 overflow-x-auto pb-2 no-scrollbar">
                <button onClick={() => setActiveTab('upcoming')} className={`px-4 py-2 rounded-full font-bold shrink-0 ${activeTab === 'upcoming' ? 'bg-rose-500 text-white shadow-md' : 'bg-slate-200 text-slate-600'}`}>{t('event.upcomingTickets')}</button>
                <button onClick={() => setActiveTab('past')} className={`px-4 py-2 rounded-full font-bold shrink-0 ${activeTab === 'past' ? 'bg-rose-500 text-white shadow-md' : 'bg-slate-200 text-slate-600'}`}>{t('event.pastCompleted')}</button>
                <button onClick={() => setActiveTab('hosted')} className={`px-4 py-2 rounded-full font-bold shrink-0 ${activeTab === 'hosted' ? 'bg-purple-600 text-white shadow-md' : 'bg-slate-200 text-slate-600'}`}>{t('profile.eventsHosted')}</button>
            </div>

            <div className="space-y-4">
                {itemsToDisplay.length === 0 ? <p className="text-slate-500 text-center py-10 bg-white rounded-xl border border-dashed border-slate-300">No events found in this category.</p> : itemsToDisplay.map(item => (
                    <div key={item.id} className="bg-white p-4 rounded-2xl shadow-sm border border-slate-100 flex flex-col gap-3 relative overflow-hidden">
                         {/* Status Strip */}
                         <div className={`absolute top-0 left-0 w-1 h-full ${item.status === 'cancelled' ? 'bg-red-500' : (item.status === 'completed' ? 'bg-green-500' : 'bg-sky-500')}`}></div>
                         
                         <div className="flex justify-between items-start pl-2">
                            {/* Allow clicking on the item in ANY tab to open details (for agenda/printing) */}
                            <div onClick={() => {
                               if (activeTab === 'hosted') {
                                   onOpenEventDetail(item);
                               } else {
                                   // Look up the original event so the Modal gets the correct ID, Agenda, and Store info
                                   const originalEvent = allProducts?.find(p => p.id === item.eventId);
                                   if (originalEvent) {
                                       onOpenEventDetail({ ...originalEvent, viewContext: 'profile' });
                                   } else {
                                       // Fallback if event is missing (e.g. hard deleted), construct minimal object for modal
                                       onOpenEventDetail({
                                           ...item,
                                           id: item.eventId, // Crucial: Use EventID not GigID for booking lookup
                                           storeId: item.workerId, // Remap worker to store for chat
                                           name: item.title.replace('Event Booking: ', ''),
                                           viewContext: 'profile'
                                       });
                                   }
                               }
                            }} className="cursor-pointer flex-1 hover:opacity-80 transition-opacity">
                                <h3 className="text-lg font-bold text-slate-800 leading-tight">{item.title ? item.title.replace('Event Booking: ', '') : item.name}</h3>
                                <p className="text-sm text-rose-500 font-bold flex items-center gap-1 mt-1">
                                    <Calendar size={14}/> {new Date(item.eventDate || item.date).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })}
                                </p>
                                {activeTab !== 'hosted' && <p className="text-xs text-slate-400 mt-1">Host: {item.storeName} • {item.orderQuantity || 1} Ticket(s)</p>}
                                {activeTab === 'hosted' && <p className="text-xs text-slate-500 mt-1 font-medium">{item.bookingsCount || 0} Guests • {item.status === 'cancelled' ? 'Cancelled' : (item.isPublished ? 'Published' : 'Draft')}</p>}
                            </div>
                            
                            <div className="flex flex-col items-end gap-1">
                                <span className={`px-2 py-1 rounded text-[10px] uppercase font-bold ${item.status === 'completed' ? 'bg-green-100 text-green-700' : (item.status === 'cancelled' ? 'bg-red-100 text-red-700' : 'bg-sky-100 text-sky-700')}`}>
                                    {t(`status.${item.status}`) || item.status}
                                </span>
                            </div>
                         </div>

                         {/* Action Buttons */}
                         <div className="flex gap-2 mt-2 pl-2">
                             {/* 1. Navigation (For Upcoming Tickets) */}
                             {activeTab === 'upcoming' && item.location?.coords && item.status !== 'cancelled' && (
                                 <button onClick={() => handleNavigate(item.location.coords)} className="flex-1 bg-sky-50 text-sky-700 py-2.5 rounded-xl font-bold text-sm flex items-center justify-center gap-2 hover:bg-sky-100 border border-sky-100">
                                     <Compass size={16}/> {t('common.location')}
                                 </button>
                             )}
                             
                             {/* 2. Host Management (For Hosted Events) */}
                             {activeTab === 'hosted' && item.status !== 'cancelled' && (
                                 <button onClick={() => onOpenEventDetail(item)} className="flex-1 bg-purple-50 text-purple-700 py-2.5 rounded-xl font-bold text-sm hover:bg-purple-100 border border-purple-100">
                                     {t('event.manage')}
                                 </button>
                             )}

                             {/* 3. Completion/Rating (For Past Tickets) */}
                             {activeTab === 'past' && item.status === 'in-progress' && activeTab !== 'hosted' && (
                                 <button onClick={() => onMarkComplete(item.id)} className="flex-1 bg-green-500 text-white py-2.5 rounded-xl font-bold text-sm hover:bg-green-600 shadow-md">
                                     {t('event.markComplete')}
                                 </button>
                             )}
                             
                             {item.status === 'completed' && !item.workerRated && activeTab !== 'hosted' && (
                                 <div className="w-full flex items-center justify-between bg-slate-50 p-2 rounded-xl border border-slate-100">
                                     <span className="text-sm font-medium text-slate-600">{t('event.rateHost')}</span>
                                        <StarRating rating={0} onRating={(r) => {
                                         if (window.confirm("Submit rating?")) {
                                             onRate(item.id, item.workerId, r);
                                         }
                                        }} />
                                 </div>
                             )}
                         </div>
                    </div>
                ))}
            </div>
            {isNavOpen && navDest && <LiveNavigationModal origin={userLocation} destination={navDest} onClose={() => setIsNavOpen(false)} t={t} />}
        </div>
    );
};

const AlternativeItemsCarousel = ({ currentItem, allItems, onItemClick, t }) => {
    const alternatives = useMemo(() => {
        if (!currentItem || !allItems) return [];
        return allItems.filter(item => {
            if (item.id === currentItem.id) return false;
            // --- NEW: Strict Filtering for Marketplace Active Items ---
            if (item.isDeleted) return false; // Must not be deleted
            
            // For Gigs: Must be 'open'
            if (item.itemType === 'gig' && item.status !== 'open') return false;
            
            // For Products/Services/Events: Must be 'approved'
            if (item.itemType === 'product' && item.status !== 'approved') return false;

            // For Events specifically: Must be in the future
            if (item.type === 'event') {
                const endDate = item.endDate ? new Date(item.endDate) : new Date(item.date);
                if (endDate < new Date()) return false;
                if (item.status === 'cancelled' || item.status === 'closed') return false;
            }
            // Match Type (e.g. goods vs goods, event vs event)
            if (item.type !== currentItem.type && item.itemType !== currentItem.itemType) return false; 
            return true; 
        }).sort((a, b) => {
             // Prioritize category match
             const aCatMatch = a.categories?.some(c => currentItem.categories?.includes(c)) ? 1 : 0;
             const bCatMatch = b.categories?.some(c => currentItem.categories?.includes(c)) ? 1 : 0;
             return bCatMatch - aCatMatch;
        }).slice(0, 5); // Limit to 5 items
    }, [currentItem, allItems]);

    if (alternatives.length === 0) return null;

    return (
        <div className="mt-8 pt-6 border-t border-slate-100">
            <h3 className="font-bold text-slate-700 mb-4 text-lg">{t('common.youMightLike')}</h3>
            <div className="flex gap-4 overflow-x-auto pb-4 no-scrollbar snap-x">
                {alternatives.map(item => (
                    <div 
                        key={item.id} 
                        onClick={() => onItemClick(item)}
                        className="min-w-[160px] w-[160px] bg-white rounded-xl shadow-sm border border-slate-100 cursor-pointer hover:shadow-md hover:-translate-y-1 transition-all flex-shrink-0 snap-start group"
                    >
                        <div className="h-24 w-full overflow-hidden rounded-t-xl bg-slate-50">
                            <img 
                                src={item.images?.[0] || 'https://placehold.co/150x150/EEE/31343C?text=Item'} 
                                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" 
                                alt={item.title || item.name}
                            />
                        </div>
                        <div className="p-3">
                            <p className="font-bold text-sm truncate text-slate-800">{item.title || item.name}</p>
                            <p className="text-xs font-semibold text-sky-600 mt-1">
                                {item.currencySymbol || '$'}{(item.price || item.pay || 0).toFixed(2)}
                            </p>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    )
};

const CheckoutModal = ({product,onClose,onPlaceOrder,userLocation, allStores, onStartChat, myStore, onViewStore, setLightboxImageUrl, language, t, currentUser, allProducts, onSwitchItem})=>{
    const [quantity, setQuantity] = useState(1);
    const [paymentMethod, setPaymentMethod] = useState('cash'); // Default to cash
    const [paymentStep, setPaymentStep] = useState('select'); // 'select', 'confirming', 'confirmed'
    const [translatedDescription, setTranslatedDescription] = useState(null);
    const [isTranslating, setIsTranslating] = useState(false);
    const [isNavigationVisible, setNavigationVisible] = useState(false);
    const seller = useMemo(() => allStores.find(s => s.id === product.storeId), [allStores, product.storeId]); 
    const totalPrice = (product.price * quantity);
    const isOwnProduct = currentUser?.uid === product.storeId; // Check ownership
    const availableStock = product.stock || 0;
    const canAfford = myStore?.coinBalance >= totalPrice;
    const displayPhone = seller?.contactPhone;
    const cleanPhone = formatPhoneNumberForWhatsApp(displayPhone);
    const displayIsWhatsapp = seller?.isWhatsapp;

    const ActionIcon = ({ href, onClick, Icon, label, color, disabled }) => (
        <button 
            onClick={onClick} 
            disabled={disabled}
            className={`flex flex-col items-center gap-2 group ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
        >
            <a 
                href={disabled ? undefined : href} 
                target={href ? "_blank" : undefined} 
                rel={href ? "noopener noreferrer" : undefined}
                className={`w-14 h-14 rounded-2xl flex items-center justify-center text-white shadow-lg transition-transform ${disabled ? 'bg-slate-300' : `${color} group-hover:scale-105 group-active:scale-95`}`}
            >
                <Icon size={24} />
            </a>
            <span className="text-xs font-bold text-slate-600">{label}</span>
        </button>
    );

    const handleOrder = () => {
        if (paymentMethod === 'cash') {
            onPlaceOrder({ product, delivery: { fee: 15.00 }, buyerLocation: userLocation, quantity, paymentMethod, currencySymbol: product.currencySymbol || '$' });
            return;
        }
        
        setPaymentStep('confirming');
        // Simulate API call for card/coin
        setTimeout(() => {
            setPaymentStep('confirmed');
            setTimeout(() => {
                onPlaceOrder({product,delivery:{fee:15.00}, buyerLocation: userLocation, quantity, paymentMethod, currencySymbol: product.currencySymbol || '$'});
            }, 1000); // Wait a moment to show success before closing
        }, 1500);
    }

    // NEW: Translation Handler
    const handleTranslateDescription = async () => {
        if (translatedDescription) { // Toggle off
            setTranslatedDescription(null);
            return;
        }
        setIsTranslating(true);
        const langMap = { en: 'English', fr: 'French', es: 'Spanish', ar: 'Arabic' };
        const targetLang = langMap[language] || 'English';
        const userQuery = `Translate the following text to ${targetLang}: "${product.description}"`;
        const payload = { contents: [{ parts: [{ text: userQuery }] }], systemInstruction: { parts: [{ text: `You are a universal translator. Only return the translated text.` }] }};
        try {
            const url = `${GEMINI_API_URL}?key=${geminiApiKey}`;
            const response = await fetchWithBackoff(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
            setTranslatedDescription(response.candidates?.[0]?.content?.parts?.[0]?.text || "Translation failed.");
        } catch (error) { console.error("Translation error:", error); setTranslatedDescription("Translation error."); } finally { setIsTranslating(false); }
    };

    const PaymentOption = ({ method, selected, onClick, icon, balance, description }) => (
        <div 
            onClick={onClick}
            className={`flex-1 p-4 rounded-lg border-2 flex items-center gap-4 cursor-pointer transition-all duration-200 ${selected ? 'border-teal-500 bg-teal-50 shadow-md' : 'border-slate-200 bg-white hover:bg-slate-50'}`}
        >
            <div className={`p-2 rounded-full ${selected ? 'bg-teal-500' : 'bg-slate-100'}`}>
                {icon}
            </div>
            <div>
                <span className="font-bold text-sm capitalize">{method}</span>
                {description && <p className="text-xs text-slate-500">{description}</p>}
                {balance !== undefined && <span className="text-xs text-slate-500">({balance})</span>}
            </div>
        </div>
    );

    return(
        <>
        <ModalWrapper onClose={onClose}>
            <h2 className="text-2xl font-bold mb-2">{t('checkout.title')}</h2>
            <div className="w-full h-72 rounded-xl overflow-hidden my-4 shadow-lg relative group">
                <img src={product.images?.[0]||'https://placehold.co/400x300/EEE/31343C?text=No+Image'} alt={product.name} 
                     className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110 cursor-zoom-in" 
                     onClick={() => setLightboxImageUrl(product.images?.[0]||'https://placehold.co/400x300/EEE/31343C?text=No+Image')} />
            </div>
            <div className="bg-slate-50 rounded-lg p-4 my-4 space-y-2">
                <div className="flex justify-between">
                    <span>{product.type==='service' ? t('checkout.serviceType') : t('checkout.productType')}</span>
                    <span className="font-semibold">{product.name}</span>
                </div>
                <div className="flex justify-between">
                    <span>{t('checkout.from')}</span> 
                    <span onClick={() => onViewStore(product.storeId)} className="font-semibold cursor-pointer hover:underline text-sky-600">{product.storeName}</span>
                </div>
                {product.type === 'goods' && (
                    <>
                        <div className="flex justify-between items-center">
                            <span>{t('checkout.quantity')}</span>
                            <div className="flex items-center gap-2">
                                <button onClick={() => setQuantity(q => Math.max(1, q - 1))} className="bg-slate-200 rounded-full w-8 h-8 flex items-center justify-center font-bold">-</button>
                                <span className="font-semibold w-8 text-center">{quantity}</span>
                                <button onClick={() => setQuantity(q => Math.min(availableStock, q + 1))} className="bg-slate-200 rounded-full w-8 h-8 flex items-center justify-center font-bold">+</button>
                            </div>
                        </div>
                        <div className="text-right text-sm font-semibold text-slate-500">
                            {availableStock > 0 ? t('checkout.inStockCount').replace('{{count}}', availableStock) : t('checkout.outOfStock')}
                        </div>
                    </>
                )}
                <div className="flex justify-between border-t pt-2 font-bold text-lg">
                    <span>Total Price:</span> 
                    <span>{product.currencySymbol || '$'}{totalPrice.toFixed(2)}</span>
                </div>
                {/* Description Section with Translation */}
                <div className="border-t pt-2">
                    <div className="flex justify-between items-center mb-1">
                        <span className="text-sm font-semibold text-slate-500">Description:</span>
                        <button onClick={handleTranslateDescription} disabled={isTranslating} className="text-xs text-sky-600 hover:underline flex items-center gap-1">
                            <Languages size={14} /> {isTranslating ? 'Translating...' : (translatedDescription ? 'Show Original' : 'Translate')}
                        </button>
                    </div>
                    <p className="text-sm text-slate-600 whitespace-pre-wrap">{translatedDescription || product.description}</p>
                </div>
                 {product.categories && product.categories.length > 0 && (
                    <div className="border-t pt-2">
                        <span className="text-sm font-semibold text-slate-500">{t('checkout.categories')}</span>
                        <div className="flex flex-wrap gap-1 justify-start mt-1">
                            {product.categories.map(cat => (
                                <span key={cat} className="bg-slate-200 text-slate-700 text-xs font-medium px-2 py-1 rounded-full">{cat}</span>
                            ))}
                        </div>
                    </div>
                )}
            </div>
             {!isOwnProduct && (
                <div className="flex justify-around items-center my-6 pb-4 border-b border-slate-100">
                    <ActionIcon onClick={() => onStartChat(product.storeId, true)} Icon={MessageSquare} label="Chat" color="bg-purple-500" />
                    {displayPhone && <ActionIcon href={`tel:${displayPhone}`} Icon={Phone} label="Call" color="bg-teal-500" />}
                    {displayIsWhatsapp && <ActionIcon href={`https://wa.me/${cleanPhone}`} Icon={MessageSquare} label="WhatsApp" color="bg-green-500" />}
                    <ActionIcon 
                        onClick={() => seller?.location?.coords && setNavigationVisible(true)} 
                        Icon={seller?.location?.coords ? Compass : MapPin} 
                        label={seller?.location?.coords ? "Directions" : "No Loc"} 
                        color="bg-sky-500"
                        disabled={!seller?.location?.coords} 
                    />
                </div>
            )}

            {isOwnProduct ? (
                <div 
                    onClick={() => { onViewStore(product.storeId); onClose(); }}
                    className="mt-6 p-6 bg-slate-50 rounded-2xl border-2 border-slate-200 text-center cursor-pointer hover:bg-slate-100 hover:border-sky-300 transition-all group"
                >
                    <div className="bg-white p-3 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-3 shadow-sm group-hover:scale-110 transition-transform">
                        <Store size={30} className="text-slate-400 group-hover:text-sky-500"/>
                    </div>
                    <p className="font-bold text-slate-600 group-hover:text-slate-800">{t('checkout.ownerError')}</p>
                    <p className="text-xs text-slate-400 mt-1 group-hover:text-sky-600">{t('checkout.ownerErrorDesc')}</p>
                </div>
            ) : (
            <>

            <div className="mt-6">
                <h3 className="font-bold mb-3 text-slate-700">{t('checkout.paymentMethod')}</h3>
                <div 
                    className={`p-4 rounded-2xl border-2 cursor-pointer transition-all duration-200 flex items-center gap-4 ${paymentMethod === 'cash' ? 'border-green-500 bg-green-50 shadow-md ring-1 ring-green-500' : 'border-slate-200 bg-white hover:bg-slate-50'}`}
                    onClick={() => setPaymentMethod('cash')}
                >
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center ${paymentMethod === 'cash' ? 'bg-green-500 text-white' : 'bg-slate-100 text-slate-400'}`}>
                        <DollarSign size={24} />
                    </div>
                    <div className="flex-grow">
                        <div className="flex justify-between items-center">
                            <span className={`font-bold text-lg ${paymentMethod === 'cash' ? 'text-green-800' : 'text-slate-700'}`}>{t('checkout.cash')}</span>
                            {paymentMethod === 'cash' && <CheckCircle size={20} className="text-green-600" />}
                        </div>
                        <p className={`text-sm ${paymentMethod === 'cash' ? 'text-green-700' : 'text-slate-500'}`}>{t('checkout.cashDesc')}</p>
                    </div>
                </div>
            </div>
            
            <div className="mt-8 pb-2">
                {paymentStep === 'select' ? (
                    <button 
                        onClick={handleOrder} 
                        disabled={(paymentMethod === 'coin' && !canAfford) || (product.type === 'goods' && quantity > availableStock)}
                        className={`w-full relative overflow-hidden font-bold py-4 px-6 rounded-2xl text-white text-lg shadow-xl transition-all transform active:scale-95 flex items-center justify-center gap-3 ${
                            (paymentMethod === 'coin' && !canAfford) || (product.type === 'goods' && quantity > availableStock) 
                                ? 'bg-slate-300 cursor-not-allowed' 
                                : 'bg-gradient-to-r from-teal-500 to-emerald-500 hover:from-teal-600 hover:to-emerald-600 shadow-teal-200'
                        }`}
                    >
                        <span>{product.type === 'goods' && quantity > availableStock ? t('product.outStock') : t('checkout.placeOrder')}</span>
                        {!(product.type === 'goods' && quantity > availableStock) && (
                            <div className="flex items-center gap-2 bg-white/20 px-3 py-1 rounded-lg">
                                <span className="text-sm font-medium">{t('common.total')}:</span>
                                <span className="font-extrabold">{product.currencySymbol || '$'}{totalPrice.toFixed(2)}</span>
                            </div>
                        )}
                        <ChevronRight size={20} className="opacity-80" />
                    </button>
                ) : paymentStep === 'confirming' ? (
                    <button disabled className="w-full bg-yellow-500 text-white font-bold py-4 px-6 rounded-full text-lg flex items-center justify-center gap-2">
                        <Loader2 className="animate-spin" /> {t('checkout.confirming')}
                    </button>
                ) : (
                     <button disabled className="w-full bg-green-500 text-white font-bold py-4 px-6 rounded-full text-lg flex items-center justify-center gap-2">
                        <CheckCircle /> {t('checkout.orderConfirmed')}
                    </button>
                )}
            </div>
            <AlternativeItemsCarousel 
                currentItem={product} 
                allItems={allProducts} // Pass filtered list from parent or full list
                onItemClick={onSwitchItem} 
                t={t} 
            />
            </>
            )}
        </ModalWrapper>
        {isNavigationVisible && seller?.location?.coords && <LiveNavigationModal origin={userLocation} destination={seller.location.coords} onClose={() => setNavigationVisible(false)} t={t}/>}
        </>
    );
};

const CreateEventModal = ({ onClose, onSave, onUpdate, eventToEdit, storage, t, currencyInfo, userLocation }) => {
    // Pre-fill state if editing, otherwise default
    const [name, setName] = useState(eventToEdit?.name || '');
    const [description, setDescription] = useState(eventToEdit?.description || '');
    const [price, setPrice] = useState(eventToEdit?.price || '');
    
    // Parse existing date/time or default
    const initialDate = eventToEdit?.date ? eventToEdit.date.split('T')[0] : '';
    const initialTime = eventToEdit?.date ? eventToEdit.date.split('T')[1] : '';
    const initialEndDate = eventToEdit?.endDate ? eventToEdit.endDate.split('T')[0] : '';
    const initialEndTime = eventToEdit?.endDate ? eventToEdit.endDate.split('T')[1] : '';
    
    const [date, setDate] = useState(initialDate);
    const [time, setTime] = useState(initialTime);
    const [location, setLocation] = useState(eventToEdit?.location || null);
    const [images, setImages] = useState(eventToEdit?.images || []);
    const [categories, setCategories] = useState(eventToEdit?.categories || ['Entertainment']); 
    const [ticketLimit, setTicketLimit] = useState(eventToEdit?.ticketLimit || '');
    const [endDate, setEndDate] = useState(initialEndDate);
    const [endTime, setEndTime] = useState(initialEndTime);
    const [eventType, setEventType] = useState(eventToEdit?.eventType || 'in-person');
    const [meetingLink, setMeetingLink] = useState(eventToEdit?.meetingLink || '');
    const [agendaUrl, setAgendaUrl] = useState(eventToEdit?.agendaUrl || null);
    const [categoryInput, setCategoryInput] = useState('');
    const [isMapOpen, setIsMapOpen] = useState(false);
    const [isTuning, setIsTuning] = useState(false);
    const [aiError, setAiError] = useState(null);

    useEffect(() => {
        if (userLocation && !location && !eventToEdit && eventType === 'in-person') {
            const setDefaultLocation = async () => {
                const address = await getAddressFromCoords(userLocation.latitude, userLocation.longitude);
                setLocation({ coords: userLocation, address: address });
            };
            setDefaultLocation();
        }
    }, [userLocation, location, eventToEdit, eventType]);

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!name || !date || !time || !endDate || !endTime) { alert("Please fill all date/time fields."); return; }
       if (eventType === 'in-person' && !location) { alert("Location is required for in-person events."); return; }
       if (eventType === 'online' && !meetingLink) { alert("Meeting link is required for online events."); return; }
        
        const eventPayload = {
            name, description, price: price ? parseFloat(price) : 0,
            ticketLimit: ticketLimit ? parseInt(ticketLimit) : null,
            date: `${date}T${time}`, 
            endDate: `${endDate}T${endTime}`,
            eventType,
            meetingLink: eventType === 'online' ? meetingLink : null,
            location: eventType === 'in-person' ? location : null,
            agendaUrl,
            images, categories, type: 'event'
        };

        if (eventToEdit) {
            onUpdate(eventToEdit.id, eventPayload);
        } else {
            onSave(eventPayload);
        }
    };

    // AI Description Tuning
    const handleTuneDescription = async () => {
        if (!name) { alert("Please enter an Event Name first."); return; }
        setAiError(null); setIsTuning(true);
        
        const userQuery = description.trim() === '' 
            ? `Write a generic but exciting description for an event named "${name}". Keep it under 3 sentences.` 
            : `Refine this event description to be more exciting and professional: "${description}". Keep it concise.`;
            
        const payload = {
            contents: [{ parts: [{ text: userQuery }] }],
            systemInstruction: { parts: [{ text: "Act as a professional event promoter." }] },
        };

        try {
            const url = `${GEMINI_API_URL}?key=${geminiApiKey}`;
            const response = await fetchWithBackoff(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
            const generatedText = response.candidates?.[0]?.content?.parts?.[0]?.text || description;
            setDescription(generatedText);
        } catch (error) {
            setAiError("AI failed. Check connection.");
        } finally {
            setIsTuning(false);
        }
    };

    // Dynamic Categories
    const handleAddCategory = (cat) => {
        const newCat = cat.trim();
        if (newCat && !categories.includes(newCat)) setCategories([...categories, newCat]);
        setCategoryInput('');
    };

    const handleCategoryKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault(); // Stop form submission
            handleAddCategory(categoryInput); // Add category instead
        }
    };

    return (
        <>
        <ModalWrapper onClose={onClose}>
            <h2 className="text-2xl font-bold mb-6 text-slate-800">{eventToEdit ? t('event.editTitle') : t('event.createTitle')}</h2>
            <form onSubmit={handleSubmit} className="space-y-4">
                <div><label className="font-medium">{t('event.nameLabel')}</label><input value={name} onChange={e=>setName(e.target.value)} className="w-full mt-1 p-2 border rounded-lg" required/></div>
                
                <div>
                    <label className="font-medium flex justify-between items-center">{t('event.descriptionLabel')}
                        <button type="button" onClick={handleTuneDescription} disabled={isTuning || !name.trim()} className="text-sm text-purple-600 hover:underline flex items-center gap-1">
                            {isTuning ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14}/>} {isTuning ? t('event.aiGenerating') : (description ? t('event.aiRefine') : t('event.aiGenerate'))}
                        </button>
                    </label>
                    {aiError && <p className="text-red-500 text-xs">{aiError}</p>}
                    <textarea value={description} onChange={e=>setDescription(e.target.value)} className="w-full mt-1 p-2 border rounded-lg" rows="3"></textarea>
                </div>
                <div><label className="font-medium">{t('event.typeLabel')}</label>
                   <div className="flex gap-4 mt-1">
                       <label className="flex items-center gap-2"><input type="radio" value="in-person" checked={eventType === 'in-person'} onChange={e => setEventType(e.target.value)} /> {t('event.inPerson')}</label>
                       <label className="flex items-center gap-2"><input type="radio" value="online" checked={eventType === 'online'} onChange={e => setEventType(e.target.value)} /> {t('event.online')}</label>
                   </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                    <div><label className="font-medium">{t('event.startDate')}</label><input type="date" value={date} onChange={e=>setDate(e.target.value)} className="w-full mt-1 p-2 border rounded-lg" required/></div>
                    <div>
                        <label className="font-medium">{t('event.startTime')}</label>
                        <div className="relative mt-1">
                            <Clock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400 pointer-events-none" />
                            <input 
                                type="time" 
                                step="60" 
                                value={time} 
                                onChange={e=>setTime(e.target.value)} 
                                className="w-full pl-10 p-2 border rounded-lg" 
                                required
                            />
                        </div>
                    </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                   <div><label className="font-medium">{t('event.endDate')}</label><input type="date" value={endDate} onChange={e=>setEndDate(e.target.value)} className="w-full mt-1 p-2 border rounded-lg" required/></div>
                   <div>
                        <label className="font-medium">{t('event.endTime')}</label>
                        <div className="relative mt-1">
                            <Clock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400 pointer-events-none" />
                            <input 
                                type="time" 
                                step="60" 
                                value={endTime} 
                                onChange={e=>setEndTime(e.target.value)} 
                                className="w-full pl-10 p-2 border rounded-lg" 
                                required
                            />
                        </div>
                   </div>
               </div>

                <div><label className="font-medium">{t('event.priceLabel')} ({currencyInfo.currencySymbol})</label><input type="number" value={price} onChange={e=>setPrice(e.target.value)} placeholder={t('event.pricePlaceholder')} className="w-full mt-1 p-2 border rounded-lg"/></div>
                
                <div><label className="font-medium">{t('event.ticketsLabel')}</label><input type="number" value={ticketLimit} onChange={e=>setTicketLimit(e.target.value)} placeholder={t('event.ticketsPlaceholder')} className="w-full mt-1 p-2 border rounded-lg"/></div>

                <div>
                    <label className="font-medium">{t('event.categoriesLabel')}</label>
                    <div className="flex flex-wrap gap-2 mb-2 mt-1">
                        {categories.map(cat => (
                            <span key={cat} className="bg-purple-100 text-purple-800 text-xs px-2 py-1 rounded-full flex items-center gap-1">
                                {cat} <button type="button" onClick={() => setCategories(categories.filter(c => c !== cat))}><X size={12}/></button>
                            </span>
                        ))}
                    </div>
                    <div className="flex gap-2">
                        <input type="text" value={categoryInput} onChange={e=>setCategoryInput(e.target.value)} onKeyDown={handleCategoryKeyDown} className="flex-grow p-2 border rounded-lg" placeholder={t('event.addCategoryPlaceholder')}/>
                        <button type="button" onClick={()=>handleAddCategory(categoryInput)} className="bg-purple-500 text-white p-2 rounded-lg"><PlusCircle size={20}/></button>
                    </div>
                </div>

                {eventType === 'in-person' ? (
                   <div><label className="font-medium">{t('gig.location')}</label>
                       {!location ? (
                            <div className="flex items-center gap-2 mt-1 text-slate-500"><Loader2 size={18} className="animate-spin" /><span>{t('gig.locationFetching')}</span></div>
                       ) : (
                           <div onClick={()=>setIsMapOpen(true)} className="mt-1 p-3 bg-green-50 text-green-700 rounded-lg border border-green-200 cursor-pointer font-medium flex items-center gap-2 truncate"><MapPin size={16}/> {location.address}</div>
                       )}
                   </div>
               ) : (
                   <div><label className="font-medium">{t('event.linkLabel')}</label><input value={meetingLink} onChange={e=>setMeetingLink(e.target.value)} placeholder="https://zoom.us/j/..." className="w-full mt-1 p-2 border rounded-lg" required/></div>
               )}

               <div>
                   <label className="font-medium">{t('event.agendaLabel')}</label>
                   <DocumentUploader label={t('event.uploadAgenda')} onUpload={setAgendaUrl} storage={storage} folder="agendas" required={false} t={t}/>
               </div>

                <div><label className="font-medium">{t('event.banner')}</label>
                    <div className="mt-1">{images[0] ? <div className="relative"><img src={images[0]} className="w-full h-32 object-cover rounded-lg"/><button type="button" onClick={()=>setImages([])} className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1"><X size={12}/></button></div> : <ImageUploader storage={storage} folder="events" onUpload={(url)=>setImages([url])} t={t}/>}</div>
                </div>

                <button type="submit" className="w-full bg-purple-600 text-white font-bold py-3 rounded-xl shadow-lg hover:bg-purple-700 mt-4">
                    {eventToEdit ? t('event.saveChanges') : t('event.publish')}
                </button>
            </form>
        </ModalWrapper>
        {isMapOpen && <LocationPickerModal pinLocation={location?.coords} userLocation={userLocation} onClose={()=>setIsMapOpen(false)} onLocationSelect={setLocation}/>}
        </>
    );
};

const ManageProductModal = ({ product, onClose, onSave, myStore, storage, allProducts, allStores, t }) => {
    const [name, setName] = useState(product?.name || ''); 
    const [description, setDescription] = useState(product?.description || ''); 
    const [tuningPrompt, setTuningPrompt] = useState(''); // NEW: For AI tuning
    const [isTuning, setIsTuning] = useState(false); // NEW: For AI tuning loading
    const [tuningError, setTuningError] = useState(null); // NEW: For AI tuning error
    const [price, setPrice] = useState(product?.price || ''); 
    const [unit, setUnit] = useState(product?.unit || ''); 
    const [stock, setStock] = useState(product?.stock || 0); // NEW: Stock state
    const [type, setType] = useState(product?.type || 'goods'); 
    const [images, setImages] = useState(product?.images || []); 
    const [isPublished, setIsPublished] = useState(product?.isPublished ?? true);
    const [isInStock, setIsInStock] = useState(product?.isInStock ?? true);
    const [categories, setCategories] = useState(product?.categories || []);
    const [categoryInput, setCategoryInput] = useState('');
    const [isRemote, setIsRemote] = useState(product?.type === 'service' && !product?.location);

    // NEW: AI Description Tuning Feature
    const handleTuneDescription = async () => {
        setTuningError(null);

        // Logic: If description is empty, generate based on title. If description exists, tune based on prompt.
        const userRequest = description.trim() === '' ? `Generate a compelling description for an item named "${name}" priced at $${price}.` : `Refine the product description based on this request: "${tuningPrompt}". Original description: "${description}".`;

        setTuningError(null);
        setIsTuning(true);

        const userQuery = `${userRequest} Original description: "${description}". Keep the output concise and persuasive for a mobile marketplace.`;
        const payload = {
            contents: [{ parts: [{ text: userQuery }] }],
            systemInstruction: { parts: [{ text: "Act as a tone-of-voice specialist, refining product descriptions for a mobile marketplace. Only return the final, refined text." }] },
        };
        try {
            const url = `${GEMINI_API_URL}?key=${geminiApiKey}`;
            const response = await fetchWithBackoff(url, {
                method: 'POST', 
                headers: { 'Content-Type': 'application/json' }, 
                body: JSON.stringify(payload) 
            });
            const newDesc = response.candidates?.[0]?.content?.parts?.[0]?.text || description;
            setDescription(newDesc);
        } catch (error) {
            setTuningError("AI Refinement failed. Check network or try again.");
        } finally {
            setIsTuning(false);
        }
    };

    const allExistingCategories = useMemo(() => {
        const cats = allProducts.flatMap(p => p.categories || []);
        return [...new Set(cats)];
    }, [allProducts]);

    const currencyInfo = getCurrencyInfo(myStore?.countryCode); // Get local currency

    const suggestedCategories = useMemo(() => {
        if (!categoryInput) return [];
        return allExistingCategories.filter(c => !categories.includes(c) && c.toLowerCase().includes(categoryInput.toLowerCase()));
    }, [allExistingCategories, categories, categoryInput]);

    const handleAddCategory = (category) => {
        const newCategory = category.trim();
        if (newCategory && !categories.includes(newCategory)) {
            setCategories([...categories, newCategory]);
        }
        setCategoryInput('');
    };

    const handleCategoryKeyDown = (e) => {
        if (e.key === 'Enter' || e.key === ',') {
            e.preventDefault();
            handleAddCategory(categoryInput);
        }
    };

    const removeCategory = (categoryToRemove) => {
        setCategories(categories.filter(cat => cat !== categoryToRemove));
    };


    const handleSubmit = (e) => { 
        e.preventDefault(); 
        if(categories.length === 0) {
            alert("Please add at least one category for your item.");
            return;
        }
        if(!name || !price) return; 
        const finalLocation = (type === 'service' && isRemote) ? null : myStore?.location;

        onSave({ 
            id: product?.id, 
            name, 
            description, 
            price: parseFloat(price), 
            unit, 
            type, 
            images: images.slice(0, 1), 
            location: finalLocation, 
            isPublished, 
            isInStock, 
            categories, 
            stock: type === 'goods' ? (parseInt(stock, 10) || 0) : null
        });
    }; 
    const handleImageUpload = (url) => { setImages([url]); }; // Only keep the new image

    return (<ModalWrapper onClose={onClose}><h2 className="text-2xl font-bold mb-6">{product ? t('product.editTitle') : t('product.addTitle')}</h2><form onSubmit={handleSubmit} className="space-y-4">
        <div><label className="font-medium">{t('product.type')}</label><div className="flex gap-4 mt-1"><label className="flex items-center gap-2"><input type="radio" value="goods" checked={type==='goods'} onChange={e=>setType(e.target.value)} name="type"/>{t('product.goods')}</label><label className="flex items-center gap-2"><input type="radio" value="service" checked={type==='service'} onChange={e=>setType(e.target.value)} name="type"/>{t('product.service')}</label></div></div>
        
        <div><label className="font-medium">{t('product.name')}</label><input value={name} onChange={(e) => setName(e.target.value)} className="w-full mt-1 px-3 py-2 border rounded-lg"/></div>
        
        <div>
            <label className="font-medium flex justify-between items-center">{t('product.description')}
                <button type="button" onClick={handleTuneDescription} disabled={!name.trim() || isTuning} className="text-sm text-purple-600 hover:underline flex items-center gap-1">
                    {isTuning ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14}/>} {isTuning ? t('gig.aiSuggesting') : (description.trim() === '' ? t('product.aiGenerate') : t('product.aiRefine'))}
                </button>
            </label>
            {tuningError && <p className="text-red-500 text-xs mt-1">{tuningError}</p>}
            <textarea value={description} onChange={e => setDescription(e.target.value)} rows="3" className="w-full mt-1 px-3 py-2 border rounded-lg" required></textarea>
        </div>

        <div>
            <label className="font-medium text-slate-600">{t('product.tags')}</label>
            <div className="p-2 border-2 border-slate-200 rounded-lg mt-1">
                <div className="flex flex-wrap gap-2">
                    {categories.map(cat => (
                        <div key={cat} className="flex items-center gap-1 bg-sky-100 text-sky-800 text-sm font-semibold px-2 py-1 rounded-full">
                            <span>{cat}</span>
                            <button type="button" onClick={() => removeCategory(cat)}><X size={14} /></button>
                        </div>
                    ))}
                </div>
                <div className="relative mt-2 flex gap-2">
                    <input
                        type="text"
                        value={categoryInput}
                        onChange={e => setCategoryInput(e.target.value)}
                        onKeyDown={handleCategoryKeyDown}
                        placeholder={t('product.tagsPlaceholder')}
                        className="flex-grow px-3 py-2 border-none rounded-lg focus:ring-2 focus:ring-sky-500 dark:bg-slate-600"
                    />
                    <button type="button" onClick={() => handleAddCategory(categoryInput)} className="bg-sky-500 text-white font-semibold p-2 rounded-lg hover:bg-sky-600 flex-shrink-0">
                        <PlusCircle size={20} />
                    </button>
                    {suggestedCategories.length > 0 && (
                        <div className="absolute z-10 w-full bg-white border rounded-lg mt-1 shadow-lg max-h-32 overflow-y-auto">
                            {suggestedCategories.map(cat => (
                                <div key={cat} onClick={() => handleAddCategory(cat)} className="p-2 cursor-pointer hover:bg-slate-100 text-sm">{cat}</div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
             <p className="text-xs text-slate-500 mt-1">{t('product.tagsHelp')}</p>
        </div>
        <div className="flex gap-4">
            <div className="flex-grow"><label className="font-medium">{t('product.price')} ({currencyInfo.currencySymbol})</label><input value={price} onChange={(e) => setPrice(e.target.value)} type="number" step="0.01" className="w-full mt-1 px-3 py-2 border rounded-lg" /></div>
            {type === 'goods' && <div className="w-1/3"><label className="font-medium">{t('product.stock')}</label><input value={stock} onChange={(e) => setStock(parseInt(e.target.value, 10) || 0)} type="number" className="w-full mt-1 px-3 py-2 border rounded-lg" /></div>}
            {type === 'service' && <div className="flex-grow"><label className="font-medium">{t('product.unit')}</label><input value={unit} onChange={(e) => setUnit(e.target.value)} placeholder={t('product.unitPlaceholder')} className="w-full mt-1 px-3 py-2 border rounded-lg" /></div>}
        </div>
        <div className="flex justify-around items-center p-2 bg-slate-100 rounded-lg">
            <div className="flex items-center gap-3">
                <ToggleSwitch checked={isPublished} onChange={() => setIsPublished(!isPublished)} />
                <span className="font-semibold">{isPublished ? t('product.published') : t('product.unpublished')}</span>
            </div>
            <div className="flex items-center gap-3">
                <ToggleSwitch checked={isInStock} onChange={() => setIsInStock(!isInStock)} />
                <span className="font-semibold">{isInStock ? (type === 'service' ? t('product.available') : t('product.inStock')) : (type === 'service' ? t('product.unavailable') : t('product.outStock'))}</span>
            </div>
            {type === 'service' && (
                <div className="flex items-center gap-3 border-l pl-2">
                    <ToggleSwitch checked={isRemote} onChange={() => setIsRemote(!isRemote)} />
                    <span className="font-semibold text-purple-600">{isRemote ? "Remote (Online)" : "On-site (Map)"}</span>
                </div>
            )}
        </div>
    <div>
        <label className="font-medium">{t('product.image')}</label>
        <div className="grid grid-cols-3 gap-2 mt-1">
            {images[0] ? (
                <div className="relative w-24 h-24">
                    <img src={images[0]} className="w-full h-full object-cover rounded-lg"/>
                    <button type="button" onClick={() => setImages([])} className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-0.5"><X size={14}/></button>
                </div>
            ) : (
                 <ImageUploader onUpload={handleImageUpload} storage={storage} folder="products" t={t}/>
            )}
        </div>
    </div>
    <div className="flex justify-end pt-4"><button type="submit" className="bg-sky-600 text-white font-bold py-2 px-6 rounded-lg">{t('product.save')}</button></div></form></ModalWrapper>); };
const ChangePasswordModal = ({ onClose, auth, authError, setAuthError, t }) => {
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmNewPassword, setConfirmNewPassword] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');

    const handleSubmit = async (e) => {
        e.preventDefault();
        setAuthError(null);
        setSuccessMessage('');
        if (newPassword !== confirmNewPassword) {
            setAuthError("New passwords do not match.");
            return;
        }
        setIsLoading(true);
        try {
            const user = auth.currentUser;
            const credential = EmailAuthProvider.credential(user.email, currentPassword);
            await reauthenticateWithCredential(user, credential);
            await updatePassword(user, newPassword);
            setSuccessMessage("Password updated successfully!");
            setTimeout(onClose, 2000);
        } catch (error) {
            setAuthError(error.message);
        } finally {
            setIsLoading(false);
        }
    };
    return (<ModalWrapper onClose={onClose}>
        <h2 className="text-2xl font-bold mb-6">{t('auth.changePasswordTitle')}</h2>
        {successMessage && <p className="text-green-600 bg-green-50 p-3 rounded-lg text-sm mb-4">{successMessage}</p>}
        <form onSubmit={handleSubmit} className="space-y-4">
            <div><label className="font-medium text-slate-600">{t('auth.currentPassword')}</label><input type="password" value={currentPassword} onChange={e=>setCurrentPassword(e.target.value)} className="w-full mt-1 p-3 border-2 border-slate-200 rounded-lg" required /></div>
            <div><label className="font-medium text-slate-600">{t('auth.newPassword')}</label><input type="password" value={newPassword} onChange={e=>setNewPassword(e.target.value)} className="w-full mt-1 p-3 border-2 border-slate-200 rounded-lg" required /></div>
            <div><label className="font-medium text-slate-600">{t('auth.confirmNewPassword')}</label><input type="password" value={confirmNewPassword} onChange={e=>setConfirmNewPassword(e.target.value)} className="w-full mt-1 p-3 border-2 border-slate-200 rounded-lg" required /></div>
            {authError && <p className="text-red-500 text-sm">{authError}</p>}
            <button type="submit" disabled={isLoading} className="w-full bg-sky-600 text-white font-bold py-3 rounded-lg disabled:bg-sky-300 flex justify-center items-center">{isLoading ? <Loader2 className="animate-spin" /> : t('auth.updatePassword')}</button>
        </form>
    </ModalWrapper>)
}

const BuyCoinsModal = ({ onClose, onBuy, user }) => {
    const [amount, setAmount] = useState(10);
    return (
        <ModalWrapper onClose={onClose}>
            <h2 className="text-2xl font-bold mb-6">Buy Coins</h2>
            <div className="flex flex-col items-center">
                <p className="mb-4">1 Coin = $1.00</p>
                <input type="number" value={amount} onChange={e => setAmount(parseInt(e.target.value, 10))} className="w-full p-3 border-2 border-slate-200 rounded-lg text-center mb-4" />
                <button onClick={() => onBuy(amount)} className="w-full bg-teal-500 text-white font-bold py-3 rounded-lg">Buy {amount} Coins for ${amount.toFixed(2)}</button>
                <p className="text-xs text-slate-400 mt-2">(This is a simulation. No real money will be charged.)</p>
            </div>
        </ModalWrapper>
    );
};

const TransferCoinsModal = ({ onClose, onTransfer, myStore }) => {
    const [recipientEmail, setRecipientEmail] = useState('');
    const [amount, setAmount] = useState(0);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [isLoading, setIsLoading] = useState(false);

    const handleTransfer = async (e) => {
        e.preventDefault();
        setError('');
        setSuccess('');
        if (!recipientEmail || amount <= 0) {
            setError("Please enter a valid email and amount.");
            return;
        }
        if (myStore.coinBalance < amount) {
            setError("You don't have enough coins to complete this transfer.");
            return;
        }
        setIsLoading(true);
        try {
            await onTransfer(recipientEmail, amount);
            setSuccess(`Successfully transferred ${amount} coins to ${recipientEmail}!`);
            setTimeout(onClose, 2000);
        } catch (err) {
            setError(err.message);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <ModalWrapper onClose={onClose}>
            <h2 className="text-2xl font-bold mb-6">Transfer Coins</h2>
            <form onSubmit={handleTransfer} className="space-y-4">
                <div>
                    <label className="font-medium text-slate-600">Recipient's Email</label>
                    <input 
                        type="email" 
                        value={recipientEmail} 
                        onChange={e => setRecipientEmail(e.target.value)} 
                        className="w-full mt-1 p-3 border-2 border-slate-200 rounded-lg" 
                        placeholder="user@example.com"
                        required 
                    />
                </div>
                <div>
                    <label className="font-medium text-slate-600">Amount</label>
                    <input 
                        type="number" 
                        value={amount} 
                        onChange={e => setAmount(parseInt(e.target.value, 10) || 0)} 
                        className="w-full mt-1 p-3 border-2 border-slate-200 rounded-lg" 
                        min="1"
                        required 
                    />
                </div>
                {error && <p className="text-red-500 text-sm">{error}</p>}
                {success && <p className="text-green-600 text-sm">{success}</p>}
                <button type="submit" disabled={isLoading} className="w-full bg-sky-600 text-white font-bold py-3 rounded-lg disabled:bg-sky-300 flex justify-center items-center">
                    {isLoading ? <Loader2 className="animate-spin" /> : 'Transfer Coins'}
                </button>
            </form>
        </ModalWrapper>
    );
};

const DeleteAccountModal = ({ onClose, onDelete, authError, setAuthError }) => {
    const [password, setPassword] = useState('');
    const [isLoading, setIsLoading] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setAuthError(null);
        setIsLoading(true);
        try {
            await onDelete(password);
            // The user will be logged out automatically on success, so no need to call onClose.
        } catch (error) {
            setAuthError(error.message);
            setIsLoading(false);
        }
    };

    return (
        <ModalWrapper onClose={onClose}>
            <h2 className="text-2xl font-bold text-red-600 mb-4">Delete Account</h2>
            <p className="text-slate-600 mb-6">
                This is a permanent action. All your data, including your store, products, services, and gigs will be deleted forever. This cannot be undone.
            </p>
            <p className="font-semibold mb-4">To confirm, please enter your password.</p>
            <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                    <label className="font-medium text-slate-600">Password</label>
                    <input 
                        type="password" 
                        value={password} 
                        onChange={e => setPassword(e.target.value)} 
                        className="w-full mt-1 p-3 border-2 border-slate-200 rounded-lg" 
                        required 
                    />
                </div>
                {authError && <p className="text-red-500 text-sm">{authError}</p>}
                <button 
                    type="submit" 
                    disabled={isLoading} 
                    className="w-full bg-red-600 text-white font-bold py-3 rounded-lg disabled:bg-red-300 flex justify-center items-center"
                >
                    {isLoading ? <Loader2 className="animate-spin" /> : 'Permanently Delete My Account'}
                </button>
            </form>
        </ModalWrapper>
    );
};

const DocumentUploader = ({ label, onUpload, storage, folder, required, t }) => {
    const [uploading, setUploading] = useState(false);
    const [fileName, setFileName] = useState(null);
    const [error, setError] = useState(null);

    const handleFileChange = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        setUploading(true);
        setError(null);

        try {
            const storageRef = ref(storage, `${folder}/${Date.now()}_${file.name}`);
            const snapshot = await uploadBytes(storageRef, file);
            const downloadURL = await getDownloadURL(snapshot.ref);
            onUpload(downloadURL);
            setFileName(file.name);
        } catch (err) {
            console.error("Upload failed:", err);
            setError(t('uploader.error'));
            onUpload(null);
        } finally {
            setUploading(false);
        }
    };

    return (
        <div className="w-full">
            <label className={`flex flex-col items-center justify-center gap-2 border-2 border-dashed rounded-lg p-4 text-center text-sm text-slate-500 cursor-pointer hover:bg-slate-50 ${fileName ? 'border-green-500 bg-green-50' : ''} ${error ? 'border-red-500' : ''}`}>
                <UploadCloud size={20} className={fileName ? 'text-green-600' : ''}/>
                <span>{label}{required && <span className="text-red-500">*</span>}</span>
                {uploading ? (
                    <div className="flex items-center gap-2 mt-1 text-xs">
                        <Loader2 size={14} className="animate-spin" />
                        <span>{t('uploader.uploading')}</span>
                    </div>
                ) : fileName ? (
                    <span className="font-semibold text-green-700 text-xs mt-1 truncate max-w-full">{fileName}</span>
                ) : (
                     <span className="text-xs text-slate-400 mt-1">{t('uploader.acceptedTypes')}</span>
                )}
                 <input type="file" accept="image/*,application/pdf" className="hidden" onChange={handleFileChange} disabled={uploading}/>
            </label>
            {error && <p className="text-red-500 text-xs mt-1">{error}</p>}
        </div>
    );
};

const ForgotPasswordModal = ({ onClose, onSendResetEmail, authError, setAuthError, t }) => {
    const [email, setEmail] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');

    const handleSubmit = async (e) => {
        e.preventDefault();
        setAuthError(null);
        setSuccessMessage('');
        if (!email) {
            setAuthError(t('forgot.error.emailRequired'));
            return;
        }
        setIsLoading(true);
        try {
            await onSendResetEmail(email);
            setSuccessMessage(t('forgot.success'));
            setTimeout(onClose, 3000);
        } catch (error) {
            setAuthError(error.message);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <ModalWrapper onClose={onClose} isFullScreen={false}>
            <h2 className="text-2xl font-bold mb-4">{t('forgot.title')}</h2>
            <p className="text-slate-600 mb-6">{t('forgot.desc')}</p>
            {successMessage ? (
                <p className="text-green-600 bg-green-50 p-3 rounded-lg text-sm mb-4">{successMessage}</p>
            ) : (
                <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                        <label className="font-medium text-slate-600">{t('auth.email')}</label>
                        <input type="email" value={email} onChange={e => setEmail(e.target.value)} className="w-full mt-1 p-3 border-2 border-slate-200 rounded-lg" required />
                    </div>
                    {authError && <p className="text-red-500 text-sm">{authError}</p>}
                    <button type="submit" disabled={isLoading} className="w-full bg-sky-600 text-white font-bold py-3 rounded-lg disabled:bg-sky-300 flex justify-center items-center">
                        {isLoading ? <Loader2 className="animate-spin" /> : t('forgot.sendLink')}
                    </button>
                </form>
            )}
        </ModalWrapper>
    );
};


// --- Page Components ---
const AuthPage = ({ auth, db, setAuthError, authError, storage, onForgotPasswordClick, userLocation, allStores, language, setAndSaveLanguage, t }) => {
    const [localDb, setLocalDb] = useState(null); // Local DB instance for pre-auth reads
    const [isLogin, setIsLogin] = useState(true);
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [shopName, setShopName] = useState('');
    const [markets, setMarkets] = useState([]);
    const [selectedMarket, setSelectedMarket] = useState('');
    const [newMarketName, setNewMarketName] = useState('');
    const [phone, setPhone] = useState('');
    const [isWhatsapp, setIsWhatsapp] = useState(true);
    const [isDeliveryDriver, setIsDeliveryDriver] = useState(false);
    const [gender, setGender] = useState('male');
    const [dob, setDob] = useState('');
    const [country, setCountry] = useState('MA');
    const [isLoading, setIsLoading] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [idDocumentUrl, setIdDocumentUrl] = useState(null); // Default to null
    const [driverLicenseUrl, setDriverLicenseUrl] = useState(null); // Default to null
    const [profileImageUrl, setProfileImageUrl] = useState(null); // NEW: Optional Profile Image
    const selectedCountry = useMemo(() => countries.find(c => c.code === country), [country]);
    const [location, setLocation] = useState(null); // NEW: Mandatory Location
    const [isMapOpen, setIsMapOpen] = useState(false); // NEW: Map Modal

    useEffect(() => {
        // Initialize a lightweight, local DB instance ONLY for fetching public lists (markets)
        // This runs independently of the main app's authentication state.
        if (!localDb) {
            try {
                const app = initializeApp(firebaseConfig, `auth-reader-${Date.now()}`); // Use a unique name
                setLocalDb(getFirestore(app));
            } catch (e) {
                console.error("AuthPage DB init failed:", e);
            }
        }
        if (!isLogin && (localDb || allStores) && country) {
            const q = query(collection(localDb, `artifacts/${appId}/public/data/stores`), where("countryCode", "==", country));
            const unsubscribe = onSnapshot(q, (snapshot) => {
                const marketsFromDb = snapshot.docs.map(doc => doc.data().marketName).filter(Boolean);
                const uniqueMarkets = [...new Set(marketsFromDb)];
                setMarkets(uniqueMarkets);
                if (uniqueMarkets.length > 0) {
                    setSelectedMarket(uniqueMarkets[0]);
                } else {
                    setSelectedMarket('Other');
                }
            });
            return () => unsubscribe();
        }
    }, [country, isLogin, localDb]);

    // NEW: Auto-fetch user location for registration
    useEffect(() => {
        if (userLocation && !isLogin && !location) {
            const setDefaultLocation = async () => {
                const address = await getAddressFromCoords(userLocation.latitude, userLocation.longitude);
                setLocation({ coords: userLocation, address: address });
            };
            setDefaultLocation();
        }
    }, [userLocation, isLogin, location]);

    // NEW: Function to map Firebase error codes to friendly messages
    const mapAuthError = (errorCode) => {
        switch (errorCode) {
            case 'auth/user-not-found':
            case 'auth/wrong-password':
            case 'auth/invalid-credential':
                return t('auth.error.invalidCredential');
                return t('auth.error.emailInUse');
            case 'auth/weak-password':
                return t('auth.error.weakPassword');
            default:
                return t('auth.error.default');
        }
    };

    const clearForm = () => {
        setEmail('');
        setPassword('');
        setConfirmPassword('');
    }

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        setAuthError(null);
        setSuccessMessage('');

        const finalMarketName = selectedMarket === 'Other' ? newMarketName : selectedMarket;
        
        if (!isLogin && password !== confirmPassword) {
            setAuthError(t('auth.error.passwordsNoMatch'));
            setIsLoading(false);
            return;
        }

        if (!isLogin && !finalMarketName.trim()) {
            setAuthError(t('auth.error.marketRequired'));
            setIsLoading(false);
            return;
        }

        // NEW: Location Validation
        if (!isLogin && (!location || !location.coords)) {
            setAuthError("Store Location is mandatory. Please wait for it to fetch or click to set it on the map.");
            setIsLoading(false);
            return;
        }

        try {
            if (isLogin) {
                const auth = getAuth(); // Get the default auth instance
                await signInWithEmailAndPassword(auth, email, password);
            } else {
                 /* // Validation commented out for easier registration
                 if (!idDocumentUrl) {
                    setAuthError("Please upload your ID document.");
                    setIsLoading(false);
                    return;
                }
                if (isDeliveryDriver && !driverLicenseUrl) {
                    setAuthError("Please upload your driver's license to sign up for deliveries.");
                    setIsLoading(false);
                    return;
                }
                */
                const userCredential = await createUserWithEmailAndPassword(auth, email, password);
                const db = getFirestore(); // Get the default (main app) firestore instance for writing
                const user = userCredential.user;
                
                await setDoc(doc(db, `artifacts/${appId}/public/data/stores`, user.uid), {
                    profileImageUrl: profileImageUrl || null, // Save optional profile image
                    name: shopName,
                    currencySymbol: selectedCountry.currencySymbol, // <-- FIX: Save currency symbol
                    currencyCode: selectedCountry.currency, // <-- FIX: Save currency code
                    location: location, // NEW: Save Location
                    firstName,
                    lastName,
                    marketName: finalMarketName,
                    ownerId: user.uid,
                    contactPhone: `${selectedCountry.phoneCode}${phone}`,
                    isWhatsapp,
                    isDeliveryDriver: isDeliveryDriver,
                    driverLicenseUrl: driverLicenseUrl || null, // Set to null if not provided
                    idDocumentUrl: idDocumentUrl || null, // Set to null if not provided
                    contactEmail: user.email,
                    gender,
                    dob,
                    country: selectedCountry.name,
                    countryCode: selectedCountry.code,
                    sales: 0,
                    coinBalance: 0,
                    createdAt: serverTimestamp(),
                    status: 'approved' // New user status
                });

                await sendEmailVerification(user);
                setSuccessMessage("Registration successful! Please check your email to verify your account.");
                clearForm();
                setIsLogin(true);
            }
        } catch (error) {
            setAuthError(mapAuthError(error.code));
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-slate-50 flex flex-col justify-center items-center p-4">
            <div className="w-full max-w-sm">
                 <div className="flex items-center justify-center gap-2 mb-8">
                    <img 
                        src="/gignile.png" 
                        alt="GigNile Logo" 
                        className="h-12 w-auto" 
                        onError={(e) => { e.target.onerror = null; e.target.src='/favicon.ico'; }}
                    />
                 </div>
                <div className="bg-white p-8 rounded-2xl shadow-xl border relative">
                    {/* NEW: Language Selector */}
                    <div className="absolute top-4 right-4">
                        <div className="relative">
                            <Languages size={16} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-500 pointer-events-none"/>
                            <select 
                                value={language} 
                                onChange={(e) => setAndSaveLanguage(e.target.value)} 
                                className="p-1 pl-7 pr-3 rounded-full bg-slate-100 hover:bg-slate-200 font-bold uppercase text-xs appearance-none cursor-pointer focus:outline-none focus:ring-2 focus:ring-sky-500"
                            >
                                <option value="en">EN</option>
                                <option value="fr">FR</option>
                                <option value="es">ES</option>
                                <option value="ar">AR</option>
                            </select>
                        </div>
                    </div>
                    <h2 className="text-2xl font-bold text-center mb-2">{isLogin ? t('welcomeBack') : t('createAccount')}</h2>
                    <p className="text-center text-slate-500 mb-6">{isLogin ? t('auth.signInContinue') : t('auth.signUpContinue')}</p>
                    {successMessage && <p className="text-green-600 bg-green-50 p-3 rounded-lg text-sm mb-4">{successMessage}</p>}
                    <form onSubmit={handleSubmit} className="space-y-4">
                        {!isLogin && (
                             <>
                                <div className="flex gap-4">
                                    <div className="flex-1"><label className="font-medium text-slate-600">{t('auth.firstName')}</label><input type="text" value={firstName} onChange={e => setFirstName(e.target.value)} className="w-full mt-1 p-3 border-2 border-slate-200 rounded-lg" required /></div>
                                    <div className="flex-1"><label className="font-medium text-slate-600">{t('auth.lastName')}</label><input type="text" value={lastName} onChange={e => setLastName(e.target.value)} className="w-full mt-1 p-3 border-2 border-slate-200 rounded-lg" required /></div>
                                </div>
                                <div><label className="font-medium text-slate-600">{t('auth.profileName')}</label><input type="text" value={shopName} onChange={e => setShopName(e.target.value)} className="w-full mt-1 p-3 border-2 border-slate-200 rounded-lg" placeholder={t('auth.profileNamePlaceholder')} required /></div>
                                
                                <div><label className="font-medium text-slate-600">{t('auth.country')}</label>
                                    <select value={country} onChange={e => setCountry(e.target.value)} className="w-full mt-1 p-3 border-2 border-slate-200 rounded-lg bg-white">
                                        {countries.map(c => <option key={c.code} value={c.code}>{c.name}</option>)}
                                    </select>
                                </div>
                                <div>
                                    <label className="font-medium text-slate-600">{t('auth.city')}</label>
                                    <select value={selectedMarket} onChange={e => setSelectedMarket(e.target.value)} className="w-full mt-1 p-3 border-2 border-slate-200 rounded-lg bg-white appearance-none">
                                        <option value="" disabled>Select a city</option>
                                        {markets.map(m => <option key={m} value={m}>{getTranslatedCity(m, t)}</option>)}
                                        <option value="Other">Other (Please specify)</option>
                                    </select>
                                </div>

                                {selectedMarket === 'Other' && (
                                    <div>
                                        <label className="font-medium text-slate-600">New City</label>
                                        <input type="text" value={newMarketName} onChange={e => setNewMarketName(e.target.value)} placeholder="Enter your city name" className="w-full mt-1 p-3 border-2 border-slate-200 rounded-lg" required />
                                    </div>
                                )}

                                <div><label className="font-medium">{t('auth.location')}</label>
                                {
                                    !location ? (
                                        <div className="flex items-center gap-2 mt-1 text-slate-500">
                                            <Loader2 size={18} className="animate-spin" />
                                            <span>{t('auth.locationFetching')}</span>
                                        </div>
                                    ) :
                                    <div className="text-sm p-3 bg-slate-200 text-slate-800 rounded-lg mt-1 border-2 border-green-500">
                                        <p className="font-bold truncate"><MapPin size={14} className="inline mr-1"/>{location.address}</p>
                                    </div>
                                }
                                <p className="text-xs text-slate-500 mt-1">{t('auth.locationNote')}</p>
                                </div>

                                <div>
                                    <label className="font-medium text-slate-600">{t('auth.phone')}</label>
                                    <div className="flex mt-1">
                                        <span className="inline-flex items-center px-3 text-sm text-slate-600 bg-slate-200 border border-r-0 border-slate-200 rounded-l-lg">
                                            {selectedCountry.phoneCode}
                                        </span>
                                        <input type="tel" value={phone} onChange={e => setPhone(e.target.value)} className="w-full p-3 border-2 border-slate-200 rounded-r-lg" required />
                                    </div>
                                    <div className="flex items-center mt-2"><input type="checkbox" checked={isWhatsapp} onChange={e => setIsWhatsapp(e.target.checked)} id="isWhatsapp" className="mr-2"/><label htmlFor="isWhatsapp" className="text-sm text-slate-600">{t('auth.whatsapp')}</label></div>
                                </div>
                                <div className="flex gap-4"><div className="flex-1"><label className="font-medium text-slate-600">{t('auth.gender')}</label><select value={gender} onChange={e => setGender(e.target.value)} className="w-full mt-1 p-3 border-2 border-slate-200 rounded-lg bg-white"><option value="male">{t('auth.gender.male')}</option><option value="female">{t('auth.gender.female')}</option><option value="other">{t('auth.gender.other')}</option></select></div><div className="flex-1"><label className="font-medium text-slate-600">{t('auth.dob')}</label><input type="date" value={dob} onChange={e => setDob(e.target.value)} className="w-full mt-1 p-3 border-2 border-slate-200 rounded-lg" required /></div></div>
                                
                                { /* <DocumentUploader label="Residence Permit or Passport" onUpload={setIdDocumentUrl} storage={storage} folder="documents" required={true}/> */ }
                                { /* <div className="p-4 border border-slate-200 rounded-lg bg-slate-50 flex flex-col items-center space-y-2">
                                    <label className="font-medium text-slate-600">Profile Picture (Optional)</label>
                                    {profileImageUrl && <img src={profileImageUrl} alt="Profile" className="w-20 h-20 rounded-full object-cover border-2 border-sky-500" />}
                                    <ImageUploader storage={storage} folder="profiles" onUpload={setProfileImageUrl} />
                                </div> */}

                                <div className="flex items-center gap-2 text-sm text-slate-600"><input type="checkbox" checked={isDeliveryDriver} onChange={e => setIsDeliveryDriver(e.target.checked)} id="isDeliveryDriver" className="mr-2 h-4 w-4 rounded border-gray-300 text-sky-600 focus:ring-sky-500"/><label htmlFor="isDeliveryDriver">{t('auth.signUpDelivery')}</label></div>
                                
                                { /* isDeliveryDriver && <DocumentUploader label="Driver's License" onUpload={setDriverLicenseUrl} storage={storage} folder="documents" required={true}/> */ }
                            </>
                        )}
                        <div><label className="font-medium text-slate-600">{t('auth.email')}</label><input type="email" value={email} onChange={e => setEmail(e.target.value)} className="w-full mt-1 p-3 border-2 border-slate-200 rounded-lg" required /></div>
                        
                        <div className="relative">
                            <div className="flex justify-between items-center mb-1">
                                <label className="font-medium text-slate-600">{t('auth.password')}</label>
                                {isLogin && <button type="button" onClick={onForgotPasswordClick} className="text-sm font-semibold text-sky-600 hover:underline">{t('auth.forgot')}</button>}
                            </div>
                           <input type={showPassword ? 'text':'password'} value={password} onChange={e => setPassword(e.target.value)} className="w-full p-3 border-2 border-slate-200 rounded-lg" required />
                           <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-9 text-slate-400">{showPassword ? <EyeOff size={20}/> : <Eye size={20}/>}</button>
                        </div>

                        {!isLogin && <div className="relative">
                            <label className="font-medium text-slate-600">{t('auth.confirmPassword')}</label>
                            <input type={showPassword ? 'text':'password'} value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} className="w-full mt-1 p-3 border-2 border-slate-200 rounded-lg" required />
                           <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-10 text-slate-400">{showPassword ? <EyeOff size={20}/> : <Eye size={20}/>}</button>
                        </div>}

                        {authError && <p className="text-red-500 text-sm font-semibold p-3 bg-red-50 rounded-lg">{authError}</p>}
                        <button type="submit" disabled={isLoading} className="w-full bg-sky-600 text-white font-bold py-3 rounded-lg disabled:bg-sky-300 flex justify-center items-center">
                            {isLoading ? <Loader2 className="animate-spin" /> : (isLogin ? t('auth.login') : t('auth.register'))}
                        </button>
                    </form>
                    <p className="text-center text-sm text-slate-500 mt-6">
                        <button onClick={() => { setIsLogin(!isLogin); setAuthError(null); setSuccessMessage(''); }} className="font-bold text-sky-600 ml-1">
                            {isLogin ? t('auth.noAccount') + " " + t('auth.register') : t('auth.haveAccount') + " " + t('auth.login')}
                        </button>
                    </p>
                </div>
            </div>
            {isMapOpen && <LocationPickerModal pinLocation={location?.coords} userLocation={userLocation} onClose={() => setIsMapOpen(false)} onLocationSelect={setLocation} />}
            {/* Support Button - Fixed Bottom Left */}
            <a 
                href="https://www.gignile.com/contactus" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="fixed bottom-6 left-6 z-50 flex items-center gap-2 bg-white/90 backdrop-blur-sm px-4 py-2 rounded-full shadow-lg border border-slate-200 text-sm font-bold text-slate-600 hover:text-sky-600 hover:shadow-xl hover:-translate-y-0.5 transition-all"
                aria-label="Contact Support"
            >
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M3 11h3a2 2 0 0 1 2 2v3a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-5Zm0 0a9 9 0 1 1 18 0m0 0v5a2 2 0 0 1-2 2h-1a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h3"/>
                    <path d="M21 16v2a4 4 0 0 1-4 4h-5"/>
                </svg>
                <span>Support</span>
            </a>
        </div>
    );
};
const MyStorePage = ({ store, products, onManageStoreClick, onManageProduct, onDeleteProduct, allStores, t }) => { 
    const [tab, setTab] = useState('goods'); // NEW: Initialize state for tabs
    const [selectedCategory, setSelectedCategory] = useState('All'); // NEW: State for category filter
    
// NEW: Filter products based on type
     const activeProducts = products.filter(p => !p.isDeleted);
     const activeGoods = activeProducts.filter(p => p.type === 'goods');
     const activeServices = activeProducts.filter(p => p.type === 'service');

     // NEW: Get all unique categories for the current tab
    const availableCategories = useMemo(() => {
        const itemsToFilter = (tab === 'goods') ? activeGoods : activeServices;
        const cats = itemsToFilter.flatMap(p => p.categories || []);
        return ['All', ...new Set(cats)].sort();
    }, [tab, activeGoods, activeServices]);

    // NEW: Apply the category filter to the lists+    const filteredGoods = useMemo(() => (selectedCategory === 'All' ? activeGoods : activeGoods.filter(p => p.categories?.includes(selectedCategory))), [activeGoods, selectedCategory]);
    const filteredGoods = useMemo(() => (selectedCategory === 'All' ? activeGoods : activeGoods.filter(p => p.categories?.includes(selectedCategory))), [activeGoods, selectedCategory]);
    const filteredServices = useMemo(() => (selectedCategory === 'All' ? activeServices : activeServices.filter(p => p.categories?.includes(selectedCategory))), [activeServices, selectedCategory]);
    
    // NEW: Reset category when tab changes
    useEffect(() => {
        setSelectedCategory('All');
    }, [tab]);
    const sellerTier = getSellerTier(store?.sales || 0, store?.averageRating || 0, t); if (!store) { return ( <div className="max-w-md mx-auto text-center bg-white p-8 rounded-xl shadow-md border"><h2 className="text-2xl font-bold mb-4">{t('store.createTitle')}</h2><p className="text-slate-500 mb-6">{t('store.setupDesc')}</p><button onClick={() => onManageStoreClick(null)} className="bg-sky-600 text-white font-bold py-2 px-6 rounded-lg">{t('store.getStarted')}</button></div> ); } 
     return ( 
        <div>
            {/* HEADER: Made responsive (stacks on mobile, row on sm+) */}
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start mb-6">
                {/* Text content */}
                <div>
                    <div className="flex items-center gap-3">
                        <h2 className="text-2xl md:text-3xl font-bold text-slate-700">{store.name}</h2>
                        <span className={`flex items-center gap-1 text-sm font-bold px-3 py-1 rounded-full ${sellerTier.color} bg-opacity-20`}>{sellerTier.icon}{sellerTier.name}</span>
                    </div>
                    <p className="text-slate-500">{store.description || "No description."}</p>
                </div>
                {/* Button container (full-width on mobile, auto on sm+) */}
                <div className="flex gap-2 mt-4 sm:mt-0 w-full sm:w-auto">
                    <button onClick={() => onManageStoreClick(store)} className="flex-1 sm:flex-none flex items-center justify-center gap-2 bg-slate-200 text-slate-800 font-bold py-2 px-4 rounded-lg shadow-sm hover:bg-slate-300">
                        <Edit size={16} /> {t('store.manage')}
                    </button>
                    <button onClick={() => onManageProduct(null)} className="flex-1 sm:flex-none flex items-center justify-center gap-2 bg-sky-600 text-white font-bold py-2 px-4 rounded-lg shadow-md hover:bg-sky-700">
                        <PlusCircle size={16} /> {t('store.addItem')}
                    </button>
                </div>
            </div>
            
            {store.images && store.images.length > 0 && <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">{store.images.filter(img => img).map((img, i) => <img key={i} src={img} alt={`Store image ${i+1}`} className="aspect-video w-full h-auto object-cover rounded-lg shadow-md" onError={(e) => { e.target.onerror = null; e.target.src='https://placehold.co/600x400/EEE/31343C?text=Image+Error'; }}/>)}</div>}
            
            {/* --- NEW Tab Navigation for Goods/Services --- */}
            <div className="flex border-b mb-4">
                <button 
                    onClick={() => setTab('goods')}
                    className={`py-2 px-4 font-semibold ${tab === 'goods' ? 'border-b-2 border-sky-500 text-sky-600' : 'text-slate-500'}`}
                >
                    {t('store.goodsTab')} ({activeGoods.length})
                </button>
                <button 
                    onClick={() => setTab('services')}
                    className={`py-2 px-4 font-semibold ${tab === 'services' ? 'border-b-2 border-sky-500 text-sky-600' : 'text-slate-500'}`}
                >
                    {t('store.servicesTab')} ({activeServices.length})
                </button>
            </div>

            {/* --- NEW: Category Filter Dropdown --- */}
            <div className="mb-4">
                <label className="text-sm font-semibold text-slate-500 mr-2">{t('store.filterCategory')}</label>
                <select 
                    value={selectedCategory} 
                    onChange={(e) => setSelectedCategory(e.target.value)}
                    className="w-full sm:w-auto appearance-none bg-white py-2 pl-4 pr-10 border-2 border-slate-200 rounded-xl"
                >
                    {availableCategories.map(cat => (
                        <option key={cat} value={cat}>{cat}</option>
                    ))}
                </select>
            </div>

            {/* --- Conditional List Rendering --- */}
            {tab === 'goods' && (
                filteredGoods.length > 0 ? <div className="bg-white rounded-xl shadow-sm border divide-y">{filteredGoods.map(p => ( <div key={p.id} className="p-4 flex justify-between items-center"><div className="flex items-center gap-4"><img src={p.images?.[0] || 'https://placehold.co/100x100/EEE/31343C?text=No+Image'} alt={p.name} className="w-16 h-16 object-cover rounded-md" /><div><p className="font-bold">{p.name}</p><p className="text-sm text-slate-500">Goods</p></div></div><div className="flex items-center gap-4"><span className="font-semibold text-lg">{p.currencySymbol || '$'}{(p.price || 0).toFixed(2)}</span><button onClick={() => onManageProduct(p)} className="text-slate-500 hover:text-sky-600"><Edit size={18}/></button><button onClick={() => onDeleteProduct(p.id)} className="text-slate-500 hover:text-red-600"><Trash2 size={18}/></button></div></div> ))}</div> 
                : <div className="text-center bg-white p-8 rounded-xl shadow-sm border"><p className="text-slate-500">{t('store.noGoods')}</p></div>
            )}

            {tab === 'services' && (
                filteredServices.length > 0 ? <div className="bg-white rounded-xl shadow-sm border divide-y">{filteredServices.map(p => ( <div key={p.id} className="p-4 flex justify-between items-center"><div className="flex items-center gap-4"><img src={p.images?.[0] || 'https://placehold.co/100x100/EEE/31343C?text=No+Image'} alt={p.name} className="w-16 h-16 object-cover rounded-md" /><div><p className="font-bold">{p.name}</p><p className="text-sm text-slate-500">Service</p></div></div><div className="flex items-center gap-4"><span className="font-semibold text-lg">{p.currencySymbol || '$'}{(p.price || 0).toFixed(2)}</span><button onClick={() => onManageProduct(p)} className="text-slate-500 hover:text-sky-600"><Edit size={18}/></button><button onClick={() => onDeleteProduct(p.id)} className="text-slate-500 hover:text-red-600"><Trash2 size={18}/></button></div></div> ))}</div> 
                : <div className="text-center bg-white p-8 rounded-xl shadow-sm border"><p className="text-slate-500">{t('store.noServices')}</p></div>
            )}
        </div> 
     ); 
 };
const ProfilePage = ({myOrders, user, myStore, gigs, myProducts, onManageStoreClick, onChangePasswordClick, handleRate, onBuyCoinsClick, onTransferCoinsClick, onMarkOrderComplete, onDeleteAccountClick, isAdmin, setPage, onLogout, language, setAndSaveLanguage, t}) => {
    const handleLogout = () => { signOut(getAuth()); }
    const averageRating = myStore && myStore.ratingCount > 0 ? myStore.totalRating / myStore.ratingCount : 0;
    const sellerTier = getSellerTier(myStore?.sales || 0, averageRating, t); // <-- FIX: Pass t function

    // NEW: Calculate Dashboard Stats
    const dashboardStats = useMemo(() => {
        const gigsPosted = gigs.filter(g => g.creatorId === user.uid);
        const gigsCompletedAsWorker = gigs.filter(g => g.workerId === user.uid && g.status === 'completed');
        
        // Calculate earnings from completed gigs (assuming 'pay' is the final amount)
        const gigEarnings = gigsCompletedAsWorker.reduce((acc, gig) => acc + (gig.pay || 0), 0);

        // Calculate total spent on orders
        const totalSpentOnOrders = myOrders.filter(o => o.status === 'completed').reduce((acc, o) => acc + (o.price || 0), 0);

        // Filter Events
        const eventsHosted = myProducts.filter(p => p.type === 'event' && !p.isDeleted);

        return {
            gigsPostedCount: gigsPosted.length,
            gigsCompletedCount: gigsCompletedAsWorker.length,
            productsPostedCount: myProducts.filter(p => p.type !== 'event' && !p.isDeleted).length, // Exclude events from general products count
            eventsHostedCount: eventsHosted.length,
            ordersPlacedCount: myOrders.length,
            totalSpent: totalSpentOnOrders,
            totalGigEarnings: gigEarnings,
            // Note: Store sales (myStore.sales) are separate from gig earnings.
        };
    }, [myOrders, user, gigs, myProducts]);

    const currencySymbol = myStore?.currencySymbol || '$';

    // NEW: Stat Card Component
    const StatCard = ({ label, value, icon, color }) => (
        <div className={`bg-white p-4 rounded-xl shadow-md flex items-center gap-4 border-l-4 ${color}`}>
            <div className="p-3 rounded-full bg-slate-100">
                {icon}
            </div>
            <div>
                <p className="text-2xl font-extrabold text-slate-700">{value}</p>
                <p className="text-sm font-semibold text-slate-500">{label}</p>
            </div>
        </div>
    );

    const ProfileAction = ({ icon, label, onClick, colorClass = 'text-slate-600', isDestructive = false }) => (
        <button onClick={onClick} className={`w-full flex items-center gap-4 p-4 rounded-lg hover:bg-slate-100 transition-colors ${isDestructive ? 'text-red-600 hover:bg-red-50' : colorClass}`}>
            {icon}
            <span className="font-semibold">{label}</span>
            {!isDestructive && <ChevronRight size={20} className="ml-auto text-slate-400" />}
        </button>
    );

    return (
        <div className="space-y-8">
            {/* Profile Header */}
            <div className="relative bg-gradient-to-br from-sky-500 to-purple-600 p-6 rounded-2xl shadow-lg text-white text-center">
                <div className="absolute top-0 left-0 w-full h-full bg-black/10 rounded-2xl"></div>
                <div className="relative z-10 cursor-pointer" onClick={() => onManageStoreClick(myStore)}>
                    <div className="relative inline-block">
                        <img 
                            src={myStore?.profileImageUrl || 'https://placehold.co/80x80/EEE/31343C?text=👤'} 
                            alt="Profile" 
                            className="mx-auto w-20 h-20 rounded-full object-cover border-4 border-white/50" 
                            onError={(e) => { e.target.onerror = null; e.target.src='https://placehold.co/80x80/EEE/31343C?text=👤'; }} />
                        <Edit size={16} className="absolute bottom-0 right-0 bg-white text-sky-600 rounded-full p-1 border border-sky-600"/>
                    </div>
                    <h2 className="text-3xl font-bold mt-3">{myStore?.firstName || 'User'} {myStore?.lastName}</h2>
                    <p className="opacity-80">{user.email}</p>
                    <div className={`mt-3 inline-flex items-center gap-2 text-sm font-bold px-4 py-1.5 rounded-full bg-white/20`}>
                        {sellerTier.icon} {sellerTier.name} {t('profile.seller')}
                    </div>
                </div>
            </div>

            {/* NEW: User Dashboard */}
            <div className="bg-white p-6 rounded-2xl shadow-md border">
                <h3 className="text-xl font-bold mb-4 text-slate-700">{t('profile.dashboardTitle')}</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <StatCard label={t('profile.gigsPosted')} value={dashboardStats.gigsPostedCount} icon={<Briefcase size={24} className="text-purple-500"/>} color="border-purple-500" />
                    <StatCard label={t('profile.gigsCompleted')} value={dashboardStats.gigsCompletedCount} icon={<CheckCircle size={24} className="text-green-500"/>} color="border-green-500" />
                    <StatCard label={t('profile.productsPosted')} value={dashboardStats.productsPostedCount} icon={<Package size={24} className="text-sky-500"/>} color="border-sky-500" />
                    <StatCard label={t('profile.eventsHosted')} value={dashboardStats.eventsHostedCount} icon={<Calendar size={24} className="text-rose-500"/>} color="border-rose-500" />
                    <StatCard label={t('profile.ordersPlaced')} value={dashboardStats.ordersPlacedCount} icon={<ShoppingCart size={24} className="text-teal-500"/>} color="border-teal-500" />
                    <StatCard label={t('profile.totalSpent')} value={`${currencySymbol}${dashboardStats.totalSpent.toFixed(2)}`} icon={<CreditCard size={24} className="text-red-500"/>} color="border-red-500" />
                    <StatCard label={t('profile.totalGigEarnings')} value={`${currencySymbol}${dashboardStats.totalGigEarnings.toFixed(2)}`} icon={<DollarSign size={24} className="text-amber-500"/>} color="border-amber-500" />
                </div>
            </div>

            {/* Coin Wallet
            <div className="bg-white p-6 rounded-2xl shadow-md border">
                 <h3 className="text-xl font-bold mb-4 text-slate-700">My Wallet</h3>
                <div className="flex items-center justify-between bg-amber-50 p-4 rounded-lg">
                    <div>
                        <p className="text-sm text-amber-800">Available Balance</p>
                        <p className="text-3xl font-extrabold text-amber-600">{myStore?.coinBalance || 0}</p>
                    </div>
                    <Coins size={40} className="text-amber-400" />
                </div>
                <div className="grid grid-cols-2 gap-4 mt-4">
                    <button onClick={onBuyCoinsClick} className="w-full bg-teal-500 text-white font-bold py-3 rounded-lg shadow-md hover:bg-teal-600 transition-colors flex items-center justify-center gap-2">
                        <PlusCircle size={18}/> Buy Coins
                    </button>
                    <button onClick={onTransferCoinsClick} className="w-full bg-sky-500 text-white font-bold py-3 rounded-lg shadow-md hover:bg-sky-600 transition-colors flex items-center justify-center gap-2">
                       <Send size={18}/> Transfer
                    </button>
                </div>
            </div> */}
            
            {/* Actions */}
            <div className="bg-white p-2 rounded-2xl shadow-md border">
                {/* NEW: Language Selector Moved to Profile */}
                <div className="w-full flex items-center gap-4 p-4 rounded-lg">
                    <Languages size={20} className="text-slate-600"/>
                    <span className="font-semibold text-slate-600">{t('language')}</span>
                    <select 
                        value={language} 
                        onChange={(e) => setAndSaveLanguage(e.target.value)} 
                        className="ml-auto p-1.5 rounded-full bg-slate-100 font-bold uppercase text-xs appearance-none cursor-pointer focus:outline-none focus:ring-2 focus:ring-sky-500"
                    >
                        <option value="en">EN</option>
                        <option value="fr">FR</option>
                        <option value="es">ES</option>
                        <option value="ar">AR</option>
                    </select>
                </div>
                <ProfileAction icon={<ShoppingCart size={20} className="text-teal-500"/>} label={t('profile.myOrders')} onClick={() => setPage('my-orders')} />
                <ProfileAction icon={<Ticket size={20} className="text-rose-500"/>} label={t('profile.myEventsTickets')} onClick={() => setPage('my-events')} />
                <ProfileAction icon={<User size={20} className="text-sky-500"/>} label={t('profile.editProfile')} onClick={() => onManageStoreClick(myStore)} />
                <ProfileAction icon={<Shield size={20} className="text-slate-500"/>} label={t('profile.changePassword')} onClick={onChangePasswordClick} />
                {isAdmin && <ProfileAction icon={<ShieldCheck size={20} className="text-purple-500"/>} label={t('profile.adminPanel')} onClick={() => setPage('admin')} />}
                <div className="border-t my-2"></div>
                <ProfileAction icon={<LogOut size={20} />} label={t('profile.logout')} onClick={onLogout} isDestructive />
                <ProfileAction icon={<Trash2 size={20} />} label={t('profile.deleteAccount')} onClick={onDeleteAccountClick} isDestructive />
            </div>
        </div>
    );
};
const MyGigsPage = ({myGigs, onGigClick, userLocation, allStores, user, onRateWorker, onRateCreator, onDeleteGig, onReportUser, t}) => {
    const gigFilter = useMemo(() => {
        if (!user) return { active: [], archived: [] };
        const userGigs = myGigs.filter(g => g.creatorId === user.uid || g.workerId === user.uid);

        const activeGigs = userGigs.filter(g => {
            if (g.status === 'open' || g.status === 'in-progress') return true;
            if (g.status === 'completed') {
                if (g.creatorId === user.uid && !g.workerRated) return true; 
                if (g.workerId === user.uid && !g.creatorRated) return true; 
            }
            return false;
        });

        const archivedGigs = userGigs.filter(g => {
            if (g.status === 'closed') return true;
            if (g.status === 'completed') {
                if (g.creatorId === user.uid && g.workerRated) return true;
                if (g.workerId === user.uid && g.creatorRated) return true;
            }
            return false;
        });

        return { active: activeGigs, archived: archivedGigs };
    }, [myGigs, user]);

    return (
        <GigActivity gigs={gigFilter} onGigClick={onGigClick} userLocation={userLocation} allStores={allStores} user={user} onRateWorker={onRateWorker} onRateCreator={onRateCreator} onDeleteGig={onDeleteGig} onReportUser={onReportUser} t={t}/>
    );
};
const ChatsPage = ({ chats, user, onStartChat, activeChatPartner, setActiveChatPartner, db, allStores, myStore, language, notifications, t }) => {
    // FIX: Check which chats have unread messages
    const chatsWithUnread = useMemo(() => {
        return chats.map(chat => {
            const partnerId = chat.participants.find(p => p !== user.uid);
            const hasUnread = notifications.some(n => !n.read && n.chatPartnerId === partnerId);
            return { ...chat, hasUnread };
        });
    }, [chats, notifications, user.uid]);

    if (activeChatPartner) {
        return <ChatWindow partnerId={activeChatPartner} user={user} db={db} onBack={() => setActiveChatPartner(null)} allStores={allStores} myStore={myStore} language={language} t={t}/>;
    }
    return (
        <ChatActivity chats={chatsWithUnread} user={user} onStartChat={onStartChat} db={db} allStores={allStores} myStore={myStore} />
    );
};
const GigActivity = ({gigs, onGigClick, userLocation, allStores, user, onRateWorker, onRateCreator, onDeleteGig, onReportUser, t}) => {
    const [filter, setFilter] = useState('active');
    const gigsToDisplay = gigs[filter];

    return (
        <div>
            <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl md:text-3xl font-bold">{t('gig.myGigsTitle')}</h2>
                <div className="flex items-center gap-2 p-1 bg-slate-200 rounded-lg">
                    <button onClick={() => setFilter('active')} className={`px-4 py-1.5 rounded-md font-semibold text-sm transition-colors ${filter === 'active' ? 'bg-white shadow' : 'bg-transparent text-slate-600'}`}>Active</button>
                    <button onClick={() => setFilter('archived')} className={`px-4 py-1.5 rounded-md font-semibold text-sm transition-colors ${filter === 'archived' ? 'bg-white shadow' : 'bg-transparent text-slate-600'}`}>Archived</button>
                </div>
            </div>
            {gigsToDisplay.length === 0 ? (
                 <div className="text-center bg-white p-8 rounded-xl shadow-sm border"><p className="text-slate-500">No gigs here right now.</p></div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {gigsToDisplay.map(gig => {
                         const isCreatorOfCompletedGig = gig.creatorId === user?.uid && gig.status === 'completed' && !gig.workerRated;
                         const isWorkerOfCompletedGig = gig.workerId === user?.uid && gig.status === 'completed' && !gig.creatorRated;
                         return (
                            <div key={gig.id} className="bg-white rounded-lg shadow border flex flex-col">
                                <GigCard gig={gig} onClick={()=>onGigClick(gig)} userLocation={userLocation} showStatus={true} allStores={allStores} t={t} />
                                <div className="p-4 border-t space-y-2">
                                     {isCreatorOfCompletedGig && (
                                        <>
                                            <p className="text-sm font-semibold mb-2">Rate your experience with {gig.workerName}:</p>
                                            <StarRating rating={0} onRating={(rating) => onRateWorker(gig.id, gig.workerId, rating)} />
                                            <button onClick={() => onReportUser(gig.workerId, gig.id)} className="w-full mt-2 text-xs text-red-500 hover:underline text-right">Report User</button>
                                        </>
                                    )}
                                    {isWorkerOfCompletedGig && (
                                         <>
                                            <p className="text-sm font-semibold mb-2">Rate your experience with {gig.creatorName}:</p>
                                            <StarRating rating={0} onRating={(rating) => onRateCreator(gig.id, gig.creatorId, rating)} />
                                             <button onClick={() => onReportUser(gig.creatorId, gig.id)} className="w-full mt-2 text-xs text-red-500 hover:underline text-right">Report User</button>
                                        </>
                                    )}
                                    {filter === 'archived' && <button onClick={() => onDeleteGig(gig.id)} className="w-full flex items-center justify-center gap-2 bg-red-100 text-red-700 font-bold py-2 px-4 rounded-lg shadow-sm hover:bg-red-200"><Trash2 size={16} /> Delete</button>}
                                </div>
                            </div>
                         )
                    })}
                </div>
            )}
        </div>
    )
}
const OrderActivity = ({orders, onRate, user, onMarkOrderComplete, setPage, handleDeleteOrder, onCreateGig, t }) => {
    const [selectedIds, setSelectedIds] = useState([]);
    const toggleSelect = (id) => setSelectedIds(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);
    const pendingOrders = orders.filter(o => o.status === 'pending');

    return (
    <div>
        <button onClick={() => setPage('profile')} className="flex items-center gap-2 mb-6 font-semibold text-slate-600 hover:text-slate-900">
            <ArrowLeft size={20}/> {t('profile.backToProfile')}
        </button>
        
        <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl md:text-3xl font-bold">{t('profile.myOrders')}</h2>
            {selectedIds.length > 0 && (
                <button 
                    onClick={() => { onCreateGig(selectedIds); setSelectedIds([]); }}
                    className="bg-purple-600 text-white font-bold py-2 px-4 rounded-lg shadow-md hover:bg-purple-700 animate-fade-in"
                >
                    Request Delivery/Service ({selectedIds.length})
                </button>
            )}
        </div>

        {orders.length===0?<div className="text-center bg-white p-8 rounded-xl shadow-sm border"><p className="text-slate-500">{t('profile.noOrders')}</p></div>:<div className="bg-white rounded-xl shadow-sm border divide-y">{orders.map(order=>(
            <div key={order.id} className={`p-4 transition-colors ${selectedIds.includes(order.id) ? 'bg-purple-50' : ''}`}>
                <div className="flex justify-between items-center">
                    <div className="flex items-center gap-3">
                        {order.status === 'pending' && (
                            <input 
                                type="checkbox" 
                                checked={selectedIds.includes(order.id)} 
                                onChange={() => toggleSelect(order.id)}
                                className="w-5 h-5 rounded border-slate-300 text-purple-600 focus:ring-purple-500"
                            />
                        )}
                        <div>
                            <p className="font-bold">{order.productName} <span className="text-xs text-slate-400">x{order.quantity}</span></p>
                            <p className="text-sm text-slate-500">From: {order.storeName}</p>
                        </div>
                    </div>
                    <div className="text-right"><p className="font-semibold">{order.currencySymbol || '$'}{(order.price || 0).toFixed(2)}</p><span className={`text-xs font-bold px-2 py-1 rounded-full capitalize ${order.status === 'pending' ? 'bg-slate-200 text-slate-700' : 'bg-yellow-100 text-yellow-700'}`}>{order.status.replace('-',' ')}</span></div>
                </div>

                {order.status === 'processed' && <p className="text-xs text-sky-600 mt-2 font-semibold">Request Created (See My Gigs)</p>}
                
                {order.status === 'completed' && !order.rated && <div><p className="text-sm mt-2">Rate your experience:</p><StarRating rating={0} onRating={(rating) => onRate(order, rating)} /></div>}
                
                <button onClick={() => handleDeleteOrder(order.id)} className="w-full mt-2 text-xs text-red-500 hover:underline text-right flex items-center justify-end gap-1">
                    <Trash2 size={12} /> {t('profile.deleteRecord')}
                </button>
            </div>
        ))}</div>}
    </div>
    );
};
const ChatActivity = ({ chats, user, onStartChat, db, allStores, myStore }) => {
    return (
        <div>
            <h2 className="text-2xl md:text-3xl font-bold mb-6">My Chats</h2>
            {chats.length===0?<div className="text-center bg-white p-8 rounded-xl shadow-sm border"><p className="text-slate-500">You have no chats yet. Start a conversation from a store or gig page.</p></div>:<div className="bg-white rounded-xl shadow-sm border divide-y">{chats.map(chat=>(
                <div key={chat.id} onClick={() => onStartChat(chat.participants.find(p => p !== user.uid), false)} className="p-4 cursor-pointer hover:bg-slate-50 flex justify-between items-center">
                    <p className={`font-bold ${chat.hasUnread ? 'text-sky-600' : ''}`}>{chat.participantNames[chat.participants.find(p=>p !== user.uid)]}</p>
                    {chat.hasUnread && <span className="w-3 h-3 bg-red-500 rounded-full"></span>}
                </div>
            ))}</div>}
        </div>
    );
};
const ChatWindow = ({ partnerId, user, db, onBack, isModal = false, allStores, myStore, language, t}) => {
    const [messages, setMessages] = useState([]);
    const [newMessage, setNewMessage] = useState('');
    const [translatedMessages, setTranslatedMessages] = useState({}); // NEW: For translation storage
    const [isTranslating, setIsTranslating] = useState(false); // NEW: For translation loading
    const messagesEndRef = useRef(null);
    const partnerStore = useMemo(() => allStores?.find(s => s.id === partnerId), [allStores, partnerId]);

    // NEW: Gemini Translation Function
    const handleTranslateMessage = async (msgId, text, currentAppLanguage) => {
        if (translatedMessages[msgId]) {
            setTranslatedMessages(prev => { 
                const newState = { ...prev }; 
                delete newState[msgId]; 
                return newState; 
            }); // Toggle off
            return;
        }
        setIsTranslating(true);
        // Map ISO code to full language name for the AI
        const langMap = { en: 'English', fr: 'French', es: 'Spanish', ar: 'Arabic' };
        const targetLang = langMap[currentAppLanguage] || 'English';
        
        // The goal is always to translate TO the user's active app language (targetLang).
        const userQuery = `Translate the following chat message to ${targetLang}. The message is: "${text}"`;
        const payload = { contents: [{ parts: [{ text: userQuery }] }], systemInstruction: { parts: [{ text: `You are a universal translator for a chat application. Translate the given text accurately into ${targetLang}. Only return the translated text.` }] }};
        try {
            const url = `${GEMINI_API_URL}?key=${geminiApiKey}`;
            const response = await fetchWithBackoff(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
            const translatedText = response.candidates?.[0]?.content?.parts?.[0]?.text || "Translation failed.";
            setTranslatedMessages(prev => ({ ...prev, [msgId]: translatedText }));
        } catch (error) { console.error("Translation error:", error); } finally { setIsTranslating(false); }
    };

    useEffect(() => {
        const chatId = [user.uid, partnerId].sort().join('_');
        const q = query(collection(db, `artifacts/${appId}/public/data/chats/${chatId}/messages`), orderBy("timestamp"));
        const unsubscribe = onSnapshot(q, snapshot => {
            setMessages(snapshot.docs.map(doc => ({id: doc.id, ...doc.data()})));
        }, (error) => {
            console.error(`Error fetching messages for chat ${chatId}:`, error);
        });
        return () => unsubscribe();
    }, [partnerId, user.uid, db]);

    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [messages]);

    const handleSendMessage = async (e) => {
        e.preventDefault();
        if (newMessage.trim() === '') return;
        const chatId = [user.uid, partnerId].sort().join('_');
        const messagesCol = collection(db, `artifacts/${appId}/public/data/chats/${chatId}/messages`);
        await addDoc(messagesCol, {
            text: newMessage,
            senderId: user.uid,
            timestamp: serverTimestamp()
        });
         // Send notification to partner
         await addDoc(collection(db, `artifacts/${appId}/users/${partnerId}/notifications`), {
            message: t('msg.newMessage').replace('{{name}}', myStore?.name || 'User'),
            chatPartnerId: user.uid,
            read: false,
            timestamp: serverTimestamp()
        });

        // --- Send Email Notification ---
        if (partnerStore?.contactEmail) {
            addDoc(collection(db, `artifacts/${appId}/public/data/mail`), {
                to: [partnerStore.contactEmail],
                message: {
                    subject: `New Message from ${myStore?.name || 'User'} on GigNile`,
                    html: `<p>You have received a new message from <strong>${myStore?.name}</strong>:</p><p>"${newMessage}"</p><br><p>Log in to GigNile to reply.</p>`
                },
                createdAt: serverTimestamp()
            }).catch(err => console.error("Failed to queue email:", err));
        }

        setNewMessage('');
    };

    return (
        <div className="bg-white rounded-xl h-full flex flex-col">
            {/* Chat Header with Partner's Photo */}
            {!isModal && (
                <div className="p-4 border-b flex items-center gap-3">
                    <button onClick={onBack}><ArrowLeft size={20}/></button>
                    <img 
                        src={partnerStore?.profileImageUrl || 'https://placehold.co/40x40/EEE/31343C?text=👤'} 
                        alt={partnerStore?.name} 
                        className="w-8 h-8 rounded-full object-cover" 
                        onError={(e) => { e.target.onerror = null; e.target.src='https://placehold.co/40x40/EEE/31343C?text=👤'; }}
                    />
                    <h3 className="font-bold">{partnerStore?.name || 'Chat'}</h3>
                </div>
            )}
            <div className="flex-grow p-4 overflow-y-auto">
                {messages.map(msg => (
                    <div key={msg.id} className={`flex flex-col mb-3 ${msg.senderId === user.uid ? 'items-end' : 'items-start'} items-end`}>
                        {msg.senderId !== user.uid && <img src={partnerStore?.profileImageUrl || 'https://placehold.co/40x40/EEE/31343C?text=👤'} alt="Partner" className="w-8 h-8 rounded-full object-cover mr-2 mb-1" onError={(e) => { e.target.onerror = null; e.target.src='https://placehold.co/40x40/EEE/31343C?text=👤'; }}/>}
                        <div className={`p-3 rounded-2xl max-w-xs ${msg.senderId === user.uid ? 'bg-sky-500 text-white' : 'bg-slate-200'}`}>
                            {msg.senderId !== user.uid && <img src={partnerStore?.profileImageUrl || 'https://placehold.co/40x40/EEE/31343C?text=👤'} alt="Partner" className="w-8 h-8 rounded-full object-cover mr-2 mb-1" onError={(e) => { e.target.onerror = null; e.target.src='https://placehold.co/40x40/EEE/31343C?text=👤'; }}/>}
                            <p>{msg.text}</p>
                            {translatedMessages[msg.id] && (
                                <p className="mt-1 text-xs font-semibold italic border-t border-white/30 pt-1">
                                    {translatedMessages[msg.id]}
                                </p>
                            )}
                        </div>
                        {/* Translation Button for INCOMING messages only */}
                        {msg.senderId !== user.uid && (
                            <button 
                                onClick={() => handleTranslateMessage(msg.id, msg.text, language)} 
                                disabled={isTranslating}
                                className="text-xs text-slate-500 mt-1 flex items-center gap-1"
                            >
                                <Languages size={14} /> 
                                {isTranslating ? 'Translating...' : (translatedMessages[msg.id] ? 'Hide Translation' : 'Translate')}
                            </button>
                        )}
                    </div>
                ))}
                <div ref={messagesEndRef} />
            </div>
            <div className="p-4 border-t">
                <form onSubmit={handleSendMessage} className="flex gap-2">
                    <input value={newMessage} onChange={e => setNewMessage(e.target.value)} placeholder="Type a message..." className="flex-grow p-2 border rounded-lg"/>
                    <button type="submit" className="p-2 bg-sky-500 text-white rounded-lg"><Send size={20}/></button>
                </form>
            </div>
        </div>
    );
};

const ChatModal = ({ partnerId, user, db, onClose, allStores, myStore, language, t}) => {
    const partnerStore = useMemo(() => allStores?.find(s => s.id === partnerId), [allStores, partnerId]);

    return (
        <div className="fixed bottom-16 right-0 w-full h-[60vh] bg-white rounded-t-2xl shadow-2xl flex flex-col z-50 animate-slide-in-up sm:w-96 sm:bottom-20 sm:right-4 sm:rounded-xl sm:h-[60vh]">
            <div className="p-3 border-b flex items-center justify-between bg-slate-100 rounded-t-2xl sm:rounded-t-xl">
                <h3 className="font-bold">{partnerStore?.name || 'Chat'}</h3>
                <button onClick={onClose} className="p-1 rounded-full hover:bg-slate-200"><X size={18}/></button>
            </div>
            <ChatWindow partnerId={partnerId} user={user} db={db} onClose={onClose} isModal={true} allStores={allStores} myStore={myStore} language={language} t={t}/>
        </div>
    );
};

const StoreDetailPage = ({ storeId, allStores, allProducts, onProductClick, onBack, userLocation, onStartChat, currentUser, t }) => {
    const store = useMemo(() => allStores.find(s => s.id === storeId), [storeId, allStores]);
    const products = useMemo(() => allProducts.filter(p => p.storeId === storeId && !p.isDeleted), [storeId, allProducts]);
    const distance = useMemo(() => haversineDistance(userLocation, store?.location?.coords), [userLocation, store]);
    const sellerTier = getSellerTier(store?.sales || 0, 4, t); 
    const [isNavigationVisible, setNavigationVisible] = useState(false);
    
    if (!store) return <LoadingSpinner />;
    
    return (
        <>
        <div>
            <button onClick={onBack} className="flex items-center gap-2 mb-6 font-semibold text-slate-600 hover:text-slate-900">
                <ArrowLeft size={20}/> {t('common.back')}
            </button>
            <div className="flex justify-between items-start mb-4">
                <div className="flex items-center gap-4">
                    <img 
                        src={store.profileImageUrl || 'https://placehold.co/100x100/EEE/31343C?text=👤'} 
                        alt={store.name} 
                        className="w-24 h-24 rounded-full object-cover border-4 border-sky-500 shadow-md"
                        onError={(e) => { e.target.onerror = null; e.target.src='https://placehold.co/100x100/EEE/31343C?text=👤'; }}
                    />
                    <div>
                        <h2 className="text-3xl font-bold">{store.name}</h2>
                        <span className={`mt-1 inline-flex items-center gap-1 text-sm font-bold px-3 py-1 rounded-full ${sellerTier.color} bg-opacity-20`}>{sellerTier.icon}{sellerTier.name} Seller</span>
                    </div>
                </div>
                {distance !== null && <span className="font-bold text-slate-600 bg-slate-200 px-3 py-1 rounded-full">{distance.toFixed(1)} km</span>}
            </div>
            <p className="text-slate-500 mt-1 mb-6">{store.description || 'Welcome!'}</p>
            
            <div className="flex flex-col sm:flex-row gap-2 mb-4">
                {store.id !== currentUser.uid && (
                    <button onClick={() => onStartChat(store.id, true)} className="flex-1 flex items-center justify-center gap-2 bg-purple-500 text-white font-bold py-3 px-4 rounded-lg shadow-md hover:bg-purple-600 transition-colors">
                        <MessageSquare size={20}/>Chat with Seller
                    </button>
                )}
                {store.location?.coords ? (
                    <button onClick={() => setNavigationVisible(true)} className="flex-1 flex items-center justify-center gap-2 bg-sky-500 text-white font-bold py-3 px-4 rounded-lg shadow-md hover:bg-sky-600">
                        <Compass size={20}/> Take me to Seller
                    </button>
                ) : (
                    <button disabled className="flex-1 flex items-center justify-center gap-2 bg-slate-300 text-slate-500 font-bold py-3 px-4 rounded-lg cursor-not-allowed">
                        <MapPin size={20} /> <span>Location not set in {store.country}</span>
                    </button>
                )}
            </div>

            {store.images && store.images.length > 0 && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 my-8">
                    {store.images.filter(img => img).map((img, i) => (
                        <img key={i} src={img} alt={`Store image ${i+1}`} className={`rounded-lg shadow-lg object-cover ${i === 0 ? 'md:col-span-2 md:row-span-2 h-full' : 'aspect-video'}`} onError={(e) => { e.target.onerror = null; e.target.src='https://placehold.co/600x400/EEE/31343C?text=Image+Error'; }}/>
                    ))}
                </div>
            )}
            
            <h3 className="text-2xl font-bold mb-4">Store Items</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {products.map(p => <ProductCard key={p.id} product={p} onActionClick={() => onProductClick(p)} allStores={allStores} t={t} />)}
            </div>
        </div>
        {isNavigationVisible && store.location?.coords && <LiveNavigationModal origin={userLocation} destination={store.location.coords} onClose={() => setNavigationVisible(false)} t={t} />}
        </>
    );
};


const MarketplacePage = ({ setPage, t, onGlobalSearch }) => {
    const [searchTerm, setSearchTerm] = useState('');

    const handleSearch = (e) => {
        e.preventDefault();
        if (searchTerm.trim()) {
            onGlobalSearch(searchTerm.trim());
        }
    };

    return (
        <div className="flex-grow flex flex-col items-center animate-fade-in">
            <div className="text-center z-10 mb-8 w-full max-w-2xl">
                <h1 className="text-4xl md:text-5xl font-bold text-slate-800 mb-2">{t('welcomeMessage')}</h1>
                <p className="text-slate-500 mb-6">{t('tagline')}</p>
                <form onSubmit={handleSearch} className="relative">
                     <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                     <input 
                        type="text" 
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        placeholder={t('marketplace.searchPlaceholder')}
                        className="w-full pl-12 pr-4 py-4 border-2 border-slate-200 rounded-xl focus:ring-2 focus:ring-sky-500 focus:border-sky-500 transition shadow-sm"
                    />
                </form>
            </div>
            {/* Hero Gig Card */}
            <div className="w-full max-w-4xl mt-6 mb-8 px-2 sm:px-0">
                <div 
                    onClick={() => setPage('marketplace-gigs')} 
                    className="bg-gradient-to-r from-purple-600 to-indigo-600 rounded-3xl p-8 shadow-xl cursor-pointer transform transition-all hover:scale-[1.02] flex flex-col md:flex-row items-center justify-between text-white relative overflow-hidden group"
                >
                    {/* Background decoration */}
                    <div className="absolute top-0 right-0 w-64 h-64 bg-white opacity-5 rounded-full -mr-16 -mt-16 pointer-events-none"></div>
                    
                    <div className="flex flex-col md:flex-row items-center gap-6 z-10">
                        <div className="bg-white/20 p-5 rounded-2xl backdrop-blur-sm shadow-inner">
                            <Briefcase size={48} className="text-white"/>
                        </div>
                        <div className="text-center md:text-left">
                            <h3 className="text-3xl font-bold mb-2">{t('gigs')}</h3>
                            <p className="text-purple-100 text-lg max-w-md">{t('gigsDesc')}</p>
                        </div>
                    </div>
                    <div className="mt-6 md:mt-0 z-10 bg-white text-purple-600 px-6 py-3 rounded-xl font-bold flex items-center gap-2 shadow-lg group-hover:bg-purple-50 transition-colors">
                        <span>{t('explore')}</span> <ChevronRight size={20} />
                    </div>
                </div>
            </div>

            {/* Secondary Categories Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 w-full max-w-4xl px-2 sm:px-0">
                <CategoryCard icon={<Package size={32} className="text-purple-500"/>} title={t('goods')} description={t('goodsDesc')} onClick={() => setPage('marketplace-goods')} t={t} />
                <CategoryCard icon={<Wrench size={32} className="text-purple-500"/>} title={t('services')} description={t('servicesDesc')} onClick={() => setPage('marketplace-services')} t={t}/>
                <CategoryCard icon={<Ticket size={32} className="text-purple-500"/>} title={t('event.title')} description={t('event.desc')} onClick={() => setPage('marketplace-entertainment')} t={t}/>
            </div>
        </div>
    );
};
const CategoryCard = ({icon, title, description, onClick, t}) => ( <div onClick={onClick} className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-2xl hover:-translate-y-2 transition-all duration-300 cursor-pointer flex flex-col items-center text-center"> <div className="bg-slate-100 p-4 rounded-full mb-6"> {icon} </div> <h3 className="text-xl font-bold text-slate-800 mb-2">{title}</h3> <p className="text-slate-500 mb-6 flex-grow">{description}</p> <div className="flex items-center font-semibold text-sky-600"> <span>{t('explore')}</span> <ChevronRight size={20} /> </div> </div> )

const CategorySearchPage = ({ title, items, onActionClick, CardComponent, userLocation, allStores, t, showMarketFilter = true, onViewStore, myStore, isAdmin }) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedMarket, setSelectedMarket] = useState(myStore?.marketName || 'All'); // Default to user's registered market
    const [selectedCategory, setSelectedCategory] = useState('All Categories');
    const [selectedDistance, setSelectedDistance] = useState('all'); // FIX: Default to 'all'

    const markets = useMemo(() => {
        let uniqueMarkets;
        if (isAdmin) {
            uniqueMarkets = [...new Set(allStores.filter(s => s.marketName).map(s => s.marketName))];
        } else if (myStore) {
            uniqueMarkets = [...new Set(
                allStores
                    .filter(s => s.countryCode === myStore.countryCode && s.marketName)
                    .map(s => s.marketName)
            )];
        } else {
            uniqueMarkets = [];
        }
        return ['All', ...uniqueMarkets];
    }, [allStores, myStore, isAdmin]);

    const categories = useMemo(() => {
        const allCats = items.flatMap(item => item.categories || []);
        return ['All Categories', ...[...new Set(allCats)].sort()];
    }, [items]);

    const filteredItems = useMemo(() => {
        let results = items;
        if (showMarketFilter && selectedMarket !== 'All') {
            results = results.filter(item => item.marketName === selectedMarket);
        }
        if (selectedCategory !== 'All Categories') {
            results = results.filter(item => item.categories?.includes(selectedCategory));
        }
        if (searchTerm) {
            results = results.filter(item => (item.title || item.name).toLowerCase().includes(searchTerm.toLowerCase()));
        }
        // Apply distance filter
        if (selectedDistance !== 'all' && userLocation) {
            const maxDistance = parseInt(selectedDistance, 10);
            results = results.filter(item => {
                // Use gig's location/pickup, or product's location (which comes from the store)
                // Determine the location for the item:
                // 1. Gigs use their own location fields.
                // 2. Products/Services use the location saved in their PARENT store document.
                const storeOwner = allStores.find(s => s.id === (item.storeId || item.creatorId));
                
                const itemLocation = item.itemType === 'gig' 
                    ? (item.location?.coords || item.pickupLocation?.coords) 
                    : storeOwner?.location?.coords;
                if (!itemLocation) return false; // Filter out items with no location if distance filter is on
                const distance = haversineDistance(userLocation, itemLocation);
                return distance !== null && distance <= maxDistance;
            });
        }
        return results;
    }, [items, searchTerm, selectedMarket, selectedCategory, showMarketFilter, selectedDistance, userLocation]);

    return (
        <div>
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-slate-700">{title}</h2>
            <div className="flex flex-col md:flex-row gap-4 mb-8">
                <div className="relative flex-grow">
                    <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <input type="text" placeholder={`${t('searchFor')} ${title.toLowerCase()}...`} value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="w-full pl-12 pr-4 py-3 border-2 border-slate-200 rounded-xl focus:ring-2 focus:ring-sky-500 focus:border-sky-500 transition"/>
                </div>
                <div className="flex flex-col sm:flex-row gap-4">
                    <div className="relative">
                        <select value={selectedDistance} onChange={(e) => setSelectedDistance(e.target.value)} className="w-full sm:w-auto appearance-none bg-white py-3 pl-4 pr-10 border-2 border-slate-200 rounded-xl focus:ring-2 focus:ring-sky-500 focus:border-sky-500 transition">
                            <option value="all">All Distances</option>
                            <option value="10">Within 10 km</option>
                            <option value="25">Within 25 km</option>
                            <option value="50">Within 50 km</option>
                            <option value="100">Within 100 km</option>
                            <option value="250">Within 250 km</option>
                            <option value="500">Within 500 km</option>
                        </select>
                    </div>
                    {showMarketFilter && (
                        <div className="relative">
                            <select value={selectedMarket} onChange={(e) => setSelectedMarket(e.target.value)} className="w-full sm:w-auto appearance-none bg-white py-3 pl-4 pr-10 border-2 border-slate-200 rounded-xl focus:ring-2 focus:ring-sky-500 focus:border-sky-500 transition">
                                {markets.map(market => <option key={market} value={market}>{market === 'All' ? 'All City' : market}</option>)}
                            </select>
                        </div>
                    )}

                    {/* Category Filter (HIDDEN for gigs) */}
                    {title !== t('gigs') && (
                        <div className="relative">
                            <select value={selectedCategory} onChange={(e) => setSelectedCategory(e.target.value)} className="w-full sm:w-auto appearance-none bg-white py-3 pl-4 pr-10 border-2 border-slate-200 rounded-xl focus:ring-2 focus:ring-sky-500 focus:border-sky-500 transition">
                                {categories.map(cat => <option key={cat} value={cat}>{cat}</option>)}
                            </select>
                        </div>
                    )}
                </div>
            </div>
            {filteredItems.length === 0 ? <div className="text-center bg-white p-8 rounded-xl shadow-sm border"><p className="text-slate-500">No {title.toLowerCase()} found.</p></div> : <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">{filteredItems.map(item => {
                const props = {
                    key: item.id, // Corrected syntax
                    onActionClick: () => onActionClick(item),
                    userLocation: userLocation,
                    allStores: allStores,
                    t: t, // <-- FIX: Pass the translation function
                };
                if (CardComponent === GigCard) {
                    props.gig = item;
                    props.onClick = () => onActionClick(item);
                    props.onViewProfile = onViewStore; // GigCard needs onViewProfile
                } else {
                    props.product = item;
                    props.onViewStore = onViewStore;
                }
                return <CardComponent {...props} />;
            })}</div>}
        </div>
    )
}

const SearchResultsPage = ({ query, allProducts, gigs, onActionClick, userLocation, allStores, onViewStore, t }) => {
    const searchResults = useMemo(() => {
        if (!query) return [];
        const lowerCaseQuery = query.toLowerCase();

        const matchingProducts = allProducts.filter(p =>
            (p.status === 'approved' && !p.isDeleted) &&
            (p.name.toLowerCase().includes(lowerCaseQuery) || p.description.toLowerCase().includes(lowerCaseQuery) ||
            (p.categories && p.categories.some(cat => cat.toLowerCase().includes(lowerCaseQuery))))
        );

        const matchingGigs = gigs.filter(g =>
            g.status === 'open' &&
            (g.title.toLowerCase().includes(lowerCaseQuery) || g.description.toLowerCase().includes(lowerCaseQuery))
        );

        return [...matchingProducts, ...matchingGigs];
    }, [query, allProducts, gigs]);

    return (
        <div>
            <h2 className="text-3xl font-bold mb-6">Search Results for "<span className="text-sky-600">{query}</span>"</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {searchResults.length > 0 ? (
                    searchResults.map(item => {
                        if (item.itemType === 'gig') {
                            return <GigCard key={item.id} gig={item} onClick={() => onActionClick(item)} userLocation={userLocation} allStores={allStores} t={t} />;
                        } else {
                            return <ProductCard key={item.id} product={item} onActionClick={() => onActionClick(item)} allStores={allStores} onViewStore={onViewStore} t={t} />;
                        }
                    })
                ) : (
                    <p className="text-slate-500 col-span-full text-center bg-white p-8 rounded-xl shadow-sm border">No results found for your search.</p>
                )}
            </div>
        </div>
    );
};

const UserDetailsModal = ({ user, onClose }) => {
    if (!user) return null;
    
    const DetailItem = ({ label, value, isStatus = false, isLink = false }) => (
        <div className="py-2 border-b">
            <span className="font-semibold text-slate-600">{label}:</span> 
            {isStatus ? (
                 <span className={`font-bold ml-2 px-2 py-1 rounded-full text-xs ${
                    value === 'approved' ? 'bg-green-100 text-green-700' : 
                    value === 'pending_approval' ? 'bg-yellow-100 text-yellow-700' : 
                    value === 'suspended' ? 'bg-orange-100 text-orange-700' :
                    'bg-red-100 text-red-700'
                 }`}>{value.replace('_', ' ')}</span>
            ) : isLink && value ? (
                 <a href={value} target="_blank" rel="noopener noreferrer" className="ml-2 text-sky-600 hover:underline">View Document</a>
            ) : (
                <span className="ml-2 text-slate-800">{value || 'N/A'}</span>
            )}
        </div>
    );

    return (
        <ModalWrapper onClose={onClose}>
            <h2 className="text-2xl font-bold mb-4 flex items-center gap-3"><img src={user.profileImageUrl || 'https://placehold.co/40x40/EEE/31343C?text=👤'} alt="Profile" className="w-10 h-10 rounded-full object-cover"/>{user.firstName} {user.lastName}'s Details</h2>
            <div className="space-y-2 text-sm">
                <DetailItem label="Shop Name" value={user.name} />
                <DetailItem label="Email" value={user.contactEmail} />
                <DetailItem label="Phone" value={user.contactPhone} />
                <DetailItem label="Country" value={user.country} />
                <DetailItem label="Market" value={user.marketName} />
                <DetailItem label="Date of Birth" value={user.dob} />
                <DetailItem label="Gender" value={user.gender} />
                <DetailItem label="Status" value={user.status || 'approved'} isStatus={true} />
                <DetailItem label="ID Document" value={user.idDocumentUrl} isLink={true} />
                <DetailItem label="Delivery Driver" value={user.isDeliveryDriver ? 'Yes' : 'No'} />
                {user.isDeliveryDriver && <DetailItem label="Driver's License" value={user.driverLicenseUrl} isLink={true} />}
            </div>
        </ModalWrapper>
    );
};

const AdminPage = ({ 
    allStores = [], // FIX: Default to empty array
    allProducts = [], // FIX: Default to empty array
    reports = [], // FIX: Default to empty array
    gigs = [], // FIX: Default to empty array
    admins = [], // FIX: Default to empty array
    onApproveProduct, onRejectProduct, 
    onMakeAdmin, onRemoveAdmin, 
    onAdminChat, onSuspendUser, onReactivateUser, 
    onApproveUser, onRejectUser, openUserDetails, 
    currentUser, openProductDetails, openReportDetails, 
    onDeleteUser, onDeleteGig, onDeleteProduct, t
}) => {
    const [adminTab, setAdminTab] = useState('dashboard');
    const [userSearchTerm, setUserSearchTerm] = useState('');
    const [userFilter, setUserFilter] = useState('all');
    const [productSearchTerm, setProductSearchTerm] = useState(''); // NEW: Dedicated search for products
    const [productFilter, setProductFilter] = useState('pending'); // 'pending' vs 'all'
    const [gigSearchTerm, setGigSearchTerm] = useState('');
    const [eventSearchTerm, setEventSearchTerm] = useState('');

    // --- NEW: Dashboard Analytics Aggregation ---
    const chartData = useMemo(() => {
        // 1. Users by Country (Pie Chart)
        const countryCounts = allStores.reduce((acc, store) => {
            const country = store.country || 'Unknown';
            acc[country] = (acc[country] || 0) + 1;
            return acc;
        }, {});
        const userCountryData = {
            labels: Object.keys(countryCounts),
            datasets: [{
                label: '# of Users',
                data: Object.values(countryCounts),
                backgroundColor: ['#3B82F6', '#10B981', '#F59E0B', '#8B5CF6', '#EF4444', '#14B8A6'],
            }],
        };

        // 2. Content Type Breakdown (Bar Chart)
        const gigCount = gigs.length;
        const goodsCount = allProducts.filter(p => p.type === 'goods').length;
        const serviceCount = allProducts.filter(p => p.type === 'service').length;
        const eventCount = allProducts.filter(p => p.type === 'event').length;
        const contentTypeData = {
            labels: ['Gigs', 'Goods', 'Services', 'Events'],
            datasets: [{
                label: 'Total Listings',
                data: [gigCount, goodsCount, serviceCount, eventCount],
                backgroundColor: ['#8B5CF6', '#3B82F6', '#10B981', '#F43F5E'],
            }],
        };
        
        // 3. Activity Over Time (Line Chart)
        const aggregateByMonth = (items) => {
            const months = new Array(12).fill(0);
            items.forEach(item => {
                if (item.createdAt && item.createdAt.toDate) { // Check if it's a Firestore timestamp
                    const month = item.createdAt.toDate().getMonth(); // 0 = Jan
                    months[month] += 1;
                }
            });
            return months;
        };

        const events = allProducts.filter(p => p.type === 'event');
        const activityOverTimeData = {
            labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
            datasets: [
                { label: 'Gigs Created', data: aggregateByMonth(gigs), borderColor: '#8B5CF6', tension: 0.1 },
                { label: 'Products/Services', data: aggregateByMonth(allProducts.filter(p => p.type !== 'event')), borderColor: '#3B82F6', tension: 0.1 },
                { label: 'Events Created', data: aggregateByMonth(events), borderColor: '#F43F5E', tension: 0.1 }
            ]
        };

        // 4. User Growth (New Chart)
        const userGrowthData = {
           labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
           datasets: [
                { 
                    label: 'New User Signups', 
                    data: aggregateByMonth(allStores), 
                    borderColor: '#10B981', 
                    backgroundColor: 'rgba(16, 185, 129, 0.1)',
                    fill: true,
                    tension: 0.3 
                }
           ]
        };

        // 5. Activity by Market (City)

        const marketActivity = {};
        gigs.forEach(g => {
            const market = g.marketName || 'Unknown';
            marketActivity[market] = (marketActivity[market] || 0) + 1;
        });
        allProducts.forEach(p => {
            const market = p.marketName || 'Unknown';
            marketActivity[market] = (marketActivity[market] || 0) + 1;
        });
        const sortedMarkets = Object.entries(marketActivity).sort((a, b) => b[1] - a[1]).slice(0, 10);
        const marketData = {
            labels: sortedMarkets.map(m => m[0]),
            datasets: [{ label: 'Total Activity (Gigs + Products)', data: sortedMarkets.map(m => m[1]), backgroundColor: '#10B981' }]
        };

        // 5. User Leaderboards
        const userStats = {};
        allStores.forEach(s => {
            userStats[s.id] = { name: s.name, sales: s.sales || 0, gigsPosted: 0, deliveriesCompleted: 0 };
        });
        gigs.forEach(g => {
            if (userStats[g.creatorId]) userStats[g.creatorId].gigsPosted++;
            if (g.status === 'completed' && g.workerId && userStats[g.workerId] && g.type === 'delivery') {
                userStats[g.workerId].deliveriesCompleted++;
            }
        });
        
        const topGigPosters = Object.values(userStats).sort((a, b) => b.gigsPosted - a.gigsPosted).slice(0, 5);
        const topDeliverers = Object.values(userStats).sort((a, b) => b.deliveriesCompleted - a.deliveriesCompleted).slice(0, 5);
        const topSellers = Object.values(userStats).sort((a, b) => b.sales - a.sales).slice(0, 5);

        return { 
            userCountryData, 
            contentTypeData, 
            activityOverTimeData, 
            userGrowthData,
            marketData, 
            topGigPosters, 
            topDeliverers, 
            topSellers 
        };
    }, [allStores, allProducts, gigs]);

    const pendingProducts = useMemo(() => allProducts.filter(p => p.status === 'pending'), [allProducts]);
    const newReports = useMemo(() => reports.filter(r => r.status === 'new'), [reports]);

    const filteredProducts = useMemo(() => {
        const list = productFilter === 'pending' ? pendingProducts : allProducts.filter(p => !p.isDeleted);
        if (!productSearchTerm) return list;
        const lowerSearchTerm = productSearchTerm.toLowerCase();
        
        return list.filter(p => 
            p.name.toLowerCase().includes(lowerSearchTerm) || 
            p.storeName.toLowerCase().includes(lowerSearchTerm) ||
            (p.price || 0).toString().includes(lowerSearchTerm) // Search by price
        );
    }, [allProducts, productFilter, pendingProducts, productSearchTerm]);

    // NEW: Filtered Gigs list for Admin
    const filteredGigs = useMemo(() => {
        if (!gigSearchTerm) return gigs;
        return gigs.filter(g => g.title.toLowerCase().includes(gigSearchTerm.toLowerCase()) || g.creatorName.toLowerCase().includes(gigSearchTerm.toLowerCase()));
    }, [gigs, gigSearchTerm]);
    
    const filteredStores = useMemo(() => {
        let stores = allStores;
        
        if (currentUser.email !== ADMIN_EMAIL) {
            stores = stores.filter(store => store.contactEmail !== ADMIN_EMAIL);
        }

        if (userFilter === 'pending') {
            stores = stores.filter(s => s.status === 'pending_approval');
        } else if (userFilter === 'suspended') {
            stores = stores.filter(s => s.status === 'suspended');
        }

        if (!userSearchTerm) return stores;

        return stores.filter(store => 
            store.name.toLowerCase().includes(userSearchTerm.toLowerCase()) ||
            store.contactEmail.toLowerCase().includes(userSearchTerm.toLowerCase())
        );
    }, [allStores, userSearchTerm, userFilter, currentUser.email]);
    
    const filteredEvents = useMemo(() => {
       const events = allProducts.filter(p => p.type === 'event' && !p.isDeleted); // Filter out deleted events
       if (!eventSearchTerm) return events;
       return events.filter(e => e.name.toLowerCase().includes(eventSearchTerm.toLowerCase()) || e.storeName.toLowerCase().includes(eventSearchTerm.toLowerCase()));
    }, [allProducts, eventSearchTerm]);

    return (
        <div>
            <h2 className="text-3xl font-bold mb-6">{t('admin.dashboard')}</h2>
            
            <div className="flex border-b mb-6 overflow-x-auto">
                 <button onClick={() => setAdminTab('dashboard')} className={`py-2 px-4 font-semibold shrink-0 ${adminTab === 'dashboard' ? 'border-b-2 border-sky-500 text-sky-600' : 'text-slate-500'}`}>Dashboard</button>
                <button onClick={() => setAdminTab('products')} className={`py-2 px-4 font-semibold relative shrink-0 ${adminTab === 'products' ? 'border-b-2 border-sky-500 text-sky-600' : 'text-slate-500'}`}>
                    Products
                    {pendingProducts.length > 0 && <span className="absolute top-1 right-1 w-4 h-4 bg-red-500 text-white text-xs rounded-full">{pendingProducts.length}</span>}
                </button>
                <button onClick={() => setAdminTab('users')} className={`py-2 px-4 font-semibold shrink-0 ${adminTab === 'users' ? 'border-b-2 border-sky-500 text-sky-600' : 'text-slate-500'}`}>Users</button>
                <button onClick={() => setAdminTab('gigs')} className={`py-2 px-4 font-semibold shrink-0 ${adminTab === 'gigs' ? 'border-b-2 border-sky-500 text-sky-600' : 'text-slate-500'}`}>Gigs</button>
                <button onClick={() => setAdminTab('events')} className={`py-2 px-4 font-semibold shrink-0 ${adminTab === 'events' ? 'border-b-2 border-sky-500 text-sky-600' : 'text-slate-500'}`}>Events</button>
                <button onClick={() => setAdminTab('reports')} className={`py-2 px-4 font-semibold shrink-0 relative ${adminTab === 'reports' ? 'border-b-2 border-sky-500 text-sky-600' : 'text-slate-500'}`}>
                    Reports
                    {newReports.length > 0 && <span className="absolute top-1 right-1 w-4 h-4 bg-red-500 text-white text-xs rounded-full">{newReports.length}</span>}
                </button>
            </div>

            {adminTab === 'dashboard' && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8 animate-fade-in">
                     <div className="bg-white p-8 rounded-xl shadow-lg border-l-4 border-sky-500 md:col-span-3 flex items-center justify-center flex-col">
                        <h3 className="text-2xl font-semibold text-slate-500">{t('admin.totalUsers')}</h3>
                        <p className="text-8xl font-bold text-slate-800 my-4">{allStores.length}</p>
                        <Users className="w-16 h-16 text-sky-200" />
                    </div>

                    {/* --- NEW: Charts --- */}
                    <div className="md:col-span-3 bg-white p-6 rounded-xl shadow-lg border">
                       <h3 className="text-xl font-bold mb-4 text-slate-700">{t('admin.userGrowth')}</h3>
                       <Line data={chartData.userGrowthData} />
                    </div>
                    <div className="md:col-span-3 bg-white p-6 rounded-xl shadow-lg border">
                        <h3 className="text-xl font-bold mb-4 text-slate-700">{t('admin.activity')}</h3>
                        <Line data={chartData.activityOverTimeData} />
                    </div>
                    <div className="md:col-span-2 bg-white p-6 rounded-xl shadow-lg border">
                        <h3 className="text-xl font-bold mb-4 text-slate-700">{t('admin.contentBreakdown')}</h3>
                        <Bar data={chartData.contentTypeData} />
                    </div>
                    <div className="md:col-span-1 bg-white p-6 rounded-xl shadow-lg border">
                        <h3 className="text-xl font-bold mb-4 text-slate-700">{t('admin.usersByCountry')}</h3>
                        <Pie data={chartData.userCountryData} />
                    </div>
                    <div className="md:col-span-3 bg-white p-6 rounded-xl shadow-lg border">
                        <h3 className="text-xl font-bold mb-4 text-slate-700">{t('admin.topCities')}</h3>
                        <Bar data={chartData.marketData} />
                    </div>

                    <div className="md:col-span-1 bg-white p-6 rounded-xl shadow-lg border">
                        <h3 className="font-bold text-slate-700 mb-3 flex items-center gap-2"><Briefcase size={18}/> {t('admin.topPosters')}</h3>
                        <ul className="space-y-2">
                            {chartData.topGigPosters.map((u, i) => (
                                <li key={i} className="flex justify-between text-sm border-b pb-1"><span>{u.name}</span> <span className="font-bold text-purple-600">{u.gigsPosted}</span></li>
                            ))}
                        </ul>
                    </div>
                    <div className="md:col-span-1 bg-white p-6 rounded-xl shadow-lg border">
                        <h3 className="font-bold text-slate-700 mb-3 flex items-center gap-2"><ShoppingCart size={18}/> {t('admin.topSellers')}</h3>
                         <ul className="space-y-2">
                            {chartData.topSellers.map((u, i) => (
                                <li key={i} className="flex justify-between text-sm border-b pb-1"><span>{u.name}</span> <span className="font-bold text-teal-600">{u.sales} sold</span></li>
                            ))}
                        </ul>
                    </div>
                    <div className="md:col-span-1 bg-white p-6 rounded-xl shadow-lg border">
                         <h3 className="font-bold text-slate-700 mb-3 flex items-center gap-2"><Package size={18}/> {t('admin.topDeliverers')}</h3>
                         <ul className="space-y-2">
                            {chartData.topDeliverers.map((u, i) => (
                                <li key={i} className="flex justify-between text-sm border-b pb-1"><span>{u.name}</span> <span className="font-bold text-sky-600">{u.deliveriesCompleted}</span></li>
                            ))}
                        </ul>
                    </div>
                </div>
            )}
            {adminTab === 'users' && (
                <div className="bg-white p-4 rounded-xl shadow-md border">
                    <h3 className="text-xl font-bold mb-4">Manage Users</h3>
                    <div className="flex gap-2 mb-4">
                        <button onClick={() => setUserFilter('all')} className={`px-3 py-1 rounded-full text-sm font-semibold ${userFilter === 'all' ? 'bg-sky-500 text-white' : 'bg-slate-200'}`}>All</button>
                        <button onClick={() => setUserFilter('pending')} className={`px-3 py-1 rounded-full text-sm font-semibold relative ${userFilter === 'pending' ? 'bg-yellow-500 text-white' : 'bg-slate-200'}`}>
                            Pending
                            {allStores.filter(s => s.status === 'pending_approval').length > 0 && (
                                <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white text-xs rounded-full">{allStores.filter(s => s.status === 'pending_approval').length}</span>
                            )}
                        </button>
                        <button onClick={() => setUserFilter('suspended')} className={`px-3 py-1 rounded-full text-sm font-semibold ${userFilter === 'suspended' ? 'bg-orange-500 text-white' : 'bg-slate-200'}`}>Suspended</button>
                    </div>
                    <div className="relative mb-4">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                        <input 
                            type="text" 
                            placeholder="Search by name or email..." 
                            value={userSearchTerm}
                            onChange={(e) => setUserSearchTerm(e.target.value)}
                            className="w-full pl-10 pr-4 py-2 border rounded-lg"
                        />
                    </div>
                    <ul className="divide-y">
                        {filteredStores.map(store => (
                            <li key={store.id} className="py-3 flex flex-col sm:flex-row justify-between sm:items-center">
                                <div onClick={() => openUserDetails(store)} className="cursor-pointer">
                                    <p className="font-semibold hover:underline">{store.name} ({store.firstName} {store.lastName})</p>
                                    <p className="text-sm text-slate-500">{store.contactEmail}</p>
                                </div>
                                <div className="flex items-center gap-2 mt-2 sm:mt-0 flex-wrap">
                                     {store.status === 'pending_approval' ? (
                                        <>
                                            <button onClick={() => onApproveUser(store.id)} className="bg-green-500 text-white text-xs font-bold py-1 px-3 rounded-full flex items-center gap-1"><CheckCircle size={14}/> Approve</button>
                                            <button onClick={() => onRejectUser(store)} className="bg-red-500 text-white text-xs font-bold py-1 px-3 rounded-full flex items-center gap-1"><UserX size={14}/> Reject</button>
                                        </>
                                    ) : (
                                        <>
                                            <button onClick={() => onAdminChat(store.id, true)} className="bg-sky-500 text-white text-xs font-bold py-1 px-3 rounded-full flex items-center gap-1"><MessageSquare size={14}/> Chat</button>
                                            {store.status === 'suspended' ? (
                                                <button onClick={() => onReactivateUser(store.id)} className="bg-yellow-500 text-white text-xs font-bold py-1 px-3 rounded-full">Reactivate</button>
                                            ) : store.contactEmail !== ADMIN_EMAIL && (
                                                <button onClick={() => onSuspendUser(store.id)} className="bg-orange-500 text-white text-xs font-bold py-1 px-3 rounded-full">Suspend</button>
                                            )}
                                            {store.contactEmail !== ADMIN_EMAIL && (
                                                admins.includes(store.id) ? (
                                                    <button onClick={() => onRemoveAdmin(store.id)} className="bg-amber-500 text-white text-xs font-bold py-1 px-3 rounded-full">Remove Admin</button>
                                                ) : (
                                                    <button onClick={() => onMakeAdmin(store.id)} className="bg-blue-500 text-white text-xs font-bold py-1 px-3 rounded-full">Make Admin</button>
                                                )
                                            )}
                                            {store.contactEmail !== ADMIN_EMAIL && <button onClick={() => onDeleteUser(store)} className="bg-red-600 text-white text-xs font-bold py-1 px-3 rounded-full flex items-center gap-1"><Trash2 size={14}/> Delete</button>}
                                        </>
                                     )}
                                </div>
                            </li>
                        ))}
                    </ul>
                </div>
            )}
            {adminTab === 'products' && (
                 <div className="bg-white p-4 rounded-xl shadow-md border">
                    <h3 className="text-xl font-bold mb-4">Manage Products ({filteredProducts.length})</h3>
                    <div className="flex gap-2 mb-4">
                        <button onClick={() => setProductFilter('pending')} className={`px-3 py-1 rounded-full text-sm font-semibold ${productFilter === 'pending' ? 'bg-yellow-500 text-white' : 'bg-slate-200'}`}>Pending ({pendingProducts.length})</button>
                        <button onClick={() => setProductFilter('all')} className={`px-3 py-1 rounded-full text-sm font-semibold ${productFilter === 'all' ? 'bg-sky-500 text-white' : 'bg-slate-200'}`}>All Products ({allProducts.filter(p => !p.isDeleted).length})</button>
                    </div>
                    {/* NEW: Search input for products */}
                    <div className="relative mb-4">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                        <input 
                            type="text" 
                            placeholder="Search by Product Name, Store Name, or Price..." 
                            value={productSearchTerm}
                            onChange={(e) => setProductSearchTerm(e.target.value)}
                            className="w-full pl-10 pr-4 py-2 border rounded-lg"
                        />
                    </div>
                    {filteredProducts.length === 0 ? <p className="text-slate-500">No products found for this filter.</p> : (
                         <div className="space-y-4">
                            {filteredProducts.map(product => (
                                <div key={product.id} className="border p-4 rounded-lg flex items-center justify-between">
                                    <div className="flex items-center gap-4">
                                        <img src={product.images?.[0] || 'https://placehold.co/100x100/EEE/31343C?text=No+Image'} alt={product.name} className="w-16 h-16 object-cover rounded-md" />
                                        <div>
                                            <p className="font-bold">{product.name}</p>
                                            <p className="text-sm text-slate-500">{product.storeName} | {product.currencySymbol || '$'}{(product.price || 0).toFixed(2)}</p>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        {product.status === 'pending' ? (
                                            <button onClick={() => openProductDetails(product)} className="text-xs font-bold text-yellow-700 bg-yellow-100 px-2 py-1 rounded-full hover:bg-yellow-200">Pending Review</button>
                                        ) : (
                                           <span className="text-xs font-bold text-green-700 bg-green-100 px-2 py-1 rounded-full">Approved</span>
                                        )}
                                        <button onClick={() => onDeleteProduct(product.id)} className="bg-red-100 text-red-600 p-2 rounded-full hover:bg-red-200">
                                            <Trash2 size={16} />
                                        </button>
                                    </div>
                                </div>
                            ))}
                         </div>
                    )}
                 </div>
            )}
            {/* NEW: Gigs Management Tab */}
            {adminTab === 'gigs' && (
                 <div className="bg-white p-4 rounded-xl shadow-md border">
                    <h3 className="text-xl font-bold mb-4">Manage Gigs ({filteredGigs.length})</h3>
                    <div className="relative mb-4">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                        <input 
                            type="text" 
                            placeholder="Search by gig title or creator name..." 
                            value={gigSearchTerm}
                            onChange={(e) => setGigSearchTerm(e.target.value)}
                            className="w-full pl-10 pr-4 py-2 border rounded-lg"
                        />
                    </div>
                    {filteredGigs.length === 0 ? <p className="text-slate-500">No gigs found.</p> : (
                         <div className="space-y-4">
                            {filteredGigs.map(gig => (
                                <div key={gig.id} className="border p-4 rounded-lg flex items-center justify-between">
                                    <div>
                                        <p className="font-bold">{gig.title}</p>
                                        <p className="text-sm text-slate-500">By: {gig.creatorName} | Status: {gig.status} | Price: {gig.currencySymbol || '$'}{gig.pay}</p>
                                    </div>
                                    <button onClick={() => onDeleteGig(gig.id)} className="bg-red-100 text-red-600 p-2 rounded-full hover:bg-red-200">
                                        <Trash2 size={16} />
                                    </button>
                                </div>
                            ))}
                         </div>
                    )}
                 </div>
            )}

            {adminTab === 'events' && (
                <div className="bg-white p-4 rounded-xl shadow-md border">
                   <h3 className="text-xl font-bold mb-4">Manage Events ({filteredEvents.length})</h3>
                   <div className="relative mb-4">
                       <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                       <input type="text" placeholder="Search events..." value={eventSearchTerm} onChange={(e) => setEventSearchTerm(e.target.value)} className="w-full pl-10 pr-4 py-2 border rounded-lg"/>
                   </div>
                   <div className="space-y-4">
                       {filteredEvents.map(event => (
                           <div key={event.id} className="border p-4 rounded-lg flex items-center justify-between">
                               <div className="flex items-center gap-4">
                                   <img src={event.images?.[0]} className="w-12 h-12 rounded object-cover" />
                                   <div>
                                       <p className="font-bold">{event.name}</p>
                                       <p className="text-sm text-slate-500">By: {event.storeName} | {new Date(event.date).toLocaleDateString()}</p>
                                   </div>
                               </div>
                               <button onClick={() => onDeleteProduct(event.id)} className="bg-red-100 text-red-600 p-2 rounded-full hover:bg-red-200"><Trash2 size={16}/></button>
                           </div>
                       ))}
                   </div>
                </div>
           )}

            {adminTab === 'reports' && (
                 <div className="bg-white p-4 rounded-xl shadow-md border">
                    <h3 className="text-xl font-bold mb-4">User Reports ({newReports.length} new)</h3>
                     {reports.length === 0 ? <p className="text-slate-500">No reports have been filed.</p> : (
                         <div className="space-y-2">
                             {reports.map(report => {
                                 const reporter = allStores.find(s => s.id === report.reporterId);
                                 const reported = allStores.find(s => s.id === report.reportedId);
                                 return (
                                     <div key={report.id} onClick={() => openReportDetails(report)} className={`border p-3 rounded-lg cursor-pointer ${report.status === 'new' ? 'border-l-4 border-red-500 bg-red-50' : 'bg-slate-50 text-slate-500'}`}>
                                         <p><span className="font-semibold">{reporter?.name || 'A user'}</span> reported <span className="font-semibold">{reported?.name || 'another user'}</span></p>
                                         <p className="text-xs text-slate-400">{formatTimeAgo(report.timestamp)}</p>
                                     </div>
                                 )
                             })}
                         </div>
                     )}
                 </div>
            )}
        </div>
    );
};


// --- Main App Component ---
export default function App() {
    // --- State Management ---
    const [user, setUser] = useState(null);
    const [isAdmin, setIsAdmin] = useState(false);
    const [admins, setAdmins] = useState([]);
    const [auth, setAuth] = useState(null);
    const [db, setDb] = useState(null);
    const [storage, setStorage] = useState(null);
    const [authState, setAuthState] = useState('loading'); // loading, unauthenticated, authenticated
    const [authError, setAuthError] = useState(null);
    const [currentUserLocation, setCurrentUserLocation] = useState(null);
    const [language, setLanguage] = useState(localStorage.getItem('gignileLanguage') || 'en'); // Load saved language
    
    const [gigs, setGigs] = useState([]);
    const [allProducts, setAllProducts] = useState([]);
    const [allStores, setAllStores] = useState([]);
    const [myStore, setMyStore] = useState(null);
    const [history, setHistory] = useState([]);
    const [bookings, setBookings] = useState([]);
    const [myProducts, setMyProducts] = useState([]);
    const snapshotListeners = useRef([]);
    const [orders, setOrders] = useState([]);
    const [chats, setChats] = useState([]);
    const [activeChatPartner, setActiveChatPartner] = useState(null);
    const [activeChatModalPartner, setActiveChatModalPartner] = useState(null);
    const [selectedMarketplaceState, setSelectedMarketplaceState] = useState(null); // Tracks the user's active browsing state
    const [lightboxImageUrl, setLightboxImageUrl] = useState(null); // NEW: State for Lightbox
    const [currencyInfo, setCurrencyInfo] = useState({ currencySymbol: '$', currency: 'USD' }); // NEW: Global currency state
    const [pendingMultiStoreData, setPendingMultiStoreData] = useState(null); // Holds data while user chooses delivery mode

    const [isLoading, setIsLoading] = useState(true);
    const [pageDetail, setPageDetail] = useState(null);

    const [modal, setModal] = useState(null); 
    const [selectedItem, setSelectedItem] = useState(null);
    const [isFabMenuOpen, setIsFabMenuOpen] = useState(false);
    const [notification, setNotification] = useState(null);
    const [notifications, setNotifications] = useState([]);
    const [isNotificationsOpen, setNotificationsOpen] = useState(false);
    const [unreadBellCount, setUnreadBellCount] = useState(0); // For general notifications
    const [unreadChatCount, setUnreadChatCount] = useState(0); // Specifically for chat
    const [reports, setReports] = useState([]);
    const [reportModalData, setReportModalData] = useState(null);
    const [isDarkMode, setIsDarkMode] = useState(false);
    const [page, setPage] = useState(localStorage.getItem('gignilePage') || 'marketplace'); // NEW: Load page state from localStorage

    const t = (key) => translations[language][key] || key;
    const pendingOrdersCount = useMemo(() => orders.filter(o => o.status === 'pending').length, [orders]);

    // --- Handle Android/Browser Back Button ---
    useEffect(() => {
        const handlePopState = (event) => {
            // When user presses Back, browser pops state. We must update React state to match.
            setHistory(prev => {
                if (prev.length > 0) {
                    const newHistory = [...prev];
                    const prevState = newHistory.pop(); // Get the breadcrumb we just came from
                    
                    // Restore the UI to the previous state
                    setPage(prevState.page);
                    setPageDetail(prevState.detail); 
                    
                    return newHistory;
                } else {
                    // If history is empty, we are at root. 
                    // If user presses back again, browser default handles it (exits/backgrounds app)
                    setPage('marketplace');
                    return [];
                }
            });
        };

        // Push an initial state on mount so the first back press works correctly if we start deep
        if (window.history.state === null) {
             window.history.replaceState({ page: 'marketplace' }, '');
        }

        window.addEventListener('popstate', handlePopState);
        return () => window.removeEventListener('popstate', handlePopState);
    }, []);

    const processGoodsGigCreation = async (mode, goodsByStore, uniqueStores, goodsOrders) => {
        if (!db || !user) return;
        const batch = writeBatch(db);
        const firstOrder = goodsOrders[0];

        if (mode === 'fast') {
            // Option A: Separate Gigs (One per store)
            for (const storeId in goodsByStore) {
                const storeOrders = goodsByStore[storeId];
                const fOrder = storeOrders[0];
                const title = `Deliver: ${storeOrders.length} items from ${fOrder.storeName}`;
                const description = `Pickup from ${fOrder.storeName} (${fOrder.pickupAddress}). Deliver to: ${fOrder.dropoffAddress}. Items: ${storeOrders.map(o => `${o.quantity}x ${o.productName}`).join(', ')}`;
                
                const gigRef = doc(collection(db, `artifacts/${appId}/public/data/gigs`));
                batch.set(gigRef, {
                    title, description, pay: 15.00, type: 'delivery',
                    orderIds: storeOrders.map(o => o.id),
                    storeName: fOrder.storeName, creatorId: user.uid,
                    creatorName: `${myStore.firstName} ${myStore.lastName}`,
                    status: 'open', createdAt: serverTimestamp(),
                    pickupLocation: fOrder.location, dropoffLocation: fOrder.dropoffLocation,
                    paymentType: 'coin', applicants: [], marketName: fOrder.marketName,
                    currencySymbol: fOrder.currencySymbol || '$', countryCode: fOrder.countryCode, images: fOrder.images || []
                });
                storeOrders.forEach(order => batch.update(doc(db, `artifacts/${appId}/users/${user.uid}/orders`, order.id), { status: 'processed', gigId: gigRef.id }));
                handleSendGigNotifications(title, fOrder.countryCode, fOrder.marketName);
            }
        } else {
            // Option B: Consolidated Gig (One Driver)
            let compositeDesc = `Multi-stop Pickup. Deliver to: ${firstOrder.dropoffAddress}.\n`;
            uniqueStores.forEach((sid, index) => {
                const sOrders = goodsByStore[sid];
                compositeDesc += `\n${index+1}. PICKUP: ${sOrders[0].storeName} (${sOrders[0].pickupAddress}). Items: ${sOrders.map(o => o.productName).join(', ')}`;
            });

            const gigRef = doc(collection(db, `artifacts/${appId}/public/data/gigs`));
            batch.set(gigRef, {
                title: `Multi-Store Delivery (${uniqueStores.length} Stops)`, 
                description: compositeDesc,
                pay: 15.00 + ((uniqueStores.length - 1) * 5), 
                type: 'delivery',
                orderIds: goodsOrders.map(o => o.id),
                storeName: "Multiple Stores", creatorId: user.uid,
                creatorName: `${myStore.firstName} ${myStore.lastName}`,
                status: 'open', createdAt: serverTimestamp(),
                pickupLocation: firstOrder.location, 
                dropoffLocation: firstOrder.dropoffLocation,
                paymentType: 'coin', applicants: [], marketName: firstOrder.marketName,
                currencySymbol: firstOrder.currencySymbol || '$', countryCode: firstOrder.countryCode, images: firstOrder.images || []
            });
            goodsOrders.forEach(order => batch.update(doc(db, `artifacts/${appId}/users/${user.uid}/orders`, order.id), { status: 'processed', gigId: gigRef.id }));
            handleSendGigNotifications(`Multi-Store Delivery`, firstOrder.countryCode, firstOrder.marketName);
        }

        await batch.commit();
        setNotification(t('notif.appSubmitted')); // Reusing relevant success message
        setTimeout(() => setNotification(null), 3000);
        setPendingMultiStoreData(null); // Close modal
    };

    // --- Bulk Gig Creation with Multi-Store Logic ---
    const handleCreateGigFromOrders = async (selectedOrderIds) => {
        if (!db || !user || selectedOrderIds.length === 0) return;

        const batch = writeBatch(db);
        const selectedOrders = orders.filter(o => selectedOrderIds.includes(o.id));
        
        // Define these variables first!
        const serviceOrders = selectedOrders.filter(o => o.type === 'service');
        const goodsOrders = selectedOrders.filter(o => o.type === 'goods');

        // 1. Handle Services (Always separate gigs per provider)
        const servicesByStore = serviceOrders.reduce((acc, order) => {
            if (!acc[order.storeId]) acc[order.storeId] = [];
            acc[order.storeId].push(order);
            return acc;
        }, {});

        for (const storeId in servicesByStore) {
            const storeOrders = servicesByStore[storeId];
            const firstOrder = storeOrders[0];
            const title = `Service: ${storeOrders.length}x items (${firstOrder.productName}...)`;
            const totalPay = storeOrders.reduce((sum, o) => sum + o.price, 0);

            const gigRef = doc(collection(db, `artifacts/${appId}/public/data/gigs`));
            batch.set(gigRef, {
                title, 
                description: `Booking for: ${storeOrders.map(o => `${o.quantity}x ${o.productName}`).join(', ')}. Location: ${firstOrder.dropoffAddress}.`,
                pay: totalPay, type: 'service',
                orderIds: storeOrders.map(o => o.id),
                storeName: firstOrder.storeName, creatorId: user.uid,
                creatorName: `${myStore.firstName} ${myStore.lastName}`,
                workerId: storeId, workerName: firstOrder.storeName,
                status: 'in-progress', createdAt: serverTimestamp(),
                location: firstOrder.location, paymentType: 'coin',
                applicants: [], marketName: firstOrder.marketName,
                currencySymbol: firstOrder.currencySymbol || '$', countryCode: firstOrder.countryCode, images: firstOrder.images || []
            });

            storeOrders.forEach(order => batch.update(doc(db, `artifacts/${appId}/users/${user.uid}/orders`, order.id), { status: 'processed', gigId: gigRef.id }));
                batch.set(doc(collection(db, `artifacts/${appId}/users/${storeId}/notifications`)), { message: t('msg.newService').replace('{{title}}', title), read: false, timestamp: serverTimestamp(), type: 'booking' });
        }
        
        // Commit Services immediately so we can process Goods separately (if utilizing modal flow)
        if (serviceOrders.length > 0) {
            await batch.commit();
        }

        // 2. Handle Goods (Logic for Single vs Multi-Store)
        if (goodsOrders.length > 0) {
            const goodsByStore = goodsOrders.reduce((acc, order) => {
                if (!acc[order.storeId]) acc[order.storeId] = [];
                acc[order.storeId].push(order);
                return acc;
            }, {});

            const uniqueStores = Object.keys(goodsByStore);

            if (uniqueStores.length > 1) {
                // PAUSE: Open Modal for user choice
                setPendingMultiStoreData({ goodsByStore, uniqueStores, goodsOrders });
                return; // Stop here, wait for modal callback
            } else {
                // Single store: Process immediately as 'fast' (separate/single)
                await processGoodsGigCreation('fast', goodsByStore, uniqueStores, goodsOrders);
            }
        } else if (serviceOrders.length > 0) {
             // If only services, show success
             setNotification(t('notif.appSubmitted')); // Reusing success message or create new one
             setTimeout(() => setNotification(null), 3000);
        }
    };

    // CRITICAL FIX: This ensures that if a modal is open (selectedItem is not null),
    // it will receive real-time updates when the background data lists (gigs or allProducts) change.
    // This solves the bug where a new gig applicant doesn't appear until the modal is closed and reopened.
    useEffect(() => {
        if (selectedItem && selectedItem.itemType === 'gig') {
            const newVersion = gigs.find(g => g.id === selectedItem.id);
            if (newVersion) {
                setSelectedItem(newVersion);
            }
        }
    }, [gigs]); // This effect runs every time the global 'gigs' list updates

    useEffect(() => {
        if (selectedItem && selectedItem.itemType === 'product') {
            const newVersion = allProducts.find(p => p.id === selectedItem.id);
            if (newVersion) {
                setSelectedItem(newVersion);
            }
        }
    }, [allProducts]); // This effect runs every time the global 'allProducts' list updates

    // NEW: Save language preference to localStorage whenever it changes
    const setAndSaveLanguage = useCallback((newLang) => {
        setLanguage(newLang);
        localStorage.setItem('gignileLanguage', newLang);
    }, []);
    
    const handleNotificationClick = () => {
        setNotificationsOpen(!isNotificationsOpen);
        if (!isNotificationsOpen && db && user) {
            // Mark all as read when opening the panel
            const batch = writeBatch(db);
            notifications.forEach(n => {
                if (!n.read) {
                    const notifRef = doc(db, `artifacts/${appId}/users/${user.uid}/notifications`, n.id);
                    batch.update(notifRef, { read: true });
                }
            });
            batch.commit().catch(console.error);
        }
    };
    
    const handleNotificationItemClick = (notification) => {
        setNotificationsOpen(false); // Close the panel

        // Mark this specific notification as read
        if (!notification.read && db && user) {
            const notifRef = doc(db, `artifacts/${appId}/users/${user.uid}/notifications`, notification.id);
            updateDoc(notifRef, { read: true }).catch(console.error);
        }

        // Navigate based on notification content
        if (notification.chatPartnerId) {
            // Open chat. `handleStartChat` handles modal vs. page logic.
            // We'll open in a modal by default unless already on the chats page.
            const openInModal = page !== 'chats';
            handleStartChat(notification.chatPartnerId, openInModal); 

        } else if (notification.gigId) {
            // Find the gig in the local state
            const gigToOpen = gigs.find(g => g.id === notification.gigId);
            if (gigToOpen) {
                // If found, open the GigDetail modal with this gig's data
                openModal('gigDetail', gigToOpen);
            } else {
                // Fallback if gig isn't in the current list (e.g., archived or in a different market)
                console.warn(`Could not find gig with ID: ${notification.gigId} in local list. Navigating to My Gigs page.`);
                setPage('my-gigs'); // Navigate to the 'My Gigs' page as a fallback
            }
        }
    };

    // --- Geolocation ---
    useEffect(() => {
        let isMounted = true;
        if (navigator.geolocation) {
            // Get the user's location once, instead of watching for changes, to prevent constant re-renders.
            navigator.geolocation.getCurrentPosition(
                (position) => {
                    if (isMounted) {
                        const { latitude, longitude, accuracy } = position.coords;
                        setCurrentUserLocation({ latitude, longitude, accuracy });
                    }
                },
                (error) => {
                     if (isMounted) {
                        console.warn(`Geolocation failed: ${error.message}. Using a default location.`);
                        if (!currentUserLocation) { // Only set default if no location is set at all
                            setCurrentUserLocation({ latitude: 35.7595, longitude: -5.8340, accuracy: 1000 }); // Default to Tangier
                        }
                    }
                },
                {
                    enableHighAccuracy: true,
                    timeout: 10000,
                    maximumAge: 60000 // Allow caching location for 1 minute to improve performance
                }
            );
        } else {
            console.warn("Geolocation is not supported. Using a default location.");
            if (isMounted) {
                setCurrentUserLocation({ latitude: 35.7595, longitude: -5.8340, accuracy: 1000 }); // Default to Tangier
            }
        }

        return () => {
            isMounted = false;
        };
    }, []);


    // --- Firebase Initialization and Auth ---
    useEffect(() => {  
        try { 
            const app = initializeApp(firebaseConfig); 
            const authInstance = getAuth(app); 
            const dbInstance = getFirestore(app);
            const storageInstance = getStorage(app);
            setAuth(authInstance);
            setDb(dbInstance);
            setStorage(storageInstance);
            
            onAuthStateChanged(authInstance, (currentUser) => { 
                if (currentUser) {
                    setUser(currentUser);
                    setAuthState('authenticated');
                } else {
                    setUser(null);
                    setIsAdmin(false);
                    setAdmins([]);
                    setAuthState('unauthenticated');
                }
            }); 
        } catch (error) { 
            console.error("Firebase init error:", error); 
            setAuthState('unauthenticated');
        } 
    }, []);

    // --- Firestore Data Listeners ---
    useEffect(() => {
        if (authState !== 'authenticated' || !db || !user) { if(authState !== 'loading') setIsLoading(false); return; }

        snapshotListeners.current = []; // Clear old listeners
        
        const unsubAdmins = onSnapshot(collection(db, `artifacts/${appId}/public/data/admins`), (snapshot) => {
            const adminUIDs = snapshot.docs.map(doc => doc.id);
            setAdmins(adminUIDs);
            setIsAdmin(user.email === ADMIN_EMAIL || adminUIDs.includes(user.uid));
        });

        const unsubStore = onSnapshot(doc(db, `artifacts/${appId}/public/data/stores`, user.uid), d => {
            if (!d.exists()) {
                // This handles users who were rejected and had their data deleted.
                // If they try to log in, they will be authenticated but have no store document.
                // We log them out to prevent access and show an appropriate message.
                signOut(auth);
                setAuthError("Please register again or contact support if you believe this is an error.");
                setIsLoading(false);
                return;
            }
            const storeData = {id:d.id, ...d.data()};
            setMyStore(storeData);
            setCurrencyInfo(getCurrencyInfo(storeData.countryCode)); // Set currency based on user's country
            if (storeData && !isAdmin) {
                setNotification(`Welcome! You are browsing markets in ${storeData.country}.`);
                setTimeout(() => setNotification(null), 3000);
            }
            setIsLoading(false); 
        });

        const unsubMyProducts = onSnapshot(query(collection(db, `artifacts/${appId}/public/data/products`), where('storeId', '==', user.uid)), s => setMyProducts(s.docs.map(d=>({id:d.id, ...d.data()}))));
        const unsubOrders = onSnapshot(collection(db, `artifacts/${appId}/users/${user.uid}/orders`), s => setOrders(s.docs.map(d=>({id:d.id, ...d.data()})).sort((a,b)=>(b.createdAt?.toMillis()||0)-(a.createdAt?.toMillis()||0))));
        const unsubBookings = onSnapshot(collection(db, `artifacts/${appId}/public/data/bookings`), s => setBookings(s.docs.map(d=>({id:d.id, ...d.data()})))); // NEW Listener
        const unsubChats = onSnapshot(query(collection(db, `artifacts/${appId}/public/data/chats`), where("participants", "array-contains", user.uid)), (snapshot) => { setChats(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }))); });
        const unsubNotifications = onSnapshot(query(collection(db, `artifacts/${appId}/users/${user.uid}/notifications`), orderBy('timestamp', 'desc')), s => {
            const newNotifications = s.docs.map(d=>({id:d.id, ...d.data()}));
            setNotifications(newNotifications);
            // NEW: Calculate both counts
            const allUnread = newNotifications.filter(n => !n.read);
            const chatUnread = allUnread.filter(n => n.chatPartnerId); // Has chatPartnerId
            const bellUnread = allUnread.filter(n => !n.chatPartnerId); // Does NOT have chatPartnerId (e.g., gigs, approvals)
            
            setUnreadChatCount(chatUnread.length);
            setUnreadBellCount(bellUnread.length);

            if (bellUnread.length > 0 && document.hidden) {
                setNotification(bellUnread[0].message); // Only show non-chat notifications as a toast
                setTimeout(() => setNotification(null), 5000);
            }
        });
        const unsubAllStores = onSnapshot(collection(db, `artifacts/${appId}/public/data/stores`), s => setAllStores(s.docs.map(d=>({id:d.id, ...d.data()}))));
        
        const unsubReports = onSnapshot(query(collection(db, `artifacts/${appId}/public/data/reports`), orderBy('timestamp', 'desc')), s => {
            setReports(s.docs.map(d => ({id: d.id, ...d.data()})));
        });

        // Add all listeners to the ref array
        snapshotListeners.current.push(unsubStore, unsubMyProducts, unsubOrders, unsubBookings, unsubNotifications, unsubChats, unsubAllStores, unsubAdmins, unsubReports);

        return () => { 
            // Detach all listeners on unmount
            snapshotListeners.current.forEach(unsub => unsub()); 
        };
    }, [authState, db, user]);

    useEffect(() => {
        if ((!myStore?.countryCode && !isAdmin) || !db) return; // Only filter by country
        let gigsQuery, productsQuery;

        if (isAdmin) {
            // Admin gets all data
            gigsQuery = query(collection(db, `artifacts/${appId}/public/data/gigs`));
            productsQuery = query(collection(db, `artifacts/${appId}/public/data/products`));
        } else {
            // Regular user gets country-specific data
            gigsQuery = query(collection(db, `artifacts/${appId}/public/data/gigs`), where("countryCode", "==", myStore.countryCode));
            productsQuery = query(collection(db, `artifacts/${appId}/public/data/products`), where("countryCode", "==", myStore.countryCode), where("status", "==", "approved"));
        }
        
        const unsubGigs = onSnapshot(gigsQuery, s => {
            const newGigs = s.docs.map(d=>({id:d.id, ...d.data(), itemType: 'gig'})).sort((a,b)=>(b.createdAt?.toMillis()||0)-(a.createdAt?.toMillis()||0));
            setGigs(newGigs);
        });

        const unsubProducts = onSnapshot(productsQuery, s => { setAllProducts(s.docs.map(d=>({id:d.id, ...d.data(), itemType: 'product'}))); });

        snapshotListeners.current.push(unsubGigs, unsubProducts);
        return () => { unsubGigs(); unsubProducts(); };
    }, [myStore, db, user, isAdmin]);

    useEffect(() => {
        // NEW: Persist page state to localStorage whenever it changes
        localStorage.setItem('gignilePage', page);
    }, [page]);

    // --- Navigation Handlers ---
    const navigate = (newPage, detail = null) => {
        if (newPage !== page) {
            window.history.pushState({ page: newPage }, '');            
            setHistory(prev => [...prev, { page, detail: pageDetail }]);
            setPage(newPage);
        }
        if (detail !== null) {
            setPageDetail(detail);
        }
    };

    const goBack = () => {
        // Trigger the browser back action. This fires the 'popstate' event we added above.
        if (history.length > 0) {
            window.history.back();
        } else {
            setPage('marketplace');
        }
    };

    const resetNavigation = () => {
        setHistory([]); // Clear breadcrumbs
        // Clear browser history stack for the app session (optional, effectively resets flow)
        window.history.replaceState({ page: 'marketplace' }, '');
        setPage('marketplace');
    };
    
    // --- Handlers ---
    const handleSendGigNotifications = async (gigTitle, countryCode, marketName) => {
        if (!db || !user) return;
        
        const driversQuery = query(
            collection(db, `artifacts/${appId}/public/data/stores`),
            where("isDeliveryDriver", "==", true),
            where("countryCode", "==", countryCode)
        );

        try {
            const querySnapshot = await getDocs(driversQuery);
            const batch = writeBatch(db);

            querySnapshot.forEach(driverDoc => {
                const driverId = driverDoc.id;
                // Don't notify the user who created the gig
                if (driverId !== user.uid) {
                    const notificationRef = doc(collection(db, `artifacts/${appId}/users/${driverId}/notifications`));
                    batch.set(notificationRef, {
                        message: t('msg.newGig').replace('{{market}}', marketName).replace('{{title}}', gigTitle),
                        read: false,
                        timestamp: serverTimestamp()
                    });
                    
                }
            });

            await batch.commit();
        } catch (error) {
            console.error("Error sending gig notifications:", error);
        }
    };
    
    const handleManageStore = async (storeData) => {
        if (!db || !user) return;
        const storeRef = doc(db, `artifacts/${appId}/public/data/stores`, user.uid);
    
        // Data should already be sanitized by LocationPickerModal, 
        // but double-check/deep copy just in case.
        const sanitizedData = JSON.parse(JSON.stringify(storeData)); 
        
        if (sanitizedData.location && typeof sanitizedData.location === 'object') {
            const newLocation = {};
            if (typeof sanitizedData.location.address === 'string') {
                newLocation.address = sanitizedData.location.address;
            }
            if (sanitizedData.location.coords && 
                typeof sanitizedData.location.coords.latitude === 'number' && 
                typeof sanitizedData.location.coords.longitude === 'number') {
                newLocation.coords = { 
                    latitude: sanitizedData.location.coords.latitude, 
                    longitude: sanitizedData.location.coords.longitude 
                };
            }
            if (newLocation.coords) {
                 sanitizedData.location = newLocation;
            } else {
                 delete sanitizedData.location;
            }
        } else {
             delete sanitizedData.location;
        }
    
        if (myStore) { // Updating existing store
            await updateDoc(storeRef, sanitizedData);
        } else { // Creating a new store
            await setDoc(storeRef, { 
                ...sanitizedData, 
                ownerId: user.uid, 
                sales: 0, 
                ratingCount: 0, 
                totalRating: 0, 
                createdAt: serverTimestamp(), 
                coinBalance: 0, 
                status: 'pending_approval' 
            });
        }
        setModal(null);
    };
    const handleManageProduct = async (productData) => { if (!db || !user || !myStore ) return; const { id, ...data } = productData; 
        let productPayload = { 
            storeId: user.uid, 
            storeName: myStore.name, 
            location: myStore.location || null, 
            marketName: myStore.marketName, 
            countryCode: myStore.countryCode, 
            currencySymbol: currencyInfo.currencySymbol,
            ...data 
        };
        if (id) { await updateDoc(doc(db, `artifacts/${appId}/public/data/products`, id), productPayload); } else { productPayload.status = 'pending'; await addDoc(collection(db, `artifacts/${appId}/public/data/products`), productPayload); setNotification(t('notif.productSubmitted')); setTimeout(() => setNotification(null), 3000); } setModal(null); };
    
    const handleUpdateEvent = async (eventId, updatedData) => {
        if (!db || !user) return;
        try {
            const eventRef = doc(db, `artifacts/${appId}/public/data/products`, eventId);
            await updateDoc(eventRef, updatedData);
            setNotification(t('notif.eventUpdated'));
            setModal(null);
        } catch (e) {
            console.error("Update error", e);
            setNotification(t('notif.eventUpdateFailed'));
        }
        setTimeout(() => setNotification(null), 3000);
    };

    const handleCreateEvent = async (eventData) => {
        if (!db || !user || !myStore) return;
        
        // Events are stored as 'products' with type='event' for simplicity in searching
        await addDoc(collection(db, `artifacts/${appId}/public/data/products`), {
            ...eventData,
            storeId: user.uid,
            storeName: myStore.name,
            marketName: myStore.marketName,
            countryCode: myStore.countryCode,
            currencySymbol: currencyInfo.currencySymbol,
            status: 'approved', // Auto-approve events for now or 'pending'
            isPublished: true,
            createdAt: serverTimestamp()
        });
        setModal(null);
        setNotification(t('notif.eventPublished'));
        setTimeout(()=>setNotification(null), 3000);
    };

    // Allow creator to delete/close their event
    const handleCloseEvent = async (eventId) => {
        if (window.confirm("Are you sure you want to cancel/close this event? This will remove it from the marketplace.")) {
            try {
                const batch = writeBatch(db);
                // Set isDeleted or status to closed
               const eventRef = doc(db, `artifacts/${appId}/public/data/products`, eventId);
               batch.update(eventRef, {
                    isDeleted: true,
                   status: 'cancelled',
                   isPublished: false
                });

                // Find ALL Tickets (Gigs) for this event and mark them Cancelled
               const ticketsQuery = query(
                   collection(db, `artifacts/${appId}/public/data/bookings`), 
                   where('eventId', '==', eventId),
                   where('type', '==', 'event-booking')
               );
               const ticketsSnap = await getDocs(ticketsQuery);
               
               ticketsSnap.forEach(t => {
                   batch.update(t.ref, { status: 'cancelled' });
               });

               await batch.commit();

                setNotification(t('notif.eventClosed'));
                setModal(null);
            } catch (e) {
                console.error("Error closing event", e);
                setNotification(t('notif.eventCloseFailed'));
            }
            setTimeout(()=>setNotification(null), 3000);
        }
    };

    const handleRemoveAttendee = async (gigId, eventId, userId, qty) => {
        if (!window.confirm("Remove this attendee? They will be notified.")) return;
        try {
            // 1. Delete the booking ticket (gig)
            await deleteDoc(doc(db, `artifacts/${appId}/public/data/bookings`, gigId));
            
            // 2. Restore ticket count on the event product
            const productRef = doc(db, `artifacts/${appId}/public/data/products`, eventId);
            const productSnap = await getDoc(productRef);
            if(productSnap.exists()) {
                const currentCount = productSnap.data().bookingsCount || 0;
                await updateDoc(productRef, {
                     bookingsCount: Math.max(0, currentCount - qty)
                });
            }

            // 3. Notify the user
            await addDoc(collection(db, `artifacts/${appId}/users/${userId}/notifications`), {
                message: t('msg.bookingCancelled'),
                read: false,
                timestamp: serverTimestamp()
            });
            
            setNotification(t('notif.attendeeRemoved'));
        } catch (e) {
            console.error("Error removing attendee", e);
            alert("Failed to remove attendee.");
        }
    };

    const handleBookEvent = async (event, qty = 1, guestNames = []) => {
       if (!db || !user || !myStore) { 
           alert("Please login first to book tickets."); 
           return; 
       }
              // Safely parse limits to avoid string vs number issues (fixes "nothing happens")
       const limit = event.ticketLimit ? parseInt(event.ticketLimit, 10) : Infinity;
       const currentCount = parseInt(event.bookingsCount || 0, 10);

       if (currentCount + qty > limit) {
           alert(`Sorry, not enough tickets available. Only ${Math.max(0, limit - currentCount)} left.`);
           return;
       }

       // 2. Confirmation
       if (window.confirm(`Confirm ${event.price === 0 ? 'attendance' : 'booking'} for "${event.name}"?`)) {
           try {
               const bookingRef = doc(collection(db, `artifacts/${appId}/public/data/bookings`));
               const totalPrice = (event.price || 0) * qty;

               // 3. Create Ticket (This adds it to "My Events" for User B and "Attendees" for User A)
               await setDoc(bookingRef, {
                   title: `Event Booking: ${event.name}`,
                   description: `Ticket for ${event.name} (${qty} pax).`,
                   pay: totalPrice,
                   orderQuantity: qty,
                   guestNames, // Save the guest names array
                   type: 'event-booking', 
                   eventId: event.id, 
                   eventDate: event.date, // Critical for sorting
                   eventEndDate: event.endDate || event.date, // Save End Date for "Past" filtering
                   storeName: event.storeName, // Host Name
                   creatorId: user.uid, // Attendee ID (User B)
                   creatorName: `${myStore.firstName} ${myStore.lastName}`,
                   workerId: event.storeId, // Host ID (User A)
                   workerName: event.storeName,
                   status: 'in-progress', // Active Ticket
                   createdAt: serverTimestamp(),
                   location: event.location,
                   paymentType: totalPrice === 0 ? 'free' : 'cash', 
                   marketName: event.marketName,
                   currencySymbol: event.currencySymbol || '$',
                   countryCode: event.countryCode,
                   images: event.images || []
               });

               // 4. Update Event Ticket Count
               const productRef = doc(db, `artifacts/${appId}/public/data/products`, event.id);
               await updateDoc(productRef, {
                    bookingsCount: currentCount + qty
               }).catch(e => console.warn("Count update error", e));

               // 5. Notify Host (User A) directly
               await addDoc(collection(db, `artifacts/${appId}/users/${event.storeId}/notifications`), {
                   message: t('msg.newAttendee').replace('{{name}}', myStore.firstName).replace('{{qty}}', qty).replace('{{event}}', event.name),
                   bookingId: bookingRef.id,
                   read: false,
                   timestamp: serverTimestamp(),
                   type: 'booking'
               });

               // --- Send Email to Host ---
               const hostStore = allStores.find(s => s.id === event.storeId);
               if (hostStore?.contactEmail) {
                   addDoc(collection(db, `artifacts/${appId}/public/data/mail`), {
                       to: [hostStore.contactEmail],
                       message: {
                           subject: `New Booking: ${event.name}`,
                           html: `<p><strong>${myStore.firstName} ${myStore.lastName}</strong> has booked <strong>${qty}</strong> ticket(s) for your event <strong>${event.name}</strong>.</p>`
                       },
                       createdAt: serverTimestamp()
                   }).catch(err => console.error("Failed to queue email:", err));
               }

               setNotification(t('notif.bookingConfirmed'));
               // Note: We don't close the modal here so the user sees the "You are going!" status update immediately.
           } catch (error) {
               console.error("Booking failed:", error);
               setNotification(t('notif.bookingFailed'));
           }
           setTimeout(()=>setNotification(null), 4000);
       }
    };
    
    const handleCreateGig = async (newGig) => {
        if (!db || !user || !myStore) { alert("Your profile isn't loaded yet. Please try again in a moment."); return; }
        const creatorName = `${myStore.firstName} ${myStore.lastName}`;
        
        // Sanitize location data to remove unsupported fields before sending to Firestore.
        const gigData = { ...newGig };
        if (gigData.location && gigData.location.coords) {
            const { latitude, longitude } = gigData.location.coords;
            gigData.location.coords = { latitude, longitude };
        }

        await addDoc(collection(db, `artifacts/${appId}/public/data/gigs`), { 
            ...gigData, 
            type: 'task', // Ensure gig type is set for manually created gigs
            creatorId: user.uid, 
            scheduleType: newGig.scheduleType, // NEW: Save schedule type
            scheduledAt: newGig.scheduledAt, // NEW: Save schedule time
            creatorName, 
            status: 'open', 
            createdAt: serverTimestamp(), 
            applicants: [],
            currencySymbol: currencyInfo.currencySymbol, // Save currency symbo 
            marketName: myStore.marketName, 
            images: gigData.images || [], // <-- FIX: Ensure images are saved
            countryCode: myStore.countryCode 
        });
        await handleSendGigNotifications(newGig.title, myStore.countryCode, myStore.marketName);
        setModal(null);
    };
    const handleApplyForGig = async(gigId, creatorId, gigTitle, proposedPrice) => {
        if (!db || !user || !myStore) return;
        
        // Ensure we have the user's current device location to save with the application
        if (!currentUserLocation) {
            alert("Could not get your current location. Please ensure location services are enabled and try again.");
            return;
        }

        const gigRef = doc(db, `artifacts/${appId}/public/data/gigs`, gigId);
        await runTransaction(db, async (transaction) => {
            const gigDoc = await transaction.get(gigRef);
            if (!gigDoc.exists()) { throw "Document does not exist!"; }
            const applicants = gigDoc.data().applicants || [];
            if (!applicants.some(app => app.uid === user.uid)) {
                transaction.update(gigRef, { 
                    applicants: [...applicants, { 
                        uid: user.uid, 
                        name: myStore?.name || 'A User', 
                        sales: myStore?.sales || 0,
                        proposedPrice: proposedPrice,
                        location: currentUserLocation // Save applicant's CURRENT DEVICE location
                    }] 
                });
            }
        });
        await addDoc(collection(db, `artifacts/${appId}/users/${creatorId}/notifications`), {
            message: t('msg.gigApplied').replace('{{name}}', myStore?.name || 'A user').replace('{{gig}}', gigTitle).replace('{{price}}', `$${proposedPrice.toFixed(2)}`),
            gigId: gigId,
            read: false,
            timestamp: serverTimestamp()
        });

        // --- Send Email to Creator ---
        const creatorStore = allStores.find(s => s.id === creatorId);
        if (creatorStore?.contactEmail) {
            addDoc(collection(db, `artifacts/${appId}/public/data/mail`), {
                to: [creatorStore.contactEmail],
                message: {
                    subject: `New Application for: ${gigTitle}`,
                    html: `<p><strong>${myStore?.name}</strong> has applied for your gig <strong>"${gigTitle}"</strong> with an offer of <strong>$${proposedPrice.toFixed(2)}</strong>.</p><p>Check your dashboard to accept or chat.</p>`
                },
                createdAt: serverTimestamp()
            }).catch(err => console.error("Failed to queue email:", err));
        }

        setModal(null);
        setNotification(t('notif.appSubmitted'));
        setTimeout(() => setNotification(null), 3000);
    };
    const handleChooseApplicant = async(gigId, worker, gigTitle, acceptedPrice) => {
        //if (!db || !user || !myStore) return;

        // Check if buyer has enough coins
        //if (myStore.coinBalance < acceptedPrice) {
          //  setNotification("Insufficient coins to hire this worker. Please buy more coins.");
           // setTimeout(() => setNotification(null), 3000);
            //return;
        //}

        const gigRef = doc(db, `artifacts/${appId}/public/data/gigs`, gigId);
        //const buyerStoreRef = doc(db, `artifacts/${appId}/public/data/stores`, user.uid);

        const batch = writeBatch(db);

        // 1. Hold payment in escrow (debit buyer)
        //batch.update(buyerStoreRef, {
          //  coinBalance: myStore.coinBalance - acceptedPrice
        //});

        // 2. Update gig status and lock in details
        batch.update(gigRef, {
            status: 'in-progress',
            workerId: worker.uid,
            workerName: worker.name,
            acceptedAt: serverTimestamp(),
            pay: acceptedPrice, // Update the gig's pay to the agreed-upon price
            paymentType: 'coin', // All negotiated gigs are paid in coin
            paymentStatus: 'held-in-escrow' // Set payment status to held
        });
        
        // 3. Notify worker
        batch.set(doc(collection(db, `artifacts/${appId}/users/${worker.uid}/notifications`)), {
            message: t('msg.gigChosen').replace('{{gig}}', gigTitle).replace('{{price}}', acceptedPrice.toFixed(2)),
            gigId: gigId,
            read: false,
            timestamp: serverTimestamp()
        });

        // --- Send Email to Worker ---
        // Note: 'worker' object here comes from the applicants array, so we look up full store for email
        const workerStore = allStores.find(s => s.id === worker.uid);
        if (workerStore?.contactEmail) {
            // We use a separate doc creation for email as it's a different collection than the batch
            addDoc(collection(db, `artifacts/${appId}/public/data/mail`), {
                to: [workerStore.contactEmail],
                message: {
                    subject: `You're Hired! ${gigTitle}`,
                    html: `<p>Congratulations! You have been selected for the gig <strong>"${gigTitle}"</strong>.</p><p>Agreed Price: <strong>${acceptedPrice.toFixed(2)}</strong></p><p>Please start the task or contact the poster immediately.</p>`
               },
                createdAt: serverTimestamp()
            }).catch(err => console.error("Failed to queue email:", err));
        }
        // ----------------------------
        
        await batch.commit();
        setModal(null);
    }
    const handleRate = async (order, rating) => {
        if (!db || !user) return;
        const orderRef = doc(db, `artifacts/${appId}/users/${user.uid}/orders`, order.id);
        const storeRef = doc(db, `artifacts/${appId}/public/data/stores`, order.storeId);
        await runTransaction(db, async (transaction) => {
            const storeDoc = await transaction.get(storeRef);
            if (!storeDoc.exists()) { throw "Store does not exist!"; }
            const newRatingCount = (storeDoc.data().ratingCount || 0) + 1;
            const newTotalRating = (storeDoc.data().totalRating || 0) + rating;
            transaction.update(storeRef, { ratingCount: newRatingCount, totalRating: newTotalRating });
            transaction.update(orderRef, { rated: true });
        });
        setNotification(t('notif.ratingThanks'));
        setTimeout(() => setNotification(null), 3000);
    };
    const handleRateWorker = async (gigId, workerId, rating) => {
        if (!db || !user) return;
        const gigRef = doc(db, `artifacts/${appId}/public/data/gigs`, gigId);
        const workerStoreRef = doc(db, `artifacts/${appId}/public/data/stores`, workerId);
        
        await runTransaction(db, async (transaction) => {
            const workerStoreDoc = await transaction.get(workerStoreRef);
            if (!workerStoreDoc.exists()) { throw "Worker profile does not exist!"; }
            
            const newRatingCount = (workerStoreDoc.data().ratingCount || 0) + 1;
            const newTotalRating = (workerStoreDoc.data().totalRating || 0) + rating;
            const newSales = (workerStoreDoc.data().sales || 0) + 1;

            transaction.update(workerStoreRef, { 
                ratingCount: newRatingCount, 
                totalRating: newTotalRating,
                sales: newSales 
            });
            transaction.update(gigRef, { workerRated: true });
        });

        setNotification("Thanks for your rating!");
        setTimeout(() => setNotification(null), 3000);
    };

    const handleRateCreator = async (gigId, creatorId, rating) => {
        if (!db || !user) return;
        const gigRef = doc(db, `artifacts/${appId}/public/data/gigs`, gigId);
        const creatorStoreRef = doc(db, `artifacts/${appId}/public/data/stores`, creatorId);

        await runTransaction(db, async (transaction) => {
            const creatorStoreDoc = await transaction.get(creatorStoreRef);
            if (!creatorStoreDoc.exists()) { throw "Creator profile not found."; }

            const newRatingCount = (creatorStoreDoc.data().ratingCount || 0) + 1;
            const newTotalRating = (creatorStoreDoc.data().totalRating || 0) + rating;

            transaction.update(creatorStoreRef, { ratingCount: newRatingCount, totalRating: newTotalRating });
            transaction.update(gigRef, { creatorRated: true });
        });

        setNotification("Thanks for your rating!");
        setTimeout(() => setNotification(null), 3000);
    };


    const handleBuyCoins = async (amount) => {
        if(!db || !user || !myStore) return;
        const storeRef = doc(db, `artifacts/${appId}/public/data/stores`, user.uid);
        await updateDoc(storeRef, {
            coinBalance: (myStore.coinBalance || 0) + amount
        });
        setModal(null);
        setNotification(t('notif.coinsPurchased').replace('{{amount}}', amount));
        setTimeout(() => setNotification(null), 3000);
    };
    const handleTransferCoins = async (recipientEmail, amount) => {
        if (!db || !user || !myStore) throw new Error("Could not process transfer. Please try again.");
        const usersRef = collection(db, `artifacts/${appId}/public/data/stores`);
        const q = query(usersRef, where("contactEmail", "==", recipientEmail));
        const querySnapshot = await getDocs(q);

        if (querySnapshot.empty) { throw new Error("Recipient not found."); }
        const recipientDoc = querySnapshot.docs[0];
        const recipientId = recipientDoc.id;
        const recipientData = recipientDoc.data();

        if(recipientId === user.uid) { throw new Error("You cannot transfer coins to yourself."); }
        
        const senderRef = doc(db, `artifacts/${appId}/public/data/stores`, user.uid);
        const recipientRef = doc(db, `artifacts/${appId}/public/data/stores`, recipientId);

        await runTransaction(db, async (transaction) => {
            const senderDoc = await transaction.get(senderRef);
            if (!senderDoc.exists() || (senderDoc.data().coinBalance || 0) < amount) {
                throw new Error("Insufficient funds.");
            }
            
            const newSenderBalance = (senderDoc.data().coinBalance || 0) - amount;
            transaction.update(senderRef, { coinBalance: newSenderBalance });

            const newRecipientBalance = (recipientData.coinBalance || 0) + amount;
            transaction.update(recipientRef, { coinBalance: newRecipientBalance });
        });
    };

    const handleCloseGig = async (gigId) => { if(!db || !user) return; await updateDoc(doc(db, `artifacts/${appId}/public/data/gigs`, gigId), { status: 'closed' }); setModal(null); };
    // 1. Updated Delete Handler: Now opens the confirmation modal
    const handleDeleteGig = async (gigId) => {
        if(!db || !user) return;
        const gigToDelete = gigs.find(g => g.id === gigId);
        if (!gigToDelete) return;
        
        openModal('confirmDeleteGig', {
            id: gigId, 
            name: gigToDelete.title,
            onConfirm: () => executeDeleteGig(gigId)
        });
    };

    // 2. New Execute Function: Contains the actual deletion logic
    const executeDeleteGig = async (gigId) => {
        try {
            await deleteDoc(doc(db, `artifacts/${appId}/public/data/gigs`, gigId));
        } catch (error) {
            console.error("Error deleting gig:", error);
         }
        setModal(null); // Close confirmation modal
     };
    // 1. Updated Delete Handler: Now opens the confirmation modal
    const handleDeleteProduct = async (productId) => { 
        if (!db || !user) return; 
        const productToDelete = allProducts.find(p => p.id === productId);
        if (!productToDelete) return;
        
        openModal('confirmDeleteProduct', {
            id: productId, 
            name: productToDelete.name,
            onConfirm: () => executeDeleteProduct(productId)
        });
    };

    // 2. New Execute Function: Contains the actual deletion logic
    const executeDeleteProduct = async (productId) => {
        try {
            // We set isDeleted: true instead of a hard delete, which is safer for orders
            const batch = writeBatch(db);
           
           // 1. Soft Delete the Product/Event
           const productRef = doc(db, `artifacts/${appId}/public/data/products`, productId);
           batch.update(productRef, { isDeleted: true });

           // 2. If it's an event, soft delete/cancel associated bookings so they disappear from user history
           const gigsQuery = query(
               collection(db, `artifacts/${appId}/public/data/gigs`), 
               where('eventId', '==', productId)
           );
           const gigsSnap = await getDocs(gigsQuery);
           gigsSnap.forEach(g => {
               batch.update(g.ref, { isDeleted: true, status: 'cancelled' });
           });

           await batch.commit();
           setNotification(t('notif.itemRemoved'));
            setTimeout(() => setNotification(null), 3000);
        } catch (error) {
            console.error("Error deleting product:", error);
            setNotification(t('notif.productDeleteFailed'));
            setTimeout(() => setNotification(null), 3000);
        }
        setModal(null); // Close the confirmation modal
    };
    const handlePlaceOrder = async ({ product, delivery, buyerLocation, quantity, paymentMethod }) => { 
        if(!db || !user || !myStore || !buyerLocation) {
            alert("Order failed: Missing user context or location.");
            return; 
        }

        // FIX: Perform all non-transaction async operations (like fetching an address) 
        // *before* the transaction begins.
        let buyerAddress = 'N/A';
        if (buyerLocation) {
             buyerAddress = await getAddressFromCoords(buyerLocation.latitude, buyerLocation.longitude);
        }
        
        // A transaction is REQUIRED to safely read and decrement stock
        try {
            await runTransaction(db, async (transaction) => {
                const orderId = doc(collection(db, 'id')).id;
                const buyerRef = doc(db, `artifacts/${appId}/public/data/stores`, user.uid);
                const sellerRef = doc(db, `artifacts/${appId}/public/data/stores`, product.storeId);
                const productRef = doc(db, `artifacts/${appId}/public/data/products`, product.id);

                const productDoc = await transaction.get(productRef);
                const sellerDoc = await transaction.get(sellerRef);
                const buyerDoc = await transaction.get(buyerRef);     // <-- READ #3

                if (!productDoc.exists()) {
                    throw new Error("This product no longer exists.");
                }

                if (!sellerDoc.exists()) {
                    throw new Error("The seller's store no longer exists.");
                }
                if (!buyerDoc.exists()) {
                    throw new Error("Your user profile could not be found.");
                }

                // 2. Validate Stock (if it's a 'goods' item)
                const currentStock = productDoc.data().stock || 0;
                if (product.type === 'goods' && currentStock < quantity) {
                    throw new Error("Insufficient stock. Someone may have bought this just before you.");
                }
                
                // 4. Handle Coin/Sales update
                const sellerData = sellerDoc.data();
                const buyerData = buyerDoc.data();
                
                if (paymentMethod === 'coin') {
                    if ((buyerData.coinBalance || 0) < (product.price * quantity)) {
                        throw new Error("Insufficient coin balance.");
                    }
                    transaction.update(buyerRef, { coinBalance: (buyerData.coinBalance || 0) - (product.price * quantity) });
                    transaction.update(sellerRef, { coinBalance: (sellerData.coinBalance || 0) + (product.price * quantity) });
                }

                // --- 3. ALL WRITES (Must happen second) ---

                // Set the order document
                transaction.set(doc(db, `artifacts/${appId}/users/${user.uid}/orders`, orderId), { 
                    productName: product.name, 
                    productId: product.id, 
                    price: product.price * quantity, 
                    quantity, 
                    storeName: product.storeName, 
                    storeId: product.storeId, 
                    status: 'pending', 
                    type: product.type,
                    location: product.location,
                    pickupAddress: product.location?.address || 'Store Location',
                    dropoffAddress: buyerAddress,
                    dropoffLocation: { coords: buyerLocation, address: buyerAddress },
                    description: product.description,
                    marketName: product.marketName,
                    currencySymbol: product.currencySymbol || '$',
                    countryCode: myStore.countryCode,
                    images: product.images || [],
                    createdAt: serverTimestamp(), 
                    paymentMethod });

                // Update seller sales
                transaction.update(sellerRef, { sales: (sellerData?.sales || 0) + 1 });
                
                if (product.type === 'goods') { 
                    // 5. Decrement stock
                    transaction.update(productRef, { stock: currentStock - quantity });
        
                }
            });
        
        } catch (error) {
            console.error("Order Transaction Failed: ", error);
            alert(error.message); // Show the error to the user (e.g., "Insufficient stock")
            setModal(null); // Close modal on failure
            return; // Stop execution
        }
        
        setNotification(t('notif.orderPlaced'));
        setTimeout(() => setNotification(null), 4000);

        setModal(null); 
    };
    const handleCompleteGig = async (gigId) => {
        if(!db || !user) return;
        const gigRef = doc(db, `artifacts/${appId}/public/data/gigs`, gigId);
        await updateDoc(gigRef, {status:'completed'});
        setModal(null);
    };
    const handleStartChat = async (partnerId, openInModal = false) => {
        if (!db || !user || user.uid === partnerId) return;

        // FIX: Mark chat notifications as read when chat is opened
        if (notifications.length > 0) {
            const notifsToMarkRead = notifications.filter(n => !n.read && n.chatPartnerId === partnerId);
            if (notifsToMarkRead.length > 0) {
                const batch = writeBatch(db);
                notifsToMarkRead.forEach(notif => {
                    const notifRef = doc(db, `artifacts/${appId}/users/${user.uid}/notifications`, notif.id);
                    batch.update(notifRef, { read: true });
                });
                await batch.commit().catch(console.error);
            }
        }
        
        const chatId = [user.uid, partnerId].sort().join('_');
        const chatRef = doc(db, `artifacts/${appId}/public/data/chats`, chatId);
        
        const partnerStore = allStores.find(s => s.id === partnerId);
        const myName = myStore?.name || `User...${user.uid.slice(-4)}`;
        const partnerName = partnerStore?.name || `User...${partnerId.slice(-4)}`;

        await setDoc(chatRef, {
            participants: [user.uid, partnerId],
            participantNames: {
                [user.uid]: myName,
                [partnerId]: partnerName
            },
            lastMessage: serverTimestamp(),
        }, { merge: true });

        if (openInModal) {
            setModal(null);
            setActiveChatModalPartner(partnerId);
        } else {
            setActiveChatPartner(partnerId);
            setPage('chats');
        }
    };
    const handleMarkOrderComplete = async (orderId) => {
        if (!db || !user) return;
        const orderRef = doc(db, `artifacts/${appId}/users/${user.uid}/orders`, orderId);
        await updateDoc(orderRef, { status: 'completed' });
    };

    const handleDeleteOrder = async (orderId) => {
        const orderToDelete = orders.find(o => o.id === orderId);
        if (!orderToDelete) return;
        // Open the modal, passing the item name and the function to run on confirm
        openModal('confirmDeleteOrder', {
            id: orderId, 
            name: orderToDelete.productName,
            onConfirm: () => executeDeleteOrder(orderId)
        });
    };

    // 2. New function: Contains the actual deletion logic (called by the modal)
    const executeDeleteOrder = async (orderId) => {
        try {
            const orderRef = doc(db, `artifacts/${appId}/users/${user.uid}/orders`, orderId);
            await deleteDoc(orderRef);
            setNotification(t('notif.orderDeleted'));
            setTimeout(() => setNotification(null), 3000);
        } catch (error) {
            console.error("Error deleting order:", error);
            setNotification(t('notif.orderDeleteFailed'));
            setTimeout(() => setNotification(null), 3000);
        }
        setModal(null); // Close the confirmation modal
    };
    
    const handleDeleteAccount = async (password) => {
        if (!auth || !db || !user) throw new Error("Authentication context not available.");

        const credential = EmailAuthProvider.credential(user.email, password);
        await reauthenticateWithCredential(user, credential);
    
        const batch = writeBatch(db);
    
        const productsQuery = query(collection(db, `artifacts/${appId}/public/data/products`), where('storeId', '==', user.uid));
        const productsSnapshot = await getDocs(productsQuery);
        productsSnapshot.forEach(doc => batch.delete(doc.ref));
    
        const gigsQuery = query(collection(db, `artifacts/${appId}/public/data/gigs`), where('creatorId', '==', user.uid));
        const gigsSnapshot = await getDocs(gigsQuery);
        gigsSnapshot.forEach(doc => batch.delete(doc.ref));
    
        const storeRef = doc(db, `artifacts/${appId}/public/data/stores`, user.uid);
        batch.delete(storeRef);
    
        await batch.commit();
    
        await deleteUser(user);
    };

    const handleAdminDeleteUser = (userToDelete) => {
        setSelectedItem(userToDelete);
        setModal('adminConfirmDelete');
    };

    const handleConfirmAdminDeleteUser = async () => {
        if (!db || !selectedItem) return;

        const userToDelete = selectedItem;
        const userId = userToDelete.id;
        
        try {
            const batch = writeBatch(db);

            // Delete store
            const storeRef = doc(db, `artifacts/${appId}/public/data/stores`, userId);
            batch.delete(storeRef);

            // Delete products
            const productsQuery = query(collection(db, `artifacts/${appId}/public/data/products`), where('storeId', '==', userId));
            const productsSnapshot = await getDocs(productsQuery);
            productsSnapshot.forEach(doc => batch.delete(doc.ref));

            // Delete gigs created by the user
            const gigsQuery = query(collection(db, `artifacts/${appId}/public/data/gigs`), where('creatorId', '==', userId));
            const gigsSnapshot = await getDocs(gigsQuery);
            gigsSnapshot.forEach(doc => batch.delete(doc.ref));

            await batch.commit();
            
            console.warn(`ACTION REQUIRED: Firestore data for user '${userToDelete.name}' (${userId}) deleted. To complete deletion, MANUALLY delete this user from the Firebase Authentication console.`);

            setNotification(`User ${userToDelete.name} and all their data have been permanently deleted.`);
        } catch (error) {
            console.error("Error deleting user data by admin:", error);
            setAuthError("Failed to delete user data.");
        } finally {
            setModal(null);
            setSelectedItem(null);
        }
    };

    const handleOpenReportModal = (reportedUserId, gigId, reportedName) => {
        setReportModalData({ reportedUserId, gigId, reportedName });
        openModal('reportUser');
    };
    
    const handleSubmitReport = async (reason) => {
        if (!db || !user || !reportModalData) return;
        await addDoc(collection(db, `artifacts/${appId}/public/data/reports`), {
            reporterId: user.uid,
            reportedId: reportModalData.reportedUserId,
            gigId: reportModalData.gigId,
            reason: reason,
            status: 'new',
            timestamp: serverTimestamp(),
        });
        
        setNotification(t('notif.reportSubmitted'));
        setTimeout(() => setNotification(null), 4000);
        setReportModalData(null);
        setModal(null);
    };

    const handleResolveReport = async (reportId) => {
        if (!db) return;
        await updateDoc(doc(db, `artifacts/${appId}/public/data/reports`, reportId), { status: 'resolved' });
        setModal(null); // Close the detail modal
    };
    
    const handleViewStore = (storeId) => {
        navigate('store-detail', storeId);
        setModal(null); // Close any open modal like checkout
    };
    
    const handleGlobalSearch = (query) => {
        navigate('search-results', query);
    };

    const handleApproveProduct = async (productId, ownerId) => {
        await updateDoc(doc(db, `artifacts/${appId}/public/data/products`, productId), { status: 'approved' });
        await addDoc(collection(db, `artifacts/${appId}/users/${ownerId}/notifications`), {
            message: t('msg.productApproved'),
            read: false,
            timestamp: serverTimestamp()
        });
        setModal(null);
    };

    const handleRejectProduct = async (productId, ownerId) => {
        await deleteDoc(doc(db, `artifacts/${appId}/public/data/products`, productId));
        await addDoc(collection(db, `artifacts/${appId}/users/${ownerId}/notifications`), {
            message: t('msg.productRejected'),
            read: false,
            timestamp: serverTimestamp()
        });
        setModal(null);
    };

    const handleMakeAdmin = async (userId) => {
        await setDoc(doc(db, `artifacts/${appId}/public/data/admins`, userId), { isAdmin: true });
    };

    const handleRemoveAdmin = async (userId) => {
        await deleteDoc(doc(db, `artifacts/${appId}/public/data/admins`, userId));
    };

    const handleSuspendUser = async (userId) => {
        if (!db) return;
        await updateDoc(doc(db, `artifacts/${appId}/public/data/stores`, userId), { status: 'suspended' });
    };

    const handleReactivateUser = async (userId) => {
        if (!db) return;
        await updateDoc(doc(db, `artifacts/${appId}/public/data/stores`, userId), { status: 'approved' });
    };
    
    const handleApproveUser = async (userId) => {
        if (!db) return;
        const userStoreRef = doc(db, `artifacts/${appId}/public/data/stores`, userId);
        const userStoreSnap = await getDoc(userStoreRef);
        const userStoreData = userStoreSnap.data();

        await updateDoc(userStoreRef, { status: 'approved' });
        // In-app notification
        await addDoc(collection(db, `artifacts/${appId}/users/${userId}/notifications`), {
            message: t('msg.accountApproved'),
            read: false,
            timestamp: serverTimestamp()
        });
        
        // --- Email Notification Simulation ---
        console.log(`SIMULATION: Approval email sent to ${userStoreData?.contactEmail} for user ${userId}.`);
    };
    
    const handleRejectUser = (userToReject) => {
        setSelectedItem(userToReject);
        setModal('rejectUser');
    };

    const handleConfirmRejection = async (reason) => {
        if (!db || !selectedItem) return;

        const { id: userId, contactEmail, name } = selectedItem;

        try {
            // --- SIMULATING BACKEND PROCESS FOR REJECTION & DELETION ---
            
            // Step 1: Simulate sending the rejection email with the reason.
            // In a production app, this would be a call to a secure backend Cloud Function.
            console.log(`--- SIMULATING REJECTION EMAIL ---`);
            console.log(`To: ${contactEmail}`);
            console.log(`Subject: An Update on Your GigNile Application`);
            console.log(`Body: Hi ${name}, after reviewing your application, we were unable to approve your account at this time for the following reason: ${reason}`);
            console.log(`----------------------------------`);

            // Step 2: Delete the user's data from Firestore. This blocks them from logging in 
            // and is the first step to allowing their email to be used for a new registration.
            await deleteDoc(doc(db, `artifacts/${appId}/public/data/stores`, userId));
            console.log(`Step 2: User data for '${name}' (${userId}) deleted from Firestore.`);

            // Step 3: Critical backend step (simulated with a console warning).
            // To complete the process and free up the email for re-registration, the user's
            // account MUST be deleted from Firebase Authentication. This is a privileged operation
            // that CANNOT be done securely from the client-side code.
            console.warn(`ACTION REQUIRED: To complete the rejection process, the user with UID ${userId} must be deleted from the Firebase Authentication service. This should be implemented in a backend function (e.g., a Firebase Cloud Function) or performed manually in your Firebase Console.`);
            
            setNotification(`User '${name}' has been rejected. Their data was deleted and a simulated notification was sent.`);
        } catch (error) {
            console.error("Error during user rejection process:", error);
            setAuthError("Failed to reject user. See console for details.");
        } finally {
            setModal(null);
            setSelectedItem(null);
        }
    };

    const handleSendPasswordReset = async (email) => {
        if (!auth) throw new Error("Authentication service not available.");
        await sendPasswordResetEmail(auth, email);
    };

    // NEW: Centralized Logout Handler
    const handleLogout = () => {
        console.log(`Detaching ${snapshotListeners.current.length} listeners...`);
        // 1. Manually detach all active listeners
        snapshotListeners.current.forEach(unsub => unsub());
        snapshotListeners.current = [];
        
        // 2. Sign out
        if (auth) {
            signOut(auth).catch(err => console.error("Sign out error:", err));
        }
    };

    const handleToggleGigNotifications = async () => {
        if (!db || !user || !myStore) return;
        const storeRef = doc(db, `artifacts/${appId}/public/data/stores`, user.uid);
        const newStatus = !myStore.isDeliveryDriver;
        try {
            await updateDoc(storeRef, { isDeliveryDriver: newStatus });
            setNotification(newStatus ? t('notif.gigToggleOn') : t('notif.gigToggleOff'));
        } catch (error) {
            console.error("Error updating gig notification status:", error);
            setNotification("Failed to update settings.");
        } finally {
            setTimeout(() => setNotification(null), 3000);
        }
    };

    const openModal = (modalName, item) => { setIsFabMenuOpen(false); setSelectedItem(item); setModal(modalName); };

    if (authState === 'loading') {
        return <LoadingSpinner />;
    }

    const bgPattern = "data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23d4e6ff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E";

    if (authState === 'unauthenticated') {
        return (
            <>
                <AuthPage 
                    auth={auth} 
                    db={db} 
                    storage={storage} 
                    authError={authError} 
                    setAuthError={setAuthError} 
                    onForgotPasswordClick={() => openModal('forgotPassword')} 
                    userLocation={currentUserLocation}
                    allStores={allStores} // Pass allStores for market list
                    language={language} // Pass current language
                    setAndSaveLanguage={setAndSaveLanguage} // Pass language setter
                    t={t} // Pass translation function
                />
                {modal === 'forgotPassword' && 
                    <ForgotPasswordModal 
                        onClose={() => setModal(null)} 
                        onSendResetEmail={handleSendPasswordReset} 
                        authError={authError} 
                        setAuthError={setAuthError}
                        t={t} 
                    />
                }
            </>
        );
    }
    
    {/*if (myStore && myStore.status === 'pending_approval' && !isAdmin) {
        return (
            <div className="min-h-screen bg-slate-100 flex flex-col justify-center items-center p-4 text-center">
                <Loader2 className="w-12 h-12 text-sky-500 animate-spin mb-4" />
                <h2 className="text-2xl font-bold mb-2">Account Pending Approval</h2>
                <p className="text-slate-600">Your account is currently under review by our administrators. You will be notified once it's approved.</p>
                 <button onClick={() => signOut(auth)} className="mt-6 bg-red-500 text-white font-bold py-2 px-4 rounded-lg">Logout</button>
            </div>
        );
    } */}

    if (myStore && myStore.status === 'suspended' && !isAdmin) {
         return (
            <div className="min-h-screen bg-slate-100 flex flex-col justify-center items-center p-4 text-center">
                <AlertCircle className="w-12 h-12 text-red-500 mb-4" />
                <h2 className="text-2xl font-bold mb-2">Account Suspended</h2>
                <p className="text-slate-600">Your account has been suspended. Please contact support for more information.</p>
                <button onClick={() => signOut(auth)} className="mt-6 bg-red-500 text-white font-bold py-2 px-4 rounded-lg">Logout</button>
            </div>
        );
    }


    const MainContent = ({ user, myStore, isAdmin, db, auth, storage, language, t, allStores, allProducts, gigs, myProducts, orders, chats, notifications, reports, admins, currentUserLocation }) => {
        if (isLoading || !currentUserLocation) return <LoadingSpinner />;
                
        switch(page) {
            case 'marketplace': return <MarketplacePage setPage={navigate} t={t} onGlobalSearch={handleGlobalSearch} />;
            case 'search-results': return <SearchResultsPage query={pageDetail} allProducts={allProducts} gigs={gigs} onActionClick={(item) => openModal(item.itemType === 'gig' ? 'gigDetail' : 'checkout', item)} userLocation={currentUserLocation} allStores={allStores} onViewStore={handleViewStore} t={t} />;
            case 'marketplace-goods': return <CategorySearchPage title={t('goods')} items={allProducts.filter(p => p.type === 'goods' && !p.isDeleted && p.status === 'approved')} CardComponent={ProductCard} onActionClick={(p) => openModal('checkout', p)} userLocation={currentUserLocation} allStores={allStores} t={t} onViewStore={handleViewStore} myStore={myStore} isAdmin={isAdmin}/>;
            case 'marketplace-services': return <CategorySearchPage title={t('services')} items={allProducts.filter(p => p.type === 'service' && !p.eventType && !p.isDeleted && p.status === 'approved')} CardComponent={ProductCard} onActionClick={(p) => openModal('checkout', p)} userLocation={currentUserLocation} allStores={allStores} t={t} onViewStore={handleViewStore} myStore={myStore} isAdmin={isAdmin}/>;
            case 'marketplace-gigs': return <CategorySearchPage title={t('gigs')} items={gigs.filter(g=> g.status === 'open')} CardComponent={GigCard} onActionClick={(g) => openModal('gigDetail', g)} userLocation={currentUserLocation} allStores={allStores} t={t} showMarketFilter={true} onViewStore={handleViewStore} myStore={myStore} isAdmin={isAdmin}/>;
            case 'marketplace-entertainment': return <CategorySearchPage 
               title={t('event.title')}
               
               // Filter: Type is event, Not Deleted, Not Cancelled/Closed, AND End Date is in the future
               // UPDATED: Logic to handle legacy events (fallback to start date if endDate is missing)
               items={allProducts.filter(p => {
                   if (p.type !== 'event' || p.isDeleted || p.status === 'cancelled' || p.status === 'closed') return false;
                   const end = p.endDate ? new Date(p.endDate) : new Date(p.date);
                   return end > new Date();
               })} 
               CardComponent={EventCard} 
               onActionClick={(event) => openModal('eventDetail', { ...event, viewContext: 'marketplace' })} // Ensure marketplace context
               userLocation={currentUserLocation} 
               allStores={allStores} 
               t={t} 
               onViewStore={handleViewStore} 
               myStore={myStore} 
               isAdmin={isAdmin} 
            />;
            case 'my-gigs': return <MyGigsPage myGigs={gigs.filter(g => g.creatorId === user.uid || g.workerId === user.uid)} onGigClick={(g) => openModal('gigDetail', g)} userLocation={currentUserLocation} allStores={allStores} user={user} onRateWorker={handleRateWorker} onRateCreator={handleRateCreator} onDeleteGig={handleDeleteGig} onReportUser={(id, gigId, name) => handleOpenReportModal(id, gigId, name)} t={t} />;
            case 'chats': return <ChatsPage chats={chats} user={user} onStartChat={handleStartChat} activeChatPartner={activeChatPartner} setActiveChatPartner={setActiveChatPartner} db={db} allStores={allStores} myStore={myStore} language={language} notifications={notifications} t={t}/>;
            case 'my-store': return <MyStorePage store={myStore} products={myProducts} onManageStoreClick={() => openModal('manageStore', myStore)} onManageProduct={(p) => openModal('manageProduct', p)} onDeleteProduct={handleDeleteProduct} allStores={allStores} t={t} />;
            case 'profile': return <ProfilePage myOrders={orders} user={user} myStore={myStore} gigs={gigs} myProducts={myProducts} onManageStoreClick={() => openModal('manageStore', myStore)} onChangePasswordClick={() => openModal('changePassword')} handleRate={handleRate} onBuyCoinsClick={() => openModal('buyCoins')} onTransferCoinsClick={()=>openModal('transferCoins')} onMarkOrderComplete={handleMarkOrderComplete} onDeleteAccountClick={() => openModal('deleteAccount')} isAdmin={isAdmin} setPage={navigate} onLogout={handleLogout} language={language} setAndSaveLanguage={setAndSaveLanguage} t={t} />;
            case 'store-detail': return <StoreDetailPage storeId={pageDetail} allStores={allStores} allProducts={allProducts} onProductClick={(p) => openModal('checkout', p)} onBack={goBack} userLocation={currentUserLocation} onStartChat={handleStartChat} currentUser={user} t={t} />;
            case 'my-orders': return <OrderActivity orders={orders} onRate={handleRate} user={user} onMarkOrderComplete={handleMarkOrderComplete} setPage={navigate} handleDeleteOrder={handleDeleteOrder} onCreateGig={handleCreateGigFromOrders} t={t} />;
            case 'my-events': return <MyEventsActivity 
               bookings={bookings.filter(b => b.creatorId === user.uid && !b.isDeleted)} // Read from bookings prop 
               hostedEvents={myProducts.filter(p => p.type === 'event' && !p.isDeleted)} // Filter deleted hosted events
               onRate={handleRateWorker} 
               userLocation={currentUserLocation} 
               setPage={navigate} 
               t={t} 
               onMarkComplete={handleCompleteGig} 
               allProducts={allProducts} // Pass allProducts for lookup
               onOpenEventDetail={(event) => openModal('eventDetail', { ...event, viewContext: 'profile' })}
            />;
            case 'admin': return <AdminPage 
                allStores={allStores} allProducts={allProducts} reports={reports} 
                onApproveProduct={handleApproveProduct} onRejectProduct={handleRejectProduct} 
                onMakeAdmin={handleMakeAdmin} onRemoveAdmin={handleRemoveAdmin} admins={admins} 
                onAdminChat={handleStartChat} openUserDetails={(user) => openModal('userDetails', user)} 
                currentUser={user} onSuspendUser={handleSuspendUser} onReactivateUser={handleReactivateUser} 
                onApproveUser={handleApproveUser} onRejectUser={handleRejectUser} 
                onDeleteUser={handleAdminDeleteUser}
                openProductDetails={(product) => openModal('productApproval', product)} 
                openReportDetails={(report) => openModal('reportDetails', report)}
                gigs={gigs} // <-- FIX: Pass all gigs
                onDeleteGig={handleDeleteGig} // <-- FIX: Pass delete gig handler
                onDeleteProduct={handleDeleteProduct} // <-- FIX: Pass delete product handler
                t={t}
            />;
            default: return (
                <div className="text-center p-8 bg-white rounded-lg shadow-md max-w-md mx-auto">
                    <h2 className="text-2xl font-bold text-red-500">Page Not Found</h2>
                    <p className="text-slate-600 mt-2">The page you were looking for ('{page}') could not be found.</p>
                    <button onClick={() => navigate('marketplace')} className="mt-6 bg-sky-500 text-white font-bold py-2 px-6 rounded-lg hover:bg-sky-600 transition-colors">
                        Go to Homepage
                    </button>
                </div>
            );
        }
    };

    return (
        <div className={`bg-slate-100 min-h-screen font-sans text-slate-800 overflow-x-hidden ${isDarkMode ? 'dark' : ''}`} style={{backgroundImage: `url("${bgPattern}")`}}>
             {notification && <div className="bg-teal-500 text-white text-center py-2 animate-fade-in z-50 relative">{notification}</div>}
            <Header 
                onLogoClick={resetNavigation} 
                language={language} 
                setLanguage={setAndSaveLanguage} 
                myStore={myStore} 
                onNotificationClick={handleNotificationClick} 
                unreadCount={notifications.filter(n => !n.read).length} 
                unreadBellCount={unreadBellCount} // Pass bell count
                unreadChatCount={unreadChatCount} // Pass chat count
                onToggleGigNotifications={handleToggleGigNotifications}
                isDarkMode={isDarkMode}
                onToggleDarkMode={() => setIsDarkMode(!isDarkMode)}
                onChatsClick={() => navigate('chats')}
            />
            {isNotificationsOpen && <NotificationPanel notifications={notifications} onClose={() => setNotificationsOpen(false)} onNotificationClick={handleNotificationItemClick} />}
            <main className="flex-grow container mx-auto p-4 pb-24">
                <MainContent 
                    user={user}
                    myStore={myStore}
                    isAdmin={isAdmin}
                    db={db}
                    auth={auth}
                    storage={storage}
                    language={language}
                    t={t}
                    allStores={allStores}
                    allProducts={allProducts}
                    gigs={gigs}
                    myProducts={myProducts}
                    orders={orders}
                    chats={chats}
                    notifications={notifications}
                    reports={reports}
                    admins={admins}
                    currentUserLocation={currentUserLocation}
                />
            </main> {/* Increased bottom padding */}
            {modal === 'createGig' && <CreateGigModal onClose={() => setModal(null)} onCreate={handleCreateGig} userLocation={currentUserLocation} storage={storage} myStore={myStore} allStores={allStores} currencyInfo={currencyInfo} t={t} />}
            {modal === 'gigDetail' && <GigDetailModal gig={selectedItem} onClose={() => setModal(null)} key={selectedItem.id} onApply={() => openModal('applyForGig', selectedItem)} onChooseApplicant={handleChooseApplicant} onComplete={handleCompleteGig} onCloseGig={handleCloseGig} currentUser={user} userLocation={currentUserLocation} onStartChat={handleStartChat} allStores={allStores} onViewProfile={(id) => navigate('store-detail', id)} myStore={myStore} db={db} onReportUser={(id, gigId, name) => handleOpenReportModal(id, gigId, name)} setLightboxImageUrl={setLightboxImageUrl} language={language} t={t} allGigs={gigs} onSwitchItem={(item) => setSelectedItem(item)}/>}
            {modal === 'applyForGig' && <ApplyForGigModal gig={selectedItem} onClose={() => setModal(null)} onApplySubmit={handleApplyForGig} myStore={myStore} />}
            {modal === 'checkout' && <CheckoutModal key={selectedItem.id} product={selectedItem} onClose={() => setModal(null)} onPlaceOrder={handlePlaceOrder} userLocation={currentUserLocation} allStores={allStores} onStartChat={handleStartChat} myStore={myStore} onViewStore={(id) => navigate('store-detail', id)} setLightboxImageUrl={setLightboxImageUrl} language={language} t={t} currentUser={user} allProducts={allProducts} onSwitchItem={(item) => setSelectedItem(item)} />}
            {modal === 'manageProduct' && <ManageProductModal product={selectedItem} onClose={() => setModal(null)} onSave={handleManageProduct} myStore={myStore} storage={storage} allProducts={allProducts} allStores={allStores} currencyInfo={currencyInfo} t={t} />}
            {modal === 'manageStore' && <ManageStoreModal store={selectedItem} onClose={() => setModal(null)} onSave={handleManageStore} userLocation={currentUserLocation} storage={storage} allStores={allStores} myStore={myStore} t={t} />}
            {modal === 'eventDetail' && (
                <EventDetailModal 
                    key={selectedItem.id}
                    event={selectedItem} 
                    onClose={() => setModal(null)} 
                    onBook={handleBookEvent} 
                    onCloseEvent={handleCloseEvent}
                    onRemoveAttendee={handleRemoveAttendee} // Pass the remove handler
                    onStartChat={handleStartChat} // Ensure chat handler is passed
                    allStores={allStores}
                    userLocation={currentUserLocation} 
                    t={t} 
                    currentUser={user}
                    myStore={myStore} // Fix: Pass myStore
                    gigs={bookings} // Pass bookings list to find attendees (renamed prop usage in component)
                    onEdit={() => openModal('editEvent', selectedItem)}
                    onGoToMyEvents={() => { setModal(null); navigate('my-events'); }}
                    setLightboxImageUrl={setLightboxImageUrl}
                    language={language}
                    allProducts={allProducts}
                    onSwitchItem={(item) => setSelectedItem(item)}
                />
            )}
            {/* Create Mode */}
            {modal === 'createEvent' && (
                <CreateEventModal 
                    onClose={() => setModal(null)} 
                    onSave={handleCreateEvent} 
                    storage={storage} 
                    t={t} 
                    currencyInfo={currencyInfo} 
                    userLocation={currentUserLocation} 
                />
            )}

            {/* Edit Mode - Reuses CreateEventModal with eventToEdit prop */}
            {modal === 'editEvent' && (
                <CreateEventModal 
                    eventToEdit={selectedItem}
                    onClose={() => setModal(null)} 
                    onUpdate={handleUpdateEvent} // Pass update handler
                    storage={storage} 
                    t={t} 
                    currencyInfo={currencyInfo} 
                    userLocation={currentUserLocation} 
                />
            )}
            {modal === 'changePassword' && <ChangePasswordModal onClose={() => setModal(null)} auth={auth} authError={authError} setAuthError={setAuthError} t={t}/>}
            {/* {modal === 'buyCoins' && <BuyCoinsModal onClose={() => setModal(null)} onBuy={handleBuyCoins} />} */}
            {/* {modal === 'transferCoins' && <TransferCoinsModal onClose={() => setModal(null)} onTransfer={handleTransferCoins} myStore={myStore} />} */}
            {modal === 'deleteAccount' && <DeleteAccountModal onClose={() => setModal(null)} onDelete={handleDeleteAccount} authError={authError} setAuthError={setAuthError} t={t}/>}
            {modal === 'reportUser' && <ReportModal onClose={() => setModal(null)} onSubmit={handleSubmitReport} reportedUserName={reportModalData?.reportedName} />}
            {modal === 'productApproval' && <ProductApprovalModal product={selectedItem} onClose={() => setModal(null)} onApprove={handleApproveProduct} onReject={handleRejectProduct} setLightboxImageUrl={setLightboxImageUrl} />}
            {modal === 'userDetails' && <UserDetailsModal user={selectedItem} onClose={() => setModal(null)} />}
            {modal === 'rejectUser' && <RejectionModal onClose={() => setModal(null)} onConfirm={handleConfirmRejection} userName={selectedItem?.name}/>}
            {modal === 'adminConfirmDelete' && <AdminConfirmDeleteModal onClose={() => setModal(null)} onConfirm={handleConfirmAdminDeleteUser} userName={selectedItem?.name}/>}
            {modal === 'reportDetails' && <ReportDetailModal report={selectedItem} allStores={allStores} gigs={gigs} onClose={() => setModal(null)} onChat={handleStartChat} onViewProfile={(u) => openModal('userDetails', u)} onViewGig={(g) => openModal('gigDetail', g)} onResolve={handleResolveReport} />}
            {modal === 'confirmDeleteOrder' && <ConfirmDeleteModal onClose={() => setModal(null)} onConfirm={selectedItem.onConfirm} itemName={selectedItem.name} isLoading={false} />}
            {modal === 'confirmDeleteGig' && <ConfirmDeleteModal onClose={() => setModal(null)} onConfirm={selectedItem.onConfirm} itemName={selectedItem.name} isLoading={false} />}
            {modal === 'confirmDeleteProduct' && <ConfirmDeleteModal onClose={() => setModal(null)} onConfirm={selectedItem.onConfirm} itemName={selectedItem.name} isLoading={false} />}
            {lightboxImageUrl && <ImageLightboxModal imageUrl={lightboxImageUrl} onClose={() => setLightboxImageUrl(null)} />}
            {activeChatModalPartner && <ChatModal partnerId={activeChatModalPartner} user={user} db={db} onClose={() => setActiveChatModalPartner(null)} allStores={allStores} myStore={myStore} language={language} t={t}/>}
            {/* Multi-Store Delivery Choice Modal */}
            {pendingMultiStoreData && (
                <MultiStoreChoiceModal 
                    onClose={() => setPendingMultiStoreData(null)}
                    storeCount={pendingMultiStoreData.uniqueStores.length}
                    onChoice={(mode) => processGoodsGigCreation(mode, pendingMultiStoreData.goodsByStore, pendingMultiStoreData.uniqueStores, pendingMultiStoreData.goodsOrders)}
                    t={t}                
                />
            )}
            {/* Pending Orders Floating Button */}
            {pendingOrdersCount > 0 && (
                <button 
                    onClick={() => navigate('my-orders')}
                    className="fixed bottom-24 left-4 z-40 bg-teal-500 text-white p-3 rounded-full shadow-lg flex items-center justify-center animate-bounce hover:bg-teal-600 transition-all"
                    title="You have pending orders to process"
                >
                    <ShoppingCart size={24} />
                    <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] font-bold w-5 h-5 rounded-full flex items-center justify-center border-2 border-white">
                        {pendingOrdersCount}
                    </span>
                </button>
            )}
            <BottomNavBar page={page} setPage={navigate} t={t} isAdmin={isAdmin} onPostClick={() => setIsFabMenuOpen(!isFabMenuOpen)} />

            {/* FAB Menu */}
            {isFabMenuOpen && <div className="fixed inset-0 bg-black bg-opacity-60 z-30" onClick={() => setIsFabMenuOpen(false)}></div>}
            <div className="fixed bottom-24 right-4 flex flex-col items-end gap-4 z-40"> {/* FIX: Increased gap and right inset */}
                {isFabMenuOpen && (
                    <div className="flex flex-col items-end gap-2 animate-slide-in-up"> {/* Reduced gap */}
                        {myStore ? (
                            <>
                                <button onClick={() => openModal('createGig', null)} className="flex items-center gap-3 bg-purple-500 text-white font-bold p-3 sm:py-3 sm:px-5 rounded-full shadow-lg hover:bg-purple-600 transition-all hover:scale-105">
                                  <span>{t('fab.postGig')}</span> <Briefcase size={20} />
                                </button>
                                <button onClick={() => openModal('manageProduct', { type: 'service' })} className="flex items-center gap-3 bg-purple-500 text-white font-bold p-3 sm:py-3 sm:px-5 rounded-full shadow-lg hover:bg-purple-600 transition-all hover:scale-105" style={{animationDelay: '50ms'}}>
                                  <span>{t('fab.addService')}</span> <Wrench size={20} />
                                </button>
                                <button onClick={() => openModal('manageProduct', { type: 'goods' })} className="flex items-center gap-3 bg-purple-500 text-white font-bold p-3 sm:py-3 sm:px-5 rounded-full shadow-lg hover:bg-purple-600 transition-all hover:scale-105" style={{animationDelay: '100ms'}}>
                                  <span>{t('fab.addGoods')}</span> <Package size={20} />
                                </button>
                                <button onClick={() => openModal('createEvent', null)} className="flex items-center gap-3 bg-purple-500 text-white font-bold p-3 sm:py-3 sm:px-5 rounded-full shadow-lg hover:bg-purple-600 transition-all hover:scale-105" style={{animationDelay: '150ms'}}>
                                    <span>{t('fab.addEvent')}</span> <Ticket size={20} />
                                </button>
                            </>
                        ) : (
                             <div className="bg-white text-slate-600 text-sm font-semibold py-3 px-5 rounded-full shadow-lg">
                                {t('fab.createStoreNote')}
                            </div>
                        )}
                    </div>
                )}
                
            </div>
        </div>
    );
} // Added missing closing brace for App component


// Dynamically add Leaflet CSS
const leafletCss = document.createElement('link');
leafletCss.rel = 'stylesheet';
leafletCss.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
leafletCss.integrity = 'sha256-p4NxAoJBhIIN+hmNHrzRCf9tD/miZyoHS5obTRR9BMY=';
leafletCss.crossOrigin = '';
document.head.appendChild(leafletCss);

// Dynamically add Leaflet JS
const leafletJs = document.createElement('script');
leafletJs.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
leafletJs.integrity = 'sha256-20nQCchB9co0qIjJZRGuk2/Z9VM+kNiyxNV1lvTlZBo=';
leafletJs.crossOrigin = '';
document.head.appendChild(leafletJs);

const style = document.createElement('style');
style.textContent = `
  @keyframes fade-in { from { opacity: 0; } to { opacity: 1; } }
  .animate-fade-in { animation: fade-in 0.2s ease-out forwards; }
  @keyframes slide-in-up { from { transform: translateY(100%); } to { transform: translateY(0); } }
  .animate-slide-in-up { animation: slide-in-up 0.3s ease-out forwards; }
  @keyframes pulse { 
    0% { transform: scale(0.9); box-shadow: 0 0 0 0 rgba(37, 99, 235, 0.7); } 
    70% { transform: scale(1); box-shadow: 0 0 0 15px rgba(37, 99, 235, 0); } 
    100% { transform: scale(0.9); box-shadow: 0 0 0 0 rgba(37, 99, 235, 0); } 
  } 
  .pulse-icon { 
    background-color: #2563eb; width: 20px; height: 20px; border-radius: 50%; 
    border: 3px solid white; box-shadow: 0 0 0 rgba(37, 99, 235, 0.4); animation: pulse 2s infinite; 
  } 
  .nav-marker { 
    display: flex; align-items: center; justify-content: center; 
    border-radius: 9999px; box-shadow: 0 2px 5px rgba(0,0,0,0.3); 
  } 
  .end-marker { background: #10b981; }
`;
document.head.append(style);
